"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
const assertRecord = (valueDecoder) => (x) => {
  if (typeof x !== "object" || x === null || Array.isArray(x)) {
    throw new Error(`Expected object but got ${typeof x}`);
  }
  const result = {};
  for (const [key, value] of Object.entries(x)) {
    result[key] = valueDecoder(value);
  }
  return result;
};
const encodeRecord = (valueEncoder) => (x) => {
  const result = {};
  for (const [key, value] of Object.entries(x)) {
    result[key] = valueEncoder(value);
  }
  return result;
};
const assertString = (x) => {
  if (typeof x !== "string") {
    throw new Error(`Expected string but got ${typeof x}`);
  }
  return x;
};
const assertNumber = (x) => {
  if (typeof x !== "number") {
    throw new Error(`Expected number but got ${typeof x}`);
  }
  return x;
};
const assertInt64 = (x) => {
  if (typeof x === "string") return Number(x);
  if (typeof x === "number") return x;
  throw new Error(
    `Expected number or numeric string for int64 but got ${typeof x}`
  );
};
const assertBoolean = (x) => {
  if (typeof x !== "boolean") {
    throw new Error(`Expected boolean but got ${typeof x}`);
  }
  return x;
};
const decodeTimestamp = (x) => {
  if (typeof x !== "string") {
    throw new Error(`Expected string for timestamp but got ${typeof x}`);
  }
  return new Date(x);
};
const encodeTimestamp = (x) => x.toISOString();
const optionalDecoder = (decoder) => (x) => x !== void 0 && x !== null ? decoder(x) : null;
const optionalEncoder = (encoder) => (x) => x !== void 0 && x !== null ? encoder(x) : null;
const defaultingDecoder = (decoder, defaultValue) => (x) => x !== void 0 && x !== null ? decoder(x) : defaultValue;
export function decodeAccount(json) {
  return {
    name: assertString(json.name),
    payables_account: assertBoolean(json.payables_account),
    factor_account: assertBoolean(json.factor_account),
    receivables_account: assertBoolean(json.receivables_account),
    scraper_account: assertBoolean(json.scraper_account),
    tms: decodeTMSOption(json.tms),
    created_at: decodeTimestamp(json.created_at),
    updated_at: decodeTimestamp(json.updated_at),
    carrier_term: assertString(json.carrier_term)
  };
}
export function encodeAccount(value) {
  return {
    name: value.name,
    payables_account: value.payables_account,
    factor_account: value.factor_account,
    receivables_account: value.receivables_account,
    scraper_account: value.scraper_account,
    tms: encodeTMSOption(value.tms),
    created_at: encodeTimestamp(value.created_at),
    updated_at: encodeTimestamp(value.updated_at),
    carrier_term: value.carrier_term
  };
}
export function decodeAccountSettings(json) {
  return {
    invoice_date_strategy: assertString(json.invoice_date_strategy),
    auto_generate_factor_invoice_number_strategy: assertString(
      json.auto_generate_factor_invoice_number_strategy
    ),
    auto_generate_factor_invoice_numbers: assertString(
      json.auto_generate_factor_invoice_numbers
    ),
    carrier_invoice_amount_tolerance_over: assertString(
      json.carrier_invoice_amount_tolerance_over
    ),
    carrier_invoice_amount_tolerance_under: assertString(
      json.carrier_invoice_amount_tolerance_under
    ),
    carrier_invoice_amount_tolerance_unit: decodeToleranceUnit(
      json.carrier_invoice_amount_tolerance_unit
    ),
    carrier_invoice_amount_tolerance_line_item_type_id: optionalDecoder(
      assertNumber
    )(json.carrier_invoice_amount_tolerance_line_item_type_id),
    tms_line_item_types: (json.tms_line_item_types || []).map(
      decodeAccountSettings_TMSLineItemType
    ),
    carrier_days_to_pay: assertNumber(json.carrier_days_to_pay),
    quickpay_discount: assertNumber(json.quickpay_discount),
    quickpay_discount_disabled: assertBoolean(json.quickpay_discount_disabled),
    visible_tms_reference_field_labels: (json.visible_tms_reference_field_labels || []).map(assertString),
    annotation_auto_stamp_document_type: optionalDecoder(
      decodeUnresolvedDocumentType
    )(json.annotation_auto_stamp_document_type),
    broker_customer_portal_enabled: assertBoolean(
      json.broker_customer_portal_enabled
    ),
    default_email_per_invoice: assertBoolean(json.default_email_per_invoice),
    verifications_enabled: assertBoolean(json.verifications_enabled),
    custom_exception_subject_template: assertString(
      json.custom_exception_subject_template
    ),
    custom_invoice_exception_messages: (json.custom_invoice_exception_messages || []).map(assertString),
    payables_template_variables: (json.payables_template_variables || []).map(
      assertString
    ),
    limit_reports_to_admin: assertBoolean(json.limit_reports_to_admin),
    labels: (json.labels || []).map(decodeLabel),
    possible_carrier_labels: (json.possible_carrier_labels || []).map(
      decodeLabel
    ),
    categories: (json.categories || []).map(decodeAccountSettings_Category),
    auto_approve_payables: assertBoolean(json.auto_approve_payables),
    self_serve_auto_approve_setting: assertBoolean(
      json.self_serve_auto_approve_setting
    ),
    use_submission_workflow: assertBoolean(json.use_submission_workflow),
    use_both_submission_and_traditional_workflow: assertBoolean(
      json.use_both_submission_and_traditional_workflow
    ),
    display_automated_verifications_on_submission_workflow: assertBoolean(
      json.display_automated_verifications_on_submission_workflow
    ),
    disable_outgoing_emails_in_submission_workflow: assertBoolean(
      json.disable_outgoing_emails_in_submission_workflow
    ),
    uses_office_people_map: assertBoolean(json.uses_office_people_map),
    use_load_customer_labels: assertBoolean(json.use_load_customer_labels),
    check_client_or_debtor_on_bol: assertBoolean(
      json.check_client_or_debtor_on_bol
    ),
    manually_check_amount_matches_rate_con: assertBoolean(
      json.manually_check_amount_matches_rate_con
    ),
    manually_check_pickup_dropoff_matches: assertBoolean(
      json.manually_check_pickup_dropoff_matches
    ),
    manually_check_third_party_listed_on_bol: assertBoolean(
      json.manually_check_third_party_listed_on_bol
    ),
    factor_use_source_invoice_date: assertBoolean(
      json.factor_use_source_invoice_date
    ),
    show_invoice_date_input_in_submission_workflow: assertBoolean(
      json.show_invoice_date_input_in_submission_workflow
    ),
    show_group_movement_buttons_in_submission_workflow: assertBoolean(
      json.show_group_movement_buttons_in_submission_workflow
    ),
    allow_manual_submission_reassignment_in_submission_workflow: assertBoolean(
      json.allow_manual_submission_reassignment_in_submission_workflow
    ),
    load_customer_labels: (json.load_customer_labels || []).map(
      decodeAccountSettings_LoadCustomerLabel
    ),
    failed_cadence_transmission_recipients: assertString(
      json.failed_cadence_transmission_recipients
    ),
    valid_source_date_range: assertString(json.valid_source_date_range),
    show_cadence_debtor_info: assertBoolean(json.show_cadence_debtor_info),
    consider_noa_data_for_verifications: decodeConsiderNoaDataForVerifications(
      json.consider_noa_data_for_verifications
    ),
    allow_editing_carrier_payment_strategy: assertBoolean(
      json.allow_editing_carrier_payment_strategy
    ),
    carrier_days_to_pay_integration: assertBoolean(
      json.carrier_days_to_pay_integration
    ),
    attempt_to_auto_process_document_batches: assertBoolean(
      json.attempt_to_auto_process_document_batches
    ),
    able_to_generate_customer_invoices: assertBoolean(
      json.able_to_generate_customer_invoices
    ),
    carrier_documents_account: assertBoolean(json.carrier_documents_account),
    factor_client_portal_enabled: assertBoolean(
      json.factor_client_portal_enabled
    ),
    factor_use_labels_on_processed: assertBoolean(
      json.factor_use_labels_on_processed
    ),
    use_cadence_image_types: assertBoolean(json.use_cadence_image_types),
    verifications_workflow_enabled: assertBoolean(
      json.verifications_workflow_enabled
    ),
    verifications_debtor_confirmation_enabled: assertBoolean(
      json.verifications_debtor_confirmation_enabled
    ),
    verifications_workflow_use_cadence_notes: assertBoolean(
      json.verifications_workflow_use_cadence_notes
    ),
    verifications_workflow_send_rejected_invoices_to_tms: assertBoolean(
      json.verifications_workflow_send_rejected_invoices_to_tms
    ),
    verifications_workflow_enable_carrier_percentile_check: assertBoolean(
      json.verifications_workflow_enable_carrier_percentile_check
    ),
    verifications_workflow_debtor_batch_item_custom_states: (json.verifications_workflow_debtor_batch_item_custom_states || []).map(decodeVerificationsDebtorBatchItemCustomState),
    verifications_workflow_email_cc_recipients: optionalDecoder(assertString)(
      json.verifications_workflow_email_cc_recipients
    ),
    verifications_workflow_display_name: optionalDecoder(assertString)(
      json.verifications_workflow_display_name
    ),
    review_tab_enabled: assertBoolean(json.review_tab_enabled),
    review_tab_verify_mode_enabled: assertBoolean(
      json.review_tab_verify_mode_enabled
    ),
    rules_broker_required_references: (json.rules_broker_required_references || []).map(decodeReferenceRule),
    controlled_by_tms: decodeControlledByTMS(json.controlled_by_tms),
    feature_flags: decodeFeatureFlags(json.feature_flags),
    pod_requirement_policy: decodePODRequirementPolicy(
      json.pod_requirement_policy
    ),
    rules_broker_custom_exception_messages: (json.rules_broker_custom_exception_messages || []).map(decodeCustomExceptionMessage),
    display_cadence_funding_notes: assertBoolean(
      json.display_cadence_funding_notes
    ),
    client_notes_sidebar_display: decodeClientNotesSidebarDisplay(
      json.client_notes_sidebar_display
    ),
    client_notes_configuration: (json.client_notes_configuration || []).map(
      decodeAccountSettings_ClientNoteSetting
    ),
    allow_custom_exceptions: assertBoolean(json.allow_custom_exceptions),
    load_reference_variables: (json.load_reference_variables || []).map(
      assertString
    ),
    shipment_reference_variables: (json.shipment_reference_variables || []).map(
      assertString
    ),
    non_invoice_load_document_types: (json.non_invoice_load_document_types || []).map(decodeUnresolvedDocumentType),
    show_carrier_external_vendor_id: assertBoolean(
      json.show_carrier_external_vendor_id
    ),
    processor_support_email_address: optionalDecoder(assertString)(
      json.processor_support_email_address
    ),
    enable_pod_in_submission_workflow: assertBoolean(
      json.enable_pod_in_submission_workflow
    ),
    show_cadence_verification_instructions: assertBoolean(
      json.show_cadence_verification_instructions
    ),
    show_client_verification_percentages_on_review_tab: assertBoolean(
      json.show_client_verification_percentages_on_review_tab
    ),
    per_page_on_dashboard: assertNumber(json.per_page_on_dashboard),
    line_item_visibility: assertString(json.line_item_visibility),
    use_triumph_pay_carrier_remit_to: assertBoolean(
      json.use_triumph_pay_carrier_remit_to
    ),
    payments_network_enabled: assertBoolean(json.payments_network_enabled),
    index_audit_split_enabled: assertBoolean(json.index_audit_split_enabled),
    feature_enable_processed_queue_flow_mode: assertBoolean(
      json.feature_enable_processed_queue_flow_mode
    ),
    ignore_mode: decodeIgnoreMode(json.ignore_mode),
    document_annotation_enabled: assertBoolean(
      json.document_annotation_enabled
    ),
    annotation_stamp_url: optionalDecoder(assertString)(
      json.annotation_stamp_url
    ),
    annotation_stamp_width: optionalDecoder(assertNumber)(
      json.annotation_stamp_width
    ),
    annotation_stamp_height: optionalDecoder(assertNumber)(
      json.annotation_stamp_height
    ),
    feature_enable_non_invoice_flow: assertBoolean(
      json.feature_enable_non_invoice_flow
    ),
    show_date_received_on_exception_and_waiting: assertBoolean(
      json.show_date_received_on_exception_and_waiting
    ),
    show_date_delivered_on_queues: assertBoolean(
      json.show_date_delivered_on_queues
    ),
    audit_billing_enabled: assertBoolean(json.audit_billing_enabled),
    customer_display_requires_external_id: assertBoolean(
      json.customer_display_requires_external_id
    ),
    custom_document_type_names: assertRecord(assertString)(
      json.custom_document_type_names
    ),
    factor_auto_send_invoices: assertBoolean(json.factor_auto_send_invoices),
    show_categorized_labels: assertBoolean(json.show_categorized_labels),
    api_rate_confirmation_required: assertBoolean(
      json.api_rate_confirmation_required
    ),
    customer_representative_view_enabled: assertBoolean(
      json.customer_representative_view_enabled
    ),
    disable_reorder_tms_reference_fields: assertBoolean(
      json.disable_reorder_tms_reference_fields
    ),
    saved_filter_enhancements_enabled: assertBoolean(
      json.saved_filter_enhancements_enabled
    ),
    documents_viewer_doctype_grouping_enabled: assertBoolean(
      json.documents_viewer_doctype_grouping_enabled
    ),
    ltl_reconcile_enabled: assertBoolean(json.ltl_reconcile_enabled),
    show_date_time_filters: assertBoolean(json.show_date_time_filters),
    ltl_invoice_available_tms_queues: (json.ltl_invoice_available_tms_queues || []).map(assertString),
    ltl_rerate_enabled: assertBoolean(json.ltl_rerate_enabled),
    ltl_reweigh_charge_exceptions_enabled: assertBoolean(
      json.ltl_reweigh_charge_exceptions_enabled
    ),
    ltl_accessorial_update_enabled: assertBoolean(
      json.ltl_accessorial_update_enabled
    ),
    ltl_loads_report_enabled: assertBoolean(json.ltl_loads_report_enabled),
    audit_v3_reskin_enabled_settings: assertBoolean(
      json.audit_v3_reskin_enabled_settings
    ),
    audit_v3_reskin_enabled_top_level: assertBoolean(
      json.audit_v3_reskin_enabled_top_level
    ),
    audit_v3_reskin_enabled_queues: assertBoolean(
      json.audit_v3_reskin_enabled_queues
    ),
    audit_v3_react_style_overrides: assertBoolean(
      json.audit_v3_react_style_overrides
    ),
    factor_next_gen_audit_enabled: assertBoolean(
      json.factor_next_gen_audit_enabled
    ),
    use_factor_invoice_workflow_page: assertBoolean(
      json.use_factor_invoice_workflow_page
    ),
    warn_if_amount_over: optionalDecoder(assertNumber)(
      json.warn_if_amount_over
    ),
    auditor_document_upload_enabled: assertBoolean(
      json.auditor_document_upload_enabled
    ),
    manual_review_percentage: assertNumber(json.manual_review_percentage),
    ltl_hold_edi_import_days: assertNumber(json.ltl_hold_edi_import_days),
    show_all_load_docs_enabled: assertBoolean(json.show_all_load_docs_enabled),
    scraper_status_enabled: assertBoolean(json.scraper_status_enabled),
    ltl_tolerances_enabled: assertBoolean(json.ltl_tolerances_enabled),
    ltl_carrier_invoice_amount_tolerance_over: assertString(
      json.ltl_carrier_invoice_amount_tolerance_over
    ),
    ltl_carrier_invoice_amount_tolerance_under: assertString(
      json.ltl_carrier_invoice_amount_tolerance_under
    ),
    ltl_carrier_invoice_amount_tolerance_unit: decodeToleranceUnit(
      json.ltl_carrier_invoice_amount_tolerance_unit
    ),
    multiple_custom_exception_templates: assertBoolean(
      json.multiple_custom_exception_templates
    ),
    ltl_reconcile_page_mismatch_table_enabled: assertBoolean(
      json.ltl_reconcile_page_mismatch_table_enabled
    ),
    ltl_rerate_blocked_for_hazardous_material: assertBoolean(
      json.ltl_rerate_blocked_for_hazardous_material
    ),
    run_no_documents_have_damages_or_rates_rule: assertBoolean(
      json.run_no_documents_have_damages_or_rates_rule
    ),
    run_no_documents_have_carrier_rates_rule: assertBoolean(
      json.run_no_documents_have_carrier_rates_rule
    ),
    ltl_customer_margin_tolerance_enabled: assertBoolean(
      json.ltl_customer_margin_tolerance_enabled
    ),
    ltl_default_margin_tolerance_unit: decodeToleranceUnit(
      json.ltl_default_margin_tolerance_unit
    ),
    ltl_default_margin_tolerance_minimum: optionalDecoder(assertString)(
      json.ltl_default_margin_tolerance_minimum
    ),
    ltl_default_margin_tolerance_maximum: optionalDecoder(assertString)(
      json.ltl_default_margin_tolerance_maximum
    ),
    display_customer_margin_in_header: assertBoolean(
      json.display_customer_margin_in_header
    ),
    nga_rules: optionalDecoder(decodeNgaRulesResponse)(json.nga_rules),
    require_load_status: (json.require_load_status || []).map(assertString),
    display_reference_in_header: assertBoolean(
      json.display_reference_in_header
    ),
    load_display_reference_label: assertString(
      json.load_display_reference_label
    ),
    load_display_reference_key: assertString(json.load_display_reference_key),
    allow_readonly_factor_user_selection: assertBoolean(
      json.allow_readonly_factor_user_selection
    ),
    document_type_for_truck_photos: decodeDocumentType(
      json.document_type_for_truck_photos
    ),
    carrier_tolerance_enabled_modes: (json.carrier_tolerance_enabled_modes || []).map(assertString),
    can_unapprove_invoices: assertBoolean(json.can_unapprove_invoices),
    noa_management_enabled: assertBoolean(json.noa_management_enabled),
    send_missing_documents_emails_enabled: assertBoolean(
      json.send_missing_documents_emails_enabled
    ),
    send_missing_documents_email_delay_in_hours: assertNumber(
      json.send_missing_documents_email_delay_in_hours
    ),
    factor_verification_exception_types: (json.factor_verification_exception_types || []).map(assertString),
    enable_per_relationship_autoprocessing_overrides: assertBoolean(
      json.enable_per_relationship_autoprocessing_overrides
    ),
    factor_broker_load_id_regression_rule_mode: assertString(
      json.factor_broker_load_id_regression_rule_mode
    ),
    lane_rate_exceptions_mode: assertString(json.lane_rate_exceptions_mode),
    invoice_amount_history_rule_mode: assertString(
      json.invoice_amount_history_rule_mode
    ),
    allow_implicit_carrier_invoice: assertBoolean(
      json.allow_implicit_carrier_invoice
    ),
    temp_allow_carrier_level_implicit_invoice: assertBoolean(
      json.temp_allow_carrier_level_implicit_invoice
    ),
    pod_requires_bol_validation: assertBoolean(
      json.pod_requires_bol_validation
    ),
    pod_definition_module_enabled: assertBoolean(
      json.pod_definition_module_enabled
    ),
    pod_inference_check_types: (json.pod_inference_check_types || []).map(
      decodeDocumentPODInferenceCheckType
    ),
    use_elm_document_grouping: assertBoolean(json.use_elm_document_grouping),
    pod_definition_reference_match_enabled: assertBoolean(
      json.pod_definition_reference_match_enabled
    ),
    pod_definition_expanded_reference_match_ui_enabled: assertBoolean(
      json.pod_definition_expanded_reference_match_ui_enabled
    ),
    pod_definition_customer_edit_enabled: assertBoolean(
      json.pod_definition_customer_edit_enabled
    ),
    conditional_exception_rules: (json.conditional_exception_rules || []).map(
      decodeConditionalExceptionRule
    ),
    load_id_strategies: (json.load_id_strategies || []).map(
      decodeLoadIdStrategy
    ),
    auto_index_nidf_documents_to_load: assertBoolean(
      json.auto_index_nidf_documents_to_load
    ),
    send_missing_documents_email_total_count: assertNumber(
      json.send_missing_documents_email_total_count
    ),
    send_missing_documents_email_interval_in_days: assertNumber(
      json.send_missing_documents_email_interval_in_days
    ),
    send_missing_documents_email_cadence_ui_enabled: assertBoolean(
      json.send_missing_documents_email_cadence_ui_enabled
    ),
    use_carrier_invoice_workflow_page: assertBoolean(
      json.use_carrier_invoice_workflow_page
    )
  };
}
export function encodeAccountSettings(value) {
  return {
    invoice_date_strategy: value.invoice_date_strategy,
    auto_generate_factor_invoice_number_strategy: value.auto_generate_factor_invoice_number_strategy,
    auto_generate_factor_invoice_numbers: value.auto_generate_factor_invoice_numbers,
    carrier_invoice_amount_tolerance_over: value.carrier_invoice_amount_tolerance_over,
    carrier_invoice_amount_tolerance_under: value.carrier_invoice_amount_tolerance_under,
    carrier_invoice_amount_tolerance_unit: encodeToleranceUnit(
      value.carrier_invoice_amount_tolerance_unit
    ),
    carrier_invoice_amount_tolerance_line_item_type_id: value.carrier_invoice_amount_tolerance_line_item_type_id,
    tms_line_item_types: value.tms_line_item_types.map(
      encodeAccountSettings_TMSLineItemType
    ),
    carrier_days_to_pay: value.carrier_days_to_pay,
    quickpay_discount: value.quickpay_discount,
    quickpay_discount_disabled: value.quickpay_discount_disabled,
    visible_tms_reference_field_labels: value.visible_tms_reference_field_labels,
    annotation_auto_stamp_document_type: optionalEncoder(
      encodeUnresolvedDocumentType
    )(value.annotation_auto_stamp_document_type),
    broker_customer_portal_enabled: value.broker_customer_portal_enabled,
    default_email_per_invoice: value.default_email_per_invoice,
    verifications_enabled: value.verifications_enabled,
    custom_exception_subject_template: value.custom_exception_subject_template,
    custom_invoice_exception_messages: value.custom_invoice_exception_messages,
    payables_template_variables: value.payables_template_variables,
    limit_reports_to_admin: value.limit_reports_to_admin,
    labels: value.labels.map(encodeLabel),
    possible_carrier_labels: value.possible_carrier_labels.map(encodeLabel),
    categories: value.categories.map(encodeAccountSettings_Category),
    auto_approve_payables: value.auto_approve_payables,
    self_serve_auto_approve_setting: value.self_serve_auto_approve_setting,
    use_submission_workflow: value.use_submission_workflow,
    use_both_submission_and_traditional_workflow: value.use_both_submission_and_traditional_workflow,
    display_automated_verifications_on_submission_workflow: value.display_automated_verifications_on_submission_workflow,
    disable_outgoing_emails_in_submission_workflow: value.disable_outgoing_emails_in_submission_workflow,
    uses_office_people_map: value.uses_office_people_map,
    use_load_customer_labels: value.use_load_customer_labels,
    check_client_or_debtor_on_bol: value.check_client_or_debtor_on_bol,
    manually_check_amount_matches_rate_con: value.manually_check_amount_matches_rate_con,
    manually_check_pickup_dropoff_matches: value.manually_check_pickup_dropoff_matches,
    manually_check_third_party_listed_on_bol: value.manually_check_third_party_listed_on_bol,
    factor_use_source_invoice_date: value.factor_use_source_invoice_date,
    show_invoice_date_input_in_submission_workflow: value.show_invoice_date_input_in_submission_workflow,
    show_group_movement_buttons_in_submission_workflow: value.show_group_movement_buttons_in_submission_workflow,
    allow_manual_submission_reassignment_in_submission_workflow: value.allow_manual_submission_reassignment_in_submission_workflow,
    load_customer_labels: value.load_customer_labels.map(
      encodeAccountSettings_LoadCustomerLabel
    ),
    failed_cadence_transmission_recipients: value.failed_cadence_transmission_recipients,
    valid_source_date_range: value.valid_source_date_range,
    show_cadence_debtor_info: value.show_cadence_debtor_info,
    consider_noa_data_for_verifications: encodeConsiderNoaDataForVerifications(
      value.consider_noa_data_for_verifications
    ),
    allow_editing_carrier_payment_strategy: value.allow_editing_carrier_payment_strategy,
    carrier_days_to_pay_integration: value.carrier_days_to_pay_integration,
    attempt_to_auto_process_document_batches: value.attempt_to_auto_process_document_batches,
    able_to_generate_customer_invoices: value.able_to_generate_customer_invoices,
    carrier_documents_account: value.carrier_documents_account,
    factor_client_portal_enabled: value.factor_client_portal_enabled,
    factor_use_labels_on_processed: value.factor_use_labels_on_processed,
    use_cadence_image_types: value.use_cadence_image_types,
    verifications_workflow_enabled: value.verifications_workflow_enabled,
    verifications_debtor_confirmation_enabled: value.verifications_debtor_confirmation_enabled,
    verifications_workflow_use_cadence_notes: value.verifications_workflow_use_cadence_notes,
    verifications_workflow_send_rejected_invoices_to_tms: value.verifications_workflow_send_rejected_invoices_to_tms,
    verifications_workflow_enable_carrier_percentile_check: value.verifications_workflow_enable_carrier_percentile_check,
    verifications_workflow_debtor_batch_item_custom_states: value.verifications_workflow_debtor_batch_item_custom_states.map(
      encodeVerificationsDebtorBatchItemCustomState
    ),
    verifications_workflow_email_cc_recipients: value.verifications_workflow_email_cc_recipients,
    verifications_workflow_display_name: value.verifications_workflow_display_name,
    review_tab_enabled: value.review_tab_enabled,
    review_tab_verify_mode_enabled: value.review_tab_verify_mode_enabled,
    rules_broker_required_references: value.rules_broker_required_references.map(encodeReferenceRule),
    controlled_by_tms: encodeControlledByTMS(value.controlled_by_tms),
    feature_flags: encodeFeatureFlags(value.feature_flags),
    pod_requirement_policy: encodePODRequirementPolicy(
      value.pod_requirement_policy
    ),
    rules_broker_custom_exception_messages: value.rules_broker_custom_exception_messages.map(
      encodeCustomExceptionMessage
    ),
    display_cadence_funding_notes: value.display_cadence_funding_notes,
    client_notes_sidebar_display: encodeClientNotesSidebarDisplay(
      value.client_notes_sidebar_display
    ),
    client_notes_configuration: value.client_notes_configuration.map(
      encodeAccountSettings_ClientNoteSetting
    ),
    allow_custom_exceptions: value.allow_custom_exceptions,
    load_reference_variables: value.load_reference_variables,
    shipment_reference_variables: value.shipment_reference_variables,
    non_invoice_load_document_types: value.non_invoice_load_document_types.map(
      encodeUnresolvedDocumentType
    ),
    show_carrier_external_vendor_id: value.show_carrier_external_vendor_id,
    processor_support_email_address: value.processor_support_email_address,
    enable_pod_in_submission_workflow: value.enable_pod_in_submission_workflow,
    show_cadence_verification_instructions: value.show_cadence_verification_instructions,
    show_client_verification_percentages_on_review_tab: value.show_client_verification_percentages_on_review_tab,
    per_page_on_dashboard: value.per_page_on_dashboard,
    line_item_visibility: value.line_item_visibility,
    use_triumph_pay_carrier_remit_to: value.use_triumph_pay_carrier_remit_to,
    payments_network_enabled: value.payments_network_enabled,
    index_audit_split_enabled: value.index_audit_split_enabled,
    feature_enable_processed_queue_flow_mode: value.feature_enable_processed_queue_flow_mode,
    ignore_mode: encodeIgnoreMode(value.ignore_mode),
    document_annotation_enabled: value.document_annotation_enabled,
    annotation_stamp_url: value.annotation_stamp_url,
    annotation_stamp_width: value.annotation_stamp_width,
    annotation_stamp_height: value.annotation_stamp_height,
    feature_enable_non_invoice_flow: value.feature_enable_non_invoice_flow,
    show_date_received_on_exception_and_waiting: value.show_date_received_on_exception_and_waiting,
    show_date_delivered_on_queues: value.show_date_delivered_on_queues,
    audit_billing_enabled: value.audit_billing_enabled,
    customer_display_requires_external_id: value.customer_display_requires_external_id,
    custom_document_type_names: value.custom_document_type_names,
    factor_auto_send_invoices: value.factor_auto_send_invoices,
    show_categorized_labels: value.show_categorized_labels,
    api_rate_confirmation_required: value.api_rate_confirmation_required,
    customer_representative_view_enabled: value.customer_representative_view_enabled,
    disable_reorder_tms_reference_fields: value.disable_reorder_tms_reference_fields,
    saved_filter_enhancements_enabled: value.saved_filter_enhancements_enabled,
    documents_viewer_doctype_grouping_enabled: value.documents_viewer_doctype_grouping_enabled,
    ltl_reconcile_enabled: value.ltl_reconcile_enabled,
    show_date_time_filters: value.show_date_time_filters,
    ltl_invoice_available_tms_queues: value.ltl_invoice_available_tms_queues,
    ltl_rerate_enabled: value.ltl_rerate_enabled,
    ltl_reweigh_charge_exceptions_enabled: value.ltl_reweigh_charge_exceptions_enabled,
    ltl_accessorial_update_enabled: value.ltl_accessorial_update_enabled,
    ltl_loads_report_enabled: value.ltl_loads_report_enabled,
    audit_v3_reskin_enabled_settings: value.audit_v3_reskin_enabled_settings,
    audit_v3_reskin_enabled_top_level: value.audit_v3_reskin_enabled_top_level,
    audit_v3_reskin_enabled_queues: value.audit_v3_reskin_enabled_queues,
    audit_v3_react_style_overrides: value.audit_v3_react_style_overrides,
    factor_next_gen_audit_enabled: value.factor_next_gen_audit_enabled,
    use_factor_invoice_workflow_page: value.use_factor_invoice_workflow_page,
    warn_if_amount_over: value.warn_if_amount_over,
    auditor_document_upload_enabled: value.auditor_document_upload_enabled,
    manual_review_percentage: value.manual_review_percentage,
    ltl_hold_edi_import_days: value.ltl_hold_edi_import_days,
    show_all_load_docs_enabled: value.show_all_load_docs_enabled,
    scraper_status_enabled: value.scraper_status_enabled,
    ltl_tolerances_enabled: value.ltl_tolerances_enabled,
    ltl_carrier_invoice_amount_tolerance_over: value.ltl_carrier_invoice_amount_tolerance_over,
    ltl_carrier_invoice_amount_tolerance_under: value.ltl_carrier_invoice_amount_tolerance_under,
    ltl_carrier_invoice_amount_tolerance_unit: encodeToleranceUnit(
      value.ltl_carrier_invoice_amount_tolerance_unit
    ),
    multiple_custom_exception_templates: value.multiple_custom_exception_templates,
    ltl_reconcile_page_mismatch_table_enabled: value.ltl_reconcile_page_mismatch_table_enabled,
    ltl_rerate_blocked_for_hazardous_material: value.ltl_rerate_blocked_for_hazardous_material,
    run_no_documents_have_damages_or_rates_rule: value.run_no_documents_have_damages_or_rates_rule,
    run_no_documents_have_carrier_rates_rule: value.run_no_documents_have_carrier_rates_rule,
    ltl_customer_margin_tolerance_enabled: value.ltl_customer_margin_tolerance_enabled,
    ltl_default_margin_tolerance_unit: encodeToleranceUnit(
      value.ltl_default_margin_tolerance_unit
    ),
    ltl_default_margin_tolerance_minimum: value.ltl_default_margin_tolerance_minimum,
    ltl_default_margin_tolerance_maximum: value.ltl_default_margin_tolerance_maximum,
    display_customer_margin_in_header: value.display_customer_margin_in_header,
    nga_rules: optionalEncoder(encodeNgaRulesResponse)(value.nga_rules),
    require_load_status: value.require_load_status,
    display_reference_in_header: value.display_reference_in_header,
    load_display_reference_label: value.load_display_reference_label,
    load_display_reference_key: value.load_display_reference_key,
    allow_readonly_factor_user_selection: value.allow_readonly_factor_user_selection,
    document_type_for_truck_photos: encodeDocumentType(
      value.document_type_for_truck_photos
    ),
    carrier_tolerance_enabled_modes: value.carrier_tolerance_enabled_modes,
    can_unapprove_invoices: value.can_unapprove_invoices,
    noa_management_enabled: value.noa_management_enabled,
    send_missing_documents_emails_enabled: value.send_missing_documents_emails_enabled,
    send_missing_documents_email_delay_in_hours: value.send_missing_documents_email_delay_in_hours,
    factor_verification_exception_types: value.factor_verification_exception_types,
    enable_per_relationship_autoprocessing_overrides: value.enable_per_relationship_autoprocessing_overrides,
    factor_broker_load_id_regression_rule_mode: value.factor_broker_load_id_regression_rule_mode,
    lane_rate_exceptions_mode: value.lane_rate_exceptions_mode,
    invoice_amount_history_rule_mode: value.invoice_amount_history_rule_mode,
    allow_implicit_carrier_invoice: value.allow_implicit_carrier_invoice,
    temp_allow_carrier_level_implicit_invoice: value.temp_allow_carrier_level_implicit_invoice,
    pod_requires_bol_validation: value.pod_requires_bol_validation,
    pod_definition_module_enabled: value.pod_definition_module_enabled,
    pod_inference_check_types: value.pod_inference_check_types.map(
      encodeDocumentPODInferenceCheckType
    ),
    use_elm_document_grouping: value.use_elm_document_grouping,
    pod_definition_reference_match_enabled: value.pod_definition_reference_match_enabled,
    pod_definition_expanded_reference_match_ui_enabled: value.pod_definition_expanded_reference_match_ui_enabled,
    pod_definition_customer_edit_enabled: value.pod_definition_customer_edit_enabled,
    conditional_exception_rules: value.conditional_exception_rules.map(
      encodeConditionalExceptionRule
    ),
    load_id_strategies: value.load_id_strategies.map(encodeLoadIdStrategy),
    auto_index_nidf_documents_to_load: value.auto_index_nidf_documents_to_load,
    send_missing_documents_email_total_count: value.send_missing_documents_email_total_count,
    send_missing_documents_email_interval_in_days: value.send_missing_documents_email_interval_in_days,
    send_missing_documents_email_cadence_ui_enabled: value.send_missing_documents_email_cadence_ui_enabled,
    use_carrier_invoice_workflow_page: value.use_carrier_invoice_workflow_page
  };
}
export function decodeAccountSettings_Category(json) {
  return {
    name: assertString(json.name),
    labels: (json.labels || []).map(assertString)
  };
}
export function encodeAccountSettings_Category(value) {
  return {
    name: value.name,
    labels: value.labels
  };
}
export function decodeAccountSettings_ClientNoteSetting(json) {
  return {
    note_type: assertString(json.note_type),
    display: assertString(json.display)
  };
}
export function encodeAccountSettings_ClientNoteSetting(value) {
  return {
    note_type: value.note_type,
    display: value.display
  };
}
export function decodeAccountSettings_LoadCustomerLabel(json) {
  return {
    name: assertString(json.name),
    source: assertString(json.source),
    customer_ids: (json.customer_ids || []).map(assertNumber)
  };
}
export function encodeAccountSettings_LoadCustomerLabel(value) {
  return {
    name: value.name,
    source: value.source,
    customer_ids: value.customer_ids
  };
}
export function decodeAccountSettings_TMSLineItemType(json) {
  return {
    description: assertString(json.description),
    id: assertNumber(json.id)
  };
}
export function encodeAccountSettings_TMSLineItemType(value) {
  return {
    description: value.description,
    id: value.id
  };
}
export function decodeActivityItem(json) {
  return {
    id: assertInt64(json.id),
    user_name: optionalDecoder(assertString)(json.user_name),
    created_at: decodeTimestamp(json.created_at),
    updated_at: decodeTimestamp(json.updated_at),
    automated_activity_display_name: assertString(
      json.automated_activity_display_name
    ),
    details: decodeActivityItemDetails(json.details),
    user_name_or_email: optionalDecoder(assertString)(json.user_name_or_email),
    owner: decodeActivityItem_Owner(json)
  };
}
export function encodeActivityItem(value) {
  return __spreadValues({
    id: value.id,
    user_name: value.user_name,
    created_at: encodeTimestamp(value.created_at),
    updated_at: encodeTimestamp(value.updated_at),
    automated_activity_display_name: value.automated_activity_display_name,
    details: encodeActivityItemDetails(value.details),
    user_name_or_email: value.user_name_or_email
  }, encodeActivityItem_Owner(value.owner));
}
export var ActivityItem_OwnerType = /* @__PURE__ */ ((ActivityItem_OwnerType2) => {
  ActivityItem_OwnerType2["CarrierInvoice"] = "carrier_invoice";
  ActivityItem_OwnerType2["DocumentBatch"] = "document_batch";
  ActivityItem_OwnerType2["DocumentGroup"] = "document_group";
  ActivityItem_OwnerType2["Document"] = "document";
  ActivityItem_OwnerType2["EmailMessage"] = "email_message";
  ActivityItem_OwnerType2["FactorInvoice"] = "factor_invoice";
  ActivityItem_OwnerType2["TMSCarrier"] = "tms_carrier";
  ActivityItem_OwnerType2["LoadState"] = "load_state";
  ActivityItem_OwnerType2["DocumentPage"] = "document_page";
  ActivityItem_OwnerType2["TMSLoadShipment"] = "tms_load_shipment";
  return ActivityItem_OwnerType2;
})(ActivityItem_OwnerType || {});
export function decodeActivityItem_Owner(json) {
  if (json.carrier_invoice !== void 0) {
    return {
      type: "carrier_invoice" /* CarrierInvoice */,
      carrier_invoice: decodeActivityItem_CarrierInvoiceOwner(
        json.carrier_invoice
      )
    };
  } else if (json.document_batch !== void 0) {
    return {
      type: "document_batch" /* DocumentBatch */,
      document_batch: decodeActivityItem_DocumentBatchOwner(
        json.document_batch
      )
    };
  } else if (json.document_group !== void 0) {
    return {
      type: "document_group" /* DocumentGroup */,
      document_group: decodeActivityItem_DocumentGroupOwner(
        json.document_group
      )
    };
  } else if (json.document !== void 0) {
    return {
      type: "document" /* Document */,
      document: decodeActivityItem_DocumentOwner(json.document)
    };
  } else if (json.email_message !== void 0) {
    return {
      type: "email_message" /* EmailMessage */,
      email_message: decodeActivityItem_EmailMessageOwner(json.email_message)
    };
  } else if (json.factor_invoice !== void 0) {
    return {
      type: "factor_invoice" /* FactorInvoice */,
      factor_invoice: decodeActivityItem_FactorInvoiceOwner(
        json.factor_invoice
      )
    };
  } else if (json.tms_carrier !== void 0) {
    return {
      type: "tms_carrier" /* TMSCarrier */,
      tms_carrier: decodeActivityItem_TMSCarrierOwner(json.tms_carrier)
    };
  } else if (json.load_state !== void 0) {
    return {
      type: "load_state" /* LoadState */,
      load_state: decodeActivityItem_LoadStateOwner(json.load_state)
    };
  } else if (json.document_page !== void 0) {
    return {
      type: "document_page" /* DocumentPage */,
      document_page: decodeActivityItem_DocumentPageOwner(json.document_page)
    };
  } else if (json.tms_load_shipment !== void 0) {
    return {
      type: "tms_load_shipment" /* TMSLoadShipment */,
      tms_load_shipment: decodeActivityItem_TMSLoadShipmentOwner(
        json.tms_load_shipment
      )
    };
  }
  throw new Error("No field set in oneof ActivityItem_Owner");
}
export function encodeActivityItem_Owner(value) {
  switch (value.type) {
    case "carrier_invoice" /* CarrierInvoice */:
      return {
        carrier_invoice: encodeActivityItem_CarrierInvoiceOwner(
          value.carrier_invoice
        )
      };
    case "document_batch" /* DocumentBatch */:
      return {
        document_batch: encodeActivityItem_DocumentBatchOwner(
          value.document_batch
        )
      };
    case "document_group" /* DocumentGroup */:
      return {
        document_group: encodeActivityItem_DocumentGroupOwner(
          value.document_group
        )
      };
    case "document" /* Document */:
      return { document: encodeActivityItem_DocumentOwner(value.document) };
    case "email_message" /* EmailMessage */:
      return {
        email_message: encodeActivityItem_EmailMessageOwner(
          value.email_message
        )
      };
    case "factor_invoice" /* FactorInvoice */:
      return {
        factor_invoice: encodeActivityItem_FactorInvoiceOwner(
          value.factor_invoice
        )
      };
    case "tms_carrier" /* TMSCarrier */:
      return {
        tms_carrier: encodeActivityItem_TMSCarrierOwner(value.tms_carrier)
      };
    case "load_state" /* LoadState */:
      return {
        load_state: encodeActivityItem_LoadStateOwner(value.load_state)
      };
    case "document_page" /* DocumentPage */:
      return {
        document_page: encodeActivityItem_DocumentPageOwner(
          value.document_page
        )
      };
    case "tms_load_shipment" /* TMSLoadShipment */:
      return {
        tms_load_shipment: encodeActivityItem_TMSLoadShipmentOwner(
          value.tms_load_shipment
        )
      };
    default:
      throw new Error("No field set in oneof ActivityItem_Owner");
  }
}
export function decodeActivityItem_CarrierInvoiceOwner(json) {
  return {
    id: assertInt64(json.id)
  };
}
export function encodeActivityItem_CarrierInvoiceOwner(value) {
  return {
    id: value.id
  };
}
export function decodeActivityItem_DocumentBatchOwner(json) {
  return {
    id: assertInt64(json.id)
  };
}
export function encodeActivityItem_DocumentBatchOwner(value) {
  return {
    id: value.id
  };
}
export function decodeActivityItem_DocumentGroupOwner(json) {
  return {
    id: assertInt64(json.id)
  };
}
export function encodeActivityItem_DocumentGroupOwner(value) {
  return {
    id: value.id
  };
}
export function decodeActivityItem_DocumentOwner(json) {
  return {
    id: assertInt64(json.id)
  };
}
export function encodeActivityItem_DocumentOwner(value) {
  return {
    id: value.id
  };
}
export function decodeActivityItem_DocumentPageOwner(json) {
  return {
    id: assertInt64(json.id)
  };
}
export function encodeActivityItem_DocumentPageOwner(value) {
  return {
    id: value.id
  };
}
export function decodeActivityItem_EmailMessageOwner(json) {
  return {
    id: assertInt64(json.id)
  };
}
export function encodeActivityItem_EmailMessageOwner(value) {
  return {
    id: value.id
  };
}
export function decodeActivityItem_FactorInvoiceOwner(json) {
  return {
    id: assertInt64(json.id)
  };
}
export function encodeActivityItem_FactorInvoiceOwner(value) {
  return {
    id: value.id
  };
}
export function decodeActivityItem_LoadStateOwner(json) {
  return {
    id: assertInt64(json.id)
  };
}
export function encodeActivityItem_LoadStateOwner(value) {
  return {
    id: value.id
  };
}
export function decodeActivityItem_TMSCarrierOwner(json) {
  return {
    id: assertInt64(json.id)
  };
}
export function encodeActivityItem_TMSCarrierOwner(value) {
  return {
    id: value.id
  };
}
export function decodeActivityItem_TMSLoadShipmentOwner(json) {
  return {
    id: assertInt64(json.id)
  };
}
export function encodeActivityItem_TMSLoadShipmentOwner(value) {
  return {
    id: value.id
  };
}
export var ActivityItemAccountsPayableActivityType = /* @__PURE__ */ ((ActivityItemAccountsPayableActivityType2) => {
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeApproval"] = "approval";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeCarrierPayableApproval"] = "carrier_payable_approval";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeStateChange"] = "state_change";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeNote"] = "note";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeDocumentAdded"] = "document_added";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeDocumentRemoved"] = "document_removed";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeDocumentsResendRequested"] = "documents_resend_requested";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeDocumentAssigned"] = "document_assigned";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeExternalCreation"] = "external_creation";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeSentToExternal"] = "sent_to_external";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypePause"] = "pause";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeProcessed"] = "processed";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeAutoProcessed"] = "auto_processed";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeException"] = "exception";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeInvoiceAbandoned"] = "invoice_abandoned";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeInvoiceUnapproved"] = "invoice_unapproved";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeInvoiceRevived"] = "invoice_revived";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeRuleDisabled"] = "rule_disabled";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeRuleEnabled"] = "rule_enabled";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeExceptionResolved"] = "exception_resolved";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypePaymentAddressChanged"] = "payment_address_changed";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeFactoringAssignment"] = "factoring_assignment";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeIgnore"] = "ignore";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeInvoiceMatched"] = "invoice_matched";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeRemovedFromGroup"] = "removed_from_group";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeTransmissionFailed"] = "transmission_failed";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeInvoiceRejected"] = "invoice_rejected";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeSentToCadence"] = "sent_to_cadence";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeVerifyCompleted"] = "verify_completed";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeVerifiedByCadence"] = "verified_by_cadence";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeForwardedEmail"] = "forwarded_email";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeDocSentToExternal"] = "doc_sent_to_external";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeEmailMergeTarget"] = "email_merge_target";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeEmailMergeSource"] = "email_merge_source";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeEmailIgnored"] = "email_ignored";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeEmailUnignored"] = "email_unignored";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeEmailCompleted"] = "email_completed";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeContactAdded"] = "contact_added";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeExceptionsClearedViaAPI"] = "exceptions_cleared_via_api";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeUpdatedFromExternal"] = "updated_from_external";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeAcertusShipmentApproved"] = "acertus_shipment_approved";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeAcertusShipmentRejected"] = "acertus_shipment_rejected";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeMarkedPurchasedByUser"] = "marked_purchased_by_user";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLoadDocumentsApproved"] = "load_documents_approved";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLoadDocumentsScraped"] = "load_documents_scraped";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLoadDocumentsAssigned"] = "load_documents_assigned";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLoadDocumentsSent"] = "load_documents_sent";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeBuiltLoadSentToTMS"] = "built_load_sent_to_tms";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeBuiltLoadConfirmed"] = "built_load_confirmed";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypePODRequestSentToTextLocate"] = "pod_request_sent_to_text_locate";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeSentToVerifyQueue"] = "sent_to_verify_queue";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeBypassedVerifyQueue"] = "bypassed_verify_queue";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeVerifierChangedInvoiceAmount"] = "verifier_changed_invoice_amount";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeMarkedNotReadyForVerification"] = "marked_not_ready_for_verification";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeSendNoteToPayeeViaPaymentsNetwork"] = "send_note_to_payee_via_payments_network";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeDocsSentOnException"] = "docs_sent_on_exception";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeInvoiceDocumentsForwardedToTriumph"] = "invoice_documents_forwarded_to_triumph";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeReceivedInvoice"] = "received_invoice";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeTransmissionReceived"] = "transmission_received";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeTransmissionSucceeded"] = "transmission_succeeded";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLoadShipmentApproval"] = "load_shipment_approval";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLoadShipmentUnapproval"] = "load_shipment_unapproval";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeClientOrDebtorOnBolChanged"] = "client_or_debtor_on_bol_changed";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeInvoiceExceptionEmailSent"] = "invoice_exception_email_sent";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLtlDisputeAdded"] = "ltl_dispute_added";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLtlAcceptAdded"] = "ltl_accept_added";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLtlMovedToTMSQueue"] = "ltl_moved_to_tms_queue";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLtlUpdatedEdiReceived"] = "ltl_updated_edi_received";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeHidden"] = "hidden";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeAddDocumentToLoadSuccess"] = "add_document_to_load_success";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeAddDocumentToLoadFailure"] = "add_document_to_load_failure";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeFlaggedForManualReview"] = "flagged_for_manual_review";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLtlRerateRequested"] = "ltl_rerate_requested";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeCarrierInvoiceRuleChangedViaTMS"] = "carrier_invoice_rule_changed_via_tms";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLtlRerateFailed"] = "ltl_rerate_failed";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLtlRerateCompleted"] = "ltl_rerate_completed";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeDocumentsMerged"] = "documents_merged";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeIndexingComplete"] = "indexing_complete";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeEmailZeroPages"] = "email_zero_pages";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeReadyForFMSPickup"] = "ready_for_fms_pickup";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeAcknowledgedByFMS"] = "acknowledged_by_fms";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeNonInvoiceLoadAssignment"] = "non_invoice_load_assignment";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeDuplicateEmailAutoCompleted"] = "duplicate_email_auto_completed";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeAutoCompletedWaitingExceptionsOnNewDocs"] = "auto_completed_waiting_exceptions_on_new_docs";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeDuplicateDocumentBatchAutoCompleted"] = "duplicate_document_batch_auto_completed";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeDuplicateDocumentAutoHidden"] = "duplicate_document_auto_hidden";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeDuplicateDocumentAutoMerged"] = "duplicate_document_auto_merged";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeLineItemsAutoReplaced"] = "line_items_auto_replaced";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeDebtorCorrected"] = "debtor_corrected";
  ActivityItemAccountsPayableActivityType2["ActivityItemAccountsPayableActivityTypeVerificationBatchStarted"] = "verification_batch_started";
  return ActivityItemAccountsPayableActivityType2;
})(ActivityItemAccountsPayableActivityType || {});
export const decodeActivityItemAccountsPayableActivityType = (value) => value;
export const encodeActivityItemAccountsPayableActivityType = (value) => value;
export function decodeActivityItemAddDocumentToLoadFailureData(json) {
  return {
    addDocumentToLoadUploadId: assertNumber(json.addDocumentToLoadUploadId),
    errorMessage: assertString(json.errorMessage)
  };
}
export function encodeActivityItemAddDocumentToLoadFailureData(value) {
  return {
    addDocumentToLoadUploadId: value.addDocumentToLoadUploadId,
    errorMessage: value.errorMessage
  };
}
export function decodeActivityItemAddDocumentToLoadSuccessData(json) {
  return {
    addDocumentToLoadUploadId: assertNumber(json.addDocumentToLoadUploadId),
    documents: (json.documents || []).map(
      decodeActivityItemAddDocumentToLoadSuccessData_Document
    )
  };
}
export function encodeActivityItemAddDocumentToLoadSuccessData(value) {
  return {
    addDocumentToLoadUploadId: value.addDocumentToLoadUploadId,
    documents: value.documents.map(
      encodeActivityItemAddDocumentToLoadSuccessData_Document
    )
  };
}
export function decodeActivityItemAddDocumentToLoadSuccessData_Document(json) {
  return {
    documentId: assertNumber(json.documentId),
    documentType: optionalDecoder(decodeDocumentType)(json.documentType)
  };
}
export function encodeActivityItemAddDocumentToLoadSuccessData_Document(value) {
  return {
    documentId: value.documentId,
    documentType: optionalEncoder(encodeDocumentType)(value.documentType)
  };
}
export function decodeActivityItemContextDataByCarrierInvoiceId(json) {
  return {
    load_id: assertString(json.load_id),
    carrier_name: optionalDecoder(assertString)(json.carrier_name),
    load_state_id: assertNumber(json.load_state_id)
  };
}
export function encodeActivityItemContextDataByCarrierInvoiceId(value) {
  return {
    load_id: value.load_id,
    carrier_name: value.carrier_name,
    load_state_id: value.load_state_id
  };
}
export function decodeActivityItemContextDataByDocumentBatchId(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id),
    email: optionalDecoder(
      decodeActivityItemContextDataByDocumentBatchId_EmailContext
    )(json.email),
    carrier_name: optionalDecoder(assertString)(json.carrier_name)
  };
}
export function encodeActivityItemContextDataByDocumentBatchId(value) {
  return {
    document_batch_id: value.document_batch_id,
    email: optionalEncoder(
      encodeActivityItemContextDataByDocumentBatchId_EmailContext
    )(value.email),
    carrier_name: value.carrier_name
  };
}
export function decodeActivityItemContextDataByDocumentBatchId_EmailContext(json) {
  return {
    id: assertNumber(json.id),
    subject: assertString(json.subject),
    document_batch_id: optionalDecoder(assertNumber)(json.document_batch_id),
    has_factor_client_invoice_submission: assertBoolean(
      json.has_factor_client_invoice_submission
    )
  };
}
export function encodeActivityItemContextDataByDocumentBatchId_EmailContext(value) {
  return {
    id: value.id,
    subject: value.subject,
    document_batch_id: value.document_batch_id,
    has_factor_client_invoice_submission: value.has_factor_client_invoice_submission
  };
}
export function decodeActivityItemContextDataByTMSLoadShipmentId(json) {
  return {
    load_id: assertString(json.load_id),
    customer_name: optionalDecoder(assertString)(json.customer_name),
    load_state_id: assertNumber(json.load_state_id)
  };
}
export function encodeActivityItemContextDataByTMSLoadShipmentId(value) {
  return {
    load_id: value.load_id,
    customer_name: value.customer_name,
    load_state_id: value.load_state_id
  };
}
export var ActivityItemCustomerInvoicingActivityType = /* @__PURE__ */ ((ActivityItemCustomerInvoicingActivityType2) => {
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceReceived"] = "customer_invoicing_invoice_received";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceSent"] = "customer_invoicing_invoice_sent";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceSentToMercuryGate"] = "customer_invoicing_invoice_sent_to_mercury_gate";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceResent"] = "customer_invoicing_invoice_resent";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceFailed"] = "customer_invoicing_invoice_failed";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceAlreadySent"] = "customer_invoicing_invoice_already_sent";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceReplaced"] = "customer_invoicing_invoice_replaced";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceArchived"] = "customer_invoicing_invoice_archived";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceViewed"] = "customer_invoicing_invoice_viewed";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceIgnored"] = "customer_invoicing_invoice_ignored";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceIgnoredAmount"] = "customer_invoicing_invoice_ignored_amount";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceIgnoredNoBill"] = "customer_invoicing_invoice_ignored_no_bill";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeBouncedEmailDelivery"] = "customer_invoicing_bounced_email_delivery";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeBounceIgnored"] = "customer_invoicing_bounce_ignored";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeOversizeAttachmentIgnored"] = "customer_invoicing_oversize_attachment_ignored";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeFpaLoginErrorIgnored"] = "customer_invoicing_fpa_login_error_ignored";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypePaperworkSubmissionUpdate"] = "customer_invoicing_paperwork_submission_update";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceInternalNoteCreated"] = "customer_invoicing_internal_note_created";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceSecondarySent"] = "customer_invoice_secondary_sent";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceBackupOnlySent"] = "customer_invoice_backup_only_sent";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceAPIListed"] = "customer_invoicing_invoice_api_listed";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceAPIAcknowledged"] = "customer_invoicing_invoice_api_acknowledged";
  ActivityItemCustomerInvoicingActivityType2["ActivityItemCustomerInvoicingActivityTypeInvoiceIgnoredNoDocuments"] = "customer_invoicing_invoice_ignored_no_documents";
  return ActivityItemCustomerInvoicingActivityType2;
})(ActivityItemCustomerInvoicingActivityType || {});
export const decodeActivityItemCustomerInvoicingActivityType = (value) => value;
export const encodeActivityItemCustomerInvoicingActivityType = (value) => value;
export function decodeActivityItemDataLtlAcceptAdded(json) {
  return {
    acceptItems: (json.acceptItems || []).map(
      decodeActivityItemDataLtlAcceptAdded_AcceptItem
    )
  };
}
export function encodeActivityItemDataLtlAcceptAdded(value) {
  return {
    acceptItems: value.acceptItems.map(
      encodeActivityItemDataLtlAcceptAdded_AcceptItem
    )
  };
}
export function decodeActivityItemDataLtlAcceptAdded_AcceptItem(json) {
  return {
    description: assertString(json.description),
    classChange: optionalDecoder(decodeActivityItemDataLtlAcceptAdded_Change)(
      json.classChange
    ),
    weightChange: optionalDecoder(decodeActivityItemDataLtlAcceptAdded_Change)(
      json.weightChange
    ),
    amountChange: optionalDecoder(decodeActivityItemDataLtlAcceptAdded_Change)(
      json.amountChange
    )
  };
}
export function encodeActivityItemDataLtlAcceptAdded_AcceptItem(value) {
  return {
    description: value.description,
    classChange: optionalEncoder(encodeActivityItemDataLtlAcceptAdded_Change)(
      value.classChange
    ),
    weightChange: optionalEncoder(encodeActivityItemDataLtlAcceptAdded_Change)(
      value.weightChange
    ),
    amountChange: optionalEncoder(encodeActivityItemDataLtlAcceptAdded_Change)(
      value.amountChange
    )
  };
}
export function decodeActivityItemDataLtlAcceptAdded_Change(json) {
  return {
    original: optionalDecoder(assertString)(json.original),
    updated: optionalDecoder(assertString)(json.updated)
  };
}
export function encodeActivityItemDataLtlAcceptAdded_Change(value) {
  return {
    original: value.original,
    updated: value.updated
  };
}
export function decodeActivityItemDetails(json) {
  return {
    details: decodeActivityItemDetails_Details(json)
  };
}
export function encodeActivityItemDetails(value) {
  return __spreadValues({}, encodeActivityItemDetails_Details(value.details));
}
export var ActivityItemDetails_DetailsType = /* @__PURE__ */ ((ActivityItemDetails_DetailsType2) => {
  ActivityItemDetails_DetailsType2["Approval"] = "approval";
  ActivityItemDetails_DetailsType2["Note"] = "note";
  ActivityItemDetails_DetailsType2["AcknowledgedByFMS"] = "acknowledged_by_fms";
  ActivityItemDetails_DetailsType2["AutoCompletedWaitingExceptionsOnNewDocs"] = "auto_completed_waiting_exceptions_on_new_docs";
  ActivityItemDetails_DetailsType2["BuiltLoadConfirmed"] = "built_load_confirmed";
  ActivityItemDetails_DetailsType2["BuiltLoadSentToTMS"] = "built_load_sent_to_tms";
  ActivityItemDetails_DetailsType2["BypassedVerifyQueue"] = "bypassed_verify_queue";
  ActivityItemDetails_DetailsType2["CarrierInvoiceRuleChangedViaTMS"] = "carrier_invoice_rule_changed_via_tms";
  ActivityItemDetails_DetailsType2["DocsSentOnException"] = "docs_sent_on_exception";
  ActivityItemDetails_DetailsType2["DocumentAssigned"] = "document_assigned";
  ActivityItemDetails_DetailsType2["DocumentsMerged"] = "documents_merged";
  ActivityItemDetails_DetailsType2["DocumentsResendRequested"] = "documents_resend_requested";
  ActivityItemDetails_DetailsType2["DuplicateDocumentAutoHidden"] = "duplicate_document_auto_hidden";
  ActivityItemDetails_DetailsType2["DuplicateDocumentAutoMerged"] = "duplicate_document_auto_merged";
  ActivityItemDetails_DetailsType2["EmailCompleted"] = "email_completed";
  ActivityItemDetails_DetailsType2["EmailUnignored"] = "email_unignored";
  ActivityItemDetails_DetailsType2["EmailZeroPages"] = "email_zero_pages";
  ActivityItemDetails_DetailsType2["SavedAsIncomplete"] = "saved_as_incomplete";
  ActivityItemDetails_DetailsType2["ExceptionsClearedViaAPI"] = "exceptions_cleared_via_api";
  ActivityItemDetails_DetailsType2["IndexingComplete"] = "indexing_complete";
  ActivityItemDetails_DetailsType2["InvoiceDocumentsForwardedToTriumph"] = "invoice_documents_forwarded_to_triumph";
  ActivityItemDetails_DetailsType2["InvoiceExceptionEmailSent"] = "invoice_exception_email_sent";
  ActivityItemDetails_DetailsType2["InvoiceMatched"] = "invoice_matched";
  ActivityItemDetails_DetailsType2["InvoiceRejected"] = "invoice_rejected";
  ActivityItemDetails_DetailsType2["InvoiceRevived"] = "invoice_revived";
  ActivityItemDetails_DetailsType2["InvoiceUnapproved"] = "invoice_unapproved";
  ActivityItemDetails_DetailsType2["LineItemsAutoReplaced"] = "line_items_auto_replaced";
  ActivityItemDetails_DetailsType2["LoadDocumentsApproved"] = "load_documents_approved";
  ActivityItemDetails_DetailsType2["LoadDocumentsAssigned"] = "load_documents_assigned";
  ActivityItemDetails_DetailsType2["LoadDocumentsScraped"] = "load_documents_scraped";
  ActivityItemDetails_DetailsType2["LoadDocumentsSent"] = "load_documents_sent";
  ActivityItemDetails_DetailsType2["LtlRerateCompleted"] = "ltl_rerate_completed";
  ActivityItemDetails_DetailsType2["LtlRerateRequested"] = "ltl_rerate_requested";
  ActivityItemDetails_DetailsType2["LtlUpdatedEdiReceived"] = "ltl_updated_edi_received";
  ActivityItemDetails_DetailsType2["MarkedNotReadyForVerification"] = "marked_not_ready_for_verification";
  ActivityItemDetails_DetailsType2["MarkedPurchasedByUser"] = "marked_purchased_by_user";
  ActivityItemDetails_DetailsType2["NonInvoiceLoadAssignment"] = "non_invoice_load_assignment";
  ActivityItemDetails_DetailsType2["PaymentAddressChanged"] = "payment_address_changed";
  ActivityItemDetails_DetailsType2["PODRequestSentToTextLocate"] = "pod_request_sent_to_text_locate";
  ActivityItemDetails_DetailsType2["ReadyForFMSPickup"] = "ready_for_fms_pickup";
  ActivityItemDetails_DetailsType2["ReceivedInvoice"] = "received_invoice";
  ActivityItemDetails_DetailsType2["RemovedFromGroup"] = "removed_from_group";
  ActivityItemDetails_DetailsType2["SentToVerifyQueue"] = "sent_to_verify_queue";
  ActivityItemDetails_DetailsType2["TransmissionReceived"] = "transmission_received";
  ActivityItemDetails_DetailsType2["TransmissionSucceeded"] = "transmission_succeeded";
  ActivityItemDetails_DetailsType2["VerificationBatchStarted"] = "verification_batch_started";
  ActivityItemDetails_DetailsType2["VerifiedByCadence"] = "verified_by_cadence";
  ActivityItemDetails_DetailsType2["AcertusShipmentApproved"] = "acertus_shipment_approved";
  ActivityItemDetails_DetailsType2["AcertusShipmentRejected"] = "acertus_shipment_rejected";
  ActivityItemDetails_DetailsType2["ClientOrDebtorOnBolChanged"] = "client_or_debtor_on_bol_changed";
  ActivityItemDetails_DetailsType2["ContactAdded"] = "contact_added";
  ActivityItemDetails_DetailsType2["DebtorCorrected"] = "debtor_corrected";
  ActivityItemDetails_DetailsType2["DuplicateDocumentBatchAutoCompleted"] = "duplicate_document_batch_auto_completed";
  ActivityItemDetails_DetailsType2["DuplicateEmailAutoCompleted"] = "duplicate_email_auto_completed";
  ActivityItemDetails_DetailsType2["EmailIgnored"] = "email_ignored";
  ActivityItemDetails_DetailsType2["ExceptionResolved"] = "exception_resolved";
  ActivityItemDetails_DetailsType2["Hidden"] = "hidden";
  ActivityItemDetails_DetailsType2["Ignore"] = "ignore";
  ActivityItemDetails_DetailsType2["InvoiceAbandoned"] = "invoice_abandoned";
  ActivityItemDetails_DetailsType2["LtlRerateFailed"] = "ltl_rerate_failed";
  ActivityItemDetails_DetailsType2["SendNoteToPayeeViaPaymentsNetwork"] = "send_note_to_payee_via_payments_network";
  ActivityItemDetails_DetailsType2["SentToCadence"] = "sent_to_cadence";
  ActivityItemDetails_DetailsType2["StateChange"] = "state_change";
  ActivityItemDetails_DetailsType2["TransmissionFailed"] = "transmission_failed";
  ActivityItemDetails_DetailsType2["UpdatedFromExternal"] = "updated_from_external";
  ActivityItemDetails_DetailsType2["VerifierChangedInvoiceAmount"] = "verifier_changed_invoice_amount";
  ActivityItemDetails_DetailsType2["VerifyCompleted"] = "verify_completed";
  ActivityItemDetails_DetailsType2["LtlAcceptAdded"] = "ltl_accept_added";
  ActivityItemDetails_DetailsType2["LtlDisputeAdded"] = "ltl_dispute_added";
  ActivityItemDetails_DetailsType2["LtlMovedToTMSQueue"] = "ltl_moved_to_tms_queue";
  ActivityItemDetails_DetailsType2["AddDocumentToLoadSuccess"] = "add_document_to_load_success";
  ActivityItemDetails_DetailsType2["AddDocumentToLoadFailure"] = "add_document_to_load_failure";
  ActivityItemDetails_DetailsType2["FlaggedForManualReview"] = "flagged_for_manual_review";
  ActivityItemDetails_DetailsType2["DocumentAdded"] = "document_added";
  ActivityItemDetails_DetailsType2["DocumentRemoved"] = "document_removed";
  ActivityItemDetails_DetailsType2["EmailMergeSource"] = "email_merge_source";
  ActivityItemDetails_DetailsType2["EmailMergeTarget"] = "email_merge_target";
  ActivityItemDetails_DetailsType2["ForwardedEmail"] = "forwarded_email";
  ActivityItemDetails_DetailsType2["LoadShipmentApproval"] = "load_shipment_approval";
  ActivityItemDetails_DetailsType2["LoadShipmentUnapproval"] = "load_shipment_unapproval";
  ActivityItemDetails_DetailsType2["Pause"] = "pause";
  ActivityItemDetails_DetailsType2["RuleDisabled"] = "rule_disabled";
  ActivityItemDetails_DetailsType2["RuleEnabled"] = "rule_enabled";
  ActivityItemDetails_DetailsType2["DocSentToExternal"] = "doc_sent_to_external";
  ActivityItemDetails_DetailsType2["ExternalCreation"] = "external_creation";
  ActivityItemDetails_DetailsType2["FactoringAssignment"] = "factoring_assignment";
  ActivityItemDetails_DetailsType2["Processed"] = "processed";
  ActivityItemDetails_DetailsType2["AutoProcessed"] = "auto_processed";
  ActivityItemDetails_DetailsType2["CarrierPayableApproval"] = "carrier_payable_approval";
  ActivityItemDetails_DetailsType2["SentToExternal"] = "sent_to_external";
  return ActivityItemDetails_DetailsType2;
})(ActivityItemDetails_DetailsType || {});
export function decodeActivityItemDetails_Details(json) {
  if (json.approval !== void 0) {
    return {
      type: "approval" /* Approval */,
      approval: decodeActivityItemDetails_Approval(json.approval)
    };
  } else if (json.note !== void 0) {
    return {
      type: "note" /* Note */,
      note: decodeActivityItemDetails_Note(json.note)
    };
  } else if (json.acknowledged_by_fms !== void 0) {
    return {
      type: "acknowledged_by_fms" /* AcknowledgedByFMS */,
      acknowledged_by_fms: decodeActivityItemDetails_AcknowledgedByFMS(
        json.acknowledged_by_fms
      )
    };
  } else if (json.auto_completed_waiting_exceptions_on_new_docs !== void 0) {
    return {
      type: "auto_completed_waiting_exceptions_on_new_docs" /* AutoCompletedWaitingExceptionsOnNewDocs */,
      auto_completed_waiting_exceptions_on_new_docs: decodeActivityItemDetails_AutoCompletedWaitingExceptionsOnNewDocs(
        json.auto_completed_waiting_exceptions_on_new_docs
      )
    };
  } else if (json.built_load_confirmed !== void 0) {
    return {
      type: "built_load_confirmed" /* BuiltLoadConfirmed */,
      built_load_confirmed: decodeActivityItemDetails_BuiltLoadConfirmed(
        json.built_load_confirmed
      )
    };
  } else if (json.built_load_sent_to_tms !== void 0) {
    return {
      type: "built_load_sent_to_tms" /* BuiltLoadSentToTMS */,
      built_load_sent_to_tms: decodeActivityItemDetails_BuiltLoadSentToTMS(
        json.built_load_sent_to_tms
      )
    };
  } else if (json.bypassed_verify_queue !== void 0) {
    return {
      type: "bypassed_verify_queue" /* BypassedVerifyQueue */,
      bypassed_verify_queue: decodeActivityItemDetails_BypassedVerifyQueue(
        json.bypassed_verify_queue
      )
    };
  } else if (json.carrier_invoice_rule_changed_via_tms !== void 0) {
    return {
      type: "carrier_invoice_rule_changed_via_tms" /* CarrierInvoiceRuleChangedViaTMS */,
      carrier_invoice_rule_changed_via_tms: decodeActivityItemDetails_CarrierInvoiceRuleChangedViaTMS(
        json.carrier_invoice_rule_changed_via_tms
      )
    };
  } else if (json.docs_sent_on_exception !== void 0) {
    return {
      type: "docs_sent_on_exception" /* DocsSentOnException */,
      docs_sent_on_exception: decodeActivityItemDetails_DocsSentOnException(
        json.docs_sent_on_exception
      )
    };
  } else if (json.document_assigned !== void 0) {
    return {
      type: "document_assigned" /* DocumentAssigned */,
      document_assigned: decodeActivityItemDetails_DocumentAssigned(
        json.document_assigned
      )
    };
  } else if (json.documents_merged !== void 0) {
    return {
      type: "documents_merged" /* DocumentsMerged */,
      documents_merged: decodeActivityItemDetails_DocumentsMerged(
        json.documents_merged
      )
    };
  } else if (json.documents_resend_requested !== void 0) {
    return {
      type: "documents_resend_requested" /* DocumentsResendRequested */,
      documents_resend_requested: decodeActivityItemDetails_DocumentsResendRequested(
        json.documents_resend_requested
      )
    };
  } else if (json.duplicate_document_auto_hidden !== void 0) {
    return {
      type: "duplicate_document_auto_hidden" /* DuplicateDocumentAutoHidden */,
      duplicate_document_auto_hidden: decodeActivityItemDetails_DuplicateDocumentAutoHidden(
        json.duplicate_document_auto_hidden
      )
    };
  } else if (json.duplicate_document_auto_merged !== void 0) {
    return {
      type: "duplicate_document_auto_merged" /* DuplicateDocumentAutoMerged */,
      duplicate_document_auto_merged: decodeActivityItemDetails_DuplicateDocumentAutoMerged(
        json.duplicate_document_auto_merged
      )
    };
  } else if (json.email_completed !== void 0) {
    return {
      type: "email_completed" /* EmailCompleted */,
      email_completed: decodeActivityItemDetails_EmailCompleted(
        json.email_completed
      )
    };
  } else if (json.email_unignored !== void 0) {
    return {
      type: "email_unignored" /* EmailUnignored */,
      email_unignored: decodeActivityItemDetails_EmailUnignored(
        json.email_unignored
      )
    };
  } else if (json.email_zero_pages !== void 0) {
    return {
      type: "email_zero_pages" /* EmailZeroPages */,
      email_zero_pages: decodeActivityItemDetails_EmailZeroPages(
        json.email_zero_pages
      )
    };
  } else if (json.saved_as_incomplete !== void 0) {
    return {
      type: "saved_as_incomplete" /* SavedAsIncomplete */,
      saved_as_incomplete: decodeActivityItemDetails_SavedAsIncomplete(
        json.saved_as_incomplete
      )
    };
  } else if (json.exceptions_cleared_via_api !== void 0) {
    return {
      type: "exceptions_cleared_via_api" /* ExceptionsClearedViaAPI */,
      exceptions_cleared_via_api: decodeActivityItemDetails_ExceptionsClearedViaAPI(
        json.exceptions_cleared_via_api
      )
    };
  } else if (json.indexing_complete !== void 0) {
    return {
      type: "indexing_complete" /* IndexingComplete */,
      indexing_complete: decodeActivityItemDetails_IndexingComplete(
        json.indexing_complete
      )
    };
  } else if (json.invoice_documents_forwarded_to_triumph !== void 0) {
    return {
      type: "invoice_documents_forwarded_to_triumph" /* InvoiceDocumentsForwardedToTriumph */,
      invoice_documents_forwarded_to_triumph: decodeActivityItemDetails_InvoiceDocumentsForwardedToTriumph(
        json.invoice_documents_forwarded_to_triumph
      )
    };
  } else if (json.invoice_exception_email_sent !== void 0) {
    return {
      type: "invoice_exception_email_sent" /* InvoiceExceptionEmailSent */,
      invoice_exception_email_sent: decodeActivityItemDetails_InvoiceExceptionEmailSent(
        json.invoice_exception_email_sent
      )
    };
  } else if (json.invoice_matched !== void 0) {
    return {
      type: "invoice_matched" /* InvoiceMatched */,
      invoice_matched: decodeActivityItemDetails_InvoiceMatched(
        json.invoice_matched
      )
    };
  } else if (json.invoice_rejected !== void 0) {
    return {
      type: "invoice_rejected" /* InvoiceRejected */,
      invoice_rejected: decodeActivityItemDetails_InvoiceRejected(
        json.invoice_rejected
      )
    };
  } else if (json.invoice_revived !== void 0) {
    return {
      type: "invoice_revived" /* InvoiceRevived */,
      invoice_revived: decodeActivityItemDetails_InvoiceRevived(
        json.invoice_revived
      )
    };
  } else if (json.invoice_unapproved !== void 0) {
    return {
      type: "invoice_unapproved" /* InvoiceUnapproved */,
      invoice_unapproved: decodeActivityItemDetails_InvoiceUnapproved(
        json.invoice_unapproved
      )
    };
  } else if (json.line_items_auto_replaced !== void 0) {
    return {
      type: "line_items_auto_replaced" /* LineItemsAutoReplaced */,
      line_items_auto_replaced: decodeActivityItemDetails_LineItemsAutoReplaced(
        json.line_items_auto_replaced
      )
    };
  } else if (json.load_documents_approved !== void 0) {
    return {
      type: "load_documents_approved" /* LoadDocumentsApproved */,
      load_documents_approved: decodeActivityItemDetails_LoadDocumentsApproved(
        json.load_documents_approved
      )
    };
  } else if (json.load_documents_assigned !== void 0) {
    return {
      type: "load_documents_assigned" /* LoadDocumentsAssigned */,
      load_documents_assigned: decodeActivityItemDetails_LoadDocumentsAssigned(
        json.load_documents_assigned
      )
    };
  } else if (json.load_documents_scraped !== void 0) {
    return {
      type: "load_documents_scraped" /* LoadDocumentsScraped */,
      load_documents_scraped: decodeActivityItemDetails_LoadDocumentsScraped(
        json.load_documents_scraped
      )
    };
  } else if (json.load_documents_sent !== void 0) {
    return {
      type: "load_documents_sent" /* LoadDocumentsSent */,
      load_documents_sent: decodeActivityItemDetails_LoadDocumentsSent(
        json.load_documents_sent
      )
    };
  } else if (json.ltl_rerate_completed !== void 0) {
    return {
      type: "ltl_rerate_completed" /* LtlRerateCompleted */,
      ltl_rerate_completed: decodeActivityItemDetails_LtlRerateCompleted(
        json.ltl_rerate_completed
      )
    };
  } else if (json.ltl_rerate_requested !== void 0) {
    return {
      type: "ltl_rerate_requested" /* LtlRerateRequested */,
      ltl_rerate_requested: decodeActivityItemDetails_LtlRerateRequested(
        json.ltl_rerate_requested
      )
    };
  } else if (json.ltl_updated_edi_received !== void 0) {
    return {
      type: "ltl_updated_edi_received" /* LtlUpdatedEdiReceived */,
      ltl_updated_edi_received: decodeActivityItemDetails_LtlUpdatedEdiReceived(
        json.ltl_updated_edi_received
      )
    };
  } else if (json.marked_not_ready_for_verification !== void 0) {
    return {
      type: "marked_not_ready_for_verification" /* MarkedNotReadyForVerification */,
      marked_not_ready_for_verification: decodeActivityItemDetails_MarkedNotReadyForVerification(
        json.marked_not_ready_for_verification
      )
    };
  } else if (json.marked_purchased_by_user !== void 0) {
    return {
      type: "marked_purchased_by_user" /* MarkedPurchasedByUser */,
      marked_purchased_by_user: decodeActivityItemDetails_MarkedPurchasedByUser(
        json.marked_purchased_by_user
      )
    };
  } else if (json.non_invoice_load_assignment !== void 0) {
    return {
      type: "non_invoice_load_assignment" /* NonInvoiceLoadAssignment */,
      non_invoice_load_assignment: decodeActivityItemDetails_NonInvoiceLoadAssignment(
        json.non_invoice_load_assignment
      )
    };
  } else if (json.payment_address_changed !== void 0) {
    return {
      type: "payment_address_changed" /* PaymentAddressChanged */,
      payment_address_changed: decodeActivityItemDetails_PaymentAddressChanged(
        json.payment_address_changed
      )
    };
  } else if (json.pod_request_sent_to_text_locate !== void 0) {
    return {
      type: "pod_request_sent_to_text_locate" /* PODRequestSentToTextLocate */,
      pod_request_sent_to_text_locate: decodeActivityItemDetails_PODRequestSentToTextLocate(
        json.pod_request_sent_to_text_locate
      )
    };
  } else if (json.ready_for_fms_pickup !== void 0) {
    return {
      type: "ready_for_fms_pickup" /* ReadyForFMSPickup */,
      ready_for_fms_pickup: decodeActivityItemDetails_ReadyForFMSPickup(
        json.ready_for_fms_pickup
      )
    };
  } else if (json.received_invoice !== void 0) {
    return {
      type: "received_invoice" /* ReceivedInvoice */,
      received_invoice: decodeActivityItemDetails_ReceivedInvoice(
        json.received_invoice
      )
    };
  } else if (json.removed_from_group !== void 0) {
    return {
      type: "removed_from_group" /* RemovedFromGroup */,
      removed_from_group: decodeActivityItemDetails_RemovedFromGroup(
        json.removed_from_group
      )
    };
  } else if (json.sent_to_verify_queue !== void 0) {
    return {
      type: "sent_to_verify_queue" /* SentToVerifyQueue */,
      sent_to_verify_queue: decodeActivityItemDetails_SentToVerifyQueue(
        json.sent_to_verify_queue
      )
    };
  } else if (json.transmission_received !== void 0) {
    return {
      type: "transmission_received" /* TransmissionReceived */,
      transmission_received: decodeActivityItemDetails_TransmissionReceived(
        json.transmission_received
      )
    };
  } else if (json.transmission_succeeded !== void 0) {
    return {
      type: "transmission_succeeded" /* TransmissionSucceeded */,
      transmission_succeeded: decodeActivityItemDetails_TransmissionSucceeded(
        json.transmission_succeeded
      )
    };
  } else if (json.verification_batch_started !== void 0) {
    return {
      type: "verification_batch_started" /* VerificationBatchStarted */,
      verification_batch_started: decodeActivityItemDetails_VerificationBatchStarted(
        json.verification_batch_started
      )
    };
  } else if (json.verified_by_cadence !== void 0) {
    return {
      type: "verified_by_cadence" /* VerifiedByCadence */,
      verified_by_cadence: decodeActivityItemDetails_VerifiedByCadence(
        json.verified_by_cadence
      )
    };
  } else if (json.acertus_shipment_approved !== void 0) {
    return {
      type: "acertus_shipment_approved" /* AcertusShipmentApproved */,
      acertus_shipment_approved: decodeActivityItemDetails_AcertusShipmentApproved(
        json.acertus_shipment_approved
      )
    };
  } else if (json.acertus_shipment_rejected !== void 0) {
    return {
      type: "acertus_shipment_rejected" /* AcertusShipmentRejected */,
      acertus_shipment_rejected: decodeActivityItemDetails_AcertusShipmentRejected(
        json.acertus_shipment_rejected
      )
    };
  } else if (json.client_or_debtor_on_bol_changed !== void 0) {
    return {
      type: "client_or_debtor_on_bol_changed" /* ClientOrDebtorOnBolChanged */,
      client_or_debtor_on_bol_changed: decodeActivityItemDetails_ClientOrDebtorOnBolChanged(
        json.client_or_debtor_on_bol_changed
      )
    };
  } else if (json.contact_added !== void 0) {
    return {
      type: "contact_added" /* ContactAdded */,
      contact_added: decodeActivityItemDetails_ContactAdded(json.contact_added)
    };
  } else if (json.debtor_corrected !== void 0) {
    return {
      type: "debtor_corrected" /* DebtorCorrected */,
      debtor_corrected: decodeActivityItemDetails_DebtorCorrected(
        json.debtor_corrected
      )
    };
  } else if (json.duplicate_document_batch_auto_completed !== void 0) {
    return {
      type: "duplicate_document_batch_auto_completed" /* DuplicateDocumentBatchAutoCompleted */,
      duplicate_document_batch_auto_completed: decodeActivityItemDetails_DuplicateDocumentBatchAutoCompleted(
        json.duplicate_document_batch_auto_completed
      )
    };
  } else if (json.duplicate_email_auto_completed !== void 0) {
    return {
      type: "duplicate_email_auto_completed" /* DuplicateEmailAutoCompleted */,
      duplicate_email_auto_completed: decodeActivityItemDetails_DuplicateEmailAutoCompleted(
        json.duplicate_email_auto_completed
      )
    };
  } else if (json.email_ignored !== void 0) {
    return {
      type: "email_ignored" /* EmailIgnored */,
      email_ignored: decodeActivityItemDetails_EmailIgnored(json.email_ignored)
    };
  } else if (json.exception_resolved !== void 0) {
    return {
      type: "exception_resolved" /* ExceptionResolved */,
      exception_resolved: decodeActivityItemDetails_ExceptionResolved(
        json.exception_resolved
      )
    };
  } else if (json.hidden !== void 0) {
    return {
      type: "hidden" /* Hidden */,
      hidden: decodeActivityItemDetails_Hidden(json.hidden)
    };
  } else if (json.ignore !== void 0) {
    return {
      type: "ignore" /* Ignore */,
      ignore: decodeActivityItemDetails_Ignore(json.ignore)
    };
  } else if (json.invoice_abandoned !== void 0) {
    return {
      type: "invoice_abandoned" /* InvoiceAbandoned */,
      invoice_abandoned: decodeActivityItemDetails_InvoiceAbandoned(
        json.invoice_abandoned
      )
    };
  } else if (json.ltl_rerate_failed !== void 0) {
    return {
      type: "ltl_rerate_failed" /* LtlRerateFailed */,
      ltl_rerate_failed: decodeActivityItemDetails_LtlRerateFailed(
        json.ltl_rerate_failed
      )
    };
  } else if (json.send_note_to_payee_via_payments_network !== void 0) {
    return {
      type: "send_note_to_payee_via_payments_network" /* SendNoteToPayeeViaPaymentsNetwork */,
      send_note_to_payee_via_payments_network: decodeActivityItemDetails_SendNoteToPayeeViaPaymentsNetwork(
        json.send_note_to_payee_via_payments_network
      )
    };
  } else if (json.sent_to_cadence !== void 0) {
    return {
      type: "sent_to_cadence" /* SentToCadence */,
      sent_to_cadence: decodeActivityItemDetails_SentToCadence(
        json.sent_to_cadence
      )
    };
  } else if (json.state_change !== void 0) {
    return {
      type: "state_change" /* StateChange */,
      state_change: decodeActivityItemDetails_StateChange(json.state_change)
    };
  } else if (json.transmission_failed !== void 0) {
    return {
      type: "transmission_failed" /* TransmissionFailed */,
      transmission_failed: decodeActivityItemDetails_TransmissionFailed(
        json.transmission_failed
      )
    };
  } else if (json.updated_from_external !== void 0) {
    return {
      type: "updated_from_external" /* UpdatedFromExternal */,
      updated_from_external: decodeActivityItemDetails_UpdatedFromExternal(
        json.updated_from_external
      )
    };
  } else if (json.verifier_changed_invoice_amount !== void 0) {
    return {
      type: "verifier_changed_invoice_amount" /* VerifierChangedInvoiceAmount */,
      verifier_changed_invoice_amount: decodeActivityItemDetails_VerifierChangedInvoiceAmount(
        json.verifier_changed_invoice_amount
      )
    };
  } else if (json.verify_completed !== void 0) {
    return {
      type: "verify_completed" /* VerifyCompleted */,
      verify_completed: decodeActivityItemDetails_VerifyCompleted(
        json.verify_completed
      )
    };
  } else if (json.ltl_accept_added !== void 0) {
    return {
      type: "ltl_accept_added" /* LtlAcceptAdded */,
      ltl_accept_added: decodeActivityItemDataLtlAcceptAdded(
        json.ltl_accept_added
      )
    };
  } else if (json.ltl_dispute_added !== void 0) {
    return {
      type: "ltl_dispute_added" /* LtlDisputeAdded */,
      ltl_dispute_added: decodeActivityItemLtlDisputeAddedData(
        json.ltl_dispute_added
      )
    };
  } else if (json.ltl_moved_to_tms_queue !== void 0) {
    return {
      type: "ltl_moved_to_tms_queue" /* LtlMovedToTMSQueue */,
      ltl_moved_to_tms_queue: decodeLtlInvoiceMovedToTMSQueueData(
        json.ltl_moved_to_tms_queue
      )
    };
  } else if (json.add_document_to_load_success !== void 0) {
    return {
      type: "add_document_to_load_success" /* AddDocumentToLoadSuccess */,
      add_document_to_load_success: decodeActivityItemAddDocumentToLoadSuccessData(
        json.add_document_to_load_success
      )
    };
  } else if (json.add_document_to_load_failure !== void 0) {
    return {
      type: "add_document_to_load_failure" /* AddDocumentToLoadFailure */,
      add_document_to_load_failure: decodeActivityItemAddDocumentToLoadFailureData(
        json.add_document_to_load_failure
      )
    };
  } else if (json.flagged_for_manual_review !== void 0) {
    return {
      type: "flagged_for_manual_review" /* FlaggedForManualReview */,
      flagged_for_manual_review: decodeActivityItemDetails_FlaggedForManualReview(
        json.flagged_for_manual_review
      )
    };
  } else if (json.document_added !== void 0) {
    return {
      type: "document_added" /* DocumentAdded */,
      document_added: decodeActivityItemDetails_DocumentAdded(
        json.document_added
      )
    };
  } else if (json.document_removed !== void 0) {
    return {
      type: "document_removed" /* DocumentRemoved */,
      document_removed: decodeActivityItemDetails_DocumentRemoved(
        json.document_removed
      )
    };
  } else if (json.email_merge_source !== void 0) {
    return {
      type: "email_merge_source" /* EmailMergeSource */,
      email_merge_source: decodeActivityItemDetails_EmailMergeSource(
        json.email_merge_source
      )
    };
  } else if (json.email_merge_target !== void 0) {
    return {
      type: "email_merge_target" /* EmailMergeTarget */,
      email_merge_target: decodeActivityItemDetails_EmailMergeTarget(
        json.email_merge_target
      )
    };
  } else if (json.forwarded_email !== void 0) {
    return {
      type: "forwarded_email" /* ForwardedEmail */,
      forwarded_email: decodeActivityItemDetails_ForwardedEmail(
        json.forwarded_email
      )
    };
  } else if (json.load_shipment_approval !== void 0) {
    return {
      type: "load_shipment_approval" /* LoadShipmentApproval */,
      load_shipment_approval: decodeActivityItemDetails_LoadShipmentApproval(
        json.load_shipment_approval
      )
    };
  } else if (json.load_shipment_unapproval !== void 0) {
    return {
      type: "load_shipment_unapproval" /* LoadShipmentUnapproval */,
      load_shipment_unapproval: decodeActivityItemDetails_LoadShipmentUnapproval(
        json.load_shipment_unapproval
      )
    };
  } else if (json.pause !== void 0) {
    return {
      type: "pause" /* Pause */,
      pause: decodeActivityItemDetails_Pause(json.pause)
    };
  } else if (json.rule_disabled !== void 0) {
    return {
      type: "rule_disabled" /* RuleDisabled */,
      rule_disabled: decodeActivityItemDetails_RuleDisabled(json.rule_disabled)
    };
  } else if (json.rule_enabled !== void 0) {
    return {
      type: "rule_enabled" /* RuleEnabled */,
      rule_enabled: decodeActivityItemDetails_RuleEnabled(json.rule_enabled)
    };
  } else if (json.doc_sent_to_external !== void 0) {
    return {
      type: "doc_sent_to_external" /* DocSentToExternal */,
      doc_sent_to_external: decodeActivityItemDetails_DocSentToExternal(
        json.doc_sent_to_external
      )
    };
  } else if (json.external_creation !== void 0) {
    return {
      type: "external_creation" /* ExternalCreation */,
      external_creation: decodeActivityItemDetails_ExternalCreation(
        json.external_creation
      )
    };
  } else if (json.factoring_assignment !== void 0) {
    return {
      type: "factoring_assignment" /* FactoringAssignment */,
      factoring_assignment: decodeActivityItemDetails_FactoringAssignment(
        json.factoring_assignment
      )
    };
  } else if (json.processed !== void 0) {
    return {
      type: "processed" /* Processed */,
      processed: decodeActivityItemDetails_Processed(json.processed)
    };
  } else if (json.auto_processed !== void 0) {
    return {
      type: "auto_processed" /* AutoProcessed */,
      auto_processed: decodeActivityItemDetails_FactorInvoiceDetails(
        json.auto_processed
      )
    };
  } else if (json.carrier_payable_approval !== void 0) {
    return {
      type: "carrier_payable_approval" /* CarrierPayableApproval */,
      carrier_payable_approval: decodeActivityItemDetails_CarrierInvoiceDetails(
        json.carrier_payable_approval
      )
    };
  } else if (json.sent_to_external !== void 0) {
    return {
      type: "sent_to_external" /* SentToExternal */,
      sent_to_external: decodeActivityItemDetails_SentToExternal(
        json.sent_to_external
      )
    };
  }
  throw new Error("No field set in oneof ActivityItemDetails_Details");
}
export function encodeActivityItemDetails_Details(value) {
  switch (value.type) {
    case "approval" /* Approval */:
      return { approval: encodeActivityItemDetails_Approval(value.approval) };
    case "note" /* Note */:
      return { note: encodeActivityItemDetails_Note(value.note) };
    case "acknowledged_by_fms" /* AcknowledgedByFMS */:
      return {
        acknowledged_by_fms: encodeActivityItemDetails_AcknowledgedByFMS(
          value.acknowledged_by_fms
        )
      };
    case "auto_completed_waiting_exceptions_on_new_docs" /* AutoCompletedWaitingExceptionsOnNewDocs */:
      return {
        auto_completed_waiting_exceptions_on_new_docs: encodeActivityItemDetails_AutoCompletedWaitingExceptionsOnNewDocs(
          value.auto_completed_waiting_exceptions_on_new_docs
        )
      };
    case "built_load_confirmed" /* BuiltLoadConfirmed */:
      return {
        built_load_confirmed: encodeActivityItemDetails_BuiltLoadConfirmed(
          value.built_load_confirmed
        )
      };
    case "built_load_sent_to_tms" /* BuiltLoadSentToTMS */:
      return {
        built_load_sent_to_tms: encodeActivityItemDetails_BuiltLoadSentToTMS(
          value.built_load_sent_to_tms
        )
      };
    case "bypassed_verify_queue" /* BypassedVerifyQueue */:
      return {
        bypassed_verify_queue: encodeActivityItemDetails_BypassedVerifyQueue(
          value.bypassed_verify_queue
        )
      };
    case "carrier_invoice_rule_changed_via_tms" /* CarrierInvoiceRuleChangedViaTMS */:
      return {
        carrier_invoice_rule_changed_via_tms: encodeActivityItemDetails_CarrierInvoiceRuleChangedViaTMS(
          value.carrier_invoice_rule_changed_via_tms
        )
      };
    case "docs_sent_on_exception" /* DocsSentOnException */:
      return {
        docs_sent_on_exception: encodeActivityItemDetails_DocsSentOnException(
          value.docs_sent_on_exception
        )
      };
    case "document_assigned" /* DocumentAssigned */:
      return {
        document_assigned: encodeActivityItemDetails_DocumentAssigned(
          value.document_assigned
        )
      };
    case "documents_merged" /* DocumentsMerged */:
      return {
        documents_merged: encodeActivityItemDetails_DocumentsMerged(
          value.documents_merged
        )
      };
    case "documents_resend_requested" /* DocumentsResendRequested */:
      return {
        documents_resend_requested: encodeActivityItemDetails_DocumentsResendRequested(
          value.documents_resend_requested
        )
      };
    case "duplicate_document_auto_hidden" /* DuplicateDocumentAutoHidden */:
      return {
        duplicate_document_auto_hidden: encodeActivityItemDetails_DuplicateDocumentAutoHidden(
          value.duplicate_document_auto_hidden
        )
      };
    case "duplicate_document_auto_merged" /* DuplicateDocumentAutoMerged */:
      return {
        duplicate_document_auto_merged: encodeActivityItemDetails_DuplicateDocumentAutoMerged(
          value.duplicate_document_auto_merged
        )
      };
    case "email_completed" /* EmailCompleted */:
      return {
        email_completed: encodeActivityItemDetails_EmailCompleted(
          value.email_completed
        )
      };
    case "email_unignored" /* EmailUnignored */:
      return {
        email_unignored: encodeActivityItemDetails_EmailUnignored(
          value.email_unignored
        )
      };
    case "email_zero_pages" /* EmailZeroPages */:
      return {
        email_zero_pages: encodeActivityItemDetails_EmailZeroPages(
          value.email_zero_pages
        )
      };
    case "saved_as_incomplete" /* SavedAsIncomplete */:
      return {
        saved_as_incomplete: encodeActivityItemDetails_SavedAsIncomplete(
          value.saved_as_incomplete
        )
      };
    case "exceptions_cleared_via_api" /* ExceptionsClearedViaAPI */:
      return {
        exceptions_cleared_via_api: encodeActivityItemDetails_ExceptionsClearedViaAPI(
          value.exceptions_cleared_via_api
        )
      };
    case "indexing_complete" /* IndexingComplete */:
      return {
        indexing_complete: encodeActivityItemDetails_IndexingComplete(
          value.indexing_complete
        )
      };
    case "invoice_documents_forwarded_to_triumph" /* InvoiceDocumentsForwardedToTriumph */:
      return {
        invoice_documents_forwarded_to_triumph: encodeActivityItemDetails_InvoiceDocumentsForwardedToTriumph(
          value.invoice_documents_forwarded_to_triumph
        )
      };
    case "invoice_exception_email_sent" /* InvoiceExceptionEmailSent */:
      return {
        invoice_exception_email_sent: encodeActivityItemDetails_InvoiceExceptionEmailSent(
          value.invoice_exception_email_sent
        )
      };
    case "invoice_matched" /* InvoiceMatched */:
      return {
        invoice_matched: encodeActivityItemDetails_InvoiceMatched(
          value.invoice_matched
        )
      };
    case "invoice_rejected" /* InvoiceRejected */:
      return {
        invoice_rejected: encodeActivityItemDetails_InvoiceRejected(
          value.invoice_rejected
        )
      };
    case "invoice_revived" /* InvoiceRevived */:
      return {
        invoice_revived: encodeActivityItemDetails_InvoiceRevived(
          value.invoice_revived
        )
      };
    case "invoice_unapproved" /* InvoiceUnapproved */:
      return {
        invoice_unapproved: encodeActivityItemDetails_InvoiceUnapproved(
          value.invoice_unapproved
        )
      };
    case "line_items_auto_replaced" /* LineItemsAutoReplaced */:
      return {
        line_items_auto_replaced: encodeActivityItemDetails_LineItemsAutoReplaced(
          value.line_items_auto_replaced
        )
      };
    case "load_documents_approved" /* LoadDocumentsApproved */:
      return {
        load_documents_approved: encodeActivityItemDetails_LoadDocumentsApproved(
          value.load_documents_approved
        )
      };
    case "load_documents_assigned" /* LoadDocumentsAssigned */:
      return {
        load_documents_assigned: encodeActivityItemDetails_LoadDocumentsAssigned(
          value.load_documents_assigned
        )
      };
    case "load_documents_scraped" /* LoadDocumentsScraped */:
      return {
        load_documents_scraped: encodeActivityItemDetails_LoadDocumentsScraped(
          value.load_documents_scraped
        )
      };
    case "load_documents_sent" /* LoadDocumentsSent */:
      return {
        load_documents_sent: encodeActivityItemDetails_LoadDocumentsSent(
          value.load_documents_sent
        )
      };
    case "ltl_rerate_completed" /* LtlRerateCompleted */:
      return {
        ltl_rerate_completed: encodeActivityItemDetails_LtlRerateCompleted(
          value.ltl_rerate_completed
        )
      };
    case "ltl_rerate_requested" /* LtlRerateRequested */:
      return {
        ltl_rerate_requested: encodeActivityItemDetails_LtlRerateRequested(
          value.ltl_rerate_requested
        )
      };
    case "ltl_updated_edi_received" /* LtlUpdatedEdiReceived */:
      return {
        ltl_updated_edi_received: encodeActivityItemDetails_LtlUpdatedEdiReceived(
          value.ltl_updated_edi_received
        )
      };
    case "marked_not_ready_for_verification" /* MarkedNotReadyForVerification */:
      return {
        marked_not_ready_for_verification: encodeActivityItemDetails_MarkedNotReadyForVerification(
          value.marked_not_ready_for_verification
        )
      };
    case "marked_purchased_by_user" /* MarkedPurchasedByUser */:
      return {
        marked_purchased_by_user: encodeActivityItemDetails_MarkedPurchasedByUser(
          value.marked_purchased_by_user
        )
      };
    case "non_invoice_load_assignment" /* NonInvoiceLoadAssignment */:
      return {
        non_invoice_load_assignment: encodeActivityItemDetails_NonInvoiceLoadAssignment(
          value.non_invoice_load_assignment
        )
      };
    case "payment_address_changed" /* PaymentAddressChanged */:
      return {
        payment_address_changed: encodeActivityItemDetails_PaymentAddressChanged(
          value.payment_address_changed
        )
      };
    case "pod_request_sent_to_text_locate" /* PODRequestSentToTextLocate */:
      return {
        pod_request_sent_to_text_locate: encodeActivityItemDetails_PODRequestSentToTextLocate(
          value.pod_request_sent_to_text_locate
        )
      };
    case "ready_for_fms_pickup" /* ReadyForFMSPickup */:
      return {
        ready_for_fms_pickup: encodeActivityItemDetails_ReadyForFMSPickup(
          value.ready_for_fms_pickup
        )
      };
    case "received_invoice" /* ReceivedInvoice */:
      return {
        received_invoice: encodeActivityItemDetails_ReceivedInvoice(
          value.received_invoice
        )
      };
    case "removed_from_group" /* RemovedFromGroup */:
      return {
        removed_from_group: encodeActivityItemDetails_RemovedFromGroup(
          value.removed_from_group
        )
      };
    case "sent_to_verify_queue" /* SentToVerifyQueue */:
      return {
        sent_to_verify_queue: encodeActivityItemDetails_SentToVerifyQueue(
          value.sent_to_verify_queue
        )
      };
    case "transmission_received" /* TransmissionReceived */:
      return {
        transmission_received: encodeActivityItemDetails_TransmissionReceived(
          value.transmission_received
        )
      };
    case "transmission_succeeded" /* TransmissionSucceeded */:
      return {
        transmission_succeeded: encodeActivityItemDetails_TransmissionSucceeded(
          value.transmission_succeeded
        )
      };
    case "verification_batch_started" /* VerificationBatchStarted */:
      return {
        verification_batch_started: encodeActivityItemDetails_VerificationBatchStarted(
          value.verification_batch_started
        )
      };
    case "verified_by_cadence" /* VerifiedByCadence */:
      return {
        verified_by_cadence: encodeActivityItemDetails_VerifiedByCadence(
          value.verified_by_cadence
        )
      };
    case "acertus_shipment_approved" /* AcertusShipmentApproved */:
      return {
        acertus_shipment_approved: encodeActivityItemDetails_AcertusShipmentApproved(
          value.acertus_shipment_approved
        )
      };
    case "acertus_shipment_rejected" /* AcertusShipmentRejected */:
      return {
        acertus_shipment_rejected: encodeActivityItemDetails_AcertusShipmentRejected(
          value.acertus_shipment_rejected
        )
      };
    case "client_or_debtor_on_bol_changed" /* ClientOrDebtorOnBolChanged */:
      return {
        client_or_debtor_on_bol_changed: encodeActivityItemDetails_ClientOrDebtorOnBolChanged(
          value.client_or_debtor_on_bol_changed
        )
      };
    case "contact_added" /* ContactAdded */:
      return {
        contact_added: encodeActivityItemDetails_ContactAdded(
          value.contact_added
        )
      };
    case "debtor_corrected" /* DebtorCorrected */:
      return {
        debtor_corrected: encodeActivityItemDetails_DebtorCorrected(
          value.debtor_corrected
        )
      };
    case "duplicate_document_batch_auto_completed" /* DuplicateDocumentBatchAutoCompleted */:
      return {
        duplicate_document_batch_auto_completed: encodeActivityItemDetails_DuplicateDocumentBatchAutoCompleted(
          value.duplicate_document_batch_auto_completed
        )
      };
    case "duplicate_email_auto_completed" /* DuplicateEmailAutoCompleted */:
      return {
        duplicate_email_auto_completed: encodeActivityItemDetails_DuplicateEmailAutoCompleted(
          value.duplicate_email_auto_completed
        )
      };
    case "email_ignored" /* EmailIgnored */:
      return {
        email_ignored: encodeActivityItemDetails_EmailIgnored(
          value.email_ignored
        )
      };
    case "exception_resolved" /* ExceptionResolved */:
      return {
        exception_resolved: encodeActivityItemDetails_ExceptionResolved(
          value.exception_resolved
        )
      };
    case "hidden" /* Hidden */:
      return { hidden: encodeActivityItemDetails_Hidden(value.hidden) };
    case "ignore" /* Ignore */:
      return { ignore: encodeActivityItemDetails_Ignore(value.ignore) };
    case "invoice_abandoned" /* InvoiceAbandoned */:
      return {
        invoice_abandoned: encodeActivityItemDetails_InvoiceAbandoned(
          value.invoice_abandoned
        )
      };
    case "ltl_rerate_failed" /* LtlRerateFailed */:
      return {
        ltl_rerate_failed: encodeActivityItemDetails_LtlRerateFailed(
          value.ltl_rerate_failed
        )
      };
    case "send_note_to_payee_via_payments_network" /* SendNoteToPayeeViaPaymentsNetwork */:
      return {
        send_note_to_payee_via_payments_network: encodeActivityItemDetails_SendNoteToPayeeViaPaymentsNetwork(
          value.send_note_to_payee_via_payments_network
        )
      };
    case "sent_to_cadence" /* SentToCadence */:
      return {
        sent_to_cadence: encodeActivityItemDetails_SentToCadence(
          value.sent_to_cadence
        )
      };
    case "state_change" /* StateChange */:
      return {
        state_change: encodeActivityItemDetails_StateChange(value.state_change)
      };
    case "transmission_failed" /* TransmissionFailed */:
      return {
        transmission_failed: encodeActivityItemDetails_TransmissionFailed(
          value.transmission_failed
        )
      };
    case "updated_from_external" /* UpdatedFromExternal */:
      return {
        updated_from_external: encodeActivityItemDetails_UpdatedFromExternal(
          value.updated_from_external
        )
      };
    case "verifier_changed_invoice_amount" /* VerifierChangedInvoiceAmount */:
      return {
        verifier_changed_invoice_amount: encodeActivityItemDetails_VerifierChangedInvoiceAmount(
          value.verifier_changed_invoice_amount
        )
      };
    case "verify_completed" /* VerifyCompleted */:
      return {
        verify_completed: encodeActivityItemDetails_VerifyCompleted(
          value.verify_completed
        )
      };
    case "ltl_accept_added" /* LtlAcceptAdded */:
      return {
        ltl_accept_added: encodeActivityItemDataLtlAcceptAdded(
          value.ltl_accept_added
        )
      };
    case "ltl_dispute_added" /* LtlDisputeAdded */:
      return {
        ltl_dispute_added: encodeActivityItemLtlDisputeAddedData(
          value.ltl_dispute_added
        )
      };
    case "ltl_moved_to_tms_queue" /* LtlMovedToTMSQueue */:
      return {
        ltl_moved_to_tms_queue: encodeLtlInvoiceMovedToTMSQueueData(
          value.ltl_moved_to_tms_queue
        )
      };
    case "add_document_to_load_success" /* AddDocumentToLoadSuccess */:
      return {
        add_document_to_load_success: encodeActivityItemAddDocumentToLoadSuccessData(
          value.add_document_to_load_success
        )
      };
    case "add_document_to_load_failure" /* AddDocumentToLoadFailure */:
      return {
        add_document_to_load_failure: encodeActivityItemAddDocumentToLoadFailureData(
          value.add_document_to_load_failure
        )
      };
    case "flagged_for_manual_review" /* FlaggedForManualReview */:
      return {
        flagged_for_manual_review: encodeActivityItemDetails_FlaggedForManualReview(
          value.flagged_for_manual_review
        )
      };
    case "document_added" /* DocumentAdded */:
      return {
        document_added: encodeActivityItemDetails_DocumentAdded(
          value.document_added
        )
      };
    case "document_removed" /* DocumentRemoved */:
      return {
        document_removed: encodeActivityItemDetails_DocumentRemoved(
          value.document_removed
        )
      };
    case "email_merge_source" /* EmailMergeSource */:
      return {
        email_merge_source: encodeActivityItemDetails_EmailMergeSource(
          value.email_merge_source
        )
      };
    case "email_merge_target" /* EmailMergeTarget */:
      return {
        email_merge_target: encodeActivityItemDetails_EmailMergeTarget(
          value.email_merge_target
        )
      };
    case "forwarded_email" /* ForwardedEmail */:
      return {
        forwarded_email: encodeActivityItemDetails_ForwardedEmail(
          value.forwarded_email
        )
      };
    case "load_shipment_approval" /* LoadShipmentApproval */:
      return {
        load_shipment_approval: encodeActivityItemDetails_LoadShipmentApproval(
          value.load_shipment_approval
        )
      };
    case "load_shipment_unapproval" /* LoadShipmentUnapproval */:
      return {
        load_shipment_unapproval: encodeActivityItemDetails_LoadShipmentUnapproval(
          value.load_shipment_unapproval
        )
      };
    case "pause" /* Pause */:
      return { pause: encodeActivityItemDetails_Pause(value.pause) };
    case "rule_disabled" /* RuleDisabled */:
      return {
        rule_disabled: encodeActivityItemDetails_RuleDisabled(
          value.rule_disabled
        )
      };
    case "rule_enabled" /* RuleEnabled */:
      return {
        rule_enabled: encodeActivityItemDetails_RuleEnabled(value.rule_enabled)
      };
    case "doc_sent_to_external" /* DocSentToExternal */:
      return {
        doc_sent_to_external: encodeActivityItemDetails_DocSentToExternal(
          value.doc_sent_to_external
        )
      };
    case "external_creation" /* ExternalCreation */:
      return {
        external_creation: encodeActivityItemDetails_ExternalCreation(
          value.external_creation
        )
      };
    case "factoring_assignment" /* FactoringAssignment */:
      return {
        factoring_assignment: encodeActivityItemDetails_FactoringAssignment(
          value.factoring_assignment
        )
      };
    case "processed" /* Processed */:
      return {
        processed: encodeActivityItemDetails_Processed(value.processed)
      };
    case "auto_processed" /* AutoProcessed */:
      return {
        auto_processed: encodeActivityItemDetails_FactorInvoiceDetails(
          value.auto_processed
        )
      };
    case "carrier_payable_approval" /* CarrierPayableApproval */:
      return {
        carrier_payable_approval: encodeActivityItemDetails_CarrierInvoiceDetails(
          value.carrier_payable_approval
        )
      };
    case "sent_to_external" /* SentToExternal */:
      return {
        sent_to_external: encodeActivityItemDetails_SentToExternal(
          value.sent_to_external
        )
      };
    default:
      throw new Error("No field set in oneof ActivityItemDetails_Details");
  }
}
export function decodeActivityItemDetails_AcertusShipmentApproved(json) {
  return {
    vin: assertString(json.vin)
  };
}
export function encodeActivityItemDetails_AcertusShipmentApproved(value) {
  return {
    vin: value.vin
  };
}
export function decodeActivityItemDetails_AcertusShipmentRejected(json) {
  return {
    vin: assertString(json.vin),
    reason: optionalDecoder(assertString)(json.reason)
  };
}
export function encodeActivityItemDetails_AcertusShipmentRejected(value) {
  return {
    vin: value.vin,
    reason: value.reason
  };
}
export function decodeActivityItemDetails_AcknowledgedByFMS(json) {
  return {};
}
export function encodeActivityItemDetails_AcknowledgedByFMS(value) {
  return {};
}
export function decodeActivityItemDetails_Approval(json) {
  return {
    invoice_count: optionalDecoder(assertNumber)(json.invoice_count)
  };
}
export function encodeActivityItemDetails_Approval(value) {
  return {
    invoice_count: value.invoice_count
  };
}
export function decodeActivityItemDetails_AutoCompletedWaitingExceptionsOnNewDocs(json) {
  return {};
}
export function encodeActivityItemDetails_AutoCompletedWaitingExceptionsOnNewDocs(value) {
  return {};
}
export function decodeActivityItemDetails_BuiltLoadConfirmed(json) {
  return {};
}
export function encodeActivityItemDetails_BuiltLoadConfirmed(value) {
  return {};
}
export function decodeActivityItemDetails_BuiltLoadSentToTMS(json) {
  return {};
}
export function encodeActivityItemDetails_BuiltLoadSentToTMS(value) {
  return {};
}
export function decodeActivityItemDetails_BypassedVerifyQueue(json) {
  return {};
}
export function encodeActivityItemDetails_BypassedVerifyQueue(value) {
  return {};
}
export function decodeActivityItemDetails_CarrierInvoiceDetails(json) {
  return {
    invoice_number: assertString(json.invoice_number),
    invoice_date: decodeTimestamp(json.invoice_date),
    amount_to_pay: assertString(json.amount_to_pay),
    date_to_pay: decodeTimestamp(json.date_to_pay),
    carrier_id: assertInt64(json.carrier_id),
    remit_to_address: optionalDecoder(decodeAddress)(json.remit_to_address)
  };
}
export function encodeActivityItemDetails_CarrierInvoiceDetails(value) {
  return {
    invoice_number: value.invoice_number,
    invoice_date: encodeTimestamp(value.invoice_date),
    amount_to_pay: value.amount_to_pay,
    date_to_pay: encodeTimestamp(value.date_to_pay),
    carrier_id: value.carrier_id,
    remit_to_address: optionalEncoder(encodeAddress)(value.remit_to_address)
  };
}
export function decodeActivityItemDetails_CarrierInvoiceRuleChangedViaTMS(json) {
  return {};
}
export function encodeActivityItemDetails_CarrierInvoiceRuleChangedViaTMS(value) {
  return {};
}
export function decodeActivityItemDetails_ClientOrDebtorOnBolChanged(json) {
  return {
    to: assertString(json.to)
  };
}
export function encodeActivityItemDetails_ClientOrDebtorOnBolChanged(value) {
  return {
    to: value.to
  };
}
export function decodeActivityItemDetails_ContactAdded(json) {
  return {
    carrier_name: assertString(json.carrier_name),
    contact_name: optionalDecoder(assertString)(json.contact_name),
    contact_email: assertString(json.contact_email)
  };
}
export function encodeActivityItemDetails_ContactAdded(value) {
  return {
    carrier_name: value.carrier_name,
    contact_name: value.contact_name,
    contact_email: value.contact_email
  };
}
export function decodeActivityItemDetails_DebtorCorrected(json) {
  return {
    previous_debtor_name: optionalDecoder(assertString)(
      json.previous_debtor_name
    ),
    corrected_debtor_name: assertString(json.corrected_debtor_name),
    notes: optionalDecoder(assertString)(json.notes)
  };
}
export function encodeActivityItemDetails_DebtorCorrected(value) {
  return {
    previous_debtor_name: value.previous_debtor_name,
    corrected_debtor_name: value.corrected_debtor_name,
    notes: value.notes
  };
}
export function decodeActivityItemDetails_DocSentToExternal(json) {
  return {
    document_type: decodeUnresolvedDocumentType(json.document_type),
    load_id: assertString(json.load_id)
  };
}
export function encodeActivityItemDetails_DocSentToExternal(value) {
  return {
    document_type: encodeUnresolvedDocumentType(value.document_type),
    load_id: value.load_id
  };
}
export function decodeActivityItemDetails_DocsSentOnException(json) {
  return {};
}
export function encodeActivityItemDetails_DocsSentOnException(value) {
  return {};
}
export function decodeActivityItemDetails_DocumentAdded(json) {
  return {
    document_type: decodeUnresolvedDocumentType(json.document_type),
    document_type_name: optionalDecoder(assertString)(json.document_type_name),
    original: optionalDecoder(decodeActivityItemDetails_LoadReference)(
      json.original
    )
  };
}
export function encodeActivityItemDetails_DocumentAdded(value) {
  return {
    document_type: encodeUnresolvedDocumentType(value.document_type),
    document_type_name: value.document_type_name,
    original: optionalEncoder(encodeActivityItemDetails_LoadReference)(
      value.original
    )
  };
}
export function decodeActivityItemDetails_DocumentAssigned(json) {
  return {};
}
export function encodeActivityItemDetails_DocumentAssigned(value) {
  return {};
}
export function decodeActivityItemDetails_DocumentRemoved(json) {
  return {
    document_type: decodeUnresolvedDocumentType(json.document_type),
    document_type_name: optionalDecoder(assertString)(json.document_type_name),
    new: optionalDecoder(decodeActivityItemDetails_LoadReference)(json.new)
  };
}
export function encodeActivityItemDetails_DocumentRemoved(value) {
  return {
    document_type: encodeUnresolvedDocumentType(value.document_type),
    document_type_name: value.document_type_name,
    new: optionalEncoder(encodeActivityItemDetails_LoadReference)(value.new)
  };
}
export function decodeActivityItemDetails_DocumentsMerged(json) {
  return {};
}
export function encodeActivityItemDetails_DocumentsMerged(value) {
  return {};
}
export function decodeActivityItemDetails_DocumentsResendRequested(json) {
  return {};
}
export function encodeActivityItemDetails_DocumentsResendRequested(value) {
  return {};
}
export function decodeActivityItemDetails_DuplicateDocumentAutoHidden(json) {
  return {};
}
export function encodeActivityItemDetails_DuplicateDocumentAutoHidden(value) {
  return {};
}
export function decodeActivityItemDetails_DuplicateDocumentAutoMerged(json) {
  return {};
}
export function encodeActivityItemDetails_DuplicateDocumentAutoMerged(value) {
  return {};
}
export function decodeActivityItemDetails_DuplicateDocumentBatchAutoCompleted(json) {
  return {
    duplicate_document_batch_id: optionalDecoder(assertInt64)(
      json.duplicate_document_batch_id
    )
  };
}
export function encodeActivityItemDetails_DuplicateDocumentBatchAutoCompleted(value) {
  return {
    duplicate_document_batch_id: value.duplicate_document_batch_id
  };
}
export function decodeActivityItemDetails_DuplicateEmailAutoCompleted(json) {
  return {
    duplicate_email_message_id: optionalDecoder(assertInt64)(
      json.duplicate_email_message_id
    )
  };
}
export function encodeActivityItemDetails_DuplicateEmailAutoCompleted(value) {
  return {
    duplicate_email_message_id: value.duplicate_email_message_id
  };
}
export function decodeActivityItemDetails_EmailCompleted(json) {
  return {};
}
export function encodeActivityItemDetails_EmailCompleted(value) {
  return {};
}
export function decodeActivityItemDetails_EmailIgnored(json) {
  return {
    reason: optionalDecoder(assertString)(json.reason)
  };
}
export function encodeActivityItemDetails_EmailIgnored(value) {
  return {
    reason: value.reason
  };
}
export function decodeActivityItemDetails_EmailMergeSource(json) {
  return {
    target_email_id: assertInt64(json.target_email_id),
    target_document_batch_id: assertInt64(json.target_document_batch_id)
  };
}
export function encodeActivityItemDetails_EmailMergeSource(value) {
  return {
    target_email_id: value.target_email_id,
    target_document_batch_id: value.target_document_batch_id
  };
}
export function decodeActivityItemDetails_EmailMergeTarget(json) {
  return {
    sources: (json.sources || []).map(
      decodeActivityItemDetails_EmailMergeTarget_Source
    )
  };
}
export function encodeActivityItemDetails_EmailMergeTarget(value) {
  return {
    sources: value.sources.map(
      encodeActivityItemDetails_EmailMergeTarget_Source
    )
  };
}
export function decodeActivityItemDetails_EmailMergeTarget_Source(json) {
  return {
    subject: assertString(json.subject),
    sender: assertString(json.sender),
    email_message_id: assertInt64(json.email_message_id),
    document_batch_id: assertInt64(json.document_batch_id)
  };
}
export function encodeActivityItemDetails_EmailMergeTarget_Source(value) {
  return {
    subject: value.subject,
    sender: value.sender,
    email_message_id: value.email_message_id,
    document_batch_id: value.document_batch_id
  };
}
export function decodeActivityItemDetails_EmailUnignored(json) {
  return {};
}
export function encodeActivityItemDetails_EmailUnignored(value) {
  return {};
}
export function decodeActivityItemDetails_EmailZeroPages(json) {
  return {};
}
export function encodeActivityItemDetails_EmailZeroPages(value) {
  return {};
}
export function decodeActivityItemDetails_ExceptionResolved(json) {
  return {
    message: optionalDecoder(assertString)(json.message),
    resolution: optionalDecoder(assertString)(json.resolution)
  };
}
export function encodeActivityItemDetails_ExceptionResolved(value) {
  return {
    message: value.message,
    resolution: value.resolution
  };
}
export function decodeActivityItemDetails_ExceptionsClearedViaAPI(json) {
  return {};
}
export function encodeActivityItemDetails_ExceptionsClearedViaAPI(value) {
  return {};
}
export function decodeActivityItemDetails_ExternalCreation(json) {
  return {
    invoice_number: assertString(json.invoice_number),
    invoice_date: decodeTimestamp(json.invoice_date),
    amount_to_pay: assertString(json.amount_to_pay),
    date_to_pay: decodeTimestamp(json.date_to_pay),
    carrier_id: assertInt64(json.carrier_id),
    source_system: assertString(json.source_system)
  };
}
export function encodeActivityItemDetails_ExternalCreation(value) {
  return {
    invoice_number: value.invoice_number,
    invoice_date: encodeTimestamp(value.invoice_date),
    amount_to_pay: value.amount_to_pay,
    date_to_pay: encodeTimestamp(value.date_to_pay),
    carrier_id: value.carrier_id,
    source_system: value.source_system
  };
}
export function decodeActivityItemDetails_FactorInvoiceDetails(json) {
  return {
    invoice_number: optionalDecoder(assertString)(json.invoice_number),
    load_id: optionalDecoder(assertString)(json.load_id),
    amount: optionalDecoder(assertString)(json.amount),
    invoice_date: optionalDecoder(decodeTimestamp)(json.invoice_date),
    carrier_id: assertInt64(json.carrier_id),
    debtor_name: assertString(json.debtor_name),
    field_sources: optionalDecoder(decodeActivityItemFactorInvoiceFieldSources)(
      json.field_sources
    )
  };
}
export function encodeActivityItemDetails_FactorInvoiceDetails(value) {
  return {
    invoice_number: value.invoice_number,
    load_id: value.load_id,
    amount: value.amount,
    invoice_date: optionalEncoder(encodeTimestamp)(value.invoice_date),
    carrier_id: value.carrier_id,
    debtor_name: value.debtor_name,
    field_sources: optionalEncoder(encodeActivityItemFactorInvoiceFieldSources)(
      value.field_sources
    )
  };
}
export function decodeActivityItemDetails_FactoringAssignment(json) {
  return {
    factoring_company_name: assertString(json.factoring_company_name),
    factoring_company_office_address: decodeAddress(
      json.factoring_company_office_address
    )
  };
}
export function encodeActivityItemDetails_FactoringAssignment(value) {
  return {
    factoring_company_name: value.factoring_company_name,
    factoring_company_office_address: encodeAddress(
      value.factoring_company_office_address
    )
  };
}
export function decodeActivityItemDetails_FlaggedForManualReview(json) {
  return {
    reason: decodeFlaggedForManualReviewReason(json.reason)
  };
}
export function encodeActivityItemDetails_FlaggedForManualReview(value) {
  return {
    reason: encodeFlaggedForManualReviewReason(value.reason)
  };
}
export function decodeActivityItemDetails_ForwardedEmail(json) {
  return {
    recipients: (json.recipients || []).map(assertString),
    note: optionalDecoder(assertString)(json.note)
  };
}
export function encodeActivityItemDetails_ForwardedEmail(value) {
  return {
    recipients: value.recipients,
    note: value.note
  };
}
export function decodeActivityItemDetails_Hidden(json) {
  return {
    message: assertString(json.message)
  };
}
export function encodeActivityItemDetails_Hidden(value) {
  return {
    message: value.message
  };
}
export function decodeActivityItemDetails_Ignore(json) {
  return {
    reason: optionalDecoder(assertString)(json.reason)
  };
}
export function encodeActivityItemDetails_Ignore(value) {
  return {
    reason: value.reason
  };
}
export function decodeActivityItemDetails_IndexingComplete(json) {
  return {};
}
export function encodeActivityItemDetails_IndexingComplete(value) {
  return {};
}
export function decodeActivityItemDetails_InvoiceAbandoned(json) {
  return {
    reason: optionalDecoder(assertString)(json.reason)
  };
}
export function encodeActivityItemDetails_InvoiceAbandoned(value) {
  return {
    reason: value.reason
  };
}
export function decodeActivityItemDetails_InvoiceDocumentsForwardedToTriumph(json) {
  return {};
}
export function encodeActivityItemDetails_InvoiceDocumentsForwardedToTriumph(value) {
  return {};
}
export function decodeActivityItemDetails_InvoiceExceptionEmailSent(json) {
  return {};
}
export function encodeActivityItemDetails_InvoiceExceptionEmailSent(value) {
  return {};
}
export function decodeActivityItemDetails_InvoiceMatched(json) {
  return {};
}
export function encodeActivityItemDetails_InvoiceMatched(value) {
  return {};
}
export function decodeActivityItemDetails_InvoiceRejected(json) {
  return {};
}
export function encodeActivityItemDetails_InvoiceRejected(value) {
  return {};
}
export function decodeActivityItemDetails_InvoiceRevived(json) {
  return {};
}
export function encodeActivityItemDetails_InvoiceRevived(value) {
  return {};
}
export function decodeActivityItemDetails_InvoiceUnapproved(json) {
  return {};
}
export function encodeActivityItemDetails_InvoiceUnapproved(value) {
  return {};
}
export function decodeActivityItemDetails_LineItemsAutoReplaced(json) {
  return {};
}
export function encodeActivityItemDetails_LineItemsAutoReplaced(value) {
  return {};
}
export function decodeActivityItemDetails_LoadDocumentsApproved(json) {
  return {};
}
export function encodeActivityItemDetails_LoadDocumentsApproved(value) {
  return {};
}
export function decodeActivityItemDetails_LoadDocumentsAssigned(json) {
  return {};
}
export function encodeActivityItemDetails_LoadDocumentsAssigned(value) {
  return {};
}
export function decodeActivityItemDetails_LoadDocumentsScraped(json) {
  return {};
}
export function encodeActivityItemDetails_LoadDocumentsScraped(value) {
  return {};
}
export function decodeActivityItemDetails_LoadDocumentsSent(json) {
  return {};
}
export function encodeActivityItemDetails_LoadDocumentsSent(value) {
  return {};
}
export function decodeActivityItemDetails_LoadReference(json) {
  return {
    load_state_id: assertInt64(json.load_state_id),
    load_id: assertString(json.load_id)
  };
}
export function encodeActivityItemDetails_LoadReference(value) {
  return {
    load_state_id: value.load_state_id,
    load_id: value.load_id
  };
}
export function decodeActivityItemDetails_LoadShipmentApproval(json) {
  return {
    shipment_id: assertString(json.shipment_id),
    customer_id: optionalDecoder(assertString)(json.customer_id),
    amount: optionalDecoder(assertString)(json.amount),
    customer_name: optionalDecoder(assertString)(json.customer_name)
  };
}
export function encodeActivityItemDetails_LoadShipmentApproval(value) {
  return {
    shipment_id: value.shipment_id,
    customer_id: value.customer_id,
    amount: value.amount,
    customer_name: value.customer_name
  };
}
export function decodeActivityItemDetails_LoadShipmentUnapproval(json) {
  return {
    shipment_id: assertString(json.shipment_id),
    customer_id: optionalDecoder(assertString)(json.customer_id),
    amount: optionalDecoder(assertString)(json.amount),
    customer_name: optionalDecoder(assertString)(json.customer_name)
  };
}
export function encodeActivityItemDetails_LoadShipmentUnapproval(value) {
  return {
    shipment_id: value.shipment_id,
    customer_id: value.customer_id,
    amount: value.amount,
    customer_name: value.customer_name
  };
}
export function decodeActivityItemDetails_LtlRerateCompleted(json) {
  return {};
}
export function encodeActivityItemDetails_LtlRerateCompleted(value) {
  return {};
}
export function decodeActivityItemDetails_LtlRerateFailed(json) {
  return {
    error_message: assertString(json.error_message)
  };
}
export function encodeActivityItemDetails_LtlRerateFailed(value) {
  return {
    error_message: value.error_message
  };
}
export function decodeActivityItemDetails_LtlRerateRequested(json) {
  return {};
}
export function encodeActivityItemDetails_LtlRerateRequested(value) {
  return {};
}
export function decodeActivityItemDetails_LtlUpdatedEdiReceived(json) {
  return {};
}
export function encodeActivityItemDetails_LtlUpdatedEdiReceived(value) {
  return {};
}
export function decodeActivityItemDetails_MarkedNotReadyForVerification(json) {
  return {};
}
export function encodeActivityItemDetails_MarkedNotReadyForVerification(value) {
  return {};
}
export function decodeActivityItemDetails_MarkedPurchasedByUser(json) {
  return {};
}
export function encodeActivityItemDetails_MarkedPurchasedByUser(value) {
  return {};
}
export function decodeActivityItemDetails_NonInvoiceLoadAssignment(json) {
  return {
    load_id: optionalDecoder(assertString)(json.load_id)
  };
}
export function encodeActivityItemDetails_NonInvoiceLoadAssignment(value) {
  return {
    load_id: value.load_id
  };
}
export function decodeActivityItemDetails_Note(json) {
  return {
    message: assertString(json.message)
  };
}
export function encodeActivityItemDetails_Note(value) {
  return {
    message: value.message
  };
}
export function decodeActivityItemDetails_Pause(json) {
  return {
    paused_until: decodeTimestamp(json.paused_until)
  };
}
export function encodeActivityItemDetails_Pause(value) {
  return {
    paused_until: encodeTimestamp(value.paused_until)
  };
}
export function decodeActivityItemDetails_PaymentAddressChanged(json) {
  return {};
}
export function encodeActivityItemDetails_PaymentAddressChanged(value) {
  return {};
}
export function decodeActivityItemDetails_PODRequestSentToTextLocate(json) {
  return {};
}
export function encodeActivityItemDetails_PODRequestSentToTextLocate(value) {
  return {};
}
export function decodeActivityItemDetails_Processed(json) {
  return {
    invoice: decodeActivityItemDetails_Processed_Invoice(json)
  };
}
export function encodeActivityItemDetails_Processed(value) {
  return __spreadValues({}, encodeActivityItemDetails_Processed_Invoice(value.invoice));
}
export var ActivityItemDetails_Processed_InvoiceType = /* @__PURE__ */ ((ActivityItemDetails_Processed_InvoiceType2) => {
  ActivityItemDetails_Processed_InvoiceType2["CarrierInvoice"] = "carrier_invoice";
  ActivityItemDetails_Processed_InvoiceType2["FactorInvoice"] = "factor_invoice";
  return ActivityItemDetails_Processed_InvoiceType2;
})(ActivityItemDetails_Processed_InvoiceType || {});
export function decodeActivityItemDetails_Processed_Invoice(json) {
  if (json.carrier_invoice !== void 0) {
    return {
      type: "carrier_invoice" /* CarrierInvoice */,
      carrier_invoice: decodeActivityItemDetails_CarrierInvoiceDetails(
        json.carrier_invoice
      )
    };
  } else if (json.factor_invoice !== void 0) {
    return {
      type: "factor_invoice" /* FactorInvoice */,
      factor_invoice: decodeActivityItemDetails_FactorInvoiceDetails(
        json.factor_invoice
      )
    };
  }
  throw new Error(
    "No field set in oneof ActivityItemDetails_Processed_Invoice"
  );
}
export function encodeActivityItemDetails_Processed_Invoice(value) {
  switch (value.type) {
    case "carrier_invoice" /* CarrierInvoice */:
      return {
        carrier_invoice: encodeActivityItemDetails_CarrierInvoiceDetails(
          value.carrier_invoice
        )
      };
    case "factor_invoice" /* FactorInvoice */:
      return {
        factor_invoice: encodeActivityItemDetails_FactorInvoiceDetails(
          value.factor_invoice
        )
      };
    default:
      throw new Error(
        "No field set in oneof ActivityItemDetails_Processed_Invoice"
      );
  }
}
export function decodeActivityItemDetails_ReadyForFMSPickup(json) {
  return {};
}
export function encodeActivityItemDetails_ReadyForFMSPickup(value) {
  return {};
}
export function decodeActivityItemDetails_ReceivedInvoice(json) {
  return {};
}
export function encodeActivityItemDetails_ReceivedInvoice(value) {
  return {};
}
export function decodeActivityItemDetails_RemovedFromGroup(json) {
  return {};
}
export function encodeActivityItemDetails_RemovedFromGroup(value) {
  return {};
}
export function decodeActivityItemDetails_RuleDisabled(json) {
  return {
    rule_type: assertString(json.rule_type),
    display_text: optionalDecoder(assertString)(json.display_text)
  };
}
export function encodeActivityItemDetails_RuleDisabled(value) {
  return {
    rule_type: value.rule_type,
    display_text: value.display_text
  };
}
export function decodeActivityItemDetails_RuleEnabled(json) {
  return {
    rule_type: assertString(json.rule_type),
    display_text: optionalDecoder(assertString)(json.display_text)
  };
}
export function encodeActivityItemDetails_RuleEnabled(value) {
  return {
    rule_type: value.rule_type,
    display_text: value.display_text
  };
}
export function decodeActivityItemDetails_SavedAsIncomplete(json) {
  return {};
}
export function encodeActivityItemDetails_SavedAsIncomplete(value) {
  return {};
}
export function decodeActivityItemDetails_SendNoteToPayeeViaPaymentsNetwork(json) {
  return {
    message: assertString(json.message)
  };
}
export function encodeActivityItemDetails_SendNoteToPayeeViaPaymentsNetwork(value) {
  return {
    message: value.message
  };
}
export function decodeActivityItemDetails_SentToCadence(json) {
  return {
    transmission_id: optionalDecoder(assertInt64)(json.transmission_id),
    transmission_type: optionalDecoder(assertString)(json.transmission_type),
    invoice_count: optionalDecoder(assertNumber)(json.invoice_count)
  };
}
export function encodeActivityItemDetails_SentToCadence(value) {
  return {
    transmission_id: value.transmission_id,
    transmission_type: value.transmission_type,
    invoice_count: value.invoice_count
  };
}
export function decodeActivityItemDetails_SentToExternal(json) {
  return {
    payload: assertString(json.payload),
    response: assertString(json.response),
    url: optionalDecoder(assertString)(json.url),
    filename: optionalDecoder(assertString)(json.filename)
  };
}
export function encodeActivityItemDetails_SentToExternal(value) {
  return {
    payload: value.payload,
    response: value.response,
    url: value.url,
    filename: value.filename
  };
}
export function decodeActivityItemDetails_SentToVerifyQueue(json) {
  return {};
}
export function encodeActivityItemDetails_SentToVerifyQueue(value) {
  return {};
}
export function decodeActivityItemDetails_StateChange(json) {
  return {
    original_state: assertString(json.original_state),
    new_state: assertString(json.new_state),
    reason: assertString(json.reason)
  };
}
export function encodeActivityItemDetails_StateChange(value) {
  return {
    original_state: value.original_state,
    new_state: value.new_state,
    reason: value.reason
  };
}
export function decodeActivityItemDetails_TransmissionFailed(json) {
  return {
    failure_message: optionalDecoder(assertString)(json.failure_message)
  };
}
export function encodeActivityItemDetails_TransmissionFailed(value) {
  return {
    failure_message: value.failure_message
  };
}
export function decodeActivityItemDetails_TransmissionReceived(json) {
  return {};
}
export function encodeActivityItemDetails_TransmissionReceived(value) {
  return {};
}
export function decodeActivityItemDetails_TransmissionSucceeded(json) {
  return {};
}
export function encodeActivityItemDetails_TransmissionSucceeded(value) {
  return {};
}
export function decodeActivityItemDetails_UpdatedFromExternal(json) {
  return {
    updated_attribute: assertString(json.updated_attribute),
    previous_value: optionalDecoder(assertString)(json.previous_value),
    new_value: optionalDecoder(assertString)(json.new_value)
  };
}
export function encodeActivityItemDetails_UpdatedFromExternal(value) {
  return {
    updated_attribute: value.updated_attribute,
    previous_value: value.previous_value,
    new_value: value.new_value
  };
}
export function decodeActivityItemDetails_VerificationBatchStarted(json) {
  return {};
}
export function encodeActivityItemDetails_VerificationBatchStarted(value) {
  return {};
}
export function decodeActivityItemDetails_VerifiedByCadence(json) {
  return {};
}
export function encodeActivityItemDetails_VerifiedByCadence(value) {
  return {};
}
export function decodeActivityItemDetails_VerifierChangedInvoiceAmount(json) {
  return {
    old_amount: assertString(json.old_amount),
    new_amount: assertString(json.new_amount)
  };
}
export function encodeActivityItemDetails_VerifierChangedInvoiceAmount(value) {
  return {
    old_amount: value.old_amount,
    new_amount: value.new_amount
  };
}
export function decodeActivityItemDetails_VerifyCompleted(json) {
  return {
    status: assertString(json.status),
    note: optionalDecoder(assertString)(json.note)
  };
}
export function encodeActivityItemDetails_VerifyCompleted(value) {
  return {
    status: value.status,
    note: value.note
  };
}
export function decodeActivityItemFactorInvoiceFieldSources(json) {
  return {
    invoiceNumberSource: optionalDecoder(
      decodeActivityItemFactorInvoiceFieldSources_FieldSource
    )(json.invoiceNumberSource),
    loadIdSource: optionalDecoder(
      decodeActivityItemFactorInvoiceFieldSources_FieldSource
    )(json.loadIdSource),
    amountSource: optionalDecoder(
      decodeActivityItemFactorInvoiceFieldSources_FieldSource
    )(json.amountSource),
    debtorSource: optionalDecoder(
      decodeActivityItemFactorInvoiceFieldSources_FieldSource
    )(json.debtorSource)
  };
}
export function encodeActivityItemFactorInvoiceFieldSources(value) {
  return {
    invoiceNumberSource: optionalEncoder(
      encodeActivityItemFactorInvoiceFieldSources_FieldSource
    )(value.invoiceNumberSource),
    loadIdSource: optionalEncoder(
      encodeActivityItemFactorInvoiceFieldSources_FieldSource
    )(value.loadIdSource),
    amountSource: optionalEncoder(
      encodeActivityItemFactorInvoiceFieldSources_FieldSource
    )(value.amountSource),
    debtorSource: optionalEncoder(
      encodeActivityItemFactorInvoiceFieldSources_FieldSource
    )(value.debtorSource)
  };
}
export function decodeActivityItemFactorInvoiceFieldSources_FieldSource(json) {
  return {
    kind: decodeFactorInvoiceFieldSourceKind(json.kind),
    systemValue: optionalDecoder(assertString)(json.systemValue),
    changedByName: optionalDecoder(assertString)(json.changedByName)
  };
}
export function encodeActivityItemFactorInvoiceFieldSources_FieldSource(value) {
  return {
    kind: encodeFactorInvoiceFieldSourceKind(value.kind),
    systemValue: value.systemValue,
    changedByName: value.changedByName
  };
}
export function decodeActivityItemIdsQuery(json) {
  return {
    page: assertNumber(json.page),
    activity_type: optionalDecoder(assertString)(json.activity_type),
    user_id: optionalDecoder(assertNumber)(json.user_id),
    start_date: optionalDecoder(assertString)(json.start_date),
    end_date: optionalDecoder(assertString)(json.end_date),
    factor_broker_id: optionalDecoder(assertNumber)(json.factor_broker_id),
    tms_carrier_id: optionalDecoder(assertNumber)(json.tms_carrier_id),
    tms_customer_id: optionalDecoder(assertNumber)(json.tms_customer_id),
    carrier_invoice_id: optionalDecoder(assertNumber)(json.carrier_invoice_id),
    document_batch_id: optionalDecoder(assertNumber)(json.document_batch_id),
    load_state_id: optionalDecoder(assertNumber)(json.load_state_id),
    document_group_id: optionalDecoder(assertNumber)(json.document_group_id),
    invoice_num_or_load_id: optionalDecoder(assertString)(
      json.invoice_num_or_load_id
    ),
    factor_invoice_id: optionalDecoder(assertNumber)(json.factor_invoice_id)
  };
}
export function encodeActivityItemIdsQuery(value) {
  return {
    page: value.page,
    activity_type: value.activity_type,
    user_id: value.user_id,
    start_date: value.start_date,
    end_date: value.end_date,
    factor_broker_id: value.factor_broker_id,
    tms_carrier_id: value.tms_carrier_id,
    tms_customer_id: value.tms_customer_id,
    carrier_invoice_id: value.carrier_invoice_id,
    document_batch_id: value.document_batch_id,
    load_state_id: value.load_state_id,
    document_group_id: value.document_group_id,
    invoice_num_or_load_id: value.invoice_num_or_load_id,
    factor_invoice_id: value.factor_invoice_id
  };
}
export function decodeActivityItemLtlDisputeAddedData(json) {
  return {
    disputeItems: (json.disputeItems || []).map(
      decodeActivityItemLtlDisputeAddedData_DisputeItem
    )
  };
}
export function encodeActivityItemLtlDisputeAddedData(value) {
  return {
    disputeItems: value.disputeItems.map(
      encodeActivityItemLtlDisputeAddedData_DisputeItem
    )
  };
}
export function decodeActivityItemLtlDisputeAddedData_DisputeItem(json) {
  return {
    reconcileType: decodeLtlCarrierInvoiceReconcileType(json.reconcileType),
    description: assertString(json.description)
  };
}
export function encodeActivityItemLtlDisputeAddedData_DisputeItem(value) {
  return {
    reconcileType: encodeLtlCarrierInvoiceReconcileType(value.reconcileType),
    description: value.description
  };
}
export function decodeAddDocumentToLoadRequest(json) {
  return {
    loadId: assertNumber(json.loadId),
    carrierInvoiceId: assertNumber(json.carrierInvoiceId),
    documents: (json.documents || []).map(
      decodeAddDocumentToLoadRequest_Document
    )
  };
}
export function encodeAddDocumentToLoadRequest(value) {
  return {
    loadId: value.loadId,
    carrierInvoiceId: value.carrierInvoiceId,
    documents: value.documents.map(encodeAddDocumentToLoadRequest_Document)
  };
}
export function decodeAddDocumentToLoadRequest_Document(json) {
  return {
    fileName: assertString(json.fileName),
    mimeType: assertString(json.mimeType),
    base64EncodedFile: assertString(json.base64EncodedFile)
  };
}
export function encodeAddDocumentToLoadRequest_Document(value) {
  return {
    fileName: value.fileName,
    mimeType: value.mimeType,
    base64EncodedFile: value.base64EncodedFile
  };
}
export function decodeAddDocumentToLoadResponse(json) {
  return {
    uploadStatus: decodeAddDocumentToLoadUploadStatus(json.uploadStatus)
  };
}
export function encodeAddDocumentToLoadResponse(value) {
  return {
    uploadStatus: encodeAddDocumentToLoadUploadStatus(value.uploadStatus)
  };
}
export var AddDocumentToLoadUploadStatus = /* @__PURE__ */ ((AddDocumentToLoadUploadStatus2) => {
  AddDocumentToLoadUploadStatus2["AddDocumentToLoadUploadStatusInProgress"] = "in_progress";
  AddDocumentToLoadUploadStatus2["AddDocumentToLoadUploadStatusFailed"] = "failure";
  AddDocumentToLoadUploadStatus2["AddDocumentToLoadUploadStatusSuccess"] = "success";
  return AddDocumentToLoadUploadStatus2;
})(AddDocumentToLoadUploadStatus || {});
export const decodeAddDocumentToLoadUploadStatus = (value) => value;
export const encodeAddDocumentToLoadUploadStatus = (value) => value;
export function decodeAddress(json) {
  return {
    id: optionalDecoder(assertNumber)(json.id),
    name: assertString(json.name),
    address_line_1: optionalDecoder(assertString)(json.address_line_1),
    address_line_2: optionalDecoder(assertString)(json.address_line_2),
    city: optionalDecoder(assertString)(json.city),
    state: optionalDecoder(assertString)(json.state),
    postal_code: optionalDecoder(assertString)(json.postal_code),
    country: optionalDecoder(assertString)(json.country)
  };
}
export function encodeAddress(value) {
  return {
    id: value.id,
    name: value.name,
    address_line_1: value.address_line_1,
    address_line_2: value.address_line_2,
    city: value.city,
    state: value.state,
    postal_code: value.postal_code,
    country: value.country
  };
}
export var AddressPlace = /* @__PURE__ */ ((AddressPlace2) => {
  AddressPlace2["AddressPlaceOrigin"] = "origin";
  AddressPlace2["AddressPlaceDestination"] = "destination";
  return AddressPlace2;
})(AddressPlace || {});
export const decodeAddressPlace = (value) => value;
export const encodeAddressPlace = (value) => value;
export function decodeAdminGlobalSetting(json) {
  return {
    name: assertString(json.name),
    displayTitle: assertString(json.displayTitle),
    description: assertString(json.description),
    group: assertString(json.group),
    currentValue: decodeAdminGlobalSetting_CurrentValue(json)
  };
}
export function encodeAdminGlobalSetting(value) {
  return __spreadValues({
    name: value.name,
    displayTitle: value.displayTitle,
    description: value.description,
    group: value.group
  }, encodeAdminGlobalSetting_CurrentValue(value.currentValue));
}
export var AdminGlobalSetting_CurrentValueType = /* @__PURE__ */ ((AdminGlobalSetting_CurrentValueType2) => {
  AdminGlobalSetting_CurrentValueType2["StringValue"] = "stringValue";
  AdminGlobalSetting_CurrentValueType2["BoolValue"] = "boolValue";
  AdminGlobalSetting_CurrentValueType2["IntValue"] = "intValue";
  AdminGlobalSetting_CurrentValueType2["FloatValue"] = "floatValue";
  return AdminGlobalSetting_CurrentValueType2;
})(AdminGlobalSetting_CurrentValueType || {});
export function decodeAdminGlobalSetting_CurrentValue(json) {
  if (json.stringValue !== void 0) {
    return {
      type: "stringValue" /* StringValue */,
      stringValue: assertString(json.stringValue)
    };
  } else if (json.boolValue !== void 0) {
    return {
      type: "boolValue" /* BoolValue */,
      boolValue: assertBoolean(json.boolValue)
    };
  } else if (json.intValue !== void 0) {
    return {
      type: "intValue" /* IntValue */,
      intValue: assertInt64(json.intValue)
    };
  } else if (json.floatValue !== void 0) {
    return {
      type: "floatValue" /* FloatValue */,
      floatValue: assertNumber(json.floatValue)
    };
  }
  throw new Error("No field set in oneof AdminGlobalSetting_CurrentValue");
}
export function encodeAdminGlobalSetting_CurrentValue(value) {
  switch (value.type) {
    case "stringValue" /* StringValue */:
      return { stringValue: value.stringValue };
    case "boolValue" /* BoolValue */:
      return { boolValue: value.boolValue };
    case "intValue" /* IntValue */:
      return { intValue: value.intValue };
    case "floatValue" /* FloatValue */:
      return { floatValue: value.floatValue };
    default:
      throw new Error("No field set in oneof AdminGlobalSetting_CurrentValue");
  }
}
export function decodeAdminGlobalSettingsServiceFlags(json) {
  return {
    settings: (json.settings || []).map(decodeAdminGlobalSetting)
  };
}
export function encodeAdminGlobalSettingsServiceFlags(value) {
  return {
    settings: value.settings.map(encodeAdminGlobalSetting)
  };
}
export function decodeAdminGlobalSettingsServiceUpdateSettingRequest(json) {
  return {
    name: assertString(json.name),
    value: decodeAdminGlobalSettingsServiceUpdateSettingRequest_Value(json)
  };
}
export function encodeAdminGlobalSettingsServiceUpdateSettingRequest(value) {
  return __spreadValues({
    name: value.name
  }, encodeAdminGlobalSettingsServiceUpdateSettingRequest_Value(value.value));
}
export var AdminGlobalSettingsServiceUpdateSettingRequest_ValueType = /* @__PURE__ */ ((AdminGlobalSettingsServiceUpdateSettingRequest_ValueType2) => {
  AdminGlobalSettingsServiceUpdateSettingRequest_ValueType2["StringValue"] = "stringValue";
  AdminGlobalSettingsServiceUpdateSettingRequest_ValueType2["BoolValue"] = "boolValue";
  AdminGlobalSettingsServiceUpdateSettingRequest_ValueType2["IntValue"] = "intValue";
  AdminGlobalSettingsServiceUpdateSettingRequest_ValueType2["FloatValue"] = "floatValue";
  return AdminGlobalSettingsServiceUpdateSettingRequest_ValueType2;
})(AdminGlobalSettingsServiceUpdateSettingRequest_ValueType || {});
export function decodeAdminGlobalSettingsServiceUpdateSettingRequest_Value(json) {
  if (json.stringValue !== void 0) {
    return {
      type: "stringValue" /* StringValue */,
      stringValue: assertString(json.stringValue)
    };
  } else if (json.boolValue !== void 0) {
    return {
      type: "boolValue" /* BoolValue */,
      boolValue: assertBoolean(json.boolValue)
    };
  } else if (json.intValue !== void 0) {
    return {
      type: "intValue" /* IntValue */,
      intValue: assertInt64(json.intValue)
    };
  } else if (json.floatValue !== void 0) {
    return {
      type: "floatValue" /* FloatValue */,
      floatValue: assertNumber(json.floatValue)
    };
  }
  throw new Error(
    "No field set in oneof AdminGlobalSettingsServiceUpdateSettingRequest_Value"
  );
}
export function encodeAdminGlobalSettingsServiceUpdateSettingRequest_Value(value) {
  switch (value.type) {
    case "stringValue" /* StringValue */:
      return { stringValue: value.stringValue };
    case "boolValue" /* BoolValue */:
      return { boolValue: value.boolValue };
    case "intValue" /* IntValue */:
      return { intValue: value.intValue };
    case "floatValue" /* FloatValue */:
      return { floatValue: value.floatValue };
    default:
      throw new Error(
        "No field set in oneof AdminGlobalSettingsServiceUpdateSettingRequest_Value"
      );
  }
}
export function decodeAdminGlobalSettingsServiceUpdateSettingResponse(json) {
  return {
    displayTitle: assertString(json.displayTitle),
    newValue: assertString(json.newValue)
  };
}
export function encodeAdminGlobalSettingsServiceUpdateSettingResponse(value) {
  return {
    displayTitle: value.displayTitle,
    newValue: value.newValue
  };
}
export function decodeAdminHeaderAccountSearchableRoute(json) {
  return {
    routeKey: assertString(json.routeKey),
    label: assertString(json.label),
    urlTemplate: optionalDecoder(assertString)(json.urlTemplate),
    accountParamName: assertString(json.accountParamName),
    includeOrderParam: assertBoolean(json.includeOrderParam),
    searchableColumns: (json.searchableColumns || []).map(
      decodeAdminHeaderAccountSearchableRoute_SearchableColumn
    )
  };
}
export function encodeAdminHeaderAccountSearchableRoute(value) {
  return {
    routeKey: value.routeKey,
    label: value.label,
    urlTemplate: value.urlTemplate,
    accountParamName: value.accountParamName,
    includeOrderParam: value.includeOrderParam,
    searchableColumns: value.searchableColumns.map(
      encodeAdminHeaderAccountSearchableRoute_SearchableColumn
    )
  };
}
export function decodeAdminHeaderAccountSearchableRoute_SearchableColumn(json) {
  return {
    columnName: assertString(json.columnName),
    paramName: assertString(json.paramName)
  };
}
export function encodeAdminHeaderAccountSearchableRoute_SearchableColumn(value) {
  return {
    columnName: value.columnName,
    paramName: value.paramName
  };
}
export function decodeAdminHeaderCurrentUser(json) {
  return {
    id: assertNumber(json.id),
    email: assertString(json.email)
  };
}
export function encodeAdminHeaderCurrentUser(value) {
  return {
    id: value.id,
    email: value.email
  };
}
export function decodeAdminHeaderFlags(json) {
  return {
    navigationItems: (json.navigationItems || []).map(
      decodeAdminNavigationItem
    ),
    accountSearchableRoutes: (json.accountSearchableRoutes || []).map(
      decodeAdminHeaderAccountSearchableRoute
    ),
    siteTitle: assertString(json.siteTitle),
    siteTitleHref: assertString(json.siteTitleHref),
    currentUser: decodeAdminHeaderCurrentUser(json.currentUser)
  };
}
export function encodeAdminHeaderFlags(value) {
  return {
    navigationItems: value.navigationItems.map(encodeAdminNavigationItem),
    accountSearchableRoutes: value.accountSearchableRoutes.map(
      encodeAdminHeaderAccountSearchableRoute
    ),
    siteTitle: value.siteTitle,
    siteTitleHref: value.siteTitleHref,
    currentUser: encodeAdminHeaderCurrentUser(value.currentUser)
  };
}
export function decodeAdminNavigationItem(json) {
  return {
    id: assertString(json.id),
    label: assertString(json.label),
    href: assertString(json.href),
    current: assertBoolean(json.current),
    parentLabel: optionalDecoder(assertString)(json.parentLabel),
    hasShowAction: assertBoolean(json.hasShowAction),
    singularResourceName: assertString(json.singularResourceName)
  };
}
export function encodeAdminNavigationItem(value) {
  return {
    id: value.id,
    label: value.label,
    href: value.href,
    current: value.current,
    parentLabel: value.parentLabel,
    hasShowAction: value.hasShowAction,
    singularResourceName: value.singularResourceName
  };
}
export function decodeAdminSearchBarAccount(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    demo: assertBoolean(json.demo),
    sandbox: assertBoolean(json.sandbox),
    isTestAccount: assertBoolean(json.isTestAccount),
    production: assertBoolean(json.production)
  };
}
export function encodeAdminSearchBarAccount(value) {
  return {
    id: value.id,
    name: value.name,
    demo: value.demo,
    sandbox: value.sandbox,
    isTestAccount: value.isTestAccount,
    production: value.production
  };
}
export function decodeAdminSearchBarServiceSearchAccountsRequest(json) {
  return {
    query: assertString(json.query)
  };
}
export function encodeAdminSearchBarServiceSearchAccountsRequest(value) {
  return {
    query: value.query
  };
}
export function decodeAdminSearchBarServiceSearchAccountsResponse(json) {
  return {
    accounts: (json.accounts || []).map(decodeAdminSearchBarAccount)
  };
}
export function encodeAdminSearchBarServiceSearchAccountsResponse(value) {
  return {
    accounts: value.accounts.map(encodeAdminSearchBarAccount)
  };
}
export function decodeAsyncLatchCompleted(json) {
  return {
    completion_context: assertString(json.completion_context)
  };
}
export function encodeAsyncLatchCompleted(value) {
  return {
    completion_context: value.completion_context
  };
}
export function decodeAsyncLatchCountDownResult(json) {
  return {
    outcome: decodeAsyncLatchCountDownResult_Outcome(json)
  };
}
export function encodeAsyncLatchCountDownResult(value) {
  return __spreadValues({}, encodeAsyncLatchCountDownResult_Outcome(value.outcome));
}
export var AsyncLatchCountDownResult_OutcomeType = /* @__PURE__ */ ((AsyncLatchCountDownResult_OutcomeType2) => {
  AsyncLatchCountDownResult_OutcomeType2["Completed"] = "completed";
  AsyncLatchCountDownResult_OutcomeType2["Counted"] = "counted";
  AsyncLatchCountDownResult_OutcomeType2["NotFound"] = "not_found";
  AsyncLatchCountDownResult_OutcomeType2["NotMember"] = "not_member";
  return AsyncLatchCountDownResult_OutcomeType2;
})(AsyncLatchCountDownResult_OutcomeType || {});
export function decodeAsyncLatchCountDownResult_Outcome(json) {
  if (json.completed !== void 0) {
    return {
      type: "completed" /* Completed */,
      completed: decodeAsyncLatchCompleted(json.completed)
    };
  } else if (json.counted !== void 0) {
    return {
      type: "counted" /* Counted */,
      counted: decodeAsyncLatchCounted(json.counted)
    };
  } else if (json.not_found !== void 0) {
    return {
      type: "not_found" /* NotFound */,
      not_found: decodeAsyncLatchNotFound(json.not_found)
    };
  } else if (json.not_member !== void 0) {
    return {
      type: "not_member" /* NotMember */,
      not_member: decodeAsyncLatchNotMember(json.not_member)
    };
  }
  throw new Error("No field set in oneof AsyncLatchCountDownResult_Outcome");
}
export function encodeAsyncLatchCountDownResult_Outcome(value) {
  switch (value.type) {
    case "completed" /* Completed */:
      return { completed: encodeAsyncLatchCompleted(value.completed) };
    case "counted" /* Counted */:
      return { counted: encodeAsyncLatchCounted(value.counted) };
    case "not_found" /* NotFound */:
      return { not_found: encodeAsyncLatchNotFound(value.not_found) };
    case "not_member" /* NotMember */:
      return { not_member: encodeAsyncLatchNotMember(value.not_member) };
    default:
      throw new Error(
        "No field set in oneof AsyncLatchCountDownResult_Outcome"
      );
  }
}
export function decodeAsyncLatchCounted(json) {
  return {
    remaining: assertNumber(json.remaining)
  };
}
export function encodeAsyncLatchCounted(value) {
  return {
    remaining: value.remaining
  };
}
export function decodeAsyncLatchNotFound(json) {
  return {};
}
export function encodeAsyncLatchNotFound(value) {
  return {};
}
export function decodeAsyncLatchNotMember(json) {
  return {};
}
export function encodeAsyncLatchNotMember(value) {
  return {};
}
export function decodeAttachmentInvoiceSplitsBackfillInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    starting_from: assertString(json.starting_from),
    ending_on: assertString(json.ending_on)
  };
}
export function encodeAttachmentInvoiceSplitsBackfillInput(value) {
  return {
    account_id: value.account_id,
    starting_from: value.starting_from,
    ending_on: value.ending_on
  };
}
export function decodeAutoReplyFilter(json) {
  return {
    id: assertNumber(json.id),
    email: assertString(json.email),
    subject: assertString(json.subject),
    body: assertString(json.body)
  };
}
export function encodeAutoReplyFilter(value) {
  return {
    id: value.id,
    email: value.email,
    subject: value.subject,
    body: value.body
  };
}
export var BillingInterval = /* @__PURE__ */ ((BillingInterval2) => {
  BillingInterval2["BillingIntervalDaily"] = "daily";
  BillingInterval2["BillingIntervalWeekly"] = "weekly";
  BillingInterval2["BillingIntervalMonthly"] = "monthly";
  BillingInterval2["BillingIntervalNever"] = "never";
  BillingInterval2["BillingIntervalImmediately"] = "immediately";
  return BillingInterval2;
})(BillingInterval || {});
export const decodeBillingInterval = (value) => value;
export const encodeBillingInterval = (value) => value;
export function decodeBillingRequirements(json) {
  return {
    customers: (json.customers || []).map(decodeBillingRequirements_Customer)
  };
}
export function encodeBillingRequirements(value) {
  return {
    customers: value.customers.map(encodeBillingRequirements_Customer)
  };
}
export function decodeBillingRequirements_Customer(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    account_number: optionalDecoder(assertString)(json.account_number),
    bill_to_address: optionalDecoder(decodeAddress)(json.bill_to_address),
    billing_email: assertString(json.billing_email),
    required_receivables_types: (json.required_receivables_types || []).map(
      decodeDocumentType
    ),
    pod_requirement_policy: decodePODRequirementPolicy(
      json.pod_requirement_policy
    ),
    references: (json.references || []).map(
      decodeBillingRequirements_Reference
    ),
    instructions_list: (json.instructions_list || []).map(assertString),
    custom_exception_messages: (json.custom_exception_messages || []).map(
      assertString
    ),
    billing_interval: decodeBillingInterval(json.billing_interval)
  };
}
export function encodeBillingRequirements_Customer(value) {
  return {
    id: value.id,
    name: value.name,
    account_number: value.account_number,
    bill_to_address: optionalEncoder(encodeAddress)(value.bill_to_address),
    billing_email: value.billing_email,
    required_receivables_types: value.required_receivables_types.map(encodeDocumentType),
    pod_requirement_policy: encodePODRequirementPolicy(
      value.pod_requirement_policy
    ),
    references: value.references.map(encodeBillingRequirements_Reference),
    instructions_list: value.instructions_list,
    custom_exception_messages: value.custom_exception_messages,
    billing_interval: encodeBillingInterval(value.billing_interval)
  };
}
export function decodeBillingRequirements_Reference(json) {
  return {
    name: assertString(json.name),
    values: (json.values || []).map(assertString)
  };
}
export function encodeBillingRequirements_Reference(value) {
  return {
    name: value.name,
    values: value.values
  };
}
export function decodeBrokerClientBrokeredCarrierCheck(json) {
  return {};
}
export function encodeBrokerClientBrokeredCarrierCheck(value) {
  return {};
}
export function decodeBrokerClientBrokeredCarrierTermsCheck(json) {
  return {};
}
export function encodeBrokerClientBrokeredCarrierTermsCheck(value) {
  return {};
}
export function decodeBrokerClientCarrierInvoiceNumberCheck(json) {
  return {};
}
export function encodeBrokerClientCarrierInvoiceNumberCheck(value) {
  return {};
}
export function decodeBrokerClientRequirementsCheck(json) {
  return {};
}
export function encodeBrokerClientRequirementsCheck(value) {
  return {};
}
export function decodeBrokerClientRequirementsCheckBrokeredCarrierOption(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name)
  };
}
export function encodeBrokerClientRequirementsCheckBrokeredCarrierOption(value) {
  return {
    id: value.id,
    name: value.name
  };
}
export function decodeBrokeredCarrier(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    mc_number: optionalDecoder(assertString)(json.mc_number),
    dot_number: optionalDecoder(assertString)(json.dot_number)
  };
}
export function encodeBrokeredCarrier(value) {
  return {
    id: value.id,
    name: value.name,
    mc_number: value.mc_number,
    dot_number: value.dot_number
  };
}
export function decodeBrokeredCarrierSettingsIdsQuery(json) {
  return {
    page: assertNumber(json.page),
    term: assertString(json.term)
  };
}
export function encodeBrokeredCarrierSettingsIdsQuery(value) {
  return {
    page: value.page,
    term: value.term
  };
}
export function decodeBrokeredCarrierTerm(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    days: optionalDecoder(assertNumber)(json.days),
    rate: optionalDecoder(assertString)(json.rate),
    flat_rate: optionalDecoder(assertString)(json.flat_rate)
  };
}
export function encodeBrokeredCarrierTerm(value) {
  return {
    id: value.id,
    name: value.name,
    days: value.days,
    rate: value.rate,
    flat_rate: value.flat_rate
  };
}
export function decodeBrokeredCarrierTermSettingsIdsQuery(json) {
  return {
    page: assertNumber(json.page),
    term: assertString(json.term),
    program_external_id: assertString(json.program_external_id)
  };
}
export function encodeBrokeredCarrierTermSettingsIdsQuery(value) {
  return {
    page: value.page,
    term: value.term,
    program_external_id: value.program_external_id
  };
}
export function decodeCadenceDebtorContact(json) {
  return {
    id: assertNumber(json.id),
    first_name: assertString(json.first_name),
    last_name: assertString(json.last_name),
    email: assertString(json.email),
    phone: assertString(json.phone)
  };
}
export function encodeCadenceDebtorContact(value) {
  return {
    id: value.id,
    first_name: value.first_name,
    last_name: value.last_name,
    email: value.email,
    phone: value.phone
  };
}
export function decodeCadenceVerificationMethod(json) {
  return {
    id: assertNumber(json.id),
    description: assertString(json.description)
  };
}
export function encodeCadenceVerificationMethod(value) {
  return {
    id: value.id,
    description: value.description
  };
}
export function decodeCarrierInvoice(json) {
  return {
    id: assertNumber(json.id),
    carrier_id: optionalDecoder(assertNumber)(json.carrier_id),
    load_state_id: assertNumber(json.load_state_id),
    labels: (json.labels || []).map(assertString),
    number: optionalDecoder(assertString)(json.number),
    amount_to_pay: optionalDecoder(assertString)(json.amount_to_pay),
    date_to_pay: optionalDecoder(decodeTimestamp)(json.date_to_pay),
    approved_at: optionalDecoder(decodeTimestamp)(json.approved_at),
    status: assertString(json.status),
    ltl_data: decodeCarrierInvoiceLtlData(json.ltl_data),
    processed_at: optionalDecoder(decodeTimestamp)(json.processed_at),
    customer_margin_data: decodeCarrierInvoiceCustomerMarginData(
      json.customer_margin_data
    ),
    pod_requirement_skipped: optionalDecoder(assertBoolean)(
      json.pod_requirement_skipped
    ),
    source_invoice_number: optionalDecoder(assertString)(
      json.source_invoice_number
    ),
    invoice_amount: optionalDecoder(assertString)(json.invoice_amount),
    invoice_date: optionalDecoder(assertString)(json.invoice_date),
    is_editable: assertBoolean(json.is_editable),
    finish_actions: decodeCarrierInvoiceFinishActions(json.finish_actions),
    may_save_with_errors: assertBoolean(json.may_save_with_errors),
    carrier_labels: (json.carrier_labels || []).map(assertString)
  };
}
export function encodeCarrierInvoice(value) {
  return {
    id: value.id,
    carrier_id: value.carrier_id,
    load_state_id: value.load_state_id,
    labels: value.labels,
    number: value.number,
    amount_to_pay: value.amount_to_pay,
    date_to_pay: optionalEncoder(encodeTimestamp)(value.date_to_pay),
    approved_at: optionalEncoder(encodeTimestamp)(value.approved_at),
    status: value.status,
    ltl_data: encodeCarrierInvoiceLtlData(value.ltl_data),
    processed_at: optionalEncoder(encodeTimestamp)(value.processed_at),
    customer_margin_data: encodeCarrierInvoiceCustomerMarginData(
      value.customer_margin_data
    ),
    pod_requirement_skipped: value.pod_requirement_skipped,
    source_invoice_number: value.source_invoice_number,
    invoice_amount: value.invoice_amount,
    invoice_date: value.invoice_date,
    is_editable: value.is_editable,
    finish_actions: encodeCarrierInvoiceFinishActions(value.finish_actions),
    may_save_with_errors: value.may_save_with_errors,
    carrier_labels: value.carrier_labels
  };
}
export function decodeCarrierInvoiceAuditCheck(json) {
  return {
    result: decodeCarrierInvoiceAuditCheckResult(json.result),
    details: decodeCarrierInvoiceAuditCheck_Details(json)
  };
}
export function encodeCarrierInvoiceAuditCheck(value) {
  return __spreadValues({
    result: encodeCarrierInvoiceAuditCheckResult(value.result)
  }, encodeCarrierInvoiceAuditCheck_Details(value.details));
}
export var CarrierInvoiceAuditCheck_DetailsType = /* @__PURE__ */ ((CarrierInvoiceAuditCheck_DetailsType2) => {
  CarrierInvoiceAuditCheck_DetailsType2["RuleCheck"] = "ruleCheck";
  CarrierInvoiceAuditCheck_DetailsType2["ExceptionEvent"] = "exceptionEvent";
  return CarrierInvoiceAuditCheck_DetailsType2;
})(CarrierInvoiceAuditCheck_DetailsType || {});
export function decodeCarrierInvoiceAuditCheck_Details(json) {
  if (json.ruleCheck !== void 0) {
    return {
      type: "ruleCheck" /* RuleCheck */,
      ruleCheck: decodeCarrierInvoiceAuditRuleCheck(json.ruleCheck)
    };
  } else if (json.exceptionEvent !== void 0) {
    return {
      type: "exceptionEvent" /* ExceptionEvent */,
      exceptionEvent: decodeExceptionEvent(json.exceptionEvent)
    };
  }
  throw new Error("No field set in oneof CarrierInvoiceAuditCheck_Details");
}
export function encodeCarrierInvoiceAuditCheck_Details(value) {
  switch (value.type) {
    case "ruleCheck" /* RuleCheck */:
      return { ruleCheck: encodeCarrierInvoiceAuditRuleCheck(value.ruleCheck) };
    case "exceptionEvent" /* ExceptionEvent */:
      return { exceptionEvent: encodeExceptionEvent(value.exceptionEvent) };
    default:
      throw new Error("No field set in oneof CarrierInvoiceAuditCheck_Details");
  }
}
export function decodeCarrierInvoiceAuditCheckFailure(json) {
  return {
    id: assertString(json.id),
    message: assertString(json.message),
    ignorable: assertBoolean(json.ignorable)
  };
}
export function encodeCarrierInvoiceAuditCheckFailure(value) {
  return {
    id: value.id,
    message: value.message,
    ignorable: value.ignorable
  };
}
export var CarrierInvoiceAuditCheckResult = /* @__PURE__ */ ((CarrierInvoiceAuditCheckResult2) => {
  CarrierInvoiceAuditCheckResult2["CarrierInvoiceAuditCheckResultSkipped"] = "skipped";
  CarrierInvoiceAuditCheckResult2["CarrierInvoiceAuditCheckResultPassed"] = "passed";
  CarrierInvoiceAuditCheckResult2["CarrierInvoiceAuditCheckResultFailed"] = "failed";
  CarrierInvoiceAuditCheckResult2["CarrierInvoiceAuditCheckResultIgnored"] = "ignored";
  CarrierInvoiceAuditCheckResult2["CarrierInvoiceAuditCheckResultResolved"] = "resolved";
  return CarrierInvoiceAuditCheckResult2;
})(CarrierInvoiceAuditCheckResult || {});
export const decodeCarrierInvoiceAuditCheckResult = (value) => value;
export const encodeCarrierInvoiceAuditCheckResult = (value) => value;
export function decodeCarrierInvoiceAuditCheckType(json) {
  return {
    check: decodeCarrierInvoiceAuditCheckType_Check(json)
  };
}
export function encodeCarrierInvoiceAuditCheckType(value) {
  return __spreadValues({}, encodeCarrierInvoiceAuditCheckType_Check(value.check));
}
export var CarrierInvoiceAuditCheckType_CheckType = /* @__PURE__ */ ((CarrierInvoiceAuditCheckType_CheckType2) => {
  CarrierInvoiceAuditCheckType_CheckType2["Other"] = "other";
  CarrierInvoiceAuditCheckType_CheckType2["DocumentsRequired"] = "documentsRequired";
  CarrierInvoiceAuditCheckType_CheckType2["LoadMatch"] = "loadMatch";
  CarrierInvoiceAuditCheckType_CheckType2["InvoiceAmount"] = "invoiceAmount";
  CarrierInvoiceAuditCheckType_CheckType2["CarrierMatch"] = "carrierMatch";
  CarrierInvoiceAuditCheckType_CheckType2["DuplicateInvoice"] = "duplicateInvoice";
  CarrierInvoiceAuditCheckType_CheckType2["InvoiceDate"] = "invoiceDate";
  CarrierInvoiceAuditCheckType_CheckType2["DocumentReview"] = "documentReview";
  CarrierInvoiceAuditCheckType_CheckType2["ExtractedFields"] = "extractedFields";
  CarrierInvoiceAuditCheckType_CheckType2["Osd"] = "osd";
  CarrierInvoiceAuditCheckType_CheckType2["CustomExceptionMessage"] = "customExceptionMessage";
  CarrierInvoiceAuditCheckType_CheckType2["ReferenceRequirements"] = "referenceRequirements";
  CarrierInvoiceAuditCheckType_CheckType2["LabelRuleFailure"] = "labelRuleFailure";
  CarrierInvoiceAuditCheckType_CheckType2["CarrierRate"] = "carrierRate";
  CarrierInvoiceAuditCheckType_CheckType2["CustomerInstructions"] = "customerInstructions";
  CarrierInvoiceAuditCheckType_CheckType2["NoteOnLoad"] = "noteOnLoad";
  CarrierInvoiceAuditCheckType_CheckType2["InvoiceNumber"] = "invoiceNumber";
  return CarrierInvoiceAuditCheckType_CheckType2;
})(CarrierInvoiceAuditCheckType_CheckType || {});
export function decodeCarrierInvoiceAuditCheckType_Check(json) {
  if (json.other !== void 0) {
    return {
      type: "other" /* Other */,
      other: decodeOtherCheck(json.other)
    };
  } else if (json.documentsRequired !== void 0) {
    return {
      type: "documentsRequired" /* DocumentsRequired */,
      documentsRequired: decodeDocumentsRequiredCheck(json.documentsRequired)
    };
  } else if (json.loadMatch !== void 0) {
    return {
      type: "loadMatch" /* LoadMatch */,
      loadMatch: decodeLoadMatchCheck(json.loadMatch)
    };
  } else if (json.invoiceAmount !== void 0) {
    return {
      type: "invoiceAmount" /* InvoiceAmount */,
      invoiceAmount: decodeCarrierInvoiceInvoiceAmountCheck(json.invoiceAmount)
    };
  } else if (json.carrierMatch !== void 0) {
    return {
      type: "carrierMatch" /* CarrierMatch */,
      carrierMatch: decodeCarrierMatchCheck(json.carrierMatch)
    };
  } else if (json.duplicateInvoice !== void 0) {
    return {
      type: "duplicateInvoice" /* DuplicateInvoice */,
      duplicateInvoice: decodeCarrierInvoiceDuplicateInvoiceCheck(
        json.duplicateInvoice
      )
    };
  } else if (json.invoiceDate !== void 0) {
    return {
      type: "invoiceDate" /* InvoiceDate */,
      invoiceDate: decodeCarrierInvoiceInvoiceDateCheck(json.invoiceDate)
    };
  } else if (json.documentReview !== void 0) {
    return {
      type: "documentReview" /* DocumentReview */,
      documentReview: decodeDocumentReviewCheck(json.documentReview)
    };
  } else if (json.extractedFields !== void 0) {
    return {
      type: "extractedFields" /* ExtractedFields */,
      extractedFields: decodeExtractedFieldsCheck(json.extractedFields)
    };
  } else if (json.osd !== void 0) {
    return {
      type: "osd" /* Osd */,
      osd: decodeOsdCheck(json.osd)
    };
  } else if (json.customExceptionMessage !== void 0) {
    return {
      type: "customExceptionMessage" /* CustomExceptionMessage */,
      customExceptionMessage: decodeCustomExceptionMessageCheck(
        json.customExceptionMessage
      )
    };
  } else if (json.referenceRequirements !== void 0) {
    return {
      type: "referenceRequirements" /* ReferenceRequirements */,
      referenceRequirements: decodeReferenceRequirementsCheck(
        json.referenceRequirements
      )
    };
  } else if (json.labelRuleFailure !== void 0) {
    return {
      type: "labelRuleFailure" /* LabelRuleFailure */,
      labelRuleFailure: decodeLabelRuleFailureCheck(json.labelRuleFailure)
    };
  } else if (json.carrierRate !== void 0) {
    return {
      type: "carrierRate" /* CarrierRate */,
      carrierRate: decodeCarrierRateCheck(json.carrierRate)
    };
  } else if (json.customerInstructions !== void 0) {
    return {
      type: "customerInstructions" /* CustomerInstructions */,
      customerInstructions: decodeCustomerInstructionsCheck(
        json.customerInstructions
      )
    };
  } else if (json.noteOnLoad !== void 0) {
    return {
      type: "noteOnLoad" /* NoteOnLoad */,
      noteOnLoad: decodeNoteOnLoadCheck(json.noteOnLoad)
    };
  } else if (json.invoiceNumber !== void 0) {
    return {
      type: "invoiceNumber" /* InvoiceNumber */,
      invoiceNumber: decodeCarrierInvoiceInvoiceNumberCheck(json.invoiceNumber)
    };
  }
  throw new Error("No field set in oneof CarrierInvoiceAuditCheckType_Check");
}
export function encodeCarrierInvoiceAuditCheckType_Check(value) {
  switch (value.type) {
    case "other" /* Other */:
      return { other: encodeOtherCheck(value.other) };
    case "documentsRequired" /* DocumentsRequired */:
      return {
        documentsRequired: encodeDocumentsRequiredCheck(
          value.documentsRequired
        )
      };
    case "loadMatch" /* LoadMatch */:
      return { loadMatch: encodeLoadMatchCheck(value.loadMatch) };
    case "invoiceAmount" /* InvoiceAmount */:
      return {
        invoiceAmount: encodeCarrierInvoiceInvoiceAmountCheck(
          value.invoiceAmount
        )
      };
    case "carrierMatch" /* CarrierMatch */:
      return { carrierMatch: encodeCarrierMatchCheck(value.carrierMatch) };
    case "duplicateInvoice" /* DuplicateInvoice */:
      return {
        duplicateInvoice: encodeCarrierInvoiceDuplicateInvoiceCheck(
          value.duplicateInvoice
        )
      };
    case "invoiceDate" /* InvoiceDate */:
      return {
        invoiceDate: encodeCarrierInvoiceInvoiceDateCheck(value.invoiceDate)
      };
    case "documentReview" /* DocumentReview */:
      return {
        documentReview: encodeDocumentReviewCheck(value.documentReview)
      };
    case "extractedFields" /* ExtractedFields */:
      return {
        extractedFields: encodeExtractedFieldsCheck(value.extractedFields)
      };
    case "osd" /* Osd */:
      return { osd: encodeOsdCheck(value.osd) };
    case "customExceptionMessage" /* CustomExceptionMessage */:
      return {
        customExceptionMessage: encodeCustomExceptionMessageCheck(
          value.customExceptionMessage
        )
      };
    case "referenceRequirements" /* ReferenceRequirements */:
      return {
        referenceRequirements: encodeReferenceRequirementsCheck(
          value.referenceRequirements
        )
      };
    case "labelRuleFailure" /* LabelRuleFailure */:
      return {
        labelRuleFailure: encodeLabelRuleFailureCheck(value.labelRuleFailure)
      };
    case "carrierRate" /* CarrierRate */:
      return { carrierRate: encodeCarrierRateCheck(value.carrierRate) };
    case "customerInstructions" /* CustomerInstructions */:
      return {
        customerInstructions: encodeCustomerInstructionsCheck(
          value.customerInstructions
        )
      };
    case "noteOnLoad" /* NoteOnLoad */:
      return { noteOnLoad: encodeNoteOnLoadCheck(value.noteOnLoad) };
    case "invoiceNumber" /* InvoiceNumber */:
      return {
        invoiceNumber: encodeCarrierInvoiceInvoiceNumberCheck(
          value.invoiceNumber
        )
      };
    default:
      throw new Error(
        "No field set in oneof CarrierInvoiceAuditCheckType_Check"
      );
  }
}
export function decodeCarrierInvoiceAuditRuleCheck(json) {
  return {
    checkType: decodeCarrierInvoiceAuditCheckType(json.checkType),
    failures: (json.failures || []).map(decodeCarrierInvoiceAuditCheckFailure),
    ignoredFailures: (json.ignoredFailures || []).map(
      decodeCarrierInvoiceAuditCheckFailure
    )
  };
}
export function encodeCarrierInvoiceAuditRuleCheck(value) {
  return {
    checkType: encodeCarrierInvoiceAuditCheckType(value.checkType),
    failures: value.failures.map(encodeCarrierInvoiceAuditCheckFailure),
    ignoredFailures: value.ignoredFailures.map(
      encodeCarrierInvoiceAuditCheckFailure
    )
  };
}
export function decodeCarrierInvoiceCustomerMarginData(json) {
  return {
    dollarDifference: assertNumber(json.dollarDifference),
    percentDifference: assertNumber(json.percentDifference),
    customerCharge: assertNumber(json.customerCharge),
    carrierCharge: assertNumber(json.carrierCharge)
  };
}
export function encodeCarrierInvoiceCustomerMarginData(value) {
  return {
    dollarDifference: value.dollarDifference,
    percentDifference: value.percentDifference,
    customerCharge: value.customerCharge,
    carrierCharge: value.carrierCharge
  };
}
export function decodeCarrierInvoiceDashboardRow(json) {
  return {
    id: assertNumber(json.id),
    load_state_id: assertNumber(json.load_state_id),
    labels: (json.labels || []).map(assertString),
    requires_reconciliation: assertBoolean(json.requires_reconciliation),
    load_id: assertString(json.load_id),
    load_label: optionalDecoder(
      decodeCarrierInvoiceDashboardRow_DashboardLoadLabels
    )(json.load_label),
    delivered_at: optionalDecoder(decodeTimestamp)(json.delivered_at),
    tms_carrier_id: optionalDecoder(assertNumber)(json.tms_carrier_id),
    tms_carrier_name: optionalDecoder(assertString)(json.tms_carrier_name),
    load_customer: optionalDecoder(decodeDashboardCustomer)(json.load_customer),
    shipment_customers: (json.shipment_customers || []).map(
      decodeDashboardCustomer
    )
  };
}
export function encodeCarrierInvoiceDashboardRow(value) {
  return {
    id: value.id,
    load_state_id: value.load_state_id,
    labels: value.labels,
    requires_reconciliation: value.requires_reconciliation,
    load_id: value.load_id,
    load_label: optionalEncoder(
      encodeCarrierInvoiceDashboardRow_DashboardLoadLabels
    )(value.load_label),
    delivered_at: optionalEncoder(encodeTimestamp)(value.delivered_at),
    tms_carrier_id: value.tms_carrier_id,
    tms_carrier_name: value.tms_carrier_name,
    load_customer: optionalEncoder(encodeDashboardCustomer)(
      value.load_customer
    ),
    shipment_customers: value.shipment_customers.map(encodeDashboardCustomer)
  };
}
export function decodeCarrierInvoiceDashboardRow_DashboardLoadLabels(json) {
  return {
    label: assertString(json.label),
    identifier: assertString(json.identifier)
  };
}
export function encodeCarrierInvoiceDashboardRow_DashboardLoadLabels(value) {
  return {
    label: value.label,
    identifier: value.identifier
  };
}
export function decodeCarrierInvoiceDuplicateInvoiceCheck(json) {
  return {};
}
export function encodeCarrierInvoiceDuplicateInvoiceCheck(value) {
  return {};
}
export function decodeCarrierInvoiceExpectedAmount(json) {
  return {
    rate: assertString(json.rate),
    expected: assertString(json.expected),
    billed: assertString(json.billed),
    rateSource: decodeCarrierInvoiceExpectedAmount_RateSource(json)
  };
}
export function encodeCarrierInvoiceExpectedAmount(value) {
  return __spreadValues({
    rate: value.rate,
    expected: value.expected,
    billed: value.billed
  }, encodeCarrierInvoiceExpectedAmount_RateSource(value.rateSource));
}
export var CarrierInvoiceExpectedAmount_RateSourceType = /* @__PURE__ */ ((CarrierInvoiceExpectedAmount_RateSourceType2) => {
  CarrierInvoiceExpectedAmount_RateSourceType2["CarrierLineItems"] = "carrierLineItems";
  CarrierInvoiceExpectedAmount_RateSourceType2["LoadCarrierCharge"] = "loadCarrierCharge";
  CarrierInvoiceExpectedAmount_RateSourceType2["NoCarrierLineItems"] = "noCarrierLineItems";
  return CarrierInvoiceExpectedAmount_RateSourceType2;
})(CarrierInvoiceExpectedAmount_RateSourceType || {});
export function decodeCarrierInvoiceExpectedAmount_RateSource(json) {
  if (json.carrierLineItems !== void 0) {
    return {
      type: "carrierLineItems" /* CarrierLineItems */,
      carrierLineItems: decodeCarrierInvoiceExpectedAmount_CarrierLineItems(
        json.carrierLineItems
      )
    };
  } else if (json.loadCarrierCharge !== void 0) {
    return {
      type: "loadCarrierCharge" /* LoadCarrierCharge */,
      loadCarrierCharge: decodeCarrierInvoiceExpectedAmount_LoadCarrierCharge(
        json.loadCarrierCharge
      )
    };
  } else if (json.noCarrierLineItems !== void 0) {
    return {
      type: "noCarrierLineItems" /* NoCarrierLineItems */,
      noCarrierLineItems: decodeCarrierInvoiceExpectedAmount_NoCarrierLineItems(
        json.noCarrierLineItems
      )
    };
  }
  throw new Error(
    "No field set in oneof CarrierInvoiceExpectedAmount_RateSource"
  );
}
export function encodeCarrierInvoiceExpectedAmount_RateSource(value) {
  switch (value.type) {
    case "carrierLineItems" /* CarrierLineItems */:
      return {
        carrierLineItems: encodeCarrierInvoiceExpectedAmount_CarrierLineItems(
          value.carrierLineItems
        )
      };
    case "loadCarrierCharge" /* LoadCarrierCharge */:
      return {
        loadCarrierCharge: encodeCarrierInvoiceExpectedAmount_LoadCarrierCharge(
          value.loadCarrierCharge
        )
      };
    case "noCarrierLineItems" /* NoCarrierLineItems */:
      return {
        noCarrierLineItems: encodeCarrierInvoiceExpectedAmount_NoCarrierLineItems(
          value.noCarrierLineItems
        )
      };
    default:
      throw new Error(
        "No field set in oneof CarrierInvoiceExpectedAmount_RateSource"
      );
  }
}
export function decodeCarrierInvoiceExpectedAmount_CarrierLineItems(json) {
  return {
    carrierName: optionalDecoder(assertString)(json.carrierName)
  };
}
export function encodeCarrierInvoiceExpectedAmount_CarrierLineItems(value) {
  return {
    carrierName: value.carrierName
  };
}
export function decodeCarrierInvoiceExpectedAmount_LoadCarrierCharge(json) {
  return {};
}
export function encodeCarrierInvoiceExpectedAmount_LoadCarrierCharge(value) {
  return {};
}
export function decodeCarrierInvoiceExpectedAmount_NoCarrierLineItems(json) {
  return {
    carrierName: optionalDecoder(assertString)(json.carrierName)
  };
}
export function encodeCarrierInvoiceExpectedAmount_NoCarrierLineItems(value) {
  return {
    carrierName: value.carrierName
  };
}
export function decodeCarrierInvoiceExtractedField(json) {
  return {
    name: assertString(json.name),
    label: assertString(json.label),
    value: assertString(json.value),
    required: assertBoolean(json.required)
  };
}
export function encodeCarrierInvoiceExtractedField(value) {
  return {
    name: value.name,
    label: value.label,
    value: value.value,
    required: value.required
  };
}
export function decodeCarrierInvoiceExtractedFieldUpdate(json) {
  return {
    name: assertString(json.name),
    value: assertString(json.value)
  };
}
export function encodeCarrierInvoiceExtractedFieldUpdate(value) {
  return {
    name: value.name,
    value: value.value
  };
}
export function decodeCarrierInvoiceFieldUpdate(json) {
  return {
    update: decodeCarrierInvoiceFieldUpdate_Update(json)
  };
}
export function encodeCarrierInvoiceFieldUpdate(value) {
  return __spreadValues({}, encodeCarrierInvoiceFieldUpdate_Update(value.update));
}
export var CarrierInvoiceFieldUpdate_UpdateType = /* @__PURE__ */ ((CarrierInvoiceFieldUpdate_UpdateType2) => {
  CarrierInvoiceFieldUpdate_UpdateType2["InvoiceNumberUpdate"] = "invoice_number_update";
  CarrierInvoiceFieldUpdate_UpdateType2["InvoiceAmountUpdate"] = "invoice_amount_update";
  CarrierInvoiceFieldUpdate_UpdateType2["InvoiceDateUpdate"] = "invoice_date_update";
  CarrierInvoiceFieldUpdate_UpdateType2["AmountToPayUpdate"] = "amount_to_pay_update";
  CarrierInvoiceFieldUpdate_UpdateType2["ExtractedFieldsUpdate"] = "extracted_fields_update";
  CarrierInvoiceFieldUpdate_UpdateType2["AddLabelUpdate"] = "add_label_update";
  CarrierInvoiceFieldUpdate_UpdateType2["RemoveLabelUpdate"] = "remove_label_update";
  return CarrierInvoiceFieldUpdate_UpdateType2;
})(CarrierInvoiceFieldUpdate_UpdateType || {});
export function decodeCarrierInvoiceFieldUpdate_Update(json) {
  if (json.invoice_number_update !== void 0) {
    return {
      type: "invoice_number_update" /* InvoiceNumberUpdate */,
      invoice_number_update: decodeCarrierInvoiceFieldUpdateInvoiceNumber(
        json.invoice_number_update
      )
    };
  } else if (json.invoice_amount_update !== void 0) {
    return {
      type: "invoice_amount_update" /* InvoiceAmountUpdate */,
      invoice_amount_update: decodeCarrierInvoiceFieldUpdateInvoiceAmount(
        json.invoice_amount_update
      )
    };
  } else if (json.invoice_date_update !== void 0) {
    return {
      type: "invoice_date_update" /* InvoiceDateUpdate */,
      invoice_date_update: decodeCarrierInvoiceFieldUpdateInvoiceDate(
        json.invoice_date_update
      )
    };
  } else if (json.amount_to_pay_update !== void 0) {
    return {
      type: "amount_to_pay_update" /* AmountToPayUpdate */,
      amount_to_pay_update: decodeCarrierInvoiceFieldUpdateAmountToPay(
        json.amount_to_pay_update
      )
    };
  } else if (json.extracted_fields_update !== void 0) {
    return {
      type: "extracted_fields_update" /* ExtractedFieldsUpdate */,
      extracted_fields_update: decodeCarrierInvoiceFieldUpdateExtractedFields(
        json.extracted_fields_update
      )
    };
  } else if (json.add_label_update !== void 0) {
    return {
      type: "add_label_update" /* AddLabelUpdate */,
      add_label_update: decodeCarrierInvoiceFieldUpdateAddLabel(
        json.add_label_update
      )
    };
  } else if (json.remove_label_update !== void 0) {
    return {
      type: "remove_label_update" /* RemoveLabelUpdate */,
      remove_label_update: decodeCarrierInvoiceFieldUpdateRemoveLabel(
        json.remove_label_update
      )
    };
  }
  throw new Error("No field set in oneof CarrierInvoiceFieldUpdate_Update");
}
export function encodeCarrierInvoiceFieldUpdate_Update(value) {
  switch (value.type) {
    case "invoice_number_update" /* InvoiceNumberUpdate */:
      return {
        invoice_number_update: encodeCarrierInvoiceFieldUpdateInvoiceNumber(
          value.invoice_number_update
        )
      };
    case "invoice_amount_update" /* InvoiceAmountUpdate */:
      return {
        invoice_amount_update: encodeCarrierInvoiceFieldUpdateInvoiceAmount(
          value.invoice_amount_update
        )
      };
    case "invoice_date_update" /* InvoiceDateUpdate */:
      return {
        invoice_date_update: encodeCarrierInvoiceFieldUpdateInvoiceDate(
          value.invoice_date_update
        )
      };
    case "amount_to_pay_update" /* AmountToPayUpdate */:
      return {
        amount_to_pay_update: encodeCarrierInvoiceFieldUpdateAmountToPay(
          value.amount_to_pay_update
        )
      };
    case "extracted_fields_update" /* ExtractedFieldsUpdate */:
      return {
        extracted_fields_update: encodeCarrierInvoiceFieldUpdateExtractedFields(
          value.extracted_fields_update
        )
      };
    case "add_label_update" /* AddLabelUpdate */:
      return {
        add_label_update: encodeCarrierInvoiceFieldUpdateAddLabel(
          value.add_label_update
        )
      };
    case "remove_label_update" /* RemoveLabelUpdate */:
      return {
        remove_label_update: encodeCarrierInvoiceFieldUpdateRemoveLabel(
          value.remove_label_update
        )
      };
    default:
      throw new Error("No field set in oneof CarrierInvoiceFieldUpdate_Update");
  }
}
export function decodeCarrierInvoiceFieldUpdateAddLabel(json) {
  return {
    name: assertString(json.name)
  };
}
export function encodeCarrierInvoiceFieldUpdateAddLabel(value) {
  return {
    name: value.name
  };
}
export function decodeCarrierInvoiceFieldUpdateAmountToPay(json) {
  return {
    amountToPay: assertString(json.amountToPay)
  };
}
export function encodeCarrierInvoiceFieldUpdateAmountToPay(value) {
  return {
    amountToPay: value.amountToPay
  };
}
export function decodeCarrierInvoiceFieldUpdateExtractedFields(json) {
  return {
    fields: (json.fields || []).map(decodeCarrierInvoiceExtractedFieldUpdate)
  };
}
export function encodeCarrierInvoiceFieldUpdateExtractedFields(value) {
  return {
    fields: value.fields.map(encodeCarrierInvoiceExtractedFieldUpdate)
  };
}
export function decodeCarrierInvoiceFieldUpdateInvoiceAmount(json) {
  return {
    invoiceAmount: assertString(json.invoiceAmount)
  };
}
export function encodeCarrierInvoiceFieldUpdateInvoiceAmount(value) {
  return {
    invoiceAmount: value.invoiceAmount
  };
}
export function decodeCarrierInvoiceFieldUpdateInvoiceDate(json) {
  return {
    invoiceDate: assertString(json.invoiceDate)
  };
}
export function encodeCarrierInvoiceFieldUpdateInvoiceDate(value) {
  return {
    invoiceDate: value.invoiceDate
  };
}
export function decodeCarrierInvoiceFieldUpdateInvoiceNumber(json) {
  return {
    invoiceNumber: assertString(json.invoiceNumber)
  };
}
export function encodeCarrierInvoiceFieldUpdateInvoiceNumber(value) {
  return {
    invoiceNumber: value.invoiceNumber
  };
}
export function decodeCarrierInvoiceFieldUpdateRemoveLabel(json) {
  return {
    name: assertString(json.name)
  };
}
export function encodeCarrierInvoiceFieldUpdateRemoveLabel(value) {
  return {
    name: value.name
  };
}
export var CarrierInvoiceFinishActions = /* @__PURE__ */ ((CarrierInvoiceFinishActions2) => {
  CarrierInvoiceFinishActions2["CarrierInvoiceFinishActionsNone"] = "none";
  CarrierInvoiceFinishActions2["CarrierInvoiceFinishActionsProcessOnly"] = "process_only";
  CarrierInvoiceFinishActions2["CarrierInvoiceFinishActionsProcessAndApprove"] = "process_and_approve";
  CarrierInvoiceFinishActions2["CarrierInvoiceFinishActionsProcessOrApprove"] = "process_or_approve";
  return CarrierInvoiceFinishActions2;
})(CarrierInvoiceFinishActions || {});
export const decodeCarrierInvoiceFinishActions = (value) => value;
export const encodeCarrierInvoiceFinishActions = (value) => value;
export function decodeCarrierInvoiceInvoiceAmountCheck(json) {
  return {
    expectedAmount: optionalDecoder(decodeCarrierInvoiceExpectedAmount)(
      json.expectedAmount
    )
  };
}
export function encodeCarrierInvoiceInvoiceAmountCheck(value) {
  return {
    expectedAmount: optionalEncoder(encodeCarrierInvoiceExpectedAmount)(
      value.expectedAmount
    )
  };
}
export function decodeCarrierInvoiceInvoiceDateCheck(json) {
  return {};
}
export function encodeCarrierInvoiceInvoiceDateCheck(value) {
  return {};
}
export function decodeCarrierInvoiceInvoiceNumberCheck(json) {
  return {};
}
export function encodeCarrierInvoiceInvoiceNumberCheck(value) {
  return {};
}
export function decodeCarrierInvoiceLtlData(json) {
  return {
    hasSubmission: assertBoolean(json.hasSubmission),
    requiresReconciliation: assertBoolean(json.requiresReconciliation),
    reRatingStatus: decodeCarrierInvoiceReRatingStatus(json.reRatingStatus),
    ltlShippedItemReconcileItems: (json.ltlShippedItemReconcileItems || []).map(
      decodeLtlCarrierInvoiceReconcileRequest_ShippedItemReconcileItem
    ),
    ltlLineItemReconcileItems: (json.ltlLineItemReconcileItems || []).map(
      decodeLtlCarrierInvoiceReconcileRequest_LineItemReconcileItem
    ),
    customerRerateEnabled: assertBoolean(json.customerRerateEnabled)
  };
}
export function encodeCarrierInvoiceLtlData(value) {
  return {
    hasSubmission: value.hasSubmission,
    requiresReconciliation: value.requiresReconciliation,
    reRatingStatus: encodeCarrierInvoiceReRatingStatus(value.reRatingStatus),
    ltlShippedItemReconcileItems: value.ltlShippedItemReconcileItems.map(
      encodeLtlCarrierInvoiceReconcileRequest_ShippedItemReconcileItem
    ),
    ltlLineItemReconcileItems: value.ltlLineItemReconcileItems.map(
      encodeLtlCarrierInvoiceReconcileRequest_LineItemReconcileItem
    ),
    customerRerateEnabled: value.customerRerateEnabled
  };
}
export var CarrierInvoiceReRatingStatus = /* @__PURE__ */ ((CarrierInvoiceReRatingStatus2) => {
  CarrierInvoiceReRatingStatus2["CarrierInvoiceReRatingStatusNone"] = "none";
  CarrierInvoiceReRatingStatus2["CarrierInvoiceReRatingStatusInProgress"] = "in_progress";
  CarrierInvoiceReRatingStatus2["CarrierInvoiceReRatingStatusCompleted"] = "completed";
  CarrierInvoiceReRatingStatus2["CarrierInvoiceReRatingStatusTimeout"] = "timeout";
  CarrierInvoiceReRatingStatus2["CarrierInvoiceReRatingStatusFailed"] = "failed";
  return CarrierInvoiceReRatingStatus2;
})(CarrierInvoiceReRatingStatus || {});
export const decodeCarrierInvoiceReRatingStatus = (value) => value;
export const encodeCarrierInvoiceReRatingStatus = (value) => value;
export function decodeCarrierInvoiceRequest(json) {
  return {
    carrierInvoiceId: assertNumber(json.carrierInvoiceId)
  };
}
export function encodeCarrierInvoiceRequest(value) {
  return {
    carrierInvoiceId: value.carrierInvoiceId
  };
}
export var CarrierInvoiceSaveRequestSource = /* @__PURE__ */ ((CarrierInvoiceSaveRequestSource2) => {
  CarrierInvoiceSaveRequestSource2["CarrierInvoiceSaveRequestSourceNone"] = "none";
  CarrierInvoiceSaveRequestSource2["CarrierInvoiceSaveRequestSourceLtlReconcile"] = "ltl_reconcile";
  return CarrierInvoiceSaveRequestSource2;
})(CarrierInvoiceSaveRequestSource || {});
export const decodeCarrierInvoiceSaveRequestSource = (value) => value;
export const encodeCarrierInvoiceSaveRequestSource = (value) => value;
export function decodeCarrierInvoiceSaveWithErrorsParams(json) {
  return {
    note: optionalDecoder(assertString)(json.note),
    emailId: optionalDecoder(assertNumber)(json.emailId),
    source: decodeCarrierInvoiceSaveRequestSource(json.source)
  };
}
export function encodeCarrierInvoiceSaveWithErrorsParams(value) {
  return {
    note: value.note,
    emailId: value.emailId,
    source: encodeCarrierInvoiceSaveRequestSource(value.source)
  };
}
export function decodeCarrierInvoiceServiceIgnoreFailureRequest(json) {
  return {
    carrierInvoiceId: assertNumber(json.carrierInvoiceId),
    failureId: assertString(json.failureId)
  };
}
export function encodeCarrierInvoiceServiceIgnoreFailureRequest(value) {
  return {
    carrierInvoiceId: value.carrierInvoiceId,
    failureId: value.failureId
  };
}
export function decodeCarrierInvoiceServicePlaceExceptionEventRequest(json) {
  return {
    carrierInvoiceId: assertNumber(json.carrierInvoiceId),
    message: assertString(json.message)
  };
}
export function encodeCarrierInvoiceServicePlaceExceptionEventRequest(value) {
  return {
    carrierInvoiceId: value.carrierInvoiceId,
    message: value.message
  };
}
export function decodeCarrierInvoiceServiceResolveExceptionEventRequest(json) {
  return {
    carrierInvoiceId: assertNumber(json.carrierInvoiceId),
    exceptionEventId: assertNumber(json.exceptionEventId),
    resolutionMessage: assertString(json.resolutionMessage)
  };
}
export function encodeCarrierInvoiceServiceResolveExceptionEventRequest(value) {
  return {
    carrierInvoiceId: value.carrierInvoiceId,
    exceptionEventId: value.exceptionEventId,
    resolutionMessage: value.resolutionMessage
  };
}
export function decodeCarrierInvoiceServiceSaveWithErrorsRequest(json) {
  return {
    carrierInvoiceId: assertNumber(json.carrierInvoiceId),
    replyEmailId: optionalDecoder(assertNumber)(json.replyEmailId),
    note: optionalDecoder(assertString)(json.note)
  };
}
export function encodeCarrierInvoiceServiceSaveWithErrorsRequest(value) {
  return {
    carrierInvoiceId: value.carrierInvoiceId,
    replyEmailId: value.replyEmailId,
    note: value.note
  };
}
export function decodeCarrierInvoiceServiceSubmitRequest(json) {
  return {
    carrierInvoiceId: assertNumber(json.carrierInvoiceId),
    note: optionalDecoder(assertString)(json.note)
  };
}
export function encodeCarrierInvoiceServiceSubmitRequest(value) {
  return {
    carrierInvoiceId: value.carrierInvoiceId,
    note: value.note
  };
}
export function decodeCarrierInvoiceServiceUnignoreFailureRequest(json) {
  return {
    carrierInvoiceId: assertNumber(json.carrierInvoiceId),
    failureId: assertString(json.failureId)
  };
}
export function encodeCarrierInvoiceServiceUnignoreFailureRequest(value) {
  return {
    carrierInvoiceId: value.carrierInvoiceId,
    failureId: value.failureId
  };
}
export function decodeCarrierInvoiceServiceUpdateError(json) {
  return {
    message: assertString(json.message),
    reason: decodeCarrierInvoiceServiceUpdateErrorReason(json.reason)
  };
}
export function encodeCarrierInvoiceServiceUpdateError(value) {
  return {
    message: value.message,
    reason: encodeCarrierInvoiceServiceUpdateErrorReason(value.reason)
  };
}
export var CarrierInvoiceServiceUpdateErrorReason = /* @__PURE__ */ ((CarrierInvoiceServiceUpdateErrorReason2) => {
  CarrierInvoiceServiceUpdateErrorReason2["CarrierInvoiceServiceUpdateErrorReasonReadOnly"] = "read_only";
  CarrierInvoiceServiceUpdateErrorReason2["CarrierInvoiceServiceUpdateErrorReasonInvalid"] = "invalid";
  return CarrierInvoiceServiceUpdateErrorReason2;
})(CarrierInvoiceServiceUpdateErrorReason || {});
export const decodeCarrierInvoiceServiceUpdateErrorReason = (value) => value;
export const encodeCarrierInvoiceServiceUpdateErrorReason = (value) => value;
export function decodeCarrierInvoiceServiceUpdateRequest(json) {
  return {
    carrierInvoiceId: assertNumber(json.carrierInvoiceId),
    carrierInvoiceUpdate: decodeCarrierInvoiceUpdate(json.carrierInvoiceUpdate)
  };
}
export function encodeCarrierInvoiceServiceUpdateRequest(value) {
  return {
    carrierInvoiceId: value.carrierInvoiceId,
    carrierInvoiceUpdate: encodeCarrierInvoiceUpdate(
      value.carrierInvoiceUpdate
    )
  };
}
export function decodeCarrierInvoiceServiceUpdateResponse(json) {
  return {
    outcome: decodeCarrierInvoiceServiceUpdateResponse_Outcome(json)
  };
}
export function encodeCarrierInvoiceServiceUpdateResponse(value) {
  return __spreadValues({}, encodeCarrierInvoiceServiceUpdateResponse_Outcome(value.outcome));
}
export var CarrierInvoiceServiceUpdateResponse_OutcomeType = /* @__PURE__ */ ((CarrierInvoiceServiceUpdateResponse_OutcomeType2) => {
  CarrierInvoiceServiceUpdateResponse_OutcomeType2["Success"] = "success";
  CarrierInvoiceServiceUpdateResponse_OutcomeType2["Error"] = "error";
  return CarrierInvoiceServiceUpdateResponse_OutcomeType2;
})(CarrierInvoiceServiceUpdateResponse_OutcomeType || {});
export function decodeCarrierInvoiceServiceUpdateResponse_Outcome(json) {
  if (json.success !== void 0) {
    return {
      type: "success" /* Success */,
      success: decodeCarrierInvoiceServiceUpdateSuccess(json.success)
    };
  } else if (json.error !== void 0) {
    return {
      type: "error" /* Error */,
      error: decodeCarrierInvoiceServiceUpdateError(json.error)
    };
  }
  throw new Error(
    "No field set in oneof CarrierInvoiceServiceUpdateResponse_Outcome"
  );
}
export function encodeCarrierInvoiceServiceUpdateResponse_Outcome(value) {
  switch (value.type) {
    case "success" /* Success */:
      return {
        success: encodeCarrierInvoiceServiceUpdateSuccess(value.success)
      };
    case "error" /* Error */:
      return { error: encodeCarrierInvoiceServiceUpdateError(value.error) };
    default:
      throw new Error(
        "No field set in oneof CarrierInvoiceServiceUpdateResponse_Outcome"
      );
  }
}
export function decodeCarrierInvoiceServiceUpdateSuccess(json) {
  return {};
}
export function encodeCarrierInvoiceServiceUpdateSuccess(value) {
  return {};
}
export function decodeCarrierInvoiceUpdate(json) {
  return {
    updates: (json.updates || []).map(decodeCarrierInvoiceFieldUpdate)
  };
}
export function encodeCarrierInvoiceUpdate(value) {
  return {
    updates: value.updates.map(encodeCarrierInvoiceFieldUpdate)
  };
}
export function decodeCarrierMatchCheck(json) {
  return {};
}
export function encodeCarrierMatchCheck(value) {
  return {};
}
export function decodeCarrierRateCheck(json) {
  return {
    documentIds: (json.documentIds || []).map(assertNumber)
  };
}
export function encodeCarrierRateCheck(value) {
  return {
    documentIds: value.documentIds
  };
}
export function decodeCarrierRequiredCheck(json) {
  return {};
}
export function encodeCarrierRequiredCheck(value) {
  return {};
}
export function decodeClientDebtorRelationshipCheck(json) {
  return {};
}
export function encodeClientDebtorRelationshipCheck(value) {
  return {};
}
export var ClientNotesSidebarDisplay = /* @__PURE__ */ ((ClientNotesSidebarDisplay2) => {
  ClientNotesSidebarDisplay2["ClientNotesSidebarDisplayDisabled"] = "disabled";
  ClientNotesSidebarDisplay2["ClientNotesSidebarDisplayCollapsed"] = "collapsed";
  ClientNotesSidebarDisplay2["ClientNotesSidebarDisplayExpanded"] = "expanded";
  return ClientNotesSidebarDisplay2;
})(ClientNotesSidebarDisplay || {});
export const decodeClientNotesSidebarDisplay = (value) => value;
export const encodeClientNotesSidebarDisplay = (value) => value;
export function decodeClientSubmissionIdsQuery(json) {
  return {
    page: assertNumber(json.page),
    email_message_id: assertNumber(json.email_message_id),
    term: assertString(json.term)
  };
}
export function encodeClientSubmissionIdsQuery(value) {
  return {
    page: value.page,
    email_message_id: value.email_message_id,
    term: value.term
  };
}
export function decodeClientSubmissionReviewData(json) {
  return {
    batch_email_id: assertNumber(json.batch_email_id),
    funding_methods: (json.funding_methods || []).map(assertString)
  };
}
export function encodeClientSubmissionReviewData(value) {
  return {
    batch_email_id: value.batch_email_id,
    funding_methods: value.funding_methods
  };
}
export function decodeConditionalExceptionRule(json) {
  return {
    id: assertNumber(json.id),
    exceptionMessage: assertString(json.exceptionMessage),
    conditions: decodeConditionalExceptionRule_ConditionGroup(json.conditions)
  };
}
export function encodeConditionalExceptionRule(value) {
  return {
    id: value.id,
    exceptionMessage: value.exceptionMessage,
    conditions: encodeConditionalExceptionRule_ConditionGroup(value.conditions)
  };
}
export function decodeConditionalExceptionRule_Condition(json) {
  return {
    fieldCategory: optionalDecoder(assertString)(json.fieldCategory),
    fieldId: optionalDecoder(assertString)(json.fieldId),
    predicate: assertString(json.predicate),
    value: optionalDecoder(assertString)(json.value)
  };
}
export function encodeConditionalExceptionRule_Condition(value) {
  return {
    fieldCategory: value.fieldCategory,
    fieldId: value.fieldId,
    predicate: value.predicate,
    value: value.value
  };
}
export function decodeConditionalExceptionRule_ConditionGroup(json) {
  return {
    operator: assertString(json.operator),
    children: (json.children || []).map(
      decodeConditionalExceptionRule_RuleNode
    )
  };
}
export function encodeConditionalExceptionRule_ConditionGroup(value) {
  return {
    operator: value.operator,
    children: value.children.map(encodeConditionalExceptionRule_RuleNode)
  };
}
export function decodeConditionalExceptionRule_RuleNode(json) {
  return {
    nodeType: assertString(json.nodeType),
    node: decodeConditionalExceptionRule_RuleNode_Node(json)
  };
}
export function encodeConditionalExceptionRule_RuleNode(value) {
  return __spreadValues({
    nodeType: value.nodeType
  }, encodeConditionalExceptionRule_RuleNode_Node(value.node));
}
export var ConditionalExceptionRule_RuleNode_NodeType = /* @__PURE__ */ ((ConditionalExceptionRule_RuleNode_NodeType2) => {
  ConditionalExceptionRule_RuleNode_NodeType2["Condition"] = "condition";
  ConditionalExceptionRule_RuleNode_NodeType2["Group"] = "group";
  return ConditionalExceptionRule_RuleNode_NodeType2;
})(ConditionalExceptionRule_RuleNode_NodeType || {});
export function decodeConditionalExceptionRule_RuleNode_Node(json) {
  if (json.condition !== void 0) {
    return {
      type: "condition" /* Condition */,
      condition: decodeConditionalExceptionRule_Condition(json.condition)
    };
  } else if (json.group !== void 0) {
    return {
      type: "group" /* Group */,
      group: decodeConditionalExceptionRule_ConditionGroup(json.group)
    };
  }
  throw new Error(
    "No field set in oneof ConditionalExceptionRule_RuleNode_Node"
  );
}
export function encodeConditionalExceptionRule_RuleNode_Node(value) {
  switch (value.type) {
    case "condition" /* Condition */:
      return {
        condition: encodeConditionalExceptionRule_Condition(value.condition)
      };
    case "group" /* Group */:
      return {
        group: encodeConditionalExceptionRule_ConditionGroup(value.group)
      };
    default:
      throw new Error(
        "No field set in oneof ConditionalExceptionRule_RuleNode_Node"
      );
  }
}
export var ConsiderNoaDataForVerifications = /* @__PURE__ */ ((ConsiderNoaDataForVerifications2) => {
  ConsiderNoaDataForVerifications2["ConsiderNoaDataForVerificationsDontConsiderNoa"] = "Don't Consider NOA for Verification Level";
  ConsiderNoaDataForVerifications2["ConsiderNoaDataForVerificationsSentDate"] = "Consider NOA sent at date";
  ConsiderNoaDataForVerifications2["ConsiderNoaDataForVerificationsSentAndReceivedDate"] = "Consider NOA sent at and received at dates";
  return ConsiderNoaDataForVerifications2;
})(ConsiderNoaDataForVerifications || {});
export const decodeConsiderNoaDataForVerifications = (value) => value;
export const encodeConsiderNoaDataForVerifications = (value) => value;
export function decodeContact(json) {
  return {
    contactType: decodeContact_ContactType(json)
  };
}
export function encodeContact(value) {
  return __spreadValues({}, encodeContact_ContactType(value.contactType));
}
export var Contact_ContactTypeType = /* @__PURE__ */ ((Contact_ContactTypeType2) => {
  Contact_ContactTypeType2["CarrierContact"] = "carrier_contact";
  Contact_ContactTypeType2["MultiCarrierContact"] = "multi_carrier_contact";
  return Contact_ContactTypeType2;
})(Contact_ContactTypeType || {});
export function decodeContact_ContactType(json) {
  if (json.carrier_contact !== void 0) {
    return {
      type: "carrier_contact" /* CarrierContact */,
      carrier_contact: decodeContact_CarrierContact(json.carrier_contact)
    };
  } else if (json.multi_carrier_contact !== void 0) {
    return {
      type: "multi_carrier_contact" /* MultiCarrierContact */,
      multi_carrier_contact: decodeContact_MultiCarrierContact(
        json.multi_carrier_contact
      )
    };
  }
  throw new Error("No field set in oneof Contact_ContactType");
}
export function encodeContact_ContactType(value) {
  switch (value.type) {
    case "carrier_contact" /* CarrierContact */:
      return {
        carrier_contact: encodeContact_CarrierContact(value.carrier_contact)
      };
    case "multi_carrier_contact" /* MultiCarrierContact */:
      return {
        multi_carrier_contact: encodeContact_MultiCarrierContact(
          value.multi_carrier_contact
        )
      };
    default:
      throw new Error("No field set in oneof Contact_ContactType");
  }
}
export function decodeContact_CarrierContact(json) {
  return {
    id: assertNumber(json.id),
    email: assertString(json.email),
    name: optionalDecoder(assertString)(json.name),
    tms_carrier_id: optionalDecoder(assertNumber)(json.tms_carrier_id)
  };
}
export function encodeContact_CarrierContact(value) {
  return {
    id: value.id,
    email: value.email,
    name: value.name,
    tms_carrier_id: value.tms_carrier_id
  };
}
export function decodeContact_MultiCarrierContact(json) {
  return {
    id: assertNumber(json.id),
    email: assertString(json.email),
    name: optionalDecoder(assertString)(json.name)
  };
}
export function encodeContact_MultiCarrierContact(value) {
  return {
    id: value.id,
    email: value.email,
    name: value.name
  };
}
export function decodeContactIdsQuery(json) {
  return {
    page: assertNumber(json.page),
    term: assertString(json.term),
    contact_type: decodeContactType(json.contact_type),
    carrier_name: optionalDecoder(assertString)(json.carrier_name)
  };
}
export function encodeContactIdsQuery(value) {
  return {
    page: value.page,
    term: value.term,
    contact_type: encodeContactType(value.contact_type),
    carrier_name: value.carrier_name
  };
}
export var ContactType = /* @__PURE__ */ ((ContactType2) => {
  ContactType2["ContactTypeAll"] = "all";
  ContactType2["ContactTypeCarrier"] = "carrier";
  ContactType2["ContactTypeGeneral"] = "general";
  return ContactType2;
})(ContactType || {});
export const decodeContactType = (value) => value;
export const encodeContactType = (value) => value;
export function decodeControlledByTMS(json) {
  return {
    customer_visible_docs: assertBoolean(json.customer_visible_docs),
    customer_required_docs: assertBoolean(json.customer_required_docs),
    delivery_method: assertBoolean(json.delivery_method),
    email_delivery_recipient_address: assertBoolean(
      json.email_delivery_recipient_address
    ),
    customer_billing_interval: assertBoolean(json.customer_billing_interval),
    customer_invoice_templates: assertBoolean(json.customer_invoice_templates),
    custom_exception_messages: assertBoolean(json.custom_exception_messages),
    customer_instructions: assertBoolean(json.customer_instructions),
    debtor_billing_email: assertBoolean(json.debtor_billing_email),
    customer_ltl_rerate: assertBoolean(json.customer_ltl_rerate)
  };
}
export function encodeControlledByTMS(value) {
  return {
    customer_visible_docs: value.customer_visible_docs,
    customer_required_docs: value.customer_required_docs,
    delivery_method: value.delivery_method,
    email_delivery_recipient_address: value.email_delivery_recipient_address,
    customer_billing_interval: value.customer_billing_interval,
    customer_invoice_templates: value.customer_invoice_templates,
    custom_exception_messages: value.custom_exception_messages,
    customer_instructions: value.customer_instructions,
    debtor_billing_email: value.debtor_billing_email,
    customer_ltl_rerate: value.customer_ltl_rerate
  };
}
export function decodeCorrespondent(json) {
  return {
    email: assertString(json.email)
  };
}
export function encodeCorrespondent(value) {
  return {
    email: value.email
  };
}
export var CsrfHeader = /* @__PURE__ */ ((CsrfHeader2) => {
  CsrfHeader2["CsrfHeaderToken"] = "X-CSRF-Token";
  CsrfHeader2["CsrfHeaderTokenRefresh"] = "X-CSRF-Token-Refresh";
  CsrfHeader2["CsrfHeaderRejected"] = "X-CSRF-Rejected";
  CsrfHeader2["CsrfHeaderRetry"] = "X-CSRF-Retry";
  return CsrfHeader2;
})(CsrfHeader || {});
export const decodeCsrfHeader = (value) => value;
export const encodeCsrfHeader = (value) => value;
export function decodeCustomExceptionEmailTemplate(json) {
  return {
    id: assertNumber(json.id),
    body: assertString(json.body),
    name: assertString(json.name),
    subject: assertString(json.subject),
    is_default: assertBoolean(json.is_default)
  };
}
export function encodeCustomExceptionEmailTemplate(value) {
  return {
    id: value.id,
    body: value.body,
    name: value.name,
    subject: value.subject,
    is_default: value.is_default
  };
}
export function decodeCustomExceptionEmailTemplateDeleteRequest(json) {
  return {
    id: assertNumber(json.id)
  };
}
export function encodeCustomExceptionEmailTemplateDeleteRequest(value) {
  return {
    id: value.id
  };
}
export function decodeCustomExceptionEmailTemplateSetAsDefaultRequest(json) {
  return {
    id: assertNumber(json.id)
  };
}
export function encodeCustomExceptionEmailTemplateSetAsDefaultRequest(value) {
  return {
    id: value.id
  };
}
export function decodeCustomExceptionMessage(json) {
  return {
    message: assertString(json.message),
    types_selection: decodeCustomExceptionMessageTypesSelection(
      json.types_selection
    )
  };
}
export function encodeCustomExceptionMessage(value) {
  return {
    message: value.message,
    types_selection: encodeCustomExceptionMessageTypesSelection(
      value.types_selection
    )
  };
}
export function decodeCustomExceptionMessageCheck(json) {
  return {
    message: assertString(json.message)
  };
}
export function encodeCustomExceptionMessageCheck(value) {
  return {
    message: value.message
  };
}
export var CustomExceptionMessageType = /* @__PURE__ */ ((CustomExceptionMessageType2) => {
  CustomExceptionMessageType2["CustomExceptionMessageTypePayment"] = "payment";
  CustomExceptionMessageType2["CustomExceptionMessageTypeBilling"] = "billing";
  return CustomExceptionMessageType2;
})(CustomExceptionMessageType || {});
export const decodeCustomExceptionMessageType = (value) => value;
export const encodeCustomExceptionMessageType = (value) => value;
export var CustomExceptionMessageTypesSelection = /* @__PURE__ */ ((CustomExceptionMessageTypesSelection2) => {
  CustomExceptionMessageTypesSelection2["CustomExceptionMessageTypesSelectionBoth"] = "both";
  CustomExceptionMessageTypesSelection2["CustomExceptionMessageTypesSelectionPayment"] = "payment";
  CustomExceptionMessageTypesSelection2["CustomExceptionMessageTypesSelectionBilling"] = "billing";
  CustomExceptionMessageTypesSelection2["CustomExceptionMessageTypesSelectionUnknown"] = "unknown";
  return CustomExceptionMessageTypesSelection2;
})(CustomExceptionMessageTypesSelection || {});
export const decodeCustomExceptionMessageTypesSelection = (value) => value;
export const encodeCustomExceptionMessageTypesSelection = (value) => value;
export function decodeCustomer(json) {
  return {
    name: assertString(json.name),
    external_id: optionalDecoder(assertString)(json.external_id)
  };
}
export function encodeCustomer(value) {
  return {
    name: value.name,
    external_id: value.external_id
  };
}
export function decodeCustomerInstructions(json) {
  return {
    customerName: assertString(json.customerName),
    instructions: (json.instructions || []).map(assertString)
  };
}
export function encodeCustomerInstructions(value) {
  return {
    customerName: value.customerName,
    instructions: value.instructions
  };
}
export function decodeCustomerInstructionsCheck(json) {
  return {
    customers: (json.customers || []).map(decodeCustomerInstructions)
  };
}
export function encodeCustomerInstructionsCheck(value) {
  return {
    customers: value.customers.map(encodeCustomerInstructions)
  };
}
export function decodeCustomerInvoiceTabCounts(json) {
  return {
    readyToSend: assertNumber(json.readyToSend),
    waitingForDocuments: assertNumber(json.waitingForDocuments),
    failed: assertNumber(json.failed),
    sent: assertNumber(json.sent),
    cap: assertNumber(json.cap)
  };
}
export function encodeCustomerInvoiceTabCounts(value) {
  return {
    readyToSend: value.readyToSend,
    waitingForDocuments: value.waitingForDocuments,
    failed: value.failed,
    sent: value.sent,
    cap: value.cap
  };
}
export function decodeCustomerInvoiceTemplate(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name)
  };
}
export function encodeCustomerInvoiceTemplate(value) {
  return {
    id: value.id,
    name: value.name
  };
}
export function decodeCustomerPODDefinitionGroup(json) {
  return {
    tms_customer_ids: (json.tms_customer_ids || []).map(assertNumber),
    allowed_as_pod_override_document_types: (json.allowed_as_pod_override_document_types || []).map(decodeDocumentType),
    pod_definition_checks_enabled: (json.pod_definition_checks_enabled || []).map(decodeDocumentPODInferenceCheckType),
    inherits_allowed_as_pod_override_document_types: assertBoolean(
      json.inherits_allowed_as_pod_override_document_types
    ),
    inherits_pod_definition_checks_enabled: assertBoolean(
      json.inherits_pod_definition_checks_enabled
    )
  };
}
export function encodeCustomerPODDefinitionGroup(value) {
  return {
    tms_customer_ids: value.tms_customer_ids,
    allowed_as_pod_override_document_types: value.allowed_as_pod_override_document_types.map(encodeDocumentType),
    pod_definition_checks_enabled: value.pod_definition_checks_enabled.map(
      encodeDocumentPODInferenceCheckType
    ),
    inherits_allowed_as_pod_override_document_types: value.inherits_allowed_as_pod_override_document_types,
    inherits_pod_definition_checks_enabled: value.inherits_pod_definition_checks_enabled
  };
}
export function decodeCustomerPODDefinitionServiceUpdateRequest(json) {
  return {
    original_tms_customer_ids: (json.original_tms_customer_ids || []).map(
      assertNumber
    ),
    group: decodeCustomerPODDefinitionGroup(json.group)
  };
}
export function encodeCustomerPODDefinitionServiceUpdateRequest(value) {
  return {
    original_tms_customer_ids: value.original_tms_customer_ids,
    group: encodeCustomerPODDefinitionGroup(value.group)
  };
}
export function decodeCustomerPODDefinitions(json) {
  return {
    groups: (json.groups || []).map(decodeCustomerPODDefinitionGroup),
    available_checks: (json.available_checks || []).map(
      decodeDocumentPODInferenceCheckType
    ),
    available_document_types: (json.available_document_types || []).map(
      decodeDocumentType
    )
  };
}
export function encodeCustomerPODDefinitions(value) {
  return {
    groups: value.groups.map(encodeCustomerPODDefinitionGroup),
    available_checks: value.available_checks.map(
      encodeDocumentPODInferenceCheckType
    ),
    available_document_types: value.available_document_types.map(encodeDocumentType)
  };
}
export function decodeCustomerRepresentativeAccountSettings(json) {
  return {
    custom_document_type_names: assertRecord(assertString)(
      json.custom_document_type_names
    ),
    view_enabled: assertBoolean(json.view_enabled),
    v3_reskin_enabled_top_level: assertBoolean(
      json.v3_reskin_enabled_top_level
    )
  };
}
export function encodeCustomerRepresentativeAccountSettings(value) {
  return {
    custom_document_type_names: value.custom_document_type_names,
    view_enabled: value.view_enabled,
    v3_reskin_enabled_top_level: value.v3_reskin_enabled_top_level
  };
}
export function decodeDashboardCategoryFilter(json) {
  return {
    category: assertString(json.category),
    label: (json.label || []).map(assertString),
    excluded: (json.excluded || []).map(assertString)
  };
}
export function encodeDashboardCategoryFilter(value) {
  return {
    category: value.category,
    label: value.label,
    excluded: value.excluded
  };
}
export function decodeDashboardCustomer(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    external_id: assertString(json.external_id)
  };
}
export function encodeDashboardCustomer(value) {
  return {
    id: value.id,
    name: value.name,
    external_id: value.external_id
  };
}
export function decodeDashboardExceptionStat(json) {
  return {
    id: assertString(json.id),
    name: assertString(json.name)
  };
}
export function encodeDashboardExceptionStat(value) {
  return {
    id: value.id,
    name: value.name
  };
}
export function decodeDashboardFiltersQuery(json) {
  return {
    carrier_id: (json.carrier_id || []).map(assertNumber),
    factor_broker_id: (json.factor_broker_id || []).map(assertNumber),
    customers: (json.customers || []).map(decodeCustomer),
    label_filters: (json.label_filters || []).map(decodeDashboardLabelFilter),
    categories: (json.categories || []).map(decodeDashboardCategoryFilter),
    start_date: optionalDecoder(assertString)(json.start_date),
    end_date: optionalDecoder(assertString)(json.end_date),
    start_date_time: optionalDecoder(decodeTimestamp)(json.start_date_time),
    end_date_time: optionalDecoder(decodeTimestamp)(json.end_date_time),
    range_type: decodeQueueDate(json.range_type),
    dashboard_mode: decodeDashboardMode(json.dashboard_mode),
    sort_by: decodeDashboardSortBy(json.sort_by),
    timezone_name: optionalDecoder(assertString)(json.timezone_name),
    exceptions: (json.exceptions || []).map(assertString),
    reskin_toggle: optionalDecoder(assertBoolean)(json.reskin_toggle)
  };
}
export function encodeDashboardFiltersQuery(value) {
  return {
    carrier_id: value.carrier_id,
    factor_broker_id: value.factor_broker_id,
    customers: value.customers.map(encodeCustomer),
    label_filters: value.label_filters.map(encodeDashboardLabelFilter),
    categories: value.categories.map(encodeDashboardCategoryFilter),
    start_date: value.start_date,
    end_date: value.end_date,
    start_date_time: optionalEncoder(encodeTimestamp)(value.start_date_time),
    end_date_time: optionalEncoder(encodeTimestamp)(value.end_date_time),
    range_type: encodeQueueDate(value.range_type),
    dashboard_mode: encodeDashboardMode(value.dashboard_mode),
    sort_by: encodeDashboardSortBy(value.sort_by),
    timezone_name: value.timezone_name,
    exceptions: value.exceptions,
    reskin_toggle: value.reskin_toggle
  };
}
export function decodeDashboardLabelFilter(json) {
  return {
    label: assertString(json.label),
    state: decodeLabelFilterState(json.state)
  };
}
export function encodeDashboardLabelFilter(value) {
  return {
    label: value.label,
    state: encodeLabelFilterState(value.state)
  };
}
export var DashboardMode = /* @__PURE__ */ ((DashboardMode2) => {
  DashboardMode2["DashboardModeAll"] = "all";
  DashboardMode2["DashboardModeIndex"] = "index";
  DashboardMode2["DashboardModeAudit"] = "audit";
  return DashboardMode2;
})(DashboardMode || {});
export const decodeDashboardMode = (value) => value;
export const encodeDashboardMode = (value) => value;
export function decodeDashboardSavedFilter(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    dashboard_mode: decodeDashboardMode(json.dashboard_mode),
    filter: decodeDashboardFiltersQuery(json.filter),
    is_default: assertBoolean(json.is_default)
  };
}
export function encodeDashboardSavedFilter(value) {
  return {
    id: value.id,
    name: value.name,
    dashboard_mode: encodeDashboardMode(value.dashboard_mode),
    filter: encodeDashboardFiltersQuery(value.filter),
    is_default: value.is_default
  };
}
export function decodeDashboardSavedFilterQuery(json) {
  return {
    dashboard_mode: optionalDecoder(decodeDashboardMode)(json.dashboard_mode)
  };
}
export function encodeDashboardSavedFilterQuery(value) {
  return {
    dashboard_mode: optionalEncoder(encodeDashboardMode)(value.dashboard_mode)
  };
}
export function decodeDashboardSortBy(json) {
  return {
    name: assertString(json.name),
    order: decodeDashboardSortByOrder(json.order)
  };
}
export function encodeDashboardSortBy(value) {
  return {
    name: value.name,
    order: encodeDashboardSortByOrder(value.order)
  };
}
export var DashboardSortByOrder = /* @__PURE__ */ ((DashboardSortByOrder2) => {
  DashboardSortByOrder2["DashboardSortByOrderAsc"] = "asc";
  DashboardSortByOrder2["DashboardSortByOrderDesc"] = "desc";
  return DashboardSortByOrder2;
})(DashboardSortByOrder || {});
export const decodeDashboardSortByOrder = (value) => value;
export const encodeDashboardSortByOrder = (value) => value;
export function decodeDashboardUpdateEvent(json) {
  return {
    addDocumentToLoad: optionalDecoder(
      decodeDashboardUpdateEvent_AddDocumentToLoad
    )(json.addDocumentToLoad),
    ltlPayablesCarrierInvoiceSubmissionImport: optionalDecoder(
      decodeDashboardUpdateEvent_LtlPayablesCarrierInvoiceSubmissionImport
    )(json.ltlPayablesCarrierInvoiceSubmissionImport)
  };
}
export function encodeDashboardUpdateEvent(value) {
  return {
    addDocumentToLoad: optionalEncoder(
      encodeDashboardUpdateEvent_AddDocumentToLoad
    )(value.addDocumentToLoad),
    ltlPayablesCarrierInvoiceSubmissionImport: optionalEncoder(
      encodeDashboardUpdateEvent_LtlPayablesCarrierInvoiceSubmissionImport
    )(value.ltlPayablesCarrierInvoiceSubmissionImport)
  };
}
export function decodeDashboardUpdateEvent_AddDocumentToLoad(json) {
  return {
    loadStateId: assertNumber(json.loadStateId),
    fileCount: assertNumber(json.fileCount),
    uploadStatus: decodeAddDocumentToLoadUploadStatus(json.uploadStatus)
  };
}
export function encodeDashboardUpdateEvent_AddDocumentToLoad(value) {
  return {
    loadStateId: value.loadStateId,
    fileCount: value.fileCount,
    uploadStatus: encodeAddDocumentToLoadUploadStatus(value.uploadStatus)
  };
}
export function decodeDashboardUpdateEvent_LtlPayablesCarrierInvoiceSubmissionImport(json) {
  return {
    id: assertNumber(json.id),
    importState: decodeLtlPayablesCarrierInvoiceSubmissionImportState(
      json.importState
    ),
    proNumber: optionalDecoder(assertString)(json.proNumber)
  };
}
export function encodeDashboardUpdateEvent_LtlPayablesCarrierInvoiceSubmissionImport(value) {
  return {
    id: value.id,
    importState: encodeLtlPayablesCarrierInvoiceSubmissionImportState(
      value.importState
    ),
    proNumber: value.proNumber
  };
}
export function decodeDebtorChangeDebtor(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    mc_number: assertString(json.mc_number)
  };
}
export function encodeDebtorChangeDebtor(value) {
  return {
    id: value.id,
    name: value.name,
    mc_number: value.mc_number
  };
}
export function decodeDebtorChangeInvoice(json) {
  return {
    factor_invoice_id: assertNumber(json.factor_invoice_id),
    created_at: decodeTimestamp(json.created_at),
    outcome: decodeDebtorChangeOutcome(json.outcome),
    guessed_debtor: optionalDecoder(decodeDebtorChangeDebtor)(
      json.guessed_debtor
    ),
    final_debtor: optionalDecoder(decodeDebtorChangeDebtor)(json.final_debtor),
    processed_by_user: assertBoolean(json.processed_by_user),
    rate_confirmations: (json.rate_confirmations || []).map(
      decodeDebtorChangeRateConfirmation
    )
  };
}
export function encodeDebtorChangeInvoice(value) {
  return {
    factor_invoice_id: value.factor_invoice_id,
    created_at: encodeTimestamp(value.created_at),
    outcome: encodeDebtorChangeOutcome(value.outcome),
    guessed_debtor: optionalEncoder(encodeDebtorChangeDebtor)(
      value.guessed_debtor
    ),
    final_debtor: optionalEncoder(encodeDebtorChangeDebtor)(value.final_debtor),
    processed_by_user: value.processed_by_user,
    rate_confirmations: value.rate_confirmations.map(
      encodeDebtorChangeRateConfirmation
    )
  };
}
export var DebtorChangeOutcome = /* @__PURE__ */ ((DebtorChangeOutcome2) => {
  DebtorChangeOutcome2["DebtorChangeOutcomeCorrect"] = "correct";
  DebtorChangeOutcome2["DebtorChangeOutcomeWrongGuess"] = "wrong_guess";
  DebtorChangeOutcome2["DebtorChangeOutcomeNoGuess"] = "no_guess";
  DebtorChangeOutcome2["DebtorChangeOutcomeUnlabeled"] = "unlabeled";
  return DebtorChangeOutcome2;
})(DebtorChangeOutcome || {});
export const decodeDebtorChangeOutcome = (value) => value;
export const encodeDebtorChangeOutcome = (value) => value;
export function decodeDebtorChangeRateConfirmation(json) {
  return {
    document_id: assertNumber(json.document_id),
    template_id: optionalDecoder(assertNumber)(json.template_id),
    template_editor_url: optionalDecoder(assertString)(
      json.template_editor_url
    )
  };
}
export function encodeDebtorChangeRateConfirmation(value) {
  return {
    document_id: value.document_id,
    template_id: value.template_id,
    template_editor_url: value.template_editor_url
  };
}
export function decodeDebtorChangeReport(json) {
  return {
    invoices: (json.invoices || []).map(decodeDebtorChangeInvoice),
    templates: (json.templates || []).map(decodeDebtorChangeTemplateSummary),
    error: optionalDecoder(decodeDebtorChangeReportError)(json.error),
    start_date: assertString(json.start_date),
    end_date: assertString(json.end_date)
  };
}
export function encodeDebtorChangeReport(value) {
  return {
    invoices: value.invoices.map(encodeDebtorChangeInvoice),
    templates: value.templates.map(encodeDebtorChangeTemplateSummary),
    error: optionalEncoder(encodeDebtorChangeReportError)(value.error),
    start_date: value.start_date,
    end_date: value.end_date
  };
}
export var DebtorChangeReportError = /* @__PURE__ */ ((DebtorChangeReportError2) => {
  DebtorChangeReportError2["DebtorChangeReportErrorInvalidRange"] = "invalid_range";
  DebtorChangeReportError2["DebtorChangeReportErrorTimedOut"] = "timed_out";
  return DebtorChangeReportError2;
})(DebtorChangeReportError || {});
export const decodeDebtorChangeReportError = (value) => value;
export const encodeDebtorChangeReportError = (value) => value;
export function decodeDebtorChangeReportQuery(json) {
  return {
    start_date: assertString(json.start_date),
    end_date: assertString(json.end_date)
  };
}
export function encodeDebtorChangeReportQuery(value) {
  return {
    start_date: value.start_date,
    end_date: value.end_date
  };
}
export function decodeDebtorChangeTemplate(json) {
  return {
    template: optionalDecoder(decodeDebtorChangeTemplateInfo)(json.template),
    matches: (json.matches || []).map(decodeDebtorChangeTemplateMatch),
    error: optionalDecoder(decodeDebtorChangeReportError)(json.error),
    start_date: assertString(json.start_date),
    end_date: assertString(json.end_date)
  };
}
export function encodeDebtorChangeTemplate(value) {
  return {
    template: optionalEncoder(encodeDebtorChangeTemplateInfo)(value.template),
    matches: value.matches.map(encodeDebtorChangeTemplateMatch),
    error: optionalEncoder(encodeDebtorChangeReportError)(value.error),
    start_date: value.start_date,
    end_date: value.end_date
  };
}
export function decodeDebtorChangeTemplateInfo(json) {
  return {
    id: assertNumber(json.id),
    label: optionalDecoder(assertString)(json.label),
    documents_templates_group_id: optionalDecoder(assertNumber)(
      json.documents_templates_group_id
    ),
    network_broker_id: optionalDecoder(assertNumber)(json.network_broker_id),
    account_pins: (json.account_pins || []).map(decodeDebtorChangeDebtor),
    editor_url: assertString(json.editor_url)
  };
}
export function encodeDebtorChangeTemplateInfo(value) {
  return {
    id: value.id,
    label: value.label,
    documents_templates_group_id: value.documents_templates_group_id,
    network_broker_id: value.network_broker_id,
    account_pins: value.account_pins.map(encodeDebtorChangeDebtor),
    editor_url: value.editor_url
  };
}
export function decodeDebtorChangeTemplateMatch(json) {
  return {
    document_id: assertNumber(json.document_id),
    document_type: optionalDecoder(assertString)(json.document_type),
    matched_at: decodeTimestamp(json.matched_at),
    factor_invoice_id: optionalDecoder(assertNumber)(json.factor_invoice_id),
    outcome: decodeDebtorChangeOutcome(json.outcome),
    guessed_debtor: optionalDecoder(decodeDebtorChangeDebtor)(
      json.guessed_debtor
    ),
    final_debtor: optionalDecoder(decodeDebtorChangeDebtor)(json.final_debtor),
    processed_by_user: assertBoolean(json.processed_by_user),
    template_editor_url: assertString(json.template_editor_url)
  };
}
export function encodeDebtorChangeTemplateMatch(value) {
  return {
    document_id: value.document_id,
    document_type: value.document_type,
    matched_at: encodeTimestamp(value.matched_at),
    factor_invoice_id: value.factor_invoice_id,
    outcome: encodeDebtorChangeOutcome(value.outcome),
    guessed_debtor: optionalEncoder(encodeDebtorChangeDebtor)(
      value.guessed_debtor
    ),
    final_debtor: optionalEncoder(encodeDebtorChangeDebtor)(value.final_debtor),
    processed_by_user: value.processed_by_user,
    template_editor_url: value.template_editor_url
  };
}
export function decodeDebtorChangeTemplateQuery(json) {
  return {
    template_id: assertNumber(json.template_id),
    start_date: assertString(json.start_date),
    end_date: assertString(json.end_date)
  };
}
export function encodeDebtorChangeTemplateQuery(value) {
  return {
    template_id: value.template_id,
    start_date: value.start_date,
    end_date: value.end_date
  };
}
export function decodeDebtorChangeTemplateSummary(json) {
  return {
    template: decodeDebtorChangeTemplateInfo(json.template),
    wrong_guesses: assertNumber(json.wrong_guesses),
    no_guesses: assertNumber(json.no_guesses),
    wrong_guesses_of_a_pin: assertNumber(json.wrong_guesses_of_a_pin)
  };
}
export function encodeDebtorChangeTemplateSummary(value) {
  return {
    template: encodeDebtorChangeTemplateInfo(value.template),
    wrong_guesses: value.wrong_guesses,
    no_guesses: value.no_guesses,
    wrong_guesses_of_a_pin: value.wrong_guesses_of_a_pin
  };
}
export var DebtorNoaStatus = /* @__PURE__ */ ((DebtorNoaStatus2) => {
  DebtorNoaStatus2["DebtorNoaStatusVerified"] = "verified";
  DebtorNoaStatus2["DebtorNoaStatusNotFactorOfRecord"] = "not_factor_of_record";
  DebtorNoaStatus2["DebtorNoaStatusCarrierNotFactored"] = "carrier_not_factored";
  DebtorNoaStatus2["DebtorNoaStatusRelationshipMissing"] = "relationship_missing";
  return DebtorNoaStatus2;
})(DebtorNoaStatus || {});
export const decodeDebtorNoaStatus = (value) => value;
export const encodeDebtorNoaStatus = (value) => value;
export function decodeDebtorRequiredCheck(json) {
  return {
    suggestions: (json.suggestions || []).map(
      decodeFactorInvoiceAuditCheckSuggestion
    ),
    noaStatus: optionalDecoder(decodeDebtorNoaStatus)(json.noaStatus)
  };
}
export function encodeDebtorRequiredCheck(value) {
  return {
    suggestions: value.suggestions.map(encodeFactorInvoiceAuditCheckSuggestion),
    noaStatus: optionalEncoder(encodeDebtorNoaStatus)(value.noaStatus)
  };
}
export function decodeDeliveryMethod(json) {
  return {
    method: decodeDeliveryMethod_Method(json)
  };
}
export function encodeDeliveryMethod(value) {
  return __spreadValues({}, encodeDeliveryMethod_Method(value.method));
}
export var DeliveryMethod_MethodType = /* @__PURE__ */ ((DeliveryMethod_MethodType2) => {
  DeliveryMethod_MethodType2["Email"] = "email";
  DeliveryMethod_MethodType2["Print"] = "print";
  DeliveryMethod_MethodType2["PaymentsNetwork"] = "payments_network";
  DeliveryMethod_MethodType2["Ftp"] = "ftp";
  DeliveryMethod_MethodType2["UploadCass"] = "upload_cass";
  DeliveryMethod_MethodType2["Multiple"] = "multiple";
  DeliveryMethod_MethodType2["PaymentsNetworkMultiple"] = "payments_network_multiple";
  DeliveryMethod_MethodType2["TpayAPI"] = "tpay_api";
  DeliveryMethod_MethodType2["BackupOnly"] = "backup_only";
  DeliveryMethod_MethodType2["Other"] = "other";
  return DeliveryMethod_MethodType2;
})(DeliveryMethod_MethodType || {});
export function decodeDeliveryMethod_Method(json) {
  if (json.email !== void 0) {
    return {
      type: "email" /* Email */,
      email: decodeDeliveryMethod_Email(json.email)
    };
  } else if (json.print !== void 0) {
    return {
      type: "print" /* Print */,
      print: decodeDeliveryMethod_Print(json.print)
    };
  } else if (json.payments_network !== void 0) {
    return {
      type: "payments_network" /* PaymentsNetwork */,
      payments_network: decodeDeliveryMethod_PaymentsNetwork(
        json.payments_network
      )
    };
  } else if (json.ftp !== void 0) {
    return {
      type: "ftp" /* Ftp */,
      ftp: decodeDeliveryMethod_FtpDelivery(json.ftp)
    };
  } else if (json.upload_cass !== void 0) {
    return {
      type: "upload_cass" /* UploadCass */,
      upload_cass: decodeDeliveryMethod_UploadCass(json.upload_cass)
    };
  } else if (json.multiple !== void 0) {
    return {
      type: "multiple" /* Multiple */,
      multiple: decodeDeliveryMethod_Multiple(json.multiple)
    };
  } else if (json.payments_network_multiple !== void 0) {
    return {
      type: "payments_network_multiple" /* PaymentsNetworkMultiple */,
      payments_network_multiple: decodeDeliveryMethod_PaymentsNetworkMultiple(
        json.payments_network_multiple
      )
    };
  } else if (json.tpay_api !== void 0) {
    return {
      type: "tpay_api" /* TpayAPI */,
      tpay_api: decodeDeliveryMethod_TpayAPI(json.tpay_api)
    };
  } else if (json.backup_only !== void 0) {
    return {
      type: "backup_only" /* BackupOnly */,
      backup_only: decodeDeliveryMethod_BackupOnly(json.backup_only)
    };
  } else if (json.other !== void 0) {
    return {
      type: "other" /* Other */,
      other: decodeDeliveryMethod_Other(json.other)
    };
  }
  throw new Error("No field set in oneof DeliveryMethod_Method");
}
export function encodeDeliveryMethod_Method(value) {
  switch (value.type) {
    case "email" /* Email */:
      return { email: encodeDeliveryMethod_Email(value.email) };
    case "print" /* Print */:
      return { print: encodeDeliveryMethod_Print(value.print) };
    case "payments_network" /* PaymentsNetwork */:
      return {
        payments_network: encodeDeliveryMethod_PaymentsNetwork(
          value.payments_network
        )
      };
    case "ftp" /* Ftp */:
      return { ftp: encodeDeliveryMethod_FtpDelivery(value.ftp) };
    case "upload_cass" /* UploadCass */:
      return {
        upload_cass: encodeDeliveryMethod_UploadCass(value.upload_cass)
      };
    case "multiple" /* Multiple */:
      return { multiple: encodeDeliveryMethod_Multiple(value.multiple) };
    case "payments_network_multiple" /* PaymentsNetworkMultiple */:
      return {
        payments_network_multiple: encodeDeliveryMethod_PaymentsNetworkMultiple(
          value.payments_network_multiple
        )
      };
    case "tpay_api" /* TpayAPI */:
      return { tpay_api: encodeDeliveryMethod_TpayAPI(value.tpay_api) };
    case "backup_only" /* BackupOnly */:
      return {
        backup_only: encodeDeliveryMethod_BackupOnly(value.backup_only)
      };
    case "other" /* Other */:
      return { other: encodeDeliveryMethod_Other(value.other) };
    default:
      throw new Error("No field set in oneof DeliveryMethod_Method");
  }
}
export function decodeDeliveryMethod_BackupOnly(json) {
  return {};
}
export function encodeDeliveryMethod_BackupOnly(value) {
  return {};
}
export function decodeDeliveryMethod_Choice(json) {
  return {
    regex_pattern: assertString(json.regex_pattern),
    method: decodeDeliveryMethod_Choice_Method(json)
  };
}
export function encodeDeliveryMethod_Choice(value) {
  return __spreadValues({
    regex_pattern: value.regex_pattern
  }, encodeDeliveryMethod_Choice_Method(value.method));
}
export var DeliveryMethod_Choice_MethodType = /* @__PURE__ */ ((DeliveryMethod_Choice_MethodType2) => {
  DeliveryMethod_Choice_MethodType2["Email"] = "email";
  DeliveryMethod_Choice_MethodType2["Print"] = "print";
  DeliveryMethod_Choice_MethodType2["PaymentsNetwork"] = "payments_network";
  DeliveryMethod_Choice_MethodType2["Ftp"] = "ftp";
  DeliveryMethod_Choice_MethodType2["UploadCass"] = "upload_cass";
  DeliveryMethod_Choice_MethodType2["TpayAPI"] = "tpay_api";
  DeliveryMethod_Choice_MethodType2["BackupOnly"] = "backup_only";
  DeliveryMethod_Choice_MethodType2["Other"] = "other";
  return DeliveryMethod_Choice_MethodType2;
})(DeliveryMethod_Choice_MethodType || {});
export function decodeDeliveryMethod_Choice_Method(json) {
  if (json.email !== void 0) {
    return {
      type: "email" /* Email */,
      email: decodeDeliveryMethod_Email(json.email)
    };
  } else if (json.print !== void 0) {
    return {
      type: "print" /* Print */,
      print: decodeDeliveryMethod_Print(json.print)
    };
  } else if (json.payments_network !== void 0) {
    return {
      type: "payments_network" /* PaymentsNetwork */,
      payments_network: decodeDeliveryMethod_PaymentsNetwork(
        json.payments_network
      )
    };
  } else if (json.ftp !== void 0) {
    return {
      type: "ftp" /* Ftp */,
      ftp: decodeDeliveryMethod_FtpDelivery(json.ftp)
    };
  } else if (json.upload_cass !== void 0) {
    return {
      type: "upload_cass" /* UploadCass */,
      upload_cass: decodeDeliveryMethod_UploadCass(json.upload_cass)
    };
  } else if (json.tpay_api !== void 0) {
    return {
      type: "tpay_api" /* TpayAPI */,
      tpay_api: decodeDeliveryMethod_TpayAPI(json.tpay_api)
    };
  } else if (json.backup_only !== void 0) {
    return {
      type: "backup_only" /* BackupOnly */,
      backup_only: decodeDeliveryMethod_BackupOnly(json.backup_only)
    };
  } else if (json.other !== void 0) {
    return {
      type: "other" /* Other */,
      other: decodeDeliveryMethod_Other(json.other)
    };
  }
  throw new Error("No field set in oneof DeliveryMethod_Choice_Method");
}
export function encodeDeliveryMethod_Choice_Method(value) {
  switch (value.type) {
    case "email" /* Email */:
      return { email: encodeDeliveryMethod_Email(value.email) };
    case "print" /* Print */:
      return { print: encodeDeliveryMethod_Print(value.print) };
    case "payments_network" /* PaymentsNetwork */:
      return {
        payments_network: encodeDeliveryMethod_PaymentsNetwork(
          value.payments_network
        )
      };
    case "ftp" /* Ftp */:
      return { ftp: encodeDeliveryMethod_FtpDelivery(value.ftp) };
    case "upload_cass" /* UploadCass */:
      return {
        upload_cass: encodeDeliveryMethod_UploadCass(value.upload_cass)
      };
    case "tpay_api" /* TpayAPI */:
      return { tpay_api: encodeDeliveryMethod_TpayAPI(value.tpay_api) };
    case "backup_only" /* BackupOnly */:
      return {
        backup_only: encodeDeliveryMethod_BackupOnly(value.backup_only)
      };
    case "other" /* Other */:
      return { other: encodeDeliveryMethod_Other(value.other) };
    default:
      throw new Error("No field set in oneof DeliveryMethod_Choice_Method");
  }
}
export function decodeDeliveryMethod_Email(json) {
  return {
    email_per_invoice: decodeEmailPerInvoice(json.email_per_invoice),
    attach_invoices: assertBoolean(json.attach_invoices),
    billing_email_override: assertString(json.billing_email_override),
    custom_invoice_attachment_filename: assertString(
      json.custom_invoice_attachment_filename
    )
  };
}
export function encodeDeliveryMethod_Email(value) {
  return {
    email_per_invoice: encodeEmailPerInvoice(value.email_per_invoice),
    attach_invoices: value.attach_invoices,
    billing_email_override: value.billing_email_override,
    custom_invoice_attachment_filename: value.custom_invoice_attachment_filename
  };
}
export function decodeDeliveryMethod_FtpDelivery(json) {
  return {
    filename_template: assertString(json.filename_template),
    host: assertString(json.host),
    username: assertString(json.username),
    password: assertString(json.password),
    root_directory: assertString(json.root_directory),
    ftp_port: assertString(json.ftp_port),
    protocol_type: decodeFtpProtocolType(json.protocol_type)
  };
}
export function encodeDeliveryMethod_FtpDelivery(value) {
  return {
    filename_template: value.filename_template,
    host: value.host,
    username: value.username,
    password: value.password,
    root_directory: value.root_directory,
    ftp_port: value.ftp_port,
    protocol_type: encodeFtpProtocolType(value.protocol_type)
  };
}
export function decodeDeliveryMethod_Multiple(json) {
  return {
    choices: (json.choices || []).map(decodeDeliveryMethod_Choice)
  };
}
export function encodeDeliveryMethod_Multiple(value) {
  return {
    choices: value.choices.map(encodeDeliveryMethod_Choice)
  };
}
export function decodeDeliveryMethod_Other(json) {
  return {
    external_delivery_method: assertString(json.external_delivery_method),
    external_email_recipients: assertString(json.external_email_recipients)
  };
}
export function encodeDeliveryMethod_Other(value) {
  return {
    external_delivery_method: value.external_delivery_method,
    external_email_recipients: value.external_email_recipients
  };
}
export function decodeDeliveryMethod_PaymentsNetwork(json) {
  return {};
}
export function encodeDeliveryMethod_PaymentsNetwork(value) {
  return {};
}
export function decodeDeliveryMethod_PaymentsNetworkMultiple(json) {
  return {
    choices: (json.choices || []).map(decodeDeliveryMethod_Choice)
  };
}
export function encodeDeliveryMethod_PaymentsNetworkMultiple(value) {
  return {
    choices: value.choices.map(encodeDeliveryMethod_Choice)
  };
}
export function decodeDeliveryMethod_Print(json) {
  return {};
}
export function encodeDeliveryMethod_Print(value) {
  return {};
}
export function decodeDeliveryMethod_TpayAPI(json) {
  return {};
}
export function encodeDeliveryMethod_TpayAPI(value) {
  return {};
}
export function decodeDeliveryMethod_UploadCass(json) {
  return {
    account: assertString(json.account),
    username: assertString(json.username),
    password: assertString(json.password)
  };
}
export function encodeDeliveryMethod_UploadCass(value) {
  return {
    account: value.account,
    username: value.username,
    password: value.password
  };
}
export function decodeDemoEnvironmentEmailImportCompleted(json) {
  return {
    email_message_id: assertNumber(json.email_message_id)
  };
}
export function encodeDemoEnvironmentEmailImportCompleted(value) {
  return {
    email_message_id: value.email_message_id
  };
}
export function decodeDemoEnvironmentImportEmailInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    email_index: assertNumber(json.email_index)
  };
}
export function encodeDemoEnvironmentImportEmailInput(value) {
  return {
    account_id: value.account_id,
    email_index: value.email_index
  };
}
export function decodeDemoEnvironmentImportEmailOutput(json) {
  return {
    email_message_id: assertNumber(json.email_message_id)
  };
}
export function encodeDemoEnvironmentImportEmailOutput(value) {
  return {
    email_message_id: value.email_message_id
  };
}
export function decodeDemoEnvironmentPostImportInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    admin_user_email: assertString(json.admin_user_email),
    email_message_ids: (json.email_message_ids || []).map(assertNumber)
  };
}
export function encodeDemoEnvironmentPostImportInput(value) {
  return {
    account_id: value.account_id,
    admin_user_email: value.admin_user_email,
    email_message_ids: value.email_message_ids
  };
}
export function decodeDemoEnvironmentPreImportInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    admin_user_email: assertString(json.admin_user_email)
  };
}
export function encodeDemoEnvironmentPreImportInput(value) {
  return {
    account_id: value.account_id,
    admin_user_email: value.admin_user_email
  };
}
export function decodeDemoEnvironmentPreImportOutput(json) {
  return {
    email_count: assertNumber(json.email_count)
  };
}
export function encodeDemoEnvironmentPreImportOutput(value) {
  return {
    email_count: value.email_count
  };
}
export function decodeDemoEnvironmentSignal(json) {
  return {
    signal: decodeDemoEnvironmentSignal_Signal(json)
  };
}
export function encodeDemoEnvironmentSignal(value) {
  return __spreadValues({}, encodeDemoEnvironmentSignal_Signal(value.signal));
}
export var DemoEnvironmentSignal_SignalType = /* @__PURE__ */ ((DemoEnvironmentSignal_SignalType2) => {
  DemoEnvironmentSignal_SignalType2["EmailImportCompleted"] = "email_import_completed";
  return DemoEnvironmentSignal_SignalType2;
})(DemoEnvironmentSignal_SignalType || {});
export function decodeDemoEnvironmentSignal_Signal(json) {
  if (json.email_import_completed !== void 0) {
    return {
      type: "email_import_completed" /* EmailImportCompleted */,
      email_import_completed: decodeDemoEnvironmentEmailImportCompleted(
        json.email_import_completed
      )
    };
  }
  throw new Error("No field set in oneof DemoEnvironmentSignal_Signal");
}
export function encodeDemoEnvironmentSignal_Signal(value) {
  switch (value.type) {
    case "email_import_completed" /* EmailImportCompleted */:
      return {
        email_import_completed: encodeDemoEnvironmentEmailImportCompleted(
          value.email_import_completed
        )
      };
    default:
      throw new Error("No field set in oneof DemoEnvironmentSignal_Signal");
  }
}
export function decodeDemoEnvironmentWorkflowInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    admin_user_email: assertString(json.admin_user_email)
  };
}
export function encodeDemoEnvironmentWorkflowInput(value) {
  return {
    account_id: value.account_id,
    admin_user_email: value.admin_user_email
  };
}
export function decodeDispatchInferenceStepInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id),
    skipped_stages: (json.skipped_stages || []).map(assertString)
  };
}
export function encodeDispatchInferenceStepInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id,
    skipped_stages: value.skipped_stages
  };
}
export function decodeDispatchInferenceWorkflowInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id),
    decomposed_dispatch: assertBoolean(json.decomposed_dispatch),
    skipped_stages: (json.skipped_stages || []).map(assertString),
    doc_vqa_child_workflow: assertBoolean(json.doc_vqa_child_workflow)
  };
}
export function encodeDispatchInferenceWorkflowInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id,
    decomposed_dispatch: value.decomposed_dispatch,
    skipped_stages: value.skipped_stages,
    doc_vqa_child_workflow: value.doc_vqa_child_workflow
  };
}
export function decodeDocVqaCascadeAnalyzeTierInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id),
    plan: decodeDocVqaCascadePlan(json.plan),
    tier: decodeDocVqaCascadeTier(json.tier)
  };
}
export function encodeDocVqaCascadeAnalyzeTierInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id,
    plan: encodeDocVqaCascadePlan(value.plan),
    tier: encodeDocVqaCascadeTier(value.tier)
  };
}
export function decodeDocVqaCascadeAnalyzeTierOutput(json) {
  return {
    skip: assertBoolean(json.skip),
    groups: (json.groups || []).map(decodeDocVqaCascadeTierPageGroup)
  };
}
export function encodeDocVqaCascadeAnalyzeTierOutput(value) {
  return {
    skip: value.skip,
    groups: value.groups.map(encodeDocVqaCascadeTierPageGroup)
  };
}
export function decodeDocVqaCascadeBeginInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeDocVqaCascadeBeginInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id
  };
}
export function decodeDocVqaCascadeBeginOutput(json) {
  return {
    drive_cascade: assertBoolean(json.drive_cascade)
  };
}
export function encodeDocVqaCascadeBeginOutput(value) {
  return {
    drive_cascade: value.drive_cascade
  };
}
export function decodeDocVqaCascadeFinalizeInput(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeDocVqaCascadeFinalizeInput(value) {
  return {
    document_batch_id: value.document_batch_id
  };
}
export function decodeDocVqaCascadePlan(json) {
  return {
    account_sequence: (json.account_sequence || []).map(assertString),
    enabled_sequence: (json.enabled_sequence || []).map(assertString),
    tiers: (json.tiers || []).map(decodeDocVqaCascadeTier)
  };
}
export function encodeDocVqaCascadePlan(value) {
  return {
    account_sequence: value.account_sequence,
    enabled_sequence: value.enabled_sequence,
    tiers: value.tiers.map(encodeDocVqaCascadeTier)
  };
}
export function decodeDocVqaCascadePlanInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeDocVqaCascadePlanInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id
  };
}
export function decodeDocVqaCascadePlanOutput(json) {
  return {
    plan: decodeDocVqaCascadePlan(json.plan)
  };
}
export function encodeDocVqaCascadePlanOutput(value) {
  return {
    plan: encodeDocVqaCascadePlan(value.plan)
  };
}
export function decodeDocVqaCascadeSendTierInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id),
    tier: decodeDocVqaCascadeTier(json.tier),
    groups: (json.groups || []).map(decodeDocVqaCascadeTierPageGroup)
  };
}
export function encodeDocVqaCascadeSendTierInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id,
    tier: encodeDocVqaCascadeTier(value.tier),
    groups: value.groups.map(encodeDocVqaCascadeTierPageGroup)
  };
}
export function decodeDocVqaCascadeSendTierOutput(json) {
  return {
    awaiting_results: assertBoolean(json.awaiting_results),
    import_stopped_waiting: assertBoolean(json.import_stopped_waiting)
  };
}
export function encodeDocVqaCascadeSendTierOutput(value) {
  return {
    awaiting_results: value.awaiting_results,
    import_stopped_waiting: value.import_stopped_waiting
  };
}
export function decodeDocVqaCascadeTier(json) {
  return {
    base_model: assertString(json.base_model),
    level: assertNumber(json.level)
  };
}
export function encodeDocVqaCascadeTier(value) {
  return {
    base_model: value.base_model,
    level: value.level
  };
}
export function decodeDocVqaCascadeTierCompleteSignal(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id),
    base_model: assertString(json.base_model),
    group_timed_out: assertBoolean(json.group_timed_out)
  };
}
export function encodeDocVqaCascadeTierCompleteSignal(value) {
  return {
    document_batch_id: value.document_batch_id,
    base_model: value.base_model,
    group_timed_out: value.group_timed_out
  };
}
export function decodeDocVqaCascadeTierPageGroup(json) {
  return {
    document_page_ids: (json.document_page_ids || []).map(assertNumber),
    fields: (json.fields || []).map(assertString)
  };
}
export function encodeDocVqaCascadeTierPageGroup(value) {
  return {
    document_page_ids: value.document_page_ids,
    fields: value.fields
  };
}
export function decodeDocVqaCascadeWorkflowInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeDocVqaCascadeWorkflowInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id
  };
}
export function decodeDocument(json) {
  return {
    id: assertNumber(json.id),
    number: assertNumber(json.number),
    carrier_invoice_id: optionalDecoder(assertNumber)(json.carrier_invoice_id),
    document_group_id: optionalDecoder(assertNumber)(json.document_group_id),
    document_batch_id: optionalDecoder(assertNumber)(json.document_batch_id),
    factor_invoice_id: optionalDecoder(assertNumber)(json.factor_invoice_id),
    created_at: decodeTimestamp(json.created_at),
    inline_download_url: assertString(json.inline_download_url),
    previewable: assertBoolean(json.previewable),
    broker_email_addresses: (json.broker_email_addresses || []).map(
      assertString
    ),
    token: optionalDecoder(assertString)(json.token),
    hidden: assertBoolean(json.hidden),
    is_signed: assertBoolean(json.is_signed),
    has_reference_number: assertBoolean(json.has_reference_number),
    is_pod: assertBoolean(json.is_pod),
    includes_pod: assertBoolean(json.includes_pod),
    has_missing_pages: assertBoolean(json.has_missing_pages),
    has_damage: assertBoolean(json.has_damage),
    has_carrier_rate: assertBoolean(json.has_carrier_rate),
    finished: assertBoolean(json.finished),
    documentTypeValue: decodeDocument_DocumentTypeValue(json),
    guessedDocumentTypeValue: decodeDocument_GuessedDocumentTypeValue(json)
  };
}
export function encodeDocument(value) {
  return __spreadValues(__spreadValues({
    id: value.id,
    number: value.number,
    carrier_invoice_id: value.carrier_invoice_id,
    document_group_id: value.document_group_id,
    document_batch_id: value.document_batch_id,
    factor_invoice_id: value.factor_invoice_id,
    created_at: encodeTimestamp(value.created_at),
    inline_download_url: value.inline_download_url,
    previewable: value.previewable,
    broker_email_addresses: value.broker_email_addresses,
    token: value.token,
    hidden: value.hidden,
    is_signed: value.is_signed,
    has_reference_number: value.has_reference_number,
    is_pod: value.is_pod,
    includes_pod: value.includes_pod,
    has_missing_pages: value.has_missing_pages,
    has_damage: value.has_damage,
    has_carrier_rate: value.has_carrier_rate,
    finished: value.finished
  }, encodeDocument_DocumentTypeValue(value.documentTypeValue)), encodeDocument_GuessedDocumentTypeValue(value.guessedDocumentTypeValue));
}
export var Document_DocumentTypeValueType = /* @__PURE__ */ ((Document_DocumentTypeValueType2) => {
  Document_DocumentTypeValueType2["DocumentType"] = "document_type";
  Document_DocumentTypeValueType2["DocumentTypePartial"] = "document_type_partial";
  Document_DocumentTypeValueType2["UnknownDocumentType"] = "unknown_document_type";
  return Document_DocumentTypeValueType2;
})(Document_DocumentTypeValueType || {});
export function decodeDocument_DocumentTypeValue(json) {
  if (json.document_type !== void 0) {
    return {
      type: "document_type" /* DocumentType */,
      document_type: decodeDocumentType(json.document_type)
    };
  } else if (json.document_type_partial !== void 0) {
    return {
      type: "document_type_partial" /* DocumentTypePartial */,
      document_type_partial: decodeDocumentTypePartial(
        json.document_type_partial
      )
    };
  } else if (json.unknown_document_type !== void 0) {
    return {
      type: "unknown_document_type" /* UnknownDocumentType */,
      unknown_document_type: decodeDocument_UnknownDocumentType(
        json.unknown_document_type
      )
    };
  }
  throw new Error("No field set in oneof Document_DocumentTypeValue");
}
export function encodeDocument_DocumentTypeValue(value) {
  switch (value.type) {
    case "document_type" /* DocumentType */:
      return { document_type: encodeDocumentType(value.document_type) };
    case "document_type_partial" /* DocumentTypePartial */:
      return {
        document_type_partial: encodeDocumentTypePartial(
          value.document_type_partial
        )
      };
    case "unknown_document_type" /* UnknownDocumentType */:
      return {
        unknown_document_type: encodeDocument_UnknownDocumentType(
          value.unknown_document_type
        )
      };
    default:
      throw new Error("No field set in oneof Document_DocumentTypeValue");
  }
}
export var Document_GuessedDocumentTypeValueType = /* @__PURE__ */ ((Document_GuessedDocumentTypeValueType2) => {
  Document_GuessedDocumentTypeValueType2["GuessedDocumentType"] = "guessed_document_type";
  Document_GuessedDocumentTypeValueType2["GuessedDocumentTypePartial"] = "guessed_document_type_partial";
  Document_GuessedDocumentTypeValueType2["GuessedUnknownDocumentType"] = "guessed_unknown_document_type";
  return Document_GuessedDocumentTypeValueType2;
})(Document_GuessedDocumentTypeValueType || {});
export function decodeDocument_GuessedDocumentTypeValue(json) {
  if (json.guessed_document_type !== void 0) {
    return {
      type: "guessed_document_type" /* GuessedDocumentType */,
      guessed_document_type: decodeDocumentType(json.guessed_document_type)
    };
  } else if (json.guessed_document_type_partial !== void 0) {
    return {
      type: "guessed_document_type_partial" /* GuessedDocumentTypePartial */,
      guessed_document_type_partial: decodeDocumentTypePartial(
        json.guessed_document_type_partial
      )
    };
  } else if (json.guessed_unknown_document_type !== void 0) {
    return {
      type: "guessed_unknown_document_type" /* GuessedUnknownDocumentType */,
      guessed_unknown_document_type: decodeDocument_UnknownDocumentType(
        json.guessed_unknown_document_type
      )
    };
  }
  throw new Error("No field set in oneof Document_GuessedDocumentTypeValue");
}
export function encodeDocument_GuessedDocumentTypeValue(value) {
  switch (value.type) {
    case "guessed_document_type" /* GuessedDocumentType */:
      return {
        guessed_document_type: encodeDocumentType(value.guessed_document_type)
      };
    case "guessed_document_type_partial" /* GuessedDocumentTypePartial */:
      return {
        guessed_document_type_partial: encodeDocumentTypePartial(
          value.guessed_document_type_partial
        )
      };
    case "guessed_unknown_document_type" /* GuessedUnknownDocumentType */:
      return {
        guessed_unknown_document_type: encodeDocument_UnknownDocumentType(
          value.guessed_unknown_document_type
        )
      };
    default:
      throw new Error(
        "No field set in oneof Document_GuessedDocumentTypeValue"
      );
  }
}
export function decodeDocument_UnknownDocumentType(json) {
  return {};
}
export function encodeDocument_UnknownDocumentType(value) {
  return {};
}
export function decodeDocumentAddressAnswer(json) {
  return {
    documentPageId: assertNumber(json.documentPageId),
    place: decodeAddressPlace(json.place),
    tier: assertNumber(json.tier),
    value: assertString(json.value),
    location: optionalDecoder(decodeDocumentLocation)(json.location)
  };
}
export function encodeDocumentAddressAnswer(value) {
  return {
    documentPageId: value.documentPageId,
    place: encodeAddressPlace(value.place),
    tier: value.tier,
    value: value.value,
    location: optionalEncoder(encodeDocumentLocation)(value.location)
  };
}
export function decodeDocumentBatch(json) {
  return {
    id: assertNumber(json.id),
    email_message_id: optionalDecoder(assertNumber)(json.email_message_id),
    single_carrier: optionalDecoder(assertBoolean)(json.single_carrier),
    created_at: decodeTimestamp(json.created_at),
    updated_at: decodeTimestamp(json.updated_at),
    pages: optionalDecoder(assertNumber)(json.pages),
    group_mismatch: assertBoolean(json.group_mismatch),
    batch_level_uploads: assertBoolean(json.batch_level_uploads),
    regroup_required: assertBoolean(json.regroup_required),
    fully_reviewed: assertBoolean(json.fully_reviewed),
    auto_processing_data: (json.auto_processing_data || []).map(
      decodeInvoiceAutoProcessingData
    ),
    merged_documents_download_url: assertString(
      json.merged_documents_download_url
    )
  };
}
export function encodeDocumentBatch(value) {
  return {
    id: value.id,
    email_message_id: value.email_message_id,
    single_carrier: value.single_carrier,
    created_at: encodeTimestamp(value.created_at),
    updated_at: encodeTimestamp(value.updated_at),
    pages: value.pages,
    group_mismatch: value.group_mismatch,
    batch_level_uploads: value.batch_level_uploads,
    regroup_required: value.regroup_required,
    fully_reviewed: value.fully_reviewed,
    auto_processing_data: value.auto_processing_data.map(
      encodeInvoiceAutoProcessingData
    ),
    merged_documents_download_url: value.merged_documents_download_url
  };
}
export function decodeDocumentBatchBuilderEmailIgnored(json) {
  return {
    import_process_id: assertNumber(json.import_process_id)
  };
}
export function encodeDocumentBatchBuilderEmailIgnored(value) {
  return {
    import_process_id: value.import_process_id
  };
}
export function decodeDocumentBatchBuilderEnqueuePODDetectionInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id)
  };
}
export function encodeDocumentBatchBuilderEnqueuePODDetectionInput(value) {
  return {
    import_process_id: value.import_process_id
  };
}
export function decodeDocumentBatchBuilderInferenceResolved(json) {
  return {
    import_process_id: assertNumber(json.import_process_id)
  };
}
export function encodeDocumentBatchBuilderInferenceResolved(value) {
  return {
    import_process_id: value.import_process_id
  };
}
export function decodeDocumentBatchBuilderInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    grouping_reviews_enabled: assertBoolean(json.grouping_reviews_enabled),
    group_load_reviews_enabled: assertBoolean(json.group_load_reviews_enabled),
    llm_pod_detection_enabled: assertBoolean(json.llm_pod_detection_enabled),
    no_dependents_reviews_enabled: assertBoolean(
      json.no_dependents_reviews_enabled
    )
  };
}
export function encodeDocumentBatchBuilderInput(value) {
  return {
    import_process_id: value.import_process_id,
    grouping_reviews_enabled: value.grouping_reviews_enabled,
    group_load_reviews_enabled: value.group_load_reviews_enabled,
    llm_pod_detection_enabled: value.llm_pod_detection_enabled,
    no_dependents_reviews_enabled: value.no_dependents_reviews_enabled
  };
}
export function decodeDocumentBatchBuilderNormalizerPreProcessInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id)
  };
}
export function encodeDocumentBatchBuilderNormalizerPreProcessInput(value) {
  return {
    import_process_id: value.import_process_id
  };
}
export function decodeDocumentBatchBuilderPlanLlmPODDetectionInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id)
  };
}
export function encodeDocumentBatchBuilderPlanLlmPODDetectionInput(value) {
  return {
    import_process_id: value.import_process_id
  };
}
export function decodeDocumentBatchBuilderPlanLlmPODDetectionOutput(json) {
  return {
    plan: decodeDocumentBatchBuilderPlanLlmPODDetectionOutput_Plan(json)
  };
}
export function encodeDocumentBatchBuilderPlanLlmPODDetectionOutput(value) {
  return __spreadValues({}, encodeDocumentBatchBuilderPlanLlmPODDetectionOutput_Plan(value.plan));
}
export var DocumentBatchBuilderPlanLlmPODDetectionOutput_PlanType = /* @__PURE__ */ ((DocumentBatchBuilderPlanLlmPODDetectionOutput_PlanType2) => {
  DocumentBatchBuilderPlanLlmPODDetectionOutput_PlanType2["PlanRun"] = "plan_run";
  DocumentBatchBuilderPlanLlmPODDetectionOutput_PlanType2["PlanSkip"] = "plan_skip";
  return DocumentBatchBuilderPlanLlmPODDetectionOutput_PlanType2;
})(DocumentBatchBuilderPlanLlmPODDetectionOutput_PlanType || {});
export function decodeDocumentBatchBuilderPlanLlmPODDetectionOutput_Plan(json) {
  if (json.plan_run !== void 0) {
    return {
      type: "plan_run" /* PlanRun */,
      plan_run: decodeLlmPODDetectionWorkflowInput(json.plan_run)
    };
  } else if (json.plan_skip !== void 0) {
    return {
      type: "plan_skip" /* PlanSkip */,
      plan_skip: decodePlanLlmPODDetectionOutputSkip(json.plan_skip)
    };
  }
  throw new Error(
    "No field set in oneof DocumentBatchBuilderPlanLlmPODDetectionOutput_Plan"
  );
}
export function encodeDocumentBatchBuilderPlanLlmPODDetectionOutput_Plan(value) {
  switch (value.type) {
    case "plan_run" /* PlanRun */:
      return { plan_run: encodeLlmPODDetectionWorkflowInput(value.plan_run) };
    case "plan_skip" /* PlanSkip */:
      return {
        plan_skip: encodePlanLlmPODDetectionOutputSkip(value.plan_skip)
      };
    default:
      throw new Error(
        "No field set in oneof DocumentBatchBuilderPlanLlmPODDetectionOutput_Plan"
      );
  }
}
export function decodeDocumentBatchBuilderPlanSplitInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeDocumentBatchBuilderPlanSplitInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id
  };
}
export function decodeDocumentBatchBuilderPlanSplitOutput(json) {
  return {
    plan: decodeDocumentBatchBuilderPlanSplitOutput_Plan(json)
  };
}
export function encodeDocumentBatchBuilderPlanSplitOutput(value) {
  return __spreadValues({}, encodeDocumentBatchBuilderPlanSplitOutput_Plan(value.plan));
}
export var DocumentBatchBuilderPlanSplitOutput_PlanType = /* @__PURE__ */ ((DocumentBatchBuilderPlanSplitOutput_PlanType2) => {
  DocumentBatchBuilderPlanSplitOutput_PlanType2["PlanRun"] = "plan_run";
  DocumentBatchBuilderPlanSplitOutput_PlanType2["PlanSkip"] = "plan_skip";
  return DocumentBatchBuilderPlanSplitOutput_PlanType2;
})(DocumentBatchBuilderPlanSplitOutput_PlanType || {});
export function decodeDocumentBatchBuilderPlanSplitOutput_Plan(json) {
  if (json.plan_run !== void 0) {
    return {
      type: "plan_run" /* PlanRun */,
      plan_run: decodeSplitAttachmentsWorkflowInput(json.plan_run)
    };
  } else if (json.plan_skip !== void 0) {
    return {
      type: "plan_skip" /* PlanSkip */,
      plan_skip: decodePlanSplitOutputSkip(json.plan_skip)
    };
  }
  throw new Error(
    "No field set in oneof DocumentBatchBuilderPlanSplitOutput_Plan"
  );
}
export function encodeDocumentBatchBuilderPlanSplitOutput_Plan(value) {
  switch (value.type) {
    case "plan_run" /* PlanRun */:
      return { plan_run: encodeSplitAttachmentsWorkflowInput(value.plan_run) };
    case "plan_skip" /* PlanSkip */:
      return { plan_skip: encodePlanSplitOutputSkip(value.plan_skip) };
    default:
      throw new Error(
        "No field set in oneof DocumentBatchBuilderPlanSplitOutput_Plan"
      );
  }
}
export function decodeDocumentBatchBuilderPODDetectionResolved(json) {
  return {
    import_process_id: assertNumber(json.import_process_id)
  };
}
export function encodeDocumentBatchBuilderPODDetectionResolved(value) {
  return {
    import_process_id: value.import_process_id
  };
}
export function decodeDocumentBatchBuilderPostProcessInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id)
  };
}
export function encodeDocumentBatchBuilderPostProcessInput(value) {
  return {
    import_process_id: value.import_process_id
  };
}
export function decodeDocumentBatchBuilderPrerequisitesInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id),
    report_email_ignored: assertBoolean(json.report_email_ignored)
  };
}
export function encodeDocumentBatchBuilderPrerequisitesInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id,
    report_email_ignored: value.report_email_ignored
  };
}
export function decodeDocumentBatchBuilderPrerequisitesResolved(json) {
  return {
    import_process_id: assertNumber(json.import_process_id)
  };
}
export function encodeDocumentBatchBuilderPrerequisitesResolved(value) {
  return {
    import_process_id: value.import_process_id
  };
}
export function decodeDocumentBatchBuilderProcessInferenceResultsInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id)
  };
}
export function encodeDocumentBatchBuilderProcessInferenceResultsInput(value) {
  return {
    import_process_id: value.import_process_id
  };
}
export function decodeDocumentBatchBuilderProcessInferenceResultsOutput(json) {
  return {
    batchResult: decodeDocumentBatchBuilderProcessInferenceResultsOutput_BatchResult(json)
  };
}
export function encodeDocumentBatchBuilderProcessInferenceResultsOutput(value) {
  return __spreadValues({}, encodeDocumentBatchBuilderProcessInferenceResultsOutput_BatchResult(
    value.batchResult
  ));
}
export var DocumentBatchBuilderProcessInferenceResultsOutput_BatchResultType = /* @__PURE__ */ ((DocumentBatchBuilderProcessInferenceResultsOutput_BatchResultType2) => {
  DocumentBatchBuilderProcessInferenceResultsOutput_BatchResultType2["ResultBatch"] = "result_batch";
  DocumentBatchBuilderProcessInferenceResultsOutput_BatchResultType2["ResultNoBatch"] = "result_no_batch";
  DocumentBatchBuilderProcessInferenceResultsOutput_BatchResultType2["ResultDidNotRun"] = "result_did_not_run";
  return DocumentBatchBuilderProcessInferenceResultsOutput_BatchResultType2;
})(DocumentBatchBuilderProcessInferenceResultsOutput_BatchResultType || {});
export function decodeDocumentBatchBuilderProcessInferenceResultsOutput_BatchResult(json) {
  if (json.result_batch !== void 0) {
    return {
      type: "result_batch" /* ResultBatch */,
      result_batch: decodeProcessInferenceResultsOutputBatch(json.result_batch)
    };
  } else if (json.result_no_batch !== void 0) {
    return {
      type: "result_no_batch" /* ResultNoBatch */,
      result_no_batch: decodeProcessInferenceResultsOutputNoBatch(
        json.result_no_batch
      )
    };
  } else if (json.result_did_not_run !== void 0) {
    return {
      type: "result_did_not_run" /* ResultDidNotRun */,
      result_did_not_run: decodeImportStageDidNotRun(json.result_did_not_run)
    };
  }
  throw new Error(
    "No field set in oneof DocumentBatchBuilderProcessInferenceResultsOutput_BatchResult"
  );
}
export function encodeDocumentBatchBuilderProcessInferenceResultsOutput_BatchResult(value) {
  switch (value.type) {
    case "result_batch" /* ResultBatch */:
      return {
        result_batch: encodeProcessInferenceResultsOutputBatch(
          value.result_batch
        )
      };
    case "result_no_batch" /* ResultNoBatch */:
      return {
        result_no_batch: encodeProcessInferenceResultsOutputNoBatch(
          value.result_no_batch
        )
      };
    case "result_did_not_run" /* ResultDidNotRun */:
      return {
        result_did_not_run: encodeImportStageDidNotRun(
          value.result_did_not_run
        )
      };
    default:
      throw new Error(
        "No field set in oneof DocumentBatchBuilderProcessInferenceResultsOutput_BatchResult"
      );
  }
}
export function decodeDocumentBatchBuilderSignal(json) {
  return {
    signal: decodeDocumentBatchBuilderSignal_Signal(json)
  };
}
export function encodeDocumentBatchBuilderSignal(value) {
  return __spreadValues({}, encodeDocumentBatchBuilderSignal_Signal(value.signal));
}
export var DocumentBatchBuilderSignal_SignalType = /* @__PURE__ */ ((DocumentBatchBuilderSignal_SignalType2) => {
  DocumentBatchBuilderSignal_SignalType2["PrerequisitesResolved"] = "prerequisites_resolved";
  DocumentBatchBuilderSignal_SignalType2["UnderstandingSkipped"] = "understanding_skipped";
  DocumentBatchBuilderSignal_SignalType2["InferenceResolved"] = "inference_resolved";
  DocumentBatchBuilderSignal_SignalType2["PODDetectionResolved"] = "pod_detection_resolved";
  DocumentBatchBuilderSignal_SignalType2["EmailIgnored"] = "email_ignored";
  return DocumentBatchBuilderSignal_SignalType2;
})(DocumentBatchBuilderSignal_SignalType || {});
export function decodeDocumentBatchBuilderSignal_Signal(json) {
  if (json.prerequisites_resolved !== void 0) {
    return {
      type: "prerequisites_resolved" /* PrerequisitesResolved */,
      prerequisites_resolved: decodeDocumentBatchBuilderPrerequisitesResolved(
        json.prerequisites_resolved
      )
    };
  } else if (json.understanding_skipped !== void 0) {
    return {
      type: "understanding_skipped" /* UnderstandingSkipped */,
      understanding_skipped: decodeDocumentBatchBuilderUnderstandingSkipped(
        json.understanding_skipped
      )
    };
  } else if (json.inference_resolved !== void 0) {
    return {
      type: "inference_resolved" /* InferenceResolved */,
      inference_resolved: decodeDocumentBatchBuilderInferenceResolved(
        json.inference_resolved
      )
    };
  } else if (json.pod_detection_resolved !== void 0) {
    return {
      type: "pod_detection_resolved" /* PODDetectionResolved */,
      pod_detection_resolved: decodeDocumentBatchBuilderPODDetectionResolved(
        json.pod_detection_resolved
      )
    };
  } else if (json.email_ignored !== void 0) {
    return {
      type: "email_ignored" /* EmailIgnored */,
      email_ignored: decodeDocumentBatchBuilderEmailIgnored(json.email_ignored)
    };
  }
  throw new Error("No field set in oneof DocumentBatchBuilderSignal_Signal");
}
export function encodeDocumentBatchBuilderSignal_Signal(value) {
  switch (value.type) {
    case "prerequisites_resolved" /* PrerequisitesResolved */:
      return {
        prerequisites_resolved: encodeDocumentBatchBuilderPrerequisitesResolved(
          value.prerequisites_resolved
        )
      };
    case "understanding_skipped" /* UnderstandingSkipped */:
      return {
        understanding_skipped: encodeDocumentBatchBuilderUnderstandingSkipped(
          value.understanding_skipped
        )
      };
    case "inference_resolved" /* InferenceResolved */:
      return {
        inference_resolved: encodeDocumentBatchBuilderInferenceResolved(
          value.inference_resolved
        )
      };
    case "pod_detection_resolved" /* PODDetectionResolved */:
      return {
        pod_detection_resolved: encodeDocumentBatchBuilderPODDetectionResolved(
          value.pod_detection_resolved
        )
      };
    case "email_ignored" /* EmailIgnored */:
      return {
        email_ignored: encodeDocumentBatchBuilderEmailIgnored(
          value.email_ignored
        )
      };
    default:
      throw new Error(
        "No field set in oneof DocumentBatchBuilderSignal_Signal"
      );
  }
}
export function decodeDocumentBatchBuilderUnderstandingSkipped(json) {
  return {
    import_process_id: assertNumber(json.import_process_id)
  };
}
export function encodeDocumentBatchBuilderUnderstandingSkipped(value) {
  return {
    import_process_id: value.import_process_id
  };
}
export function decodeDocumentBatchGroupingReview(json) {
  return {
    id: assertNumber(json.id),
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeDocumentBatchGroupingReview(value) {
  return {
    id: value.id,
    document_batch_id: value.document_batch_id
  };
}
export function decodeDocumentBatchGroupingReviewCompletionRequest(json) {
  return {
    documentBatchGroupingReviewId: assertNumber(
      json.documentBatchGroupingReviewId
    ),
    groupings: (json.groupings || []).map(
      decodeDocumentBatchGroupingReviewDocumentGroup
    )
  };
}
export function encodeDocumentBatchGroupingReviewCompletionRequest(value) {
  return {
    documentBatchGroupingReviewId: value.documentBatchGroupingReviewId,
    groupings: value.groupings.map(
      encodeDocumentBatchGroupingReviewDocumentGroup
    )
  };
}
export function decodeDocumentBatchGroupingReviewDocumentGroup(json) {
  return {
    position: assertNumber(json.position),
    document_ids: (json.document_ids || []).map(assertNumber)
  };
}
export function encodeDocumentBatchGroupingReviewDocumentGroup(value) {
  return {
    position: value.position,
    document_ids: value.document_ids
  };
}
export function decodeDocumentBatchGroupingReviewId(json) {
  return {
    documentBatchGroupingReviewId: optionalDecoder(assertNumber)(
      json.documentBatchGroupingReviewId
    )
  };
}
export function encodeDocumentBatchGroupingReviewId(value) {
  return {
    documentBatchGroupingReviewId: value.documentBatchGroupingReviewId
  };
}
export function decodeDocumentContentReviewsBatchGroupingCreateReviewsOutput(json) {
  return {
    reviews_created: assertBoolean(json.reviews_created)
  };
}
export function encodeDocumentContentReviewsBatchGroupingCreateReviewsOutput(value) {
  return {
    reviews_created: value.reviews_created
  };
}
export function decodeDocumentContentReviewsBatchGroupingInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeDocumentContentReviewsBatchGroupingInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id
  };
}
export function decodeDocumentContentReviewsBatchGroupingReviewsResolved(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeDocumentContentReviewsBatchGroupingReviewsResolved(value) {
  return {
    document_batch_id: value.document_batch_id
  };
}
export function decodeDocumentContentReviewsBatchGroupingSignal(json) {
  return {
    signal: decodeDocumentContentReviewsBatchGroupingSignal_Signal(json)
  };
}
export function encodeDocumentContentReviewsBatchGroupingSignal(value) {
  return __spreadValues({}, encodeDocumentContentReviewsBatchGroupingSignal_Signal(value.signal));
}
export var DocumentContentReviewsBatchGroupingSignal_SignalType = /* @__PURE__ */ ((DocumentContentReviewsBatchGroupingSignal_SignalType2) => {
  DocumentContentReviewsBatchGroupingSignal_SignalType2["ReviewsResolved"] = "reviews_resolved";
  return DocumentContentReviewsBatchGroupingSignal_SignalType2;
})(DocumentContentReviewsBatchGroupingSignal_SignalType || {});
export function decodeDocumentContentReviewsBatchGroupingSignal_Signal(json) {
  if (json.reviews_resolved !== void 0) {
    return {
      type: "reviews_resolved" /* ReviewsResolved */,
      reviews_resolved: decodeDocumentContentReviewsBatchGroupingReviewsResolved(
        json.reviews_resolved
      )
    };
  }
  throw new Error(
    "No field set in oneof DocumentContentReviewsBatchGroupingSignal_Signal"
  );
}
export function encodeDocumentContentReviewsBatchGroupingSignal_Signal(value) {
  switch (value.type) {
    case "reviews_resolved" /* ReviewsResolved */:
      return {
        reviews_resolved: encodeDocumentContentReviewsBatchGroupingReviewsResolved(
          value.reviews_resolved
        )
      };
    default:
      throw new Error(
        "No field set in oneof DocumentContentReviewsBatchGroupingSignal_Signal"
      );
  }
}
export function decodeDocumentContentReviewsNoDependentsCreateReviewsOutput(json) {
  return {
    reviews_created: assertBoolean(json.reviews_created)
  };
}
export function encodeDocumentContentReviewsNoDependentsCreateReviewsOutput(value) {
  return {
    reviews_created: value.reviews_created
  };
}
export function decodeDocumentContentReviewsNoDependentsInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeDocumentContentReviewsNoDependentsInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id
  };
}
export function decodeDocumentContentReviewsNoDependentsReviewsResolved(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeDocumentContentReviewsNoDependentsReviewsResolved(value) {
  return {
    document_batch_id: value.document_batch_id
  };
}
export function decodeDocumentContentReviewsNoDependentsSignal(json) {
  return {
    signal: decodeDocumentContentReviewsNoDependentsSignal_Signal(json)
  };
}
export function encodeDocumentContentReviewsNoDependentsSignal(value) {
  return __spreadValues({}, encodeDocumentContentReviewsNoDependentsSignal_Signal(value.signal));
}
export var DocumentContentReviewsNoDependentsSignal_SignalType = /* @__PURE__ */ ((DocumentContentReviewsNoDependentsSignal_SignalType2) => {
  DocumentContentReviewsNoDependentsSignal_SignalType2["ReviewsResolved"] = "reviews_resolved";
  return DocumentContentReviewsNoDependentsSignal_SignalType2;
})(DocumentContentReviewsNoDependentsSignal_SignalType || {});
export function decodeDocumentContentReviewsNoDependentsSignal_Signal(json) {
  if (json.reviews_resolved !== void 0) {
    return {
      type: "reviews_resolved" /* ReviewsResolved */,
      reviews_resolved: decodeDocumentContentReviewsNoDependentsReviewsResolved(
        json.reviews_resolved
      )
    };
  }
  throw new Error(
    "No field set in oneof DocumentContentReviewsNoDependentsSignal_Signal"
  );
}
export function encodeDocumentContentReviewsNoDependentsSignal_Signal(value) {
  switch (value.type) {
    case "reviews_resolved" /* ReviewsResolved */:
      return {
        reviews_resolved: encodeDocumentContentReviewsNoDependentsReviewsResolved(
          value.reviews_resolved
        )
      };
    default:
      throw new Error(
        "No field set in oneof DocumentContentReviewsNoDependentsSignal_Signal"
      );
  }
}
export function decodeDocumentGroup(json) {
  return {
    id: assertNumber(json.id),
    factor_invoice_id: optionalDecoder(assertNumber)(json.factor_invoice_id),
    display_status: optionalDecoder(decodeDocumentGroup_DisplayStatus)(
      json.display_status
    ),
    invoice_information: optionalDecoder(assertString)(
      json.invoice_information
    ),
    document_batch_id: assertNumber(json.document_batch_id),
    grouping_method: optionalDecoder(assertString)(json.grouping_method),
    document_ids: (json.document_ids || []).map(assertNumber),
    additional_labels: (json.additional_labels || []).map(assertString),
    position: assertNumber(json.position)
  };
}
export function encodeDocumentGroup(value) {
  return {
    id: value.id,
    factor_invoice_id: value.factor_invoice_id,
    display_status: optionalEncoder(encodeDocumentGroup_DisplayStatus)(
      value.display_status
    ),
    invoice_information: value.invoice_information,
    document_batch_id: value.document_batch_id,
    grouping_method: value.grouping_method,
    document_ids: value.document_ids,
    additional_labels: value.additional_labels,
    position: value.position
  };
}
export var DocumentGroup_DisplayStatus = /* @__PURE__ */ ((DocumentGroup_DisplayStatus2) => {
  DocumentGroup_DisplayStatus2["DocumentGroup_DisplayStatusApproved"] = "approved";
  DocumentGroup_DisplayStatus2["DocumentGroup_DisplayStatusRejected"] = "rejected";
  return DocumentGroup_DisplayStatus2;
})(DocumentGroup_DisplayStatus || {});
export const decodeDocumentGroup_DisplayStatus = (value) => value;
export const encodeDocumentGroup_DisplayStatus = (value) => value;
export function decodeDocumentGroupLoadReview(json) {
  return {
    id: assertNumber(json.id),
    document_group_id: assertNumber(json.document_group_id),
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeDocumentGroupLoadReview(value) {
  return {
    id: value.id,
    document_group_id: value.document_group_id,
    document_batch_id: value.document_batch_id
  };
}
export function decodeDocumentGroupLoadReviewCompletionRequest(json) {
  return {
    documentGroupLoadReviewId: assertNumber(json.documentGroupLoadReviewId),
    assignedLoadStateId: optionalDecoder(assertNumber)(
      json.assignedLoadStateId
    ),
    assignedCarrierId: optionalDecoder(assertNumber)(json.assignedCarrierId)
  };
}
export function encodeDocumentGroupLoadReviewCompletionRequest(value) {
  return {
    documentGroupLoadReviewId: value.documentGroupLoadReviewId,
    assignedLoadStateId: value.assignedLoadStateId,
    assignedCarrierId: value.assignedCarrierId
  };
}
export function decodeDocumentGroupLoadReviewId(json) {
  return {
    documentGroupLoadReviewId: optionalDecoder(assertNumber)(
      json.documentGroupLoadReviewId
    )
  };
}
export function encodeDocumentGroupLoadReviewId(value) {
  return {
    documentGroupLoadReviewId: value.documentGroupLoadReviewId
  };
}
export function decodeDocumentGroupLoadReviewsCreateReviewsOutput(json) {
  return {
    reviews_created: assertBoolean(json.reviews_created)
  };
}
export function encodeDocumentGroupLoadReviewsCreateReviewsOutput(value) {
  return {
    reviews_created: value.reviews_created
  };
}
export function decodeDocumentGroupLoadReviewsInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeDocumentGroupLoadReviewsInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id
  };
}
export function decodeDocumentGroupLoadReviewsReviewsResolved(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeDocumentGroupLoadReviewsReviewsResolved(value) {
  return {
    document_batch_id: value.document_batch_id
  };
}
export function decodeDocumentGroupLoadReviewsSignal(json) {
  return {
    signal: decodeDocumentGroupLoadReviewsSignal_Signal(json)
  };
}
export function encodeDocumentGroupLoadReviewsSignal(value) {
  return __spreadValues({}, encodeDocumentGroupLoadReviewsSignal_Signal(value.signal));
}
export var DocumentGroupLoadReviewsSignal_SignalType = /* @__PURE__ */ ((DocumentGroupLoadReviewsSignal_SignalType2) => {
  DocumentGroupLoadReviewsSignal_SignalType2["ReviewsResolved"] = "reviews_resolved";
  return DocumentGroupLoadReviewsSignal_SignalType2;
})(DocumentGroupLoadReviewsSignal_SignalType || {});
export function decodeDocumentGroupLoadReviewsSignal_Signal(json) {
  if (json.reviews_resolved !== void 0) {
    return {
      type: "reviews_resolved" /* ReviewsResolved */,
      reviews_resolved: decodeDocumentGroupLoadReviewsReviewsResolved(
        json.reviews_resolved
      )
    };
  }
  throw new Error(
    "No field set in oneof DocumentGroupLoadReviewsSignal_Signal"
  );
}
export function encodeDocumentGroupLoadReviewsSignal_Signal(value) {
  switch (value.type) {
    case "reviews_resolved" /* ReviewsResolved */:
      return {
        reviews_resolved: encodeDocumentGroupLoadReviewsReviewsResolved(
          value.reviews_resolved
        )
      };
    default:
      throw new Error(
        "No field set in oneof DocumentGroupLoadReviewsSignal_Signal"
      );
  }
}
export var DocumentGroupingError = /* @__PURE__ */ ((DocumentGroupingError2) => {
  DocumentGroupingError2["DocumentGroupingErrorBackToBackInvoices"] = "back_to_back_invoices";
  DocumentGroupingError2["DocumentGroupingErrorDoesNotStartWithAllowedDocumentType"] = "does_not_start_with_allowed_document_type";
  DocumentGroupingError2["DocumentGroupingErrorGroupByAttachmentClassifierDoesNotMeetAccuracyThreshold"] = "group_by_attachment_classifier_does_not_meet_accuracy_threshold";
  DocumentGroupingError2["DocumentGroupingErrorGroupByAttachmentClassifierDoesNotMeetTotalThreshold"] = "group_by_attachment_classifier_does_not_meet_total_threshold";
  DocumentGroupingError2["DocumentGroupingErrorGroupByAttachmentClassifierNotConsidered"] = "group_by_attachment_classifier_not_considered";
  return DocumentGroupingError2;
})(DocumentGroupingError || {});
export const decodeDocumentGroupingError = (value) => value;
export const encodeDocumentGroupingError = (value) => value;
export function decodeDocumentGroupingServiceGroupByAttachmentsError(json) {
  return {
    message: assertString(json.message)
  };
}
export function encodeDocumentGroupingServiceGroupByAttachmentsError(value) {
  return {
    message: value.message
  };
}
export function decodeDocumentGroupingServiceGroupByAttachmentsRequest(json) {
  return {
    documentBatchId: assertNumber(json.documentBatchId)
  };
}
export function encodeDocumentGroupingServiceGroupByAttachmentsRequest(value) {
  return {
    documentBatchId: value.documentBatchId
  };
}
export function decodeDocumentGroupingServiceGroupByAttachmentsResponse(json) {
  return {
    outcome: decodeDocumentGroupingServiceGroupByAttachmentsResponse_Outcome(json)
  };
}
export function encodeDocumentGroupingServiceGroupByAttachmentsResponse(value) {
  return __spreadValues({}, encodeDocumentGroupingServiceGroupByAttachmentsResponse_Outcome(
    value.outcome
  ));
}
export var DocumentGroupingServiceGroupByAttachmentsResponse_OutcomeType = /* @__PURE__ */ ((DocumentGroupingServiceGroupByAttachmentsResponse_OutcomeType2) => {
  DocumentGroupingServiceGroupByAttachmentsResponse_OutcomeType2["Success"] = "success";
  DocumentGroupingServiceGroupByAttachmentsResponse_OutcomeType2["Error"] = "error";
  return DocumentGroupingServiceGroupByAttachmentsResponse_OutcomeType2;
})(DocumentGroupingServiceGroupByAttachmentsResponse_OutcomeType || {});
export function decodeDocumentGroupingServiceGroupByAttachmentsResponse_Outcome(json) {
  if (json.success !== void 0) {
    return {
      type: "success" /* Success */,
      success: decodeDocumentGroupingServiceGroupByAttachmentsSuccess(
        json.success
      )
    };
  } else if (json.error !== void 0) {
    return {
      type: "error" /* Error */,
      error: decodeDocumentGroupingServiceGroupByAttachmentsError(json.error)
    };
  }
  throw new Error(
    "No field set in oneof DocumentGroupingServiceGroupByAttachmentsResponse_Outcome"
  );
}
export function encodeDocumentGroupingServiceGroupByAttachmentsResponse_Outcome(value) {
  switch (value.type) {
    case "success" /* Success */:
      return {
        success: encodeDocumentGroupingServiceGroupByAttachmentsSuccess(
          value.success
        )
      };
    case "error" /* Error */:
      return {
        error: encodeDocumentGroupingServiceGroupByAttachmentsError(
          value.error
        )
      };
    default:
      throw new Error(
        "No field set in oneof DocumentGroupingServiceGroupByAttachmentsResponse_Outcome"
      );
  }
}
export function decodeDocumentGroupingServiceGroupByAttachmentsSuccess(json) {
  return {
    groupCount: assertNumber(json.groupCount),
    redirectToDashboard: assertBoolean(json.redirectToDashboard)
  };
}
export function encodeDocumentGroupingServiceGroupByAttachmentsSuccess(value) {
  return {
    groupCount: value.groupCount,
    redirectToDashboard: value.redirectToDashboard
  };
}
export function decodeDocumentLoadReferenceMatchReview(json) {
  return {
    id: assertNumber(json.id),
    document_id: assertNumber(json.document_id),
    tms_load_id: assertNumber(json.tms_load_id),
    document_references_match_load: optionalDecoder(assertBoolean)(
      json.document_references_match_load
    )
  };
}
export function encodeDocumentLoadReferenceMatchReview(value) {
  return {
    id: value.id,
    document_id: value.document_id,
    tms_load_id: value.tms_load_id,
    document_references_match_load: value.document_references_match_load
  };
}
export function decodeDocumentLoadReferenceMatchReviewCompletionRequest(json) {
  return {
    documentLoadReferenceMatchReviewId: assertNumber(
      json.documentLoadReferenceMatchReviewId
    ),
    documentReferencesMatchLoad: assertBoolean(
      json.documentReferencesMatchLoad
    ),
    matchedValues: (json.matchedValues || []).map(assertString)
  };
}
export function encodeDocumentLoadReferenceMatchReviewCompletionRequest(value) {
  return {
    documentLoadReferenceMatchReviewId: value.documentLoadReferenceMatchReviewId,
    documentReferencesMatchLoad: value.documentReferencesMatchLoad,
    matchedValues: value.matchedValues
  };
}
export function decodeDocumentLoadReferenceMatchReviewId(json) {
  return {
    documentLoadReferenceMatchReviewId: optionalDecoder(assertNumber)(
      json.documentLoadReferenceMatchReviewId
    )
  };
}
export function encodeDocumentLoadReferenceMatchReviewId(value) {
  return {
    documentLoadReferenceMatchReviewId: value.documentLoadReferenceMatchReviewId
  };
}
export function decodeDocumentLoadRelationship(json) {
  return {
    document_id: assertNumber(json.document_id),
    load_state_id: assertNumber(json.load_state_id),
    pod_disposition: decodeDocumentPODDisposition(json.pod_disposition),
    signature_disposition: decodeDocumentSignatureDisposition(
      json.signature_disposition
    ),
    reference_match_disposition: decodeDocumentReferenceMatchDisposition(
      json.reference_match_disposition
    ),
    matched_reference_values: (json.matched_reference_values || []).map(
      assertString
    )
  };
}
export function encodeDocumentLoadRelationship(value) {
  return {
    document_id: value.document_id,
    load_state_id: value.load_state_id,
    pod_disposition: encodeDocumentPODDisposition(value.pod_disposition),
    signature_disposition: encodeDocumentSignatureDisposition(
      value.signature_disposition
    ),
    reference_match_disposition: encodeDocumentReferenceMatchDisposition(
      value.reference_match_disposition
    ),
    matched_reference_values: value.matched_reference_values
  };
}
export function decodeDocumentLoadRelationshipRequest(json) {
  return {
    document_ids: (json.document_ids || []).map(assertNumber),
    load_state_ids: (json.load_state_ids || []).map(assertNumber)
  };
}
export function encodeDocumentLoadRelationshipRequest(value) {
  return {
    document_ids: value.document_ids,
    load_state_ids: value.load_state_ids
  };
}
export function decodeDocumentLoadRelationshipResponse(json) {
  return {
    relationships: (json.relationships || []).map(
      decodeDocumentLoadRelationship
    )
  };
}
export function encodeDocumentLoadRelationshipResponse(value) {
  return {
    relationships: value.relationships.map(encodeDocumentLoadRelationship)
  };
}
export function decodeDocumentLocation(json) {
  return {
    documentPageId: assertNumber(json.documentPageId),
    top: assertNumber(json.top),
    left: assertNumber(json.left),
    height: assertNumber(json.height),
    width: assertNumber(json.width)
  };
}
export function encodeDocumentLocation(value) {
  return {
    documentPageId: value.documentPageId,
    top: value.top,
    left: value.left,
    height: value.height,
    width: value.width
  };
}
export function decodeDocumentPacketComparisonRow(json) {
  return {
    document_id: optionalDecoder(assertNumber)(json.document_id),
    other_document_id: optionalDecoder(assertNumber)(json.other_document_id),
    page_similarities: (json.page_similarities || []).map(
      decodeDocumentPacketPageSimilarity
    ),
    pages_scored: assertBoolean(json.pages_scored)
  };
}
export function encodeDocumentPacketComparisonRow(value) {
  return {
    document_id: value.document_id,
    other_document_id: value.other_document_id,
    page_similarities: value.page_similarities.map(
      encodeDocumentPacketPageSimilarity
    ),
    pages_scored: value.pages_scored
  };
}
export function decodeDocumentPacketPageSimilarity(json) {
  return {
    document_page_id: assertNumber(json.document_page_id),
    other_document_page_id: assertNumber(json.other_document_page_id),
    identical: assertBoolean(json.identical),
    word_overlap: assertNumber(json.word_overlap)
  };
}
export function encodeDocumentPacketPageSimilarity(value) {
  return {
    document_page_id: value.document_page_id,
    other_document_page_id: value.other_document_page_id,
    identical: value.identical,
    word_overlap: value.word_overlap
  };
}
export function decodeDocumentPage(json) {
  return {
    id: assertNumber(json.id),
    document_id: assertNumber(json.document_id),
    thumbnail_url: optionalDecoder(assertString)(json.thumbnail_url),
    full_url: optionalDecoder(assertString)(json.full_url),
    preview_url: optionalDecoder(assertString)(json.preview_url),
    rotation: assertNumber(json.rotation),
    document_batch_id: optionalDecoder(assertNumber)(json.document_batch_id),
    document_group_id: optionalDecoder(assertNumber)(json.document_group_id),
    email_message_subject: optionalDecoder(assertString)(
      json.email_message_subject
    ),
    illegible: assertBoolean(json.illegible),
    raw_full_url: optionalDecoder(assertString)(json.raw_full_url)
  };
}
export function encodeDocumentPage(value) {
  return {
    id: value.id,
    document_id: value.document_id,
    thumbnail_url: value.thumbnail_url,
    full_url: value.full_url,
    preview_url: value.preview_url,
    rotation: value.rotation,
    document_batch_id: value.document_batch_id,
    document_group_id: value.document_group_id,
    email_message_subject: value.email_message_subject,
    illegible: value.illegible,
    raw_full_url: value.raw_full_url
  };
}
export function decodeDocumentPODAttemptedReference(json) {
  return {
    label: assertString(json.label),
    matched_value: optionalDecoder(assertString)(json.matched_value),
    load_value: optionalDecoder(assertString)(json.load_value)
  };
}
export function encodeDocumentPODAttemptedReference(value) {
  return {
    label: value.label,
    matched_value: value.matched_value,
    load_value: value.load_value
  };
}
export function decodeDocumentPODDefinition(json) {
  return {
    disposition: decodeDocumentPODDisposition(json.disposition),
    is_displayed: assertBoolean(json.is_displayed),
    pod_inference_checks: (json.pod_inference_checks || []).map(
      decodeDocumentPODDefinitionInferenceCheck
    ),
    document_id: assertNumber(json.document_id),
    attempted_references: (json.attempted_references || []).map(
      decodeDocumentPODAttemptedReference
    ),
    podOverrideCheck: decodeDocumentPODDefinition_PODOverrideCheck(json)
  };
}
export function encodeDocumentPODDefinition(value) {
  return __spreadValues({
    disposition: encodeDocumentPODDisposition(value.disposition),
    is_displayed: value.is_displayed,
    pod_inference_checks: value.pod_inference_checks.map(
      encodeDocumentPODDefinitionInferenceCheck
    ),
    document_id: value.document_id,
    attempted_references: value.attempted_references.map(
      encodeDocumentPODAttemptedReference
    )
  }, encodeDocumentPODDefinition_PODOverrideCheck(value.podOverrideCheck));
}
export var DocumentPODDefinition_PODOverrideCheckType = /* @__PURE__ */ ((DocumentPODDefinition_PODOverrideCheckType2) => {
  DocumentPODDefinition_PODOverrideCheckType2["NoOverride"] = "no_override";
  DocumentPODDefinition_PODOverrideCheckType2["ManualOverride"] = "manual_override";
  DocumentPODDefinition_PODOverrideCheckType2["AutomatedOverride"] = "automated_override";
  return DocumentPODDefinition_PODOverrideCheckType2;
})(DocumentPODDefinition_PODOverrideCheckType || {});
export function decodeDocumentPODDefinition_PODOverrideCheck(json) {
  if (json.no_override !== void 0) {
    return {
      type: "no_override" /* NoOverride */,
      no_override: decodeDocumentPODDefinition_NoOverrideCheck(
        json.no_override
      )
    };
  } else if (json.manual_override !== void 0) {
    return {
      type: "manual_override" /* ManualOverride */,
      manual_override: decodeDocumentPODDefinition_ManualOverrideCheck(
        json.manual_override
      )
    };
  } else if (json.automated_override !== void 0) {
    return {
      type: "automated_override" /* AutomatedOverride */,
      automated_override: decodeDocumentPODDefinition_AutomatedOverrideCheck(
        json.automated_override
      )
    };
  }
  throw new Error(
    "No field set in oneof DocumentPODDefinition_PODOverrideCheck"
  );
}
export function encodeDocumentPODDefinition_PODOverrideCheck(value) {
  switch (value.type) {
    case "no_override" /* NoOverride */:
      return {
        no_override: encodeDocumentPODDefinition_NoOverrideCheck(
          value.no_override
        )
      };
    case "manual_override" /* ManualOverride */:
      return {
        manual_override: encodeDocumentPODDefinition_ManualOverrideCheck(
          value.manual_override
        )
      };
    case "automated_override" /* AutomatedOverride */:
      return {
        automated_override: encodeDocumentPODDefinition_AutomatedOverrideCheck(
          value.automated_override
        )
      };
    default:
      throw new Error(
        "No field set in oneof DocumentPODDefinition_PODOverrideCheck"
      );
  }
}
export function decodeDocumentPODDefinition_AutomatedOverrideCheck(json) {
  return {};
}
export function encodeDocumentPODDefinition_AutomatedOverrideCheck(value) {
  return {};
}
export function decodeDocumentPODDefinition_ManualOverrideCheck(json) {
  return {};
}
export function encodeDocumentPODDefinition_ManualOverrideCheck(value) {
  return {};
}
export function decodeDocumentPODDefinition_NoOverrideCheck(json) {
  return {};
}
export function encodeDocumentPODDefinition_NoOverrideCheck(value) {
  return {};
}
export function decodeDocumentPODDefinitionInferenceCheck(json) {
  return {
    check_type: decodeDocumentPODInferenceCheckType(json.check_type),
    passed: assertBoolean(json.passed),
    applied: assertBoolean(json.applied)
  };
}
export function encodeDocumentPODDefinitionInferenceCheck(value) {
  return {
    check_type: encodeDocumentPODInferenceCheckType(value.check_type),
    passed: value.passed,
    applied: value.applied
  };
}
export function decodeDocumentPODDefinitionOverrideInteraction(json) {
  return {
    pod_disposition: decodeDocumentPODDisposition(json.pod_disposition),
    overrides: (json.overrides || []).map(
      decodeDocumentPODDefinitionInferenceCheck
    ),
    document_id: assertNumber(json.document_id),
    user_id: assertNumber(json.user_id),
    other_reason: optionalDecoder(assertString)(json.other_reason),
    reference_overrides: (json.reference_overrides || []).map(
      decodeDocumentPODAttemptedReference
    )
  };
}
export function encodeDocumentPODDefinitionOverrideInteraction(value) {
  return {
    pod_disposition: encodeDocumentPODDisposition(value.pod_disposition),
    overrides: value.overrides.map(encodeDocumentPODDefinitionInferenceCheck),
    document_id: value.document_id,
    user_id: value.user_id,
    other_reason: value.other_reason,
    reference_overrides: value.reference_overrides.map(
      encodeDocumentPODAttemptedReference
    )
  };
}
export function decodeDocumentPODDefinitionOverrideInteractionSubmitRequest(json) {
  return {
    carrier_invoice_id: assertNumber(json.carrier_invoice_id),
    override: decodeDocumentPODDefinitionOverrideInteraction(json.override)
  };
}
export function encodeDocumentPODDefinitionOverrideInteractionSubmitRequest(value) {
  return {
    carrier_invoice_id: value.carrier_invoice_id,
    override: encodeDocumentPODDefinitionOverrideInteraction(value.override)
  };
}
export var DocumentPODDisposition = /* @__PURE__ */ ((DocumentPODDisposition2) => {
  DocumentPODDisposition2["DocumentPODDispositionUnknown"] = "unknown";
  DocumentPODDisposition2["DocumentPODDispositionKnownPOD"] = "known_pod";
  DocumentPODDisposition2["DocumentPODDispositionKnownNotPOD"] = "known_not_pod";
  return DocumentPODDisposition2;
})(DocumentPODDisposition || {});
export const decodeDocumentPODDisposition = (value) => value;
export const encodeDocumentPODDisposition = (value) => value;
export var DocumentPODInferenceCheckType = /* @__PURE__ */ ((DocumentPODInferenceCheckType2) => {
  DocumentPODInferenceCheckType2["DocumentPODInferenceCheckTypeCheckSignature"] = "check_signature";
  DocumentPODInferenceCheckType2["DocumentPODInferenceCheckTypeReferenceMatch"] = "check_for_reference_match";
  DocumentPODInferenceCheckType2["DocumentPODInferenceCheckTypeNoDamages"] = "no_damages";
  DocumentPODInferenceCheckType2["DocumentPODInferenceCheckTypeAllPagesPresent"] = "all_pages_present";
  DocumentPODInferenceCheckType2["DocumentPODInferenceCheckTypeHasReceiverDate"] = "has_receiver_date";
  DocumentPODInferenceCheckType2["DocumentPODInferenceCheckTypeNoCarrierRate"] = "no_carrier_rate";
  DocumentPODInferenceCheckType2["DocumentPODInferenceCheckTypeManualReferenceMatch"] = "manual_reference_match";
  return DocumentPODInferenceCheckType2;
})(DocumentPODInferenceCheckType || {});
export const decodeDocumentPODInferenceCheckType = (value) => value;
export const encodeDocumentPODInferenceCheckType = (value) => value;
export function decodeDocumentPODUpdate(json) {
  return {
    documentId: assertNumber(json.documentId),
    isPOD: assertBoolean(json.isPOD)
  };
}
export function encodeDocumentPODUpdate(value) {
  return {
    documentId: value.documentId,
    isPOD: value.isPOD
  };
}
export function decodeDocumentPresenceCheck(json) {
  return {
    requirement: decodeDocumentPresenceRequirement(json.requirement),
    requiredDocumentTypes: (json.requiredDocumentTypes || []).map(assertString)
  };
}
export function encodeDocumentPresenceCheck(value) {
  return {
    requirement: encodeDocumentPresenceRequirement(value.requirement),
    requiredDocumentTypes: value.requiredDocumentTypes
  };
}
export var DocumentPresenceRequirement = /* @__PURE__ */ ((DocumentPresenceRequirement2) => {
  DocumentPresenceRequirement2["DocumentPresenceRequirementRateConfirmationRequired"] = "rate_confirmation_required";
  DocumentPresenceRequirement2["DocumentPresenceRequirementBillOfLadingRequired"] = "bill_of_lading_required";
  DocumentPresenceRequirement2["DocumentPresenceRequirementPODRequired"] = "pod_required";
  DocumentPresenceRequirement2["DocumentPresenceRequirementDebtorVisibleDocumentRequired"] = "debtor_visible_document_required";
  DocumentPresenceRequirement2["DocumentPresenceRequirementClientInvoiceRequired"] = "client_invoice_required";
  DocumentPresenceRequirement2["DocumentPresenceRequirementRequireDoctypes"] = "require_doctypes";
  DocumentPresenceRequirement2["DocumentPresenceRequirementDocumentsRequired"] = "documents_required";
  DocumentPresenceRequirement2["DocumentPresenceRequirementBrokerRequiredDocuments"] = "broker_required_documents";
  DocumentPresenceRequirement2["DocumentPresenceRequirementCarrierInvoiceRequired"] = "carrier_invoice_required";
  DocumentPresenceRequirement2["DocumentPresenceRequirementCarrierRateConfirmationRequired"] = "carrier_rate_confirmation_required";
  return DocumentPresenceRequirement2;
})(DocumentPresenceRequirement || {});
export const decodeDocumentPresenceRequirement = (value) => value;
export const encodeDocumentPresenceRequirement = (value) => value;
export var DocumentReferenceMatchDisposition = /* @__PURE__ */ ((DocumentReferenceMatchDisposition2) => {
  DocumentReferenceMatchDisposition2["DocumentReferenceMatchDispositionUnknown"] = "unknown";
  DocumentReferenceMatchDisposition2["DocumentReferenceMatchDispositionKnownMatch"] = "known_match";
  DocumentReferenceMatchDisposition2["DocumentReferenceMatchDispositionKnownNoMatch"] = "known_no_match";
  return DocumentReferenceMatchDisposition2;
})(DocumentReferenceMatchDisposition || {});
export const decodeDocumentReferenceMatchDisposition = (value) => value;
export const encodeDocumentReferenceMatchDisposition = (value) => value;
export function decodeDocumentReviewCheck(json) {
  return {};
}
export function encodeDocumentReviewCheck(value) {
  return {};
}
export function decodeDocumentReviewCompletionRequest(json) {
  return {
    completedReview: decodeDocumentReviewCompletionRequest_CompletedReview(json)
  };
}
export function encodeDocumentReviewCompletionRequest(value) {
  return __spreadValues({}, encodeDocumentReviewCompletionRequest_CompletedReview(
    value.completedReview
  ));
}
export var DocumentReviewCompletionRequest_CompletedReviewType = /* @__PURE__ */ ((DocumentReviewCompletionRequest_CompletedReviewType2) => {
  DocumentReviewCompletionRequest_CompletedReviewType2["DocumentTypeReview"] = "documentTypeReview";
  DocumentReviewCompletionRequest_CompletedReviewType2["InvoiceNumberReview"] = "invoiceNumberReview";
  DocumentReviewCompletionRequest_CompletedReviewType2["InvoiceAmountReview"] = "invoiceAmountReview";
  DocumentReviewCompletionRequest_CompletedReviewType2["MultiPageBolReview"] = "multiPageBolReview";
  DocumentReviewCompletionRequest_CompletedReviewType2["DocumentLoadReferenceMatchReview"] = "documentLoadReferenceMatchReview";
  DocumentReviewCompletionRequest_CompletedReviewType2["DocumentBatchGroupingReview"] = "documentBatchGroupingReview";
  DocumentReviewCompletionRequest_CompletedReviewType2["DocumentGroupLoadReview"] = "documentGroupLoadReview";
  return DocumentReviewCompletionRequest_CompletedReviewType2;
})(DocumentReviewCompletionRequest_CompletedReviewType || {});
export function decodeDocumentReviewCompletionRequest_CompletedReview(json) {
  if (json.documentTypeReview !== void 0) {
    return {
      type: "documentTypeReview" /* DocumentTypeReview */,
      documentTypeReview: decodeDocumentTypeReviewCompletionRequest(
        json.documentTypeReview
      )
    };
  } else if (json.invoiceNumberReview !== void 0) {
    return {
      type: "invoiceNumberReview" /* InvoiceNumberReview */,
      invoiceNumberReview: decodeInvoiceNumberReviewCompletionRequest(
        json.invoiceNumberReview
      )
    };
  } else if (json.invoiceAmountReview !== void 0) {
    return {
      type: "invoiceAmountReview" /* InvoiceAmountReview */,
      invoiceAmountReview: decodeInvoiceAmountReviewCompletionRequest(
        json.invoiceAmountReview
      )
    };
  } else if (json.multiPageBolReview !== void 0) {
    return {
      type: "multiPageBolReview" /* MultiPageBolReview */,
      multiPageBolReview: decodeMultiPageBolReviewCompletionRequest(
        json.multiPageBolReview
      )
    };
  } else if (json.documentLoadReferenceMatchReview !== void 0) {
    return {
      type: "documentLoadReferenceMatchReview" /* DocumentLoadReferenceMatchReview */,
      documentLoadReferenceMatchReview: decodeDocumentLoadReferenceMatchReviewCompletionRequest(
        json.documentLoadReferenceMatchReview
      )
    };
  } else if (json.documentBatchGroupingReview !== void 0) {
    return {
      type: "documentBatchGroupingReview" /* DocumentBatchGroupingReview */,
      documentBatchGroupingReview: decodeDocumentBatchGroupingReviewCompletionRequest(
        json.documentBatchGroupingReview
      )
    };
  } else if (json.documentGroupLoadReview !== void 0) {
    return {
      type: "documentGroupLoadReview" /* DocumentGroupLoadReview */,
      documentGroupLoadReview: decodeDocumentGroupLoadReviewCompletionRequest(
        json.documentGroupLoadReview
      )
    };
  }
  throw new Error(
    "No field set in oneof DocumentReviewCompletionRequest_CompletedReview"
  );
}
export function encodeDocumentReviewCompletionRequest_CompletedReview(value) {
  switch (value.type) {
    case "documentTypeReview" /* DocumentTypeReview */:
      return {
        documentTypeReview: encodeDocumentTypeReviewCompletionRequest(
          value.documentTypeReview
        )
      };
    case "invoiceNumberReview" /* InvoiceNumberReview */:
      return {
        invoiceNumberReview: encodeInvoiceNumberReviewCompletionRequest(
          value.invoiceNumberReview
        )
      };
    case "invoiceAmountReview" /* InvoiceAmountReview */:
      return {
        invoiceAmountReview: encodeInvoiceAmountReviewCompletionRequest(
          value.invoiceAmountReview
        )
      };
    case "multiPageBolReview" /* MultiPageBolReview */:
      return {
        multiPageBolReview: encodeMultiPageBolReviewCompletionRequest(
          value.multiPageBolReview
        )
      };
    case "documentLoadReferenceMatchReview" /* DocumentLoadReferenceMatchReview */:
      return {
        documentLoadReferenceMatchReview: encodeDocumentLoadReferenceMatchReviewCompletionRequest(
          value.documentLoadReferenceMatchReview
        )
      };
    case "documentBatchGroupingReview" /* DocumentBatchGroupingReview */:
      return {
        documentBatchGroupingReview: encodeDocumentBatchGroupingReviewCompletionRequest(
          value.documentBatchGroupingReview
        )
      };
    case "documentGroupLoadReview" /* DocumentGroupLoadReview */:
      return {
        documentGroupLoadReview: encodeDocumentGroupLoadReviewCompletionRequest(
          value.documentGroupLoadReview
        )
      };
    default:
      throw new Error(
        "No field set in oneof DocumentReviewCompletionRequest_CompletedReview"
      );
  }
}
export function decodeDocumentReviewNone(json) {
  return {};
}
export function encodeDocumentReviewNone(value) {
  return {};
}
export function decodeDocumentReviewStartNextResponse(json) {
  return {
    nextReview: decodeDocumentReviewStartNextResponse_NextReview(json)
  };
}
export function encodeDocumentReviewStartNextResponse(value) {
  return __spreadValues({}, encodeDocumentReviewStartNextResponse_NextReview(value.nextReview));
}
export var DocumentReviewStartNextResponse_NextReviewType = /* @__PURE__ */ ((DocumentReviewStartNextResponse_NextReviewType2) => {
  DocumentReviewStartNextResponse_NextReviewType2["DocumentTypeReview"] = "documentTypeReview";
  DocumentReviewStartNextResponse_NextReviewType2["InvoiceNumberReview"] = "invoiceNumberReview";
  DocumentReviewStartNextResponse_NextReviewType2["InvoiceAmountReview"] = "invoiceAmountReview";
  DocumentReviewStartNextResponse_NextReviewType2["MultiPageBolReview"] = "multiPageBolReview";
  DocumentReviewStartNextResponse_NextReviewType2["NoReview"] = "noReview";
  DocumentReviewStartNextResponse_NextReviewType2["DocumentLoadReferenceMatchReview"] = "documentLoadReferenceMatchReview";
  DocumentReviewStartNextResponse_NextReviewType2["DocumentBatchGroupingReview"] = "documentBatchGroupingReview";
  DocumentReviewStartNextResponse_NextReviewType2["DocumentGroupLoadReview"] = "documentGroupLoadReview";
  return DocumentReviewStartNextResponse_NextReviewType2;
})(DocumentReviewStartNextResponse_NextReviewType || {});
export function decodeDocumentReviewStartNextResponse_NextReview(json) {
  if (json.documentTypeReview !== void 0) {
    return {
      type: "documentTypeReview" /* DocumentTypeReview */,
      documentTypeReview: decodeDocumentTypeReviewId(json.documentTypeReview)
    };
  } else if (json.invoiceNumberReview !== void 0) {
    return {
      type: "invoiceNumberReview" /* InvoiceNumberReview */,
      invoiceNumberReview: decodeInvoiceNumberReviewId(
        json.invoiceNumberReview
      )
    };
  } else if (json.invoiceAmountReview !== void 0) {
    return {
      type: "invoiceAmountReview" /* InvoiceAmountReview */,
      invoiceAmountReview: decodeInvoiceAmountReviewId(
        json.invoiceAmountReview
      )
    };
  } else if (json.multiPageBolReview !== void 0) {
    return {
      type: "multiPageBolReview" /* MultiPageBolReview */,
      multiPageBolReview: decodeMultiPageBolReviewId(json.multiPageBolReview)
    };
  } else if (json.noReview !== void 0) {
    return {
      type: "noReview" /* NoReview */,
      noReview: decodeDocumentReviewNone(json.noReview)
    };
  } else if (json.documentLoadReferenceMatchReview !== void 0) {
    return {
      type: "documentLoadReferenceMatchReview" /* DocumentLoadReferenceMatchReview */,
      documentLoadReferenceMatchReview: decodeDocumentLoadReferenceMatchReviewId(
        json.documentLoadReferenceMatchReview
      )
    };
  } else if (json.documentBatchGroupingReview !== void 0) {
    return {
      type: "documentBatchGroupingReview" /* DocumentBatchGroupingReview */,
      documentBatchGroupingReview: decodeDocumentBatchGroupingReviewId(
        json.documentBatchGroupingReview
      )
    };
  } else if (json.documentGroupLoadReview !== void 0) {
    return {
      type: "documentGroupLoadReview" /* DocumentGroupLoadReview */,
      documentGroupLoadReview: decodeDocumentGroupLoadReviewId(
        json.documentGroupLoadReview
      )
    };
  }
  throw new Error(
    "No field set in oneof DocumentReviewStartNextResponse_NextReview"
  );
}
export function encodeDocumentReviewStartNextResponse_NextReview(value) {
  switch (value.type) {
    case "documentTypeReview" /* DocumentTypeReview */:
      return {
        documentTypeReview: encodeDocumentTypeReviewId(
          value.documentTypeReview
        )
      };
    case "invoiceNumberReview" /* InvoiceNumberReview */:
      return {
        invoiceNumberReview: encodeInvoiceNumberReviewId(
          value.invoiceNumberReview
        )
      };
    case "invoiceAmountReview" /* InvoiceAmountReview */:
      return {
        invoiceAmountReview: encodeInvoiceAmountReviewId(
          value.invoiceAmountReview
        )
      };
    case "multiPageBolReview" /* MultiPageBolReview */:
      return {
        multiPageBolReview: encodeMultiPageBolReviewId(
          value.multiPageBolReview
        )
      };
    case "noReview" /* NoReview */:
      return { noReview: encodeDocumentReviewNone(value.noReview) };
    case "documentLoadReferenceMatchReview" /* DocumentLoadReferenceMatchReview */:
      return {
        documentLoadReferenceMatchReview: encodeDocumentLoadReferenceMatchReviewId(
          value.documentLoadReferenceMatchReview
        )
      };
    case "documentBatchGroupingReview" /* DocumentBatchGroupingReview */:
      return {
        documentBatchGroupingReview: encodeDocumentBatchGroupingReviewId(
          value.documentBatchGroupingReview
        )
      };
    case "documentGroupLoadReview" /* DocumentGroupLoadReview */:
      return {
        documentGroupLoadReview: encodeDocumentGroupLoadReviewId(
          value.documentGroupLoadReview
        )
      };
    default:
      throw new Error(
        "No field set in oneof DocumentReviewStartNextResponse_NextReview"
      );
  }
}
export var DocumentSignatureDisposition = /* @__PURE__ */ ((DocumentSignatureDisposition2) => {
  DocumentSignatureDisposition2["DocumentSignatureDispositionUnknown"] = "unknown";
  DocumentSignatureDisposition2["DocumentSignatureDispositionKnownSigned"] = "known_signed";
  DocumentSignatureDisposition2["DocumentSignatureDispositionKnownNotSigned"] = "known_not_signed";
  return DocumentSignatureDisposition2;
})(DocumentSignatureDisposition || {});
export const decodeDocumentSignatureDisposition = (value) => value;
export const encodeDocumentSignatureDisposition = (value) => value;
export var DocumentType = /* @__PURE__ */ ((DocumentType2) => {
  DocumentType2["DocumentTypeInvoice"] = "invoice";
  DocumentType2["DocumentTypeFactorInvoice"] = "factorInvoice";
  DocumentType2["DocumentTypeCustomerInvoice"] = "customerInvoice";
  DocumentType2["DocumentTypeCarrierInvoice"] = "carrierInvoice";
  DocumentType2["DocumentTypeCarrierRateConfirmation"] = "carrierRateConfirmation";
  DocumentType2["DocumentTypeCustomerTender"] = "customerTender";
  DocumentType2["DocumentTypeRateConfirmation"] = "rateConfirmation";
  DocumentType2["DocumentTypeProofOfDelivery"] = "proofOfDelivery";
  DocumentType2["DocumentTypeBillOfLading"] = "billOfLading";
  DocumentType2["DocumentTypeHiddenBillOfLading"] = "hiddenBillOfLading";
  DocumentType2["DocumentTypeWeightTicket"] = "weightTicket";
  DocumentType2["DocumentTypeNoticeOfAssignment"] = "noticeOfAssignment";
  DocumentType2["DocumentTypeLaborReceipt"] = "laborReceipt";
  DocumentType2["DocumentTypeLegacyFactoringAssignment"] = "factoringAssignment";
  DocumentType2["DocumentTypeLegacyLoadTender"] = "loadTender";
  DocumentType2["DocumentTypeNoticeOfRelease"] = "noticeOfRelease";
  DocumentType2["DocumentTypeCombinedNoaLor"] = "noticeOfAssignmentAndRelease";
  DocumentType2["DocumentTypePackingSlip"] = "packingSlip";
  DocumentType2["DocumentTypeNmfcCertificate"] = "nmfcCertificate";
  DocumentType2["DocumentTypeCustomerDocumentsOther"] = "customer_other";
  DocumentType2["DocumentTypeInternalDocumentsOther"] = "other";
  DocumentType2["DocumentTypePostal"] = "postal";
  DocumentType2["DocumentTypeVoidedCheck"] = "voidedCheck";
  DocumentType2["DocumentTypeLetterOfAssignment"] = "letterOfAssignment";
  DocumentType2["DocumentTypeDeliveryOrder"] = "deliveryOrder";
  DocumentType2["DocumentTypeTir"] = "tir";
  DocumentType2["DocumentTypeFundingForm"] = "fundingForm";
  DocumentType2["DocumentTypeAuditSheet"] = "auditSheet";
  DocumentType2["DocumentTypeAdvanceRequest"] = "advanceRequest";
  DocumentType2["DocumentTypeDriverForm"] = "driverForm";
  DocumentType2["DocumentTypeEscortReceipt"] = "escortReceipt";
  DocumentType2["DocumentTypeFerryReceipt"] = "ferryReceipt";
  DocumentType2["DocumentTypeRunTicket"] = "runTicket";
  DocumentType2["DocumentTypeSteamingReceipt"] = "steamingReceipt";
  DocumentType2["DocumentTypeTollReceipt"] = "tollReceipt";
  DocumentType2["DocumentTypeWashReceipt"] = "washReceipt";
  DocumentType2["DocumentTypeExitPass"] = "exitPass";
  DocumentType2["DocumentTypeInsuranceCertificate"] = "insuranceCertificate";
  DocumentType2["DocumentTypeContract"] = "contract";
  DocumentType2["DocumentTypeDetentionApproval"] = "detentionApproval";
  DocumentType2["DocumentTypeEmailApproval"] = "emailApproval";
  DocumentType2["DocumentTypeLayoverApproval"] = "layoverApproval";
  DocumentType2["DocumentTypeLumperApproval"] = "lumperApproval";
  DocumentType2["DocumentTypeTonuApproval"] = "tonuApproval";
  DocumentType2["DocumentTypeMxBol"] = "mxBOL";
  DocumentType2["DocumentTypeVendorInvoice"] = "vendorInvoice";
  DocumentType2["DocumentTypeFine"] = "fine";
  DocumentType2["DocumentTypeW9"] = "w9";
  DocumentType2["DocumentTypeAuthority"] = "authority";
  DocumentType2["DocumentTypeBalanceDue"] = "balanceDue";
  DocumentType2["DocumentTypeClaim"] = "claim";
  DocumentType2["DocumentTypeCoMCHeck"] = "comcheck";
  DocumentType2["DocumentTypeAchSetupForm"] = "achSetupForm";
  DocumentType2["DocumentTypeCustomsDocument"] = "customsDocument";
  DocumentType2["DocumentTypeQuote"] = "quoteDoc";
  DocumentType2["DocumentTypeExport"] = "exportDoc";
  DocumentType2["DocumentTypeMasterBillOfLading"] = "masterBillOfLading";
  DocumentType2["DocumentTypeHouseBillOfLading"] = "houseBillOfLading";
  DocumentType2["DocumentTypeWaybill"] = "waybill";
  DocumentType2["DocumentTypeCommercialInvoice"] = "commercialInvoice";
  DocumentType2["DocumentTypePackingList"] = "packingList";
  DocumentType2["DocumentTypeCertificateOfOrigin"] = "certificateOfOrigin";
  DocumentType2["DocumentTypeInspectionCertificate"] = "inspectionCertificate";
  DocumentType2["DocumentTypePickupInspectionCertificate"] = "pickupInspectionCertificate";
  DocumentType2["DocumentTypeDropoffInspectionCertificate"] = "dropoffInspectionCertificate";
  DocumentType2["DocumentTypeWeightTicketUnloaded"] = "weightTicketUnloaded";
  DocumentType2["DocumentTypeUnknown"] = "unknown";
  DocumentType2["DocumentTypeUnknownCadenceImport"] = "unknownCadenceImport";
  DocumentType2["DocumentTypePalletReceipt"] = "palletReceipt";
  DocumentType2["DocumentTypeForm1113"] = "form1113";
  DocumentType2["DocumentTypeGovernmentBillOfLading"] = "governmentBillOfLading";
  DocumentType2["DocumentTypeInternationalBillOfLading"] = "internationalBillOfLading";
  DocumentType2["DocumentTypeInternalProofOfDelivery"] = "internalProofOfDelivery";
  DocumentType2["DocumentTypeDemurrageReceipt"] = "demurrageReceipt";
  DocumentType2["DocumentTypePerDiemReceipt"] = "perDiemReceipt";
  DocumentType2["DocumentTypeElectronicInterchangeReceipt"] = "electronicInterchangeReceipt";
  DocumentType2["DocumentTypeRepairItem"] = "repairItem";
  DocumentType2["DocumentTypePermit"] = "permit";
  DocumentType2["DocumentTypeAgentInvoice"] = "agentInvoice";
  DocumentType2["DocumentTypeArrivalNotice"] = "arrivalNotice";
  DocumentType2["DocumentTypeAsbestosDeclaration"] = "asbestosDeclaration";
  DocumentType2["DocumentTypeAusfta"] = "ausFta";
  DocumentType2["DocumentTypeAuthorizationRelease"] = "authorizationRelease";
  DocumentType2["DocumentTypeB3CanadianCustomsEntry"] = "b3CanadianCustomsEntry";
  DocumentType2["DocumentTypeBookingConfirmation"] = "bookingConfirmation";
  DocumentType2["DocumentTypeBookingRequest"] = "bookingRequest";
  DocumentType2["DocumentTypeCartageAdviceWithReceipt"] = "cartageAdviceWithReceipt";
  DocumentType2["DocumentTypeClaimNotice"] = "claimNotice";
  DocumentType2["DocumentTypeConsignmentSecurityDeclaration"] = "consignmentSecurityDeclaration";
  DocumentType2["DocumentTypeCorrectionNotice"] = "correctionNotice";
  DocumentType2["DocumentTypeCustomerRelease"] = "customerRelease";
  DocumentType2["DocumentTypeCustomsAuthority"] = "customsAuthority";
  DocumentType2["DocumentTypeCustomsClearanceRequest"] = "customsClearanceRequest";
  DocumentType2["DocumentTypeCustomsEntrySummary"] = "customsEntrySummary";
  DocumentType2["DocumentTypeCustomsPowerOfAttorney"] = "customsPowerOfAttorney";
  DocumentType2["DocumentTypeDangerousGoodsDeclaration"] = "dangerousGoodsDeclaration";
  DocumentType2["DocumentTypeDeclarationCertificate"] = "declarationCertificate";
  DocumentType2["DocumentTypeExportAuthority"] = "exportAuthority";
  DocumentType2["DocumentTypeExportDeclaration"] = "exportDeclaration";
  DocumentType2["DocumentTypeFreetimeNotification"] = "freetimeNotification";
  DocumentType2["DocumentTypeFreightLabel"] = "freightLabel";
  DocumentType2["DocumentTypeFumigationCertificate"] = "fumigationCertificate";
  DocumentType2["DocumentTypeGatePass"] = "gatePass";
  DocumentType2["DocumentTypeHouseAirwayBill"] = "houseAirwayBill";
  DocumentType2["DocumentTypeHouseOceanBillOfLading"] = "houseOceanBillOfLading";
  DocumentType2["DocumentTypeIntermodalInTransitNotice"] = "intermodalInTransitNotice";
  DocumentType2["DocumentTypeIsfFilingInstruction"] = "isfFilingInstruction";
  DocumentType2["DocumentTypeLetterOfCredit"] = "letterOfCredit";
  DocumentType2["DocumentTypeManifest"] = "manifest";
  DocumentType2["DocumentTypeMasterAirwayBill"] = "masterAirwayBill";
  DocumentType2["DocumentTypeMasterOceanBillOfLading"] = "masterOceanBillOfLading";
  DocumentType2["DocumentTypeMotorVehicleCertificate"] = "motorVehicleCertificate";
  DocumentType2["DocumentTypeMsds"] = "MSDS";
  DocumentType2["DocumentTypeNaftaCertificate"] = "naftaCertificate";
  DocumentType2["DocumentTypePackingDeclaration"] = "packingDeclaration";
  DocumentType2["DocumentTypePermitToTransfer"] = "permitToTransfer";
  DocumentType2["DocumentTypePickupNumber"] = "pickupNumber";
  DocumentType2["DocumentTypePreAlert"] = "preAlert";
  DocumentType2["DocumentTypeProformaInvoice"] = "proformaInvoice";
  DocumentType2["DocumentTypePurchaseOrder"] = "purchaseOrder";
  DocumentType2["DocumentTypeSanitaryCertificate"] = "sanitaryCertificate";
  DocumentType2["DocumentTypeShippersLetterOfInstruction"] = "shippersLetterOfInstruction";
  DocumentType2["DocumentTypeShippersSecurityDeclaration"] = "shippersSecurityDeclaration";
  DocumentType2["DocumentTypeSolasForm"] = "solasForm";
  DocumentType2["DocumentTypeTrackingStatus"] = "trackingStatus";
  DocumentType2["DocumentTypeTelexRelease"] = "telexRelease";
  DocumentType2["DocumentTypeTruckBillOfLading"] = "truckBillOfLading";
  DocumentType2["DocumentTypeUsCustomsDocument"] = "usCustomsDocument";
  DocumentType2["DocumentTypeVeterinaryCertificate"] = "veterinaryCertificate";
  DocumentType2["DocumentTypeVgmContainerInformation"] = "vgmContainerInformation";
  DocumentType2["DocumentTypeWeightMeasurementReport"] = "weightMeasurementReport";
  DocumentType2["DocumentTypePerDiemInvoice"] = "perDiemInvoice";
  DocumentType2["DocumentTypeWasteManifest"] = "wasteManifest";
  return DocumentType2;
})(DocumentType || {});
export const decodeDocumentType = (value) => value;
export const encodeDocumentType = (value) => value;
export var DocumentTypePartial = /* @__PURE__ */ ((DocumentTypePartial2) => {
  DocumentTypePartial2["DocumentTypePartialPartiallySpecifiedOcean"] = "__partial__OceanBillOfLading";
  DocumentTypePartial2["DocumentTypePartialPartiallySpecifiedAirway"] = "__partial__AirwayBillOfLading";
  DocumentTypePartial2["DocumentTypePartialPartiallySpecifiedBillOfLading"] = "__partial__BillOfLading";
  return DocumentTypePartial2;
})(DocumentTypePartial || {});
export const decodeDocumentTypePartial = (value) => value;
export const encodeDocumentTypePartial = (value) => value;
export function decodeDocumentTypeReview(json) {
  return {
    id: assertNumber(json.id),
    document_id: assertNumber(json.document_id)
  };
}
export function encodeDocumentTypeReview(value) {
  return {
    id: value.id,
    document_id: value.document_id
  };
}
export function decodeDocumentTypeReviewCompletionRequest(json) {
  return {
    documentTypeReviewId: assertNumber(json.documentTypeReviewId),
    assignedDocumentType: decodeDocumentType(json.assignedDocumentType)
  };
}
export function encodeDocumentTypeReviewCompletionRequest(value) {
  return {
    documentTypeReviewId: value.documentTypeReviewId,
    assignedDocumentType: encodeDocumentType(value.assignedDocumentType)
  };
}
export function decodeDocumentTypeReviewId(json) {
  return {
    documentTypeReviewId: optionalDecoder(assertNumber)(
      json.documentTypeReviewId
    )
  };
}
export function encodeDocumentTypeReviewId(value) {
  return {
    documentTypeReviewId: value.documentTypeReviewId
  };
}
export function decodeDocumentViewerPageContextData(json) {
  return {
    carrierInvoiceId: optionalDecoder(assertNumber)(json.carrierInvoiceId),
    loadStateId: optionalDecoder(assertNumber)(json.loadStateId),
    carrierInvoiceHasSubmission: optionalDecoder(assertBoolean)(
      json.carrierInvoiceHasSubmission
    )
  };
}
export function encodeDocumentViewerPageContextData(value) {
  return {
    carrierInvoiceId: value.carrierInvoiceId,
    loadStateId: value.loadStateId,
    carrierInvoiceHasSubmission: value.carrierInvoiceHasSubmission
  };
}
export function decodeDocumentsRequiredCheck(json) {
  return {
    requiredDocumentTypes: (json.requiredDocumentTypes || []).map(assertString),
    title: assertString(json.title),
    passedDescription: assertString(json.passedDescription),
    invalid: assertBoolean(json.invalid),
    failureReasons: (json.failureReasons || []).map(assertString),
    missingUnits: (json.missingUnits || []).map(assertString)
  };
}
export function encodeDocumentsRequiredCheck(value) {
  return {
    requiredDocumentTypes: value.requiredDocumentTypes,
    title: value.title,
    passedDescription: value.passedDescription,
    invalid: value.invalid,
    failureReasons: value.failureReasons,
    missingUnits: value.missingUnits
  };
}
export var DuplicateEmailAutoCompletedReason = /* @__PURE__ */ ((DuplicateEmailAutoCompletedReason2) => {
  DuplicateEmailAutoCompletedReason2["DuplicateEmailAutoCompletedReasonStandard"] = "standard";
  DuplicateEmailAutoCompletedReason2["DuplicateEmailAutoCompletedReasonNoTextBody"] = "no_text_body";
  DuplicateEmailAutoCompletedReason2["DuplicateEmailAutoCompletedReasonSenderDomainOnly"] = "sender_domain_only";
  DuplicateEmailAutoCompletedReason2["DuplicateEmailAutoCompletedReasonReceiverDomainOnly"] = "receiver_domain_only";
  return DuplicateEmailAutoCompletedReason2;
})(DuplicateEmailAutoCompletedReason || {});
export const decodeDuplicateEmailAutoCompletedReason = (value) => value;
export const encodeDuplicateEmailAutoCompletedReason = (value) => value;
export function decodeDuplicateInvoiceCheck(json) {
  return {
    duplicatedInvoiceId: optionalDecoder(assertNumber)(
      json.duplicatedInvoiceId
    ),
    matchedInvoiceNumber: optionalDecoder(assertString)(
      json.matchedInvoiceNumber
    ),
    matchedDebtorName: optionalDecoder(assertString)(json.matchedDebtorName),
    matchedAmount: optionalDecoder(assertString)(json.matchedAmount),
    matchedDate: optionalDecoder(assertString)(json.matchedDate),
    matchedLoadId: optionalDecoder(assertString)(json.matchedLoadId),
    matchedCarrierName: optionalDecoder(assertString)(json.matchedCarrierName)
  };
}
export function encodeDuplicateInvoiceCheck(value) {
  return {
    duplicatedInvoiceId: value.duplicatedInvoiceId,
    matchedInvoiceNumber: value.matchedInvoiceNumber,
    matchedDebtorName: value.matchedDebtorName,
    matchedAmount: value.matchedAmount,
    matchedDate: value.matchedDate,
    matchedLoadId: value.matchedLoadId,
    matchedCarrierName: value.matchedCarrierName
  };
}
export function decodeEmailAddress(json) {
  return {
    email: optionalDecoder(assertString)(json.email),
    name: optionalDecoder(assertString)(json.name)
  };
}
export function encodeEmailAddress(value) {
  return {
    email: value.email,
    name: value.name
  };
}
export var EmailImportState = /* @__PURE__ */ ((EmailImportState2) => {
  EmailImportState2["EmailImportStateImportInProgress"] = "importing";
  EmailImportState2["EmailImportStateImportFailed"] = "import_failed";
  EmailImportState2["EmailImportStateInPODDetection"] = "pod_review";
  EmailImportState2["EmailImportStateInDocumentContentReview"] = "document_content_review";
  EmailImportState2["EmailImportStateInDocumentContentReviewDocumentType"] = "document_content_review_document_type";
  EmailImportState2["EmailImportStateInDocumentContentReviewNoDependents"] = "document_content_review_no_dependents";
  EmailImportState2["EmailImportStateInDocumentBatchGroupingReview"] = "document_batch_grouping_review";
  EmailImportState2["EmailImportStateImportCompleted"] = "import_completed";
  EmailImportState2["EmailImportStateInDocumentGroupLoadReview"] = "document_group_load_review";
  return EmailImportState2;
})(EmailImportState || {});
export const decodeEmailImportState = (value) => value;
export const encodeEmailImportState = (value) => value;
export function decodeEmailMessage(json) {
  return {
    id: assertNumber(json.id),
    in_reply_to_email_message_id: optionalDecoder(assertNumber)(
      json.in_reply_to_email_message_id
    ),
    message_type: optionalDecoder(decodeEmailMessageType)(json.message_type),
    carrier_id: optionalDecoder(assertNumber)(json.carrier_id),
    subject: assertString(json.subject),
    text_body: assertString(json.text_body),
    created_at: decodeTimestamp(json.created_at),
    updated_at: decodeTimestamp(json.updated_at),
    sender_address: assertString(json.sender_address),
    recipient_address: optionalDecoder(assertString)(json.recipient_address),
    raw_to: (json.raw_to || []).map(decodeEmailAddress),
    raw_cc: (json.raw_cc || []).map(decodeEmailAddress),
    document_batch_id: optionalDecoder(assertNumber)(json.document_batch_id),
    attachment_download_url: assertString(json.attachment_download_url),
    completed_at: optionalDecoder(decodeTimestamp)(json.completed_at),
    import_completed_at: optionalDecoder(decodeTimestamp)(
      json.import_completed_at
    ),
    labels: (json.labels || []).map(assertString),
    import_failed_at: optionalDecoder(decodeTimestamp)(json.import_failed_at),
    has_factor_client_invoice_submission: assertBoolean(
      json.has_factor_client_invoice_submission
    ),
    single_carrier: optionalDecoder(assertBoolean)(json.single_carrier),
    automatically_sent_after_indexing: assertBoolean(
      json.automatically_sent_after_indexing
    ),
    import_state: decodeEmailImportState(json.import_state),
    estimated_due_date: optionalDecoder(decodeTimestamp)(
      json.estimated_due_date
    )
  };
}
export function encodeEmailMessage(value) {
  return {
    id: value.id,
    in_reply_to_email_message_id: value.in_reply_to_email_message_id,
    message_type: optionalEncoder(encodeEmailMessageType)(value.message_type),
    carrier_id: value.carrier_id,
    subject: value.subject,
    text_body: value.text_body,
    created_at: encodeTimestamp(value.created_at),
    updated_at: encodeTimestamp(value.updated_at),
    sender_address: value.sender_address,
    recipient_address: value.recipient_address,
    raw_to: value.raw_to.map(encodeEmailAddress),
    raw_cc: value.raw_cc.map(encodeEmailAddress),
    document_batch_id: value.document_batch_id,
    attachment_download_url: value.attachment_download_url,
    completed_at: optionalEncoder(encodeTimestamp)(value.completed_at),
    import_completed_at: optionalEncoder(encodeTimestamp)(
      value.import_completed_at
    ),
    labels: value.labels,
    import_failed_at: optionalEncoder(encodeTimestamp)(value.import_failed_at),
    has_factor_client_invoice_submission: value.has_factor_client_invoice_submission,
    single_carrier: value.single_carrier,
    automatically_sent_after_indexing: value.automatically_sent_after_indexing,
    import_state: encodeEmailImportState(value.import_state),
    estimated_due_date: optionalEncoder(encodeTimestamp)(
      value.estimated_due_date
    )
  };
}
export function decodeEmailMessageIdsQuery(json) {
  return {
    page: assertNumber(json.page),
    term: optionalDecoder(assertString)(json.term),
    tms_carrier_id: optionalDecoder(assertNumber)(json.tms_carrier_id),
    start_date: optionalDecoder(assertString)(json.start_date),
    end_date: optionalDecoder(assertString)(json.end_date),
    start_date_time: optionalDecoder(assertString)(json.start_date_time),
    end_date_time: optionalDecoder(assertString)(json.end_date_time),
    include_sent_emails: assertBoolean(json.include_sent_emails)
  };
}
export function encodeEmailMessageIdsQuery(value) {
  return {
    page: value.page,
    term: value.term,
    tms_carrier_id: value.tms_carrier_id,
    start_date: value.start_date,
    end_date: value.end_date,
    start_date_time: value.start_date_time,
    end_date_time: value.end_date_time,
    include_sent_emails: value.include_sent_emails
  };
}
export var EmailMessageType = /* @__PURE__ */ ((EmailMessageType2) => {
  EmailMessageType2["EmailMessageTypeSent"] = "sent";
  EmailMessageType2["EmailMessageTypeDocuments"] = "documents";
  EmailMessageType2["EmailMessageTypeReply"] = "reply";
  EmailMessageType2["EmailMessageTypeImport"] = "import";
  return EmailMessageType2;
})(EmailMessageType || {});
export const decodeEmailMessageType = (value) => value;
export const encodeEmailMessageType = (value) => value;
export var EmailPerInvoice = /* @__PURE__ */ ((EmailPerInvoice2) => {
  EmailPerInvoice2["EmailPerInvoiceEmailAccountDefault"] = "email_account_default";
  EmailPerInvoice2["EmailPerInvoiceEmailPerInvoice"] = "email_per_invoice";
  EmailPerInvoice2["EmailPerInvoiceEmailSingle"] = "email_single";
  return EmailPerInvoice2;
})(EmailPerInvoice || {});
export const decodeEmailPerInvoice = (value) => value;
export const encodeEmailPerInvoice = (value) => value;
export function decodeEmbeddedComponentsFlags(json) {
  return {
    componentFlags: decodeEmbeddedComponentsFlags_ComponentFlags(json)
  };
}
export function encodeEmbeddedComponentsFlags(value) {
  return __spreadValues({}, encodeEmbeddedComponentsFlags_ComponentFlags(value.componentFlags));
}
export var EmbeddedComponentsFlags_ComponentFlagsType = /* @__PURE__ */ ((EmbeddedComponentsFlags_ComponentFlagsType2) => {
  EmbeddedComponentsFlags_ComponentFlagsType2["EmptyComponent"] = "empty_component";
  EmbeddedComponentsFlags_ComponentFlagsType2["PODDefinition"] = "pod_definition";
  EmbeddedComponentsFlags_ComponentFlagsType2["AutoConfirmedLoadHeader"] = "auto_confirmed_load_header";
  return EmbeddedComponentsFlags_ComponentFlagsType2;
})(EmbeddedComponentsFlags_ComponentFlagsType || {});
export function decodeEmbeddedComponentsFlags_ComponentFlags(json) {
  if (json.empty_component !== void 0) {
    return {
      type: "empty_component" /* EmptyComponent */,
      empty_component: decodeEmbeddedComponentsFlags_EmptyComponentFlags(
        json.empty_component
      )
    };
  } else if (json.pod_definition !== void 0) {
    return {
      type: "pod_definition" /* PODDefinition */,
      pod_definition: decodeEmbeddedComponentsFlags_PODDefinitionComponentFlags(
        json.pod_definition
      )
    };
  } else if (json.auto_confirmed_load_header !== void 0) {
    return {
      type: "auto_confirmed_load_header" /* AutoConfirmedLoadHeader */,
      auto_confirmed_load_header: decodeEmbeddedComponentsFlags_AutoConfirmedLoadHeaderComponentFlags(
        json.auto_confirmed_load_header
      )
    };
  }
  throw new Error(
    "No field set in oneof EmbeddedComponentsFlags_ComponentFlags"
  );
}
export function encodeEmbeddedComponentsFlags_ComponentFlags(value) {
  switch (value.type) {
    case "empty_component" /* EmptyComponent */:
      return {
        empty_component: encodeEmbeddedComponentsFlags_EmptyComponentFlags(
          value.empty_component
        )
      };
    case "pod_definition" /* PODDefinition */:
      return {
        pod_definition: encodeEmbeddedComponentsFlags_PODDefinitionComponentFlags(
          value.pod_definition
        )
      };
    case "auto_confirmed_load_header" /* AutoConfirmedLoadHeader */:
      return {
        auto_confirmed_load_header: encodeEmbeddedComponentsFlags_AutoConfirmedLoadHeaderComponentFlags(
          value.auto_confirmed_load_header
        )
      };
    default:
      throw new Error(
        "No field set in oneof EmbeddedComponentsFlags_ComponentFlags"
      );
  }
}
export function decodeEmbeddedComponentsFlags_AutoConfirmedLoadHeaderComponentFlags(json) {
  return {
    load_id: assertString(json.load_id),
    carrier_invoice_status: optionalDecoder(assertString)(
      json.carrier_invoice_status
    )
  };
}
export function encodeEmbeddedComponentsFlags_AutoConfirmedLoadHeaderComponentFlags(value) {
  return {
    load_id: value.load_id,
    carrier_invoice_status: value.carrier_invoice_status
  };
}
export function decodeEmbeddedComponentsFlags_EmptyComponentFlags(json) {
  return {};
}
export function encodeEmbeddedComponentsFlags_EmptyComponentFlags(value) {
  return {};
}
export function decodeEmbeddedComponentsFlags_PODDefinitionComponentFlags(json) {
  return {
    display_text: assertString(json.display_text),
    unskippable: assertBoolean(json.unskippable),
    ignored: assertBoolean(json.ignored),
    read_only: assertBoolean(json.read_only),
    carrier_invoice_id: assertNumber(json.carrier_invoice_id),
    rule_json: assertString(json.rule_json),
    validation_error_messages: (json.validation_error_messages || []).map(
      assertString
    ),
    rule_failure_exists: assertBoolean(json.rule_failure_exists),
    ocr_drop_zone_class: assertString(json.ocr_drop_zone_class),
    missing_pod_units: (json.missing_pod_units || []).map(assertString)
  };
}
export function encodeEmbeddedComponentsFlags_PODDefinitionComponentFlags(value) {
  return {
    display_text: value.display_text,
    unskippable: value.unskippable,
    ignored: value.ignored,
    read_only: value.read_only,
    carrier_invoice_id: value.carrier_invoice_id,
    rule_json: value.rule_json,
    validation_error_messages: value.validation_error_messages,
    rule_failure_exists: value.rule_failure_exists,
    ocr_drop_zone_class: value.ocr_drop_zone_class,
    missing_pod_units: value.missing_pod_units
  };
}
export function decodeEmpty(json) {
  return {};
}
export function encodeEmpty(value) {
  return {};
}
export function decodeExceptionEvent(json) {
  return {
    message: optionalDecoder(assertString)(json.message),
    category: decodeExceptionEventCategory(json.category),
    resolution: optionalDecoder(assertString)(json.resolution),
    id: assertNumber(json.id)
  };
}
export function encodeExceptionEvent(value) {
  return {
    message: value.message,
    category: encodeExceptionEventCategory(value.category),
    resolution: value.resolution,
    id: value.id
  };
}
export var ExceptionEventCategory = /* @__PURE__ */ ((ExceptionEventCategory2) => {
  ExceptionEventCategory2["ExceptionEventCategoryMissingPages"] = "MISSING_PAGES";
  ExceptionEventCategory2["ExceptionEventCategoryBadQuality"] = "BAD_QUALITY";
  ExceptionEventCategory2["ExceptionEventCategoryCustomException"] = "CUSTOM_EXCEPTION";
  ExceptionEventCategory2["ExceptionEventCategoryNone"] = "";
  ExceptionEventCategory2["ExceptionEventCategoryIllegible"] = "ILLEGIBLE";
  return ExceptionEventCategory2;
})(ExceptionEventCategory || {});
export const decodeExceptionEventCategory = (value) => value;
export const encodeExceptionEventCategory = (value) => value;
export function decodeExceptionSummary(json) {
  return {
    payment_exceptions: (json.payment_exceptions || []).map(assertString),
    billing_exceptions: (json.billing_exceptions || []).map(assertString)
  };
}
export function encodeExceptionSummary(value) {
  return {
    payment_exceptions: value.payment_exceptions,
    billing_exceptions: value.billing_exceptions
  };
}
export function decodeExceptionedItem(json) {
  return {
    id: assertNumber(json.id),
    created_at: optionalDecoder(decodeTimestamp)(json.created_at),
    document_group_id: optionalDecoder(assertNumber)(json.document_group_id),
    email_message_id: optionalDecoder(assertNumber)(json.email_message_id),
    labels: (json.labels || []).map(assertString),
    note: optionalDecoder(assertString)(json.note),
    last_sent_at: optionalDecoder(decodeTimestamp)(json.last_sent_at),
    next_review_on: optionalDecoder(decodeTimestamp)(json.next_review_on),
    tms_carrier_id: optionalDecoder(assertNumber)(json.tms_carrier_id),
    completed_at: optionalDecoder(decodeTimestamp)(json.completed_at),
    first_received_at: optionalDecoder(decodeTimestamp)(json.first_received_at),
    owner: decodeExceptionedItem_Owner(json)
  };
}
export function encodeExceptionedItem(value) {
  return __spreadValues({
    id: value.id,
    created_at: optionalEncoder(encodeTimestamp)(value.created_at),
    document_group_id: value.document_group_id,
    email_message_id: value.email_message_id,
    labels: value.labels,
    note: value.note,
    last_sent_at: optionalEncoder(encodeTimestamp)(value.last_sent_at),
    next_review_on: optionalEncoder(encodeTimestamp)(value.next_review_on),
    tms_carrier_id: value.tms_carrier_id,
    completed_at: optionalEncoder(encodeTimestamp)(value.completed_at),
    first_received_at: optionalEncoder(encodeTimestamp)(
      value.first_received_at
    )
  }, encodeExceptionedItem_Owner(value.owner));
}
export var ExceptionedItem_OwnerType = /* @__PURE__ */ ((ExceptionedItem_OwnerType2) => {
  ExceptionedItem_OwnerType2["CarrierInvoiceOwner"] = "carrier_invoice_owner";
  ExceptionedItem_OwnerType2["FactorInvoiceOwner"] = "factor_invoice_owner";
  ExceptionedItem_OwnerType2["DocumentGroupOwner"] = "document_group_owner";
  ExceptionedItem_OwnerType2["EmailMessageOwner"] = "email_message_owner";
  return ExceptionedItem_OwnerType2;
})(ExceptionedItem_OwnerType || {});
export function decodeExceptionedItem_Owner(json) {
  if (json.carrier_invoice_owner !== void 0) {
    return {
      type: "carrier_invoice_owner" /* CarrierInvoiceOwner */,
      carrier_invoice_owner: decodeExceptionedItem_CarrierInvoiceOwner(
        json.carrier_invoice_owner
      )
    };
  } else if (json.factor_invoice_owner !== void 0) {
    return {
      type: "factor_invoice_owner" /* FactorInvoiceOwner */,
      factor_invoice_owner: decodeExceptionedItem_FactorInvoiceOwner(
        json.factor_invoice_owner
      )
    };
  } else if (json.document_group_owner !== void 0) {
    return {
      type: "document_group_owner" /* DocumentGroupOwner */,
      document_group_owner: decodeExceptionedItem_DocumentGroupOwner(
        json.document_group_owner
      )
    };
  } else if (json.email_message_owner !== void 0) {
    return {
      type: "email_message_owner" /* EmailMessageOwner */,
      email_message_owner: decodeExceptionedItem_EmailMessageOwner(
        json.email_message_owner
      )
    };
  }
  throw new Error("No field set in oneof ExceptionedItem_Owner");
}
export function encodeExceptionedItem_Owner(value) {
  switch (value.type) {
    case "carrier_invoice_owner" /* CarrierInvoiceOwner */:
      return {
        carrier_invoice_owner: encodeExceptionedItem_CarrierInvoiceOwner(
          value.carrier_invoice_owner
        )
      };
    case "factor_invoice_owner" /* FactorInvoiceOwner */:
      return {
        factor_invoice_owner: encodeExceptionedItem_FactorInvoiceOwner(
          value.factor_invoice_owner
        )
      };
    case "document_group_owner" /* DocumentGroupOwner */:
      return {
        document_group_owner: encodeExceptionedItem_DocumentGroupOwner(
          value.document_group_owner
        )
      };
    case "email_message_owner" /* EmailMessageOwner */:
      return {
        email_message_owner: encodeExceptionedItem_EmailMessageOwner(
          value.email_message_owner
        )
      };
    default:
      throw new Error("No field set in oneof ExceptionedItem_Owner");
  }
}
export function decodeExceptionedItem_CarrierInvoiceOwner(json) {
  return {
    owner_id: assertNumber(json.owner_id)
  };
}
export function encodeExceptionedItem_CarrierInvoiceOwner(value) {
  return {
    owner_id: value.owner_id
  };
}
export function decodeExceptionedItem_DocumentGroupOwner(json) {
  return {
    owner_id: assertNumber(json.owner_id)
  };
}
export function encodeExceptionedItem_DocumentGroupOwner(value) {
  return {
    owner_id: value.owner_id
  };
}
export function decodeExceptionedItem_EmailMessageOwner(json) {
  return {
    owner_id: assertNumber(json.owner_id)
  };
}
export function encodeExceptionedItem_EmailMessageOwner(value) {
  return {
    owner_id: value.owner_id
  };
}
export function decodeExceptionedItem_FactorInvoiceOwner(json) {
  return {
    owner_id: assertNumber(json.owner_id)
  };
}
export function encodeExceptionedItem_FactorInvoiceOwner(value) {
  return {
    owner_id: value.owner_id
  };
}
export function decodeExternalLogin(json) {
  return {
    id: assertNumber(json.id),
    user: optionalDecoder(assertString)(json.user),
    passwordPresent: assertBoolean(json.passwordPresent),
    token: optionalDecoder(assertString)(json.token),
    tmsCustomerId: optionalDecoder(assertNumber)(json.tmsCustomerId),
    scrapingServices: (json.scrapingServices || []).map(decodeScrapingService),
    loginFailureDetectedAt: optionalDecoder(assertString)(
      json.loginFailureDetectedAt
    )
  };
}
export function encodeExternalLogin(value) {
  return {
    id: value.id,
    user: value.user,
    passwordPresent: value.passwordPresent,
    token: value.token,
    tmsCustomerId: value.tmsCustomerId,
    scrapingServices: value.scrapingServices.map(encodeScrapingService),
    loginFailureDetectedAt: value.loginFailureDetectedAt
  };
}
export function decodeExtractedFieldsCheck(json) {
  return {
    fields: (json.fields || []).map(decodeCarrierInvoiceExtractedField)
  };
}
export function encodeExtractedFieldsCheck(value) {
  return {
    fields: value.fields.map(encodeCarrierInvoiceExtractedField)
  };
}
export function decodeFactorAging(json) {
  return {
    noa_sent_at: optionalDecoder(decodeTimestamp)(json.noa_sent_at),
    noa_received_at: optionalDecoder(decodeTimestamp)(json.noa_received_at)
  };
}
export function encodeFactorAging(value) {
  return {
    noa_sent_at: optionalEncoder(encodeTimestamp)(value.noa_sent_at),
    noa_received_at: optionalEncoder(encodeTimestamp)(value.noa_received_at)
  };
}
export function decodeFactorBroker(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    mc_number: optionalDecoder(assertString)(json.mc_number),
    dot_number: optionalDecoder(assertString)(json.dot_number),
    common_aliases: (json.common_aliases || []).map(assertString),
    contact_info: assertString(json.contact_info),
    cadence_debtor_details: decodeFactorBroker_CadenceDebtorDetails(
      json.cadence_debtor_details
    ),
    email_domains_to_validate: (json.email_domains_to_validate || []).map(
      assertString
    ),
    billing_contact: optionalDecoder(decodeFactorBroker_BillingContact)(
      json.billing_contact
    ),
    warning: optionalDecoder(assertString)(json.warning),
    notes: assertString(json.notes),
    no_buy: assertBoolean(json.no_buy),
    created_at: decodeTimestamp(json.created_at),
    updated_at: decodeTimestamp(json.updated_at),
    verifications_type: optionalDecoder(decodeFactorBrokerVerificationType)(
      json.verifications_type
    )
  };
}
export function encodeFactorBroker(value) {
  return {
    id: value.id,
    name: value.name,
    mc_number: value.mc_number,
    dot_number: value.dot_number,
    common_aliases: value.common_aliases,
    contact_info: value.contact_info,
    cadence_debtor_details: encodeFactorBroker_CadenceDebtorDetails(
      value.cadence_debtor_details
    ),
    email_domains_to_validate: value.email_domains_to_validate,
    billing_contact: optionalEncoder(encodeFactorBroker_BillingContact)(
      value.billing_contact
    ),
    warning: value.warning,
    notes: value.notes,
    no_buy: value.no_buy,
    created_at: encodeTimestamp(value.created_at),
    updated_at: encodeTimestamp(value.updated_at),
    verifications_type: optionalEncoder(encodeFactorBrokerVerificationType)(
      value.verifications_type
    )
  };
}
export function decodeFactorBroker_BillingContact(json) {
  return {
    email: optionalDecoder(assertString)(json.email)
  };
}
export function encodeFactorBroker_BillingContact(value) {
  return {
    email: value.email
  };
}
export function decodeFactorBroker_CadenceDebtorDetails(json) {
  return {
    email: optionalDecoder(assertString)(json.email),
    phone_1: optionalDecoder(assertString)(json.phone_1),
    phone_2: optionalDecoder(assertString)(json.phone_2),
    dot_number: optionalDecoder(assertString)(json.dot_number),
    mc_number: optionalDecoder(assertString)(json.mc_number)
  };
}
export function encodeFactorBroker_CadenceDebtorDetails(value) {
  return {
    email: value.email,
    phone_1: value.phone_1,
    phone_2: value.phone_2,
    dot_number: value.dot_number,
    mc_number: value.mc_number
  };
}
export function decodeFactorBrokerIdsQuery(json) {
  return {
    term: assertString(json.term),
    carrier_id: optionalDecoder(assertNumber)(json.carrier_id),
    use_simple_search: assertBoolean(json.use_simple_search)
  };
}
export function encodeFactorBrokerIdsQuery(value) {
  return {
    term: value.term,
    carrier_id: value.carrier_id,
    use_simple_search: value.use_simple_search
  };
}
export function decodeFactorBrokerSettings(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    billing_email: assertString(json.billing_email),
    contact_info: assertString(json.contact_info),
    delivery_method: decodeDeliveryMethod(json.delivery_method),
    customer_visible_document_types: (json.customer_visible_document_types || []).map(decodeUnresolvedDocumentType),
    required_receivables_types: (json.required_receivables_types || []).map(
      decodeUnresolvedDocumentType
    ),
    billing_interval: decodeBillingInterval(json.billing_interval),
    customer_invoice_template_id: optionalDecoder(assertNumber)(
      json.customer_invoice_template_id
    ),
    skip_auto_stamp: assertBoolean(json.skip_auto_stamp),
    rate_confirmation_required: assertBoolean(json.rate_confirmation_required),
    pod_required: assertBoolean(json.pod_required),
    exclude_from_verifications_workflow: assertBoolean(
      json.exclude_from_verifications_workflow
    ),
    require_verifications_workflow: assertBoolean(
      json.require_verifications_workflow
    ),
    factor_invoice_extracted_fields: (json.factor_invoice_extracted_fields || []).map(decodeFactorBrokerSettings_InvoiceExtractedField),
    warn_if_amount_over: optionalDecoder(assertNumber)(
      json.warn_if_amount_over
    ),
    add_accessorials_to_invoice_total: assertBoolean(
      json.add_accessorials_to_invoice_total
    ),
    deduct_advances_from_invoice_total: assertBoolean(
      json.deduct_advances_from_invoice_total
    ),
    allow_autoprocessing: assertBoolean(json.allow_autoprocessing),
    factor_broker_load_id_regression_rule_mode: decodeRulesMode(
      json.factor_broker_load_id_regression_rule_mode
    ),
    load_id_auto_release: decodeFactorBrokerSettings_LoadIdAutoRelease(
      json.load_id_auto_release
    ),
    template_variables: (json.template_variables || []).map(assertString),
    load_id_strategies: (json.load_id_strategies || []).map(assertString)
  };
}
export function encodeFactorBrokerSettings(value) {
  return {
    id: value.id,
    name: value.name,
    billing_email: value.billing_email,
    contact_info: value.contact_info,
    delivery_method: encodeDeliveryMethod(value.delivery_method),
    customer_visible_document_types: value.customer_visible_document_types.map(
      encodeUnresolvedDocumentType
    ),
    required_receivables_types: value.required_receivables_types.map(
      encodeUnresolvedDocumentType
    ),
    billing_interval: encodeBillingInterval(value.billing_interval),
    customer_invoice_template_id: value.customer_invoice_template_id,
    skip_auto_stamp: value.skip_auto_stamp,
    rate_confirmation_required: value.rate_confirmation_required,
    pod_required: value.pod_required,
    exclude_from_verifications_workflow: value.exclude_from_verifications_workflow,
    require_verifications_workflow: value.require_verifications_workflow,
    factor_invoice_extracted_fields: value.factor_invoice_extracted_fields.map(
      encodeFactorBrokerSettings_InvoiceExtractedField
    ),
    warn_if_amount_over: value.warn_if_amount_over,
    add_accessorials_to_invoice_total: value.add_accessorials_to_invoice_total,
    deduct_advances_from_invoice_total: value.deduct_advances_from_invoice_total,
    allow_autoprocessing: value.allow_autoprocessing,
    factor_broker_load_id_regression_rule_mode: encodeRulesMode(
      value.factor_broker_load_id_regression_rule_mode
    ),
    load_id_auto_release: encodeFactorBrokerSettings_LoadIdAutoRelease(
      value.load_id_auto_release
    ),
    template_variables: value.template_variables,
    load_id_strategies: value.load_id_strategies
  };
}
export function decodeFactorBrokerSettings_InvoiceExtractedField(json) {
  return {
    label: assertString(json.label),
    name: assertString(json.name),
    line_item: assertBoolean(json.line_item),
    clean_amount: assertBoolean(json.clean_amount),
    advance: assertBoolean(json.advance)
  };
}
export function encodeFactorBrokerSettings_InvoiceExtractedField(value) {
  return {
    label: value.label,
    name: value.name,
    line_item: value.line_item,
    clean_amount: value.clean_amount,
    advance: value.advance
  };
}
export function decodeFactorBrokerSettings_LoadIdAutoRelease(json) {
  return {
    enabled: assertBoolean(json.enabled),
    enabled_at: optionalDecoder(decodeTimestamp)(json.enabled_at),
    enabled_by_name: optionalDecoder(assertString)(json.enabled_by_name),
    disabled_at: optionalDecoder(decodeTimestamp)(json.disabled_at),
    disabled_by_name: optionalDecoder(assertString)(json.disabled_by_name),
    kept_percentage: optionalDecoder(assertNumber)(json.kept_percentage),
    prior_count: optionalDecoder(assertNumber)(json.prior_count),
    stats_updated_at: optionalDecoder(decodeTimestamp)(json.stats_updated_at),
    eligible: assertBoolean(json.eligible)
  };
}
export function encodeFactorBrokerSettings_LoadIdAutoRelease(value) {
  return {
    enabled: value.enabled,
    enabled_at: optionalEncoder(encodeTimestamp)(value.enabled_at),
    enabled_by_name: value.enabled_by_name,
    disabled_at: optionalEncoder(encodeTimestamp)(value.disabled_at),
    disabled_by_name: value.disabled_by_name,
    kept_percentage: value.kept_percentage,
    prior_count: value.prior_count,
    stats_updated_at: optionalEncoder(encodeTimestamp)(value.stats_updated_at),
    eligible: value.eligible
  };
}
export function decodeFactorBrokerSettingsIdsQuery(json) {
  return {
    page: assertNumber(json.page),
    term: assertString(json.term)
  };
}
export function encodeFactorBrokerSettingsIdsQuery(value) {
  return {
    page: value.page,
    term: value.term
  };
}
export var FactorBrokerVerificationType = /* @__PURE__ */ ((FactorBrokerVerificationType2) => {
  FactorBrokerVerificationType2["FactorBrokerVerificationTypeScraper"] = "Scraper";
  FactorBrokerVerificationType2["FactorBrokerVerificationTypeHubtranCustomer"] = "HubtranCustomer";
  FactorBrokerVerificationType2["FactorBrokerVerificationTypeHubtranMultiAccountCustomer"] = "HubtranMultiAccountCustomer";
  FactorBrokerVerificationType2["FactorBrokerVerificationTypePaymentsNetworkCustomer"] = "PaymentsNetworkCustomer";
  FactorBrokerVerificationType2["FactorBrokerVerificationTypeOnDemandScraper"] = "OnDemandScraper";
  return FactorBrokerVerificationType2;
})(FactorBrokerVerificationType || {});
export const decodeFactorBrokerVerificationType = (value) => value;
export const encodeFactorBrokerVerificationType = (value) => value;
export var FactorCheckFailureAction = /* @__PURE__ */ ((FactorCheckFailureAction2) => {
  FactorCheckFailureAction2["FactorCheckFailureActionDoNothing"] = "do_nothing";
  FactorCheckFailureAction2["FactorCheckFailureActionRequirePaperworkAudit"] = "require_paperwork_audit";
  FactorCheckFailureAction2["FactorCheckFailureActionRequireManualVerification"] = "require_manual_verification";
  FactorCheckFailureAction2["FactorCheckFailureActionDoBoth"] = "do_both";
  return FactorCheckFailureAction2;
})(FactorCheckFailureAction || {});
export const decodeFactorCheckFailureAction = (value) => value;
export const encodeFactorCheckFailureAction = (value) => value;
export function decodeFactorInvoice(json) {
  return {};
}
export function encodeFactorInvoice(value) {
  return {};
}
export function decodeFactorInvoice_ClientDebtorNoaRelationship(json) {
  return {
    status: decodeFactorInvoice_ClientDebtorNoaRelationshipStatus(json.status),
    title: assertString(json.title),
    description: assertString(json.description)
  };
}
export function encodeFactorInvoice_ClientDebtorNoaRelationship(value) {
  return {
    status: encodeFactorInvoice_ClientDebtorNoaRelationshipStatus(value.status),
    title: value.title,
    description: value.description
  };
}
export var FactorInvoice_ClientDebtorNoaRelationshipStatus = /* @__PURE__ */ ((FactorInvoice_ClientDebtorNoaRelationshipStatus2) => {
  FactorInvoice_ClientDebtorNoaRelationshipStatus2["FactorInvoice_ClientDebtorNoaRelationshipStatusSuccess"] = "success";
  FactorInvoice_ClientDebtorNoaRelationshipStatus2["FactorInvoice_ClientDebtorNoaRelationshipStatusWarning"] = "warning";
  FactorInvoice_ClientDebtorNoaRelationshipStatus2["FactorInvoice_ClientDebtorNoaRelationshipStatusInfo"] = "info";
  FactorInvoice_ClientDebtorNoaRelationshipStatus2["FactorInvoice_ClientDebtorNoaRelationshipStatusError"] = "error";
  return FactorInvoice_ClientDebtorNoaRelationshipStatus2;
})(FactorInvoice_ClientDebtorNoaRelationshipStatus || {});
export const decodeFactorInvoice_ClientDebtorNoaRelationshipStatus = (value) => value;
export const encodeFactorInvoice_ClientDebtorNoaRelationshipStatus = (value) => value;
export function decodeFactorInvoice_DocumentBatchEmailDetails(json) {
  return {
    subject: optionalDecoder(assertString)(json.subject),
    date: optionalDecoder(decodeTimestamp)(json.date),
    sender_address: optionalDecoder(assertString)(json.sender_address),
    carrier_name: optionalDecoder(assertString)(json.carrier_name),
    carrier_account_executive: optionalDecoder(assertString)(
      json.carrier_account_executive
    )
  };
}
export function encodeFactorInvoice_DocumentBatchEmailDetails(value) {
  return {
    subject: value.subject,
    date: optionalEncoder(encodeTimestamp)(value.date),
    sender_address: value.sender_address,
    carrier_name: value.carrier_name,
    carrier_account_executive: value.carrier_account_executive
  };
}
export var FactorInvoice_State = /* @__PURE__ */ ((FactorInvoice_State2) => {
  FactorInvoice_State2["FactorInvoice_StateDraft"] = "draft";
  FactorInvoice_State2["FactorInvoice_StateNeedsIndexing"] = "needs_indexing";
  FactorInvoice_State2["FactorInvoice_StateIndexed"] = "indexed";
  FactorInvoice_State2["FactorInvoice_StateException"] = "exception";
  FactorInvoice_State2["FactorInvoice_StateAbandoned"] = "abandoned";
  FactorInvoice_State2["FactorInvoice_StateProcessed"] = "processed";
  FactorInvoice_State2["FactorInvoice_StateVerifying"] = "verifying";
  FactorInvoice_State2["FactorInvoice_StateVerifyCompleted"] = "verify_completed";
  FactorInvoice_State2["FactorInvoice_StateApproved"] = "approved";
  FactorInvoice_State2["FactorInvoice_StateVerified"] = "verified";
  FactorInvoice_State2["FactorInvoice_StatePurchased"] = "purchased";
  return FactorInvoice_State2;
})(FactorInvoice_State || {});
export const decodeFactorInvoice_State = (value) => value;
export const encodeFactorInvoice_State = (value) => value;
export function decodeFactorInvoiceAuditCheck(json) {
  return {
    result: decodeFactorInvoiceAuditCheckResult(json.result),
    details: decodeFactorInvoiceAuditCheck_Details(json)
  };
}
export function encodeFactorInvoiceAuditCheck(value) {
  return __spreadValues({
    result: encodeFactorInvoiceAuditCheckResult(value.result)
  }, encodeFactorInvoiceAuditCheck_Details(value.details));
}
export var FactorInvoiceAuditCheck_DetailsType = /* @__PURE__ */ ((FactorInvoiceAuditCheck_DetailsType2) => {
  FactorInvoiceAuditCheck_DetailsType2["RuleCheck"] = "ruleCheck";
  FactorInvoiceAuditCheck_DetailsType2["ExceptionEvent"] = "exceptionEvent";
  return FactorInvoiceAuditCheck_DetailsType2;
})(FactorInvoiceAuditCheck_DetailsType || {});
export function decodeFactorInvoiceAuditCheck_Details(json) {
  if (json.ruleCheck !== void 0) {
    return {
      type: "ruleCheck" /* RuleCheck */,
      ruleCheck: decodeFactorInvoiceAuditRuleCheck(json.ruleCheck)
    };
  } else if (json.exceptionEvent !== void 0) {
    return {
      type: "exceptionEvent" /* ExceptionEvent */,
      exceptionEvent: decodeExceptionEvent(json.exceptionEvent)
    };
  }
  throw new Error("No field set in oneof FactorInvoiceAuditCheck_Details");
}
export function encodeFactorInvoiceAuditCheck_Details(value) {
  switch (value.type) {
    case "ruleCheck" /* RuleCheck */:
      return { ruleCheck: encodeFactorInvoiceAuditRuleCheck(value.ruleCheck) };
    case "exceptionEvent" /* ExceptionEvent */:
      return { exceptionEvent: encodeExceptionEvent(value.exceptionEvent) };
    default:
      throw new Error("No field set in oneof FactorInvoiceAuditCheck_Details");
  }
}
export var FactorInvoiceAuditCheckResult = /* @__PURE__ */ ((FactorInvoiceAuditCheckResult2) => {
  FactorInvoiceAuditCheckResult2["FactorInvoiceAuditCheckResultSkipped"] = "skipped";
  FactorInvoiceAuditCheckResult2["FactorInvoiceAuditCheckResultPassed"] = "passed";
  FactorInvoiceAuditCheckResult2["FactorInvoiceAuditCheckResultFailed"] = "failed";
  FactorInvoiceAuditCheckResult2["FactorInvoiceAuditCheckResultIgnored"] = "ignored";
  FactorInvoiceAuditCheckResult2["FactorInvoiceAuditCheckResultResolved"] = "resolved";
  FactorInvoiceAuditCheckResult2["FactorInvoiceAuditCheckResultPending"] = "pending";
  return FactorInvoiceAuditCheckResult2;
})(FactorInvoiceAuditCheckResult || {});
export const decodeFactorInvoiceAuditCheckResult = (value) => value;
export const encodeFactorInvoiceAuditCheckResult = (value) => value;
export function decodeFactorInvoiceAuditCheckRule(json) {
  return {
    ruleType: assertString(json.ruleType),
    failureType: optionalDecoder(assertString)(json.failureType),
    unskippable: assertBoolean(json.unskippable),
    message: optionalDecoder(assertString)(json.message),
    ruleEvaluationId: assertNumber(json.ruleEvaluationId),
    status: decodeRuleEvaluationStatus(json.status),
    confirmable: assertBoolean(json.confirmable)
  };
}
export function encodeFactorInvoiceAuditCheckRule(value) {
  return {
    ruleType: value.ruleType,
    failureType: value.failureType,
    unskippable: value.unskippable,
    message: value.message,
    ruleEvaluationId: value.ruleEvaluationId,
    status: encodeRuleEvaluationStatus(value.status),
    confirmable: value.confirmable
  };
}
export function decodeFactorInvoiceAuditCheckSuggestion(json) {
  return {
    source: decodeSuggestionSource(json.source),
    value: assertString(json.value),
    location: optionalDecoder(decodeDocumentLocation)(json.location),
    entityId: optionalDecoder(assertString)(json.entityId),
    recommended: assertBoolean(json.recommended),
    reasoning: optionalDecoder(assertString)(json.reasoning)
  };
}
export function encodeFactorInvoiceAuditCheckSuggestion(value) {
  return {
    source: encodeSuggestionSource(value.source),
    value: value.value,
    location: optionalEncoder(encodeDocumentLocation)(value.location),
    entityId: value.entityId,
    recommended: value.recommended,
    reasoning: value.reasoning
  };
}
export function decodeFactorInvoiceAuditCheckType(json) {
  return {
    check: decodeFactorInvoiceAuditCheckType_Check(json)
  };
}
export function encodeFactorInvoiceAuditCheckType(value) {
  return __spreadValues({}, encodeFactorInvoiceAuditCheckType_Check(value.check));
}
export var FactorInvoiceAuditCheckType_CheckType = /* @__PURE__ */ ((FactorInvoiceAuditCheckType_CheckType2) => {
  FactorInvoiceAuditCheckType_CheckType2["InvoiceAmount"] = "invoiceAmount";
  FactorInvoiceAuditCheckType_CheckType2["DocumentPresence"] = "documentPresence";
  FactorInvoiceAuditCheckType_CheckType2["InvoiceDate"] = "invoiceDate";
  FactorInvoiceAuditCheckType_CheckType2["CarrierRequired"] = "carrierRequired";
  FactorInvoiceAuditCheckType_CheckType2["DebtorRequired"] = "debtorRequired";
  FactorInvoiceAuditCheckType_CheckType2["LoadIdExtractionAndConfirmation"] = "loadIdExtractionAndConfirmation";
  FactorInvoiceAuditCheckType_CheckType2["InvoiceNumber"] = "invoiceNumber";
  FactorInvoiceAuditCheckType_CheckType2["DuplicateInvoiceCheck"] = "duplicateInvoiceCheck";
  FactorInvoiceAuditCheckType_CheckType2["RateConBolConsistency"] = "rateConBolConsistency";
  FactorInvoiceAuditCheckType_CheckType2["BrokerClientRequirements"] = "brokerClientRequirements";
  FactorInvoiceAuditCheckType_CheckType2["FactorLaneRate"] = "factorLaneRate";
  FactorInvoiceAuditCheckType_CheckType2["FundingInstructionsDetected"] = "fundingInstructionsDetected";
  FactorInvoiceAuditCheckType_CheckType2["ClientDebtorRelationship"] = "clientDebtorRelationship";
  FactorInvoiceAuditCheckType_CheckType2["InvoiceDeliveryDate"] = "invoiceDeliveryDate";
  FactorInvoiceAuditCheckType_CheckType2["BrokerClientBrokeredCarrier"] = "brokerClientBrokeredCarrier";
  FactorInvoiceAuditCheckType_CheckType2["BrokerClientBrokeredCarrierTerms"] = "brokerClientBrokeredCarrierTerms";
  FactorInvoiceAuditCheckType_CheckType2["PODRequired"] = "podRequired";
  FactorInvoiceAuditCheckType_CheckType2["MaxAmountReview"] = "maxAmountReview";
  FactorInvoiceAuditCheckType_CheckType2["BrokerClientCarrierInvoiceNumber"] = "brokerClientCarrierInvoiceNumber";
  return FactorInvoiceAuditCheckType_CheckType2;
})(FactorInvoiceAuditCheckType_CheckType || {});
export function decodeFactorInvoiceAuditCheckType_Check(json) {
  if (json.invoiceAmount !== void 0) {
    return {
      type: "invoiceAmount" /* InvoiceAmount */,
      invoiceAmount: decodeInvoiceAmountCheck(json.invoiceAmount)
    };
  } else if (json.documentPresence !== void 0) {
    return {
      type: "documentPresence" /* DocumentPresence */,
      documentPresence: decodeDocumentPresenceCheck(json.documentPresence)
    };
  } else if (json.invoiceDate !== void 0) {
    return {
      type: "invoiceDate" /* InvoiceDate */,
      invoiceDate: decodeInvoiceDateCheck(json.invoiceDate)
    };
  } else if (json.carrierRequired !== void 0) {
    return {
      type: "carrierRequired" /* CarrierRequired */,
      carrierRequired: decodeCarrierRequiredCheck(json.carrierRequired)
    };
  } else if (json.debtorRequired !== void 0) {
    return {
      type: "debtorRequired" /* DebtorRequired */,
      debtorRequired: decodeDebtorRequiredCheck(json.debtorRequired)
    };
  } else if (json.loadIdExtractionAndConfirmation !== void 0) {
    return {
      type: "loadIdExtractionAndConfirmation" /* LoadIdExtractionAndConfirmation */,
      loadIdExtractionAndConfirmation: decodeLoadIdExtractionAndConfirmationCheck(
        json.loadIdExtractionAndConfirmation
      )
    };
  } else if (json.invoiceNumber !== void 0) {
    return {
      type: "invoiceNumber" /* InvoiceNumber */,
      invoiceNumber: decodeInvoiceNumberCheck(json.invoiceNumber)
    };
  } else if (json.duplicateInvoiceCheck !== void 0) {
    return {
      type: "duplicateInvoiceCheck" /* DuplicateInvoiceCheck */,
      duplicateInvoiceCheck: decodeDuplicateInvoiceCheck(
        json.duplicateInvoiceCheck
      )
    };
  } else if (json.rateConBolConsistency !== void 0) {
    return {
      type: "rateConBolConsistency" /* RateConBolConsistency */,
      rateConBolConsistency: decodeRateConBolConsistencyCheck(
        json.rateConBolConsistency
      )
    };
  } else if (json.brokerClientRequirements !== void 0) {
    return {
      type: "brokerClientRequirements" /* BrokerClientRequirements */,
      brokerClientRequirements: decodeBrokerClientRequirementsCheck(
        json.brokerClientRequirements
      )
    };
  } else if (json.factorLaneRate !== void 0) {
    return {
      type: "factorLaneRate" /* FactorLaneRate */,
      factorLaneRate: decodeFactorLaneRateCheck(json.factorLaneRate)
    };
  } else if (json.fundingInstructionsDetected !== void 0) {
    return {
      type: "fundingInstructionsDetected" /* FundingInstructionsDetected */,
      fundingInstructionsDetected: decodeFundingInstructionsDetectedCheck(
        json.fundingInstructionsDetected
      )
    };
  } else if (json.clientDebtorRelationship !== void 0) {
    return {
      type: "clientDebtorRelationship" /* ClientDebtorRelationship */,
      clientDebtorRelationship: decodeClientDebtorRelationshipCheck(
        json.clientDebtorRelationship
      )
    };
  } else if (json.invoiceDeliveryDate !== void 0) {
    return {
      type: "invoiceDeliveryDate" /* InvoiceDeliveryDate */,
      invoiceDeliveryDate: decodeInvoiceDeliveryDateCheck(
        json.invoiceDeliveryDate
      )
    };
  } else if (json.brokerClientBrokeredCarrier !== void 0) {
    return {
      type: "brokerClientBrokeredCarrier" /* BrokerClientBrokeredCarrier */,
      brokerClientBrokeredCarrier: decodeBrokerClientBrokeredCarrierCheck(
        json.brokerClientBrokeredCarrier
      )
    };
  } else if (json.brokerClientBrokeredCarrierTerms !== void 0) {
    return {
      type: "brokerClientBrokeredCarrierTerms" /* BrokerClientBrokeredCarrierTerms */,
      brokerClientBrokeredCarrierTerms: decodeBrokerClientBrokeredCarrierTermsCheck(
        json.brokerClientBrokeredCarrierTerms
      )
    };
  } else if (json.podRequired !== void 0) {
    return {
      type: "podRequired" /* PODRequired */,
      podRequired: decodePODRequiredCheck(json.podRequired)
    };
  } else if (json.maxAmountReview !== void 0) {
    return {
      type: "maxAmountReview" /* MaxAmountReview */,
      maxAmountReview: decodeMaxAmountReviewCheck(json.maxAmountReview)
    };
  } else if (json.brokerClientCarrierInvoiceNumber !== void 0) {
    return {
      type: "brokerClientCarrierInvoiceNumber" /* BrokerClientCarrierInvoiceNumber */,
      brokerClientCarrierInvoiceNumber: decodeBrokerClientCarrierInvoiceNumberCheck(
        json.brokerClientCarrierInvoiceNumber
      )
    };
  }
  throw new Error("No field set in oneof FactorInvoiceAuditCheckType_Check");
}
export function encodeFactorInvoiceAuditCheckType_Check(value) {
  switch (value.type) {
    case "invoiceAmount" /* InvoiceAmount */:
      return { invoiceAmount: encodeInvoiceAmountCheck(value.invoiceAmount) };
    case "documentPresence" /* DocumentPresence */:
      return {
        documentPresence: encodeDocumentPresenceCheck(value.documentPresence)
      };
    case "invoiceDate" /* InvoiceDate */:
      return { invoiceDate: encodeInvoiceDateCheck(value.invoiceDate) };
    case "carrierRequired" /* CarrierRequired */:
      return {
        carrierRequired: encodeCarrierRequiredCheck(value.carrierRequired)
      };
    case "debtorRequired" /* DebtorRequired */:
      return {
        debtorRequired: encodeDebtorRequiredCheck(value.debtorRequired)
      };
    case "loadIdExtractionAndConfirmation" /* LoadIdExtractionAndConfirmation */:
      return {
        loadIdExtractionAndConfirmation: encodeLoadIdExtractionAndConfirmationCheck(
          value.loadIdExtractionAndConfirmation
        )
      };
    case "invoiceNumber" /* InvoiceNumber */:
      return { invoiceNumber: encodeInvoiceNumberCheck(value.invoiceNumber) };
    case "duplicateInvoiceCheck" /* DuplicateInvoiceCheck */:
      return {
        duplicateInvoiceCheck: encodeDuplicateInvoiceCheck(
          value.duplicateInvoiceCheck
        )
      };
    case "rateConBolConsistency" /* RateConBolConsistency */:
      return {
        rateConBolConsistency: encodeRateConBolConsistencyCheck(
          value.rateConBolConsistency
        )
      };
    case "brokerClientRequirements" /* BrokerClientRequirements */:
      return {
        brokerClientRequirements: encodeBrokerClientRequirementsCheck(
          value.brokerClientRequirements
        )
      };
    case "factorLaneRate" /* FactorLaneRate */:
      return {
        factorLaneRate: encodeFactorLaneRateCheck(value.factorLaneRate)
      };
    case "fundingInstructionsDetected" /* FundingInstructionsDetected */:
      return {
        fundingInstructionsDetected: encodeFundingInstructionsDetectedCheck(
          value.fundingInstructionsDetected
        )
      };
    case "clientDebtorRelationship" /* ClientDebtorRelationship */:
      return {
        clientDebtorRelationship: encodeClientDebtorRelationshipCheck(
          value.clientDebtorRelationship
        )
      };
    case "invoiceDeliveryDate" /* InvoiceDeliveryDate */:
      return {
        invoiceDeliveryDate: encodeInvoiceDeliveryDateCheck(
          value.invoiceDeliveryDate
        )
      };
    case "brokerClientBrokeredCarrier" /* BrokerClientBrokeredCarrier */:
      return {
        brokerClientBrokeredCarrier: encodeBrokerClientBrokeredCarrierCheck(
          value.brokerClientBrokeredCarrier
        )
      };
    case "brokerClientBrokeredCarrierTerms" /* BrokerClientBrokeredCarrierTerms */:
      return {
        brokerClientBrokeredCarrierTerms: encodeBrokerClientBrokeredCarrierTermsCheck(
          value.brokerClientBrokeredCarrierTerms
        )
      };
    case "podRequired" /* PODRequired */:
      return { podRequired: encodePODRequiredCheck(value.podRequired) };
    case "maxAmountReview" /* MaxAmountReview */:
      return {
        maxAmountReview: encodeMaxAmountReviewCheck(value.maxAmountReview)
      };
    case "brokerClientCarrierInvoiceNumber" /* BrokerClientCarrierInvoiceNumber */:
      return {
        brokerClientCarrierInvoiceNumber: encodeBrokerClientCarrierInvoiceNumberCheck(
          value.brokerClientCarrierInvoiceNumber
        )
      };
    default:
      throw new Error(
        "No field set in oneof FactorInvoiceAuditCheckType_Check"
      );
  }
}
export function decodeFactorInvoiceAuditRuleCheck(json) {
  return {
    checkType: decodeFactorInvoiceAuditCheckType(json.checkType),
    failedRules: (json.failedRules || []).map(
      decodeFactorInvoiceAuditCheckRule
    ),
    resolvedRules: (json.resolvedRules || []).map(
      decodeFactorInvoiceAuditCheckRule
    ),
    ignoredRules: (json.ignoredRules || []).map(
      decodeFactorInvoiceAuditCheckRule
    )
  };
}
export function encodeFactorInvoiceAuditRuleCheck(value) {
  return {
    checkType: encodeFactorInvoiceAuditCheckType(value.checkType),
    failedRules: value.failedRules.map(encodeFactorInvoiceAuditCheckRule),
    resolvedRules: value.resolvedRules.map(encodeFactorInvoiceAuditCheckRule),
    ignoredRules: value.ignoredRules.map(encodeFactorInvoiceAuditCheckRule)
  };
}
export function decodeFactorInvoiceBatchCallbackOptions(json) {
  return {
    batch_id: assertNumber(json.batch_id),
    carrier_id: assertNumber(json.carrier_id),
    sender_email: optionalDecoder(assertString)(json.sender_email),
    subject: optionalDecoder(assertString)(json.subject)
  };
}
export function encodeFactorInvoiceBatchCallbackOptions(value) {
  return {
    batch_id: value.batch_id,
    carrier_id: value.carrier_id,
    sender_email: value.sender_email,
    subject: value.subject
  };
}
export function decodeFactorInvoiceDocumentPacketComparison(json) {
  return {
    rows: (json.rows || []).map(decodeDocumentPacketComparisonRow),
    reported_overlap_floor: assertNumber(json.reported_overlap_floor)
  };
}
export function encodeFactorInvoiceDocumentPacketComparison(value) {
  return {
    rows: value.rows.map(encodeDocumentPacketComparisonRow),
    reported_overlap_floor: value.reported_overlap_floor
  };
}
export function decodeFactorInvoiceDocumentPacketComparisonQuery(json) {
  return {
    factor_invoice_id: assertNumber(json.factor_invoice_id),
    other_invoice_id: assertNumber(json.other_invoice_id)
  };
}
export function encodeFactorInvoiceDocumentPacketComparisonQuery(value) {
  return {
    factor_invoice_id: value.factor_invoice_id,
    other_invoice_id: value.other_invoice_id
  };
}
export var FactorInvoiceFieldSourceKind = /* @__PURE__ */ ((FactorInvoiceFieldSourceKind2) => {
  FactorInvoiceFieldSourceKind2["FactorInvoiceFieldSourceKindPrefilledBySystem"] = "prefilled_by_system";
  FactorInvoiceFieldSourceKind2["FactorInvoiceFieldSourceKindChangedByUser"] = "changed_by_user";
  FactorInvoiceFieldSourceKind2["FactorInvoiceFieldSourceKindChangedBySupport"] = "changed_by_support";
  return FactorInvoiceFieldSourceKind2;
})(FactorInvoiceFieldSourceKind || {});
export const decodeFactorInvoiceFieldSourceKind = (value) => value;
export const encodeFactorInvoiceFieldSourceKind = (value) => value;
export function decodeFactorInvoiceFieldUpdate(json) {
  return {
    update: decodeFactorInvoiceFieldUpdate_Update(json)
  };
}
export function encodeFactorInvoiceFieldUpdate(value) {
  return __spreadValues({}, encodeFactorInvoiceFieldUpdate_Update(value.update));
}
export var FactorInvoiceFieldUpdate_UpdateType = /* @__PURE__ */ ((FactorInvoiceFieldUpdate_UpdateType2) => {
  FactorInvoiceFieldUpdate_UpdateType2["AmountUpdate"] = "amount_update";
  FactorInvoiceFieldUpdate_UpdateType2["InvoiceDateUpdate"] = "invoice_date_update";
  FactorInvoiceFieldUpdate_UpdateType2["CarrierUpdate"] = "carrier_update";
  FactorInvoiceFieldUpdate_UpdateType2["DebtorUpdate"] = "debtor_update";
  FactorInvoiceFieldUpdate_UpdateType2["LoadIdUpdate"] = "load_id_update";
  FactorInvoiceFieldUpdate_UpdateType2["InvoiceNumberUpdate"] = "invoice_number_update";
  FactorInvoiceFieldUpdate_UpdateType2["CarrierAmountUpdate"] = "carrier_amount_update";
  FactorInvoiceFieldUpdate_UpdateType2["BrokeredCarrierUpdate"] = "brokered_carrier_update";
  FactorInvoiceFieldUpdate_UpdateType2["BrokeredCarrierTermUpdate"] = "brokered_carrier_term_update";
  FactorInvoiceFieldUpdate_UpdateType2["NotesUpdate"] = "notes_update";
  FactorInvoiceFieldUpdate_UpdateType2["MustVerifyUpdate"] = "must_verify_update";
  FactorInvoiceFieldUpdate_UpdateType2["IsTonu"] = "is_tonu";
  FactorInvoiceFieldUpdate_UpdateType2["DocumentTypeUpdate"] = "document_type_update";
  FactorInvoiceFieldUpdate_UpdateType2["DebtorLoadIdAutofillUpdate"] = "debtor_load_id_autofill_update";
  FactorInvoiceFieldUpdate_UpdateType2["CarrierInvoiceNumberUpdate"] = "carrier_invoice_number_update";
  return FactorInvoiceFieldUpdate_UpdateType2;
})(FactorInvoiceFieldUpdate_UpdateType || {});
export function decodeFactorInvoiceFieldUpdate_Update(json) {
  if (json.amount_update !== void 0) {
    return {
      type: "amount_update" /* AmountUpdate */,
      amount_update: decodeFactorInvoiceFieldUpdateAmount(json.amount_update)
    };
  } else if (json.invoice_date_update !== void 0) {
    return {
      type: "invoice_date_update" /* InvoiceDateUpdate */,
      invoice_date_update: decodeFactorInvoiceFieldUpdateInvoiceDate(
        json.invoice_date_update
      )
    };
  } else if (json.carrier_update !== void 0) {
    return {
      type: "carrier_update" /* CarrierUpdate */,
      carrier_update: decodeFactorInvoiceFieldUpdateCarrier(
        json.carrier_update
      )
    };
  } else if (json.debtor_update !== void 0) {
    return {
      type: "debtor_update" /* DebtorUpdate */,
      debtor_update: decodeFactorInvoiceFieldUpdateDebtor(json.debtor_update)
    };
  } else if (json.load_id_update !== void 0) {
    return {
      type: "load_id_update" /* LoadIdUpdate */,
      load_id_update: decodeFactorInvoiceFieldUpdateLoadId(json.load_id_update)
    };
  } else if (json.invoice_number_update !== void 0) {
    return {
      type: "invoice_number_update" /* InvoiceNumberUpdate */,
      invoice_number_update: decodeFactorInvoiceFieldUpdateInvoiceNumber(
        json.invoice_number_update
      )
    };
  } else if (json.carrier_amount_update !== void 0) {
    return {
      type: "carrier_amount_update" /* CarrierAmountUpdate */,
      carrier_amount_update: decodeFactorInvoiceFieldUpdateCarrierAmount(
        json.carrier_amount_update
      )
    };
  } else if (json.brokered_carrier_update !== void 0) {
    return {
      type: "brokered_carrier_update" /* BrokeredCarrierUpdate */,
      brokered_carrier_update: decodeFactorInvoiceFieldUpdateBrokeredCarrier(
        json.brokered_carrier_update
      )
    };
  } else if (json.brokered_carrier_term_update !== void 0) {
    return {
      type: "brokered_carrier_term_update" /* BrokeredCarrierTermUpdate */,
      brokered_carrier_term_update: decodeFactorInvoiceFieldUpdateBrokeredCarrierTerm(
        json.brokered_carrier_term_update
      )
    };
  } else if (json.notes_update !== void 0) {
    return {
      type: "notes_update" /* NotesUpdate */,
      notes_update: decodeFactorInvoiceFieldUpdateNotes(json.notes_update)
    };
  } else if (json.must_verify_update !== void 0) {
    return {
      type: "must_verify_update" /* MustVerifyUpdate */,
      must_verify_update: decodeFactorInvoiceFieldUpdateMustVerify(
        json.must_verify_update
      )
    };
  } else if (json.is_tonu !== void 0) {
    return {
      type: "is_tonu" /* IsTonu */,
      is_tonu: decodeFactorInvoiceFieldUpdateIsTonu(json.is_tonu)
    };
  } else if (json.document_type_update !== void 0) {
    return {
      type: "document_type_update" /* DocumentTypeUpdate */,
      document_type_update: decodeFactorInvoiceFieldUpdateDocumentType(
        json.document_type_update
      )
    };
  } else if (json.debtor_load_id_autofill_update !== void 0) {
    return {
      type: "debtor_load_id_autofill_update" /* DebtorLoadIdAutofillUpdate */,
      debtor_load_id_autofill_update: decodeFactorInvoiceFieldUpdateDebtorLoadIdAutofill(
        json.debtor_load_id_autofill_update
      )
    };
  } else if (json.carrier_invoice_number_update !== void 0) {
    return {
      type: "carrier_invoice_number_update" /* CarrierInvoiceNumberUpdate */,
      carrier_invoice_number_update: decodeFactorInvoiceFieldUpdateCarrierInvoiceNumber(
        json.carrier_invoice_number_update
      )
    };
  }
  throw new Error("No field set in oneof FactorInvoiceFieldUpdate_Update");
}
export function encodeFactorInvoiceFieldUpdate_Update(value) {
  switch (value.type) {
    case "amount_update" /* AmountUpdate */:
      return {
        amount_update: encodeFactorInvoiceFieldUpdateAmount(
          value.amount_update
        )
      };
    case "invoice_date_update" /* InvoiceDateUpdate */:
      return {
        invoice_date_update: encodeFactorInvoiceFieldUpdateInvoiceDate(
          value.invoice_date_update
        )
      };
    case "carrier_update" /* CarrierUpdate */:
      return {
        carrier_update: encodeFactorInvoiceFieldUpdateCarrier(
          value.carrier_update
        )
      };
    case "debtor_update" /* DebtorUpdate */:
      return {
        debtor_update: encodeFactorInvoiceFieldUpdateDebtor(
          value.debtor_update
        )
      };
    case "load_id_update" /* LoadIdUpdate */:
      return {
        load_id_update: encodeFactorInvoiceFieldUpdateLoadId(
          value.load_id_update
        )
      };
    case "invoice_number_update" /* InvoiceNumberUpdate */:
      return {
        invoice_number_update: encodeFactorInvoiceFieldUpdateInvoiceNumber(
          value.invoice_number_update
        )
      };
    case "carrier_amount_update" /* CarrierAmountUpdate */:
      return {
        carrier_amount_update: encodeFactorInvoiceFieldUpdateCarrierAmount(
          value.carrier_amount_update
        )
      };
    case "brokered_carrier_update" /* BrokeredCarrierUpdate */:
      return {
        brokered_carrier_update: encodeFactorInvoiceFieldUpdateBrokeredCarrier(
          value.brokered_carrier_update
        )
      };
    case "brokered_carrier_term_update" /* BrokeredCarrierTermUpdate */:
      return {
        brokered_carrier_term_update: encodeFactorInvoiceFieldUpdateBrokeredCarrierTerm(
          value.brokered_carrier_term_update
        )
      };
    case "notes_update" /* NotesUpdate */:
      return {
        notes_update: encodeFactorInvoiceFieldUpdateNotes(value.notes_update)
      };
    case "must_verify_update" /* MustVerifyUpdate */:
      return {
        must_verify_update: encodeFactorInvoiceFieldUpdateMustVerify(
          value.must_verify_update
        )
      };
    case "is_tonu" /* IsTonu */:
      return { is_tonu: encodeFactorInvoiceFieldUpdateIsTonu(value.is_tonu) };
    case "document_type_update" /* DocumentTypeUpdate */:
      return {
        document_type_update: encodeFactorInvoiceFieldUpdateDocumentType(
          value.document_type_update
        )
      };
    case "debtor_load_id_autofill_update" /* DebtorLoadIdAutofillUpdate */:
      return {
        debtor_load_id_autofill_update: encodeFactorInvoiceFieldUpdateDebtorLoadIdAutofill(
          value.debtor_load_id_autofill_update
        )
      };
    case "carrier_invoice_number_update" /* CarrierInvoiceNumberUpdate */:
      return {
        carrier_invoice_number_update: encodeFactorInvoiceFieldUpdateCarrierInvoiceNumber(
          value.carrier_invoice_number_update
        )
      };
    default:
      throw new Error("No field set in oneof FactorInvoiceFieldUpdate_Update");
  }
}
export function decodeFactorInvoiceFieldUpdateAmount(json) {
  return {
    amount: assertString(json.amount)
  };
}
export function encodeFactorInvoiceFieldUpdateAmount(value) {
  return {
    amount: value.amount
  };
}
export function decodeFactorInvoiceFieldUpdateBrokeredCarrier(json) {
  return {
    brokeredCarrierId: assertNumber(json.brokeredCarrierId)
  };
}
export function encodeFactorInvoiceFieldUpdateBrokeredCarrier(value) {
  return {
    brokeredCarrierId: value.brokeredCarrierId
  };
}
export function decodeFactorInvoiceFieldUpdateBrokeredCarrierTerm(json) {
  return {
    brokeredCarrierTermId: assertNumber(json.brokeredCarrierTermId)
  };
}
export function encodeFactorInvoiceFieldUpdateBrokeredCarrierTerm(value) {
  return {
    brokeredCarrierTermId: value.brokeredCarrierTermId
  };
}
export function decodeFactorInvoiceFieldUpdateCarrier(json) {
  return {
    carrierId: assertNumber(json.carrierId)
  };
}
export function encodeFactorInvoiceFieldUpdateCarrier(value) {
  return {
    carrierId: value.carrierId
  };
}
export function decodeFactorInvoiceFieldUpdateCarrierAmount(json) {
  return {
    amount: assertString(json.amount)
  };
}
export function encodeFactorInvoiceFieldUpdateCarrierAmount(value) {
  return {
    amount: value.amount
  };
}
export function decodeFactorInvoiceFieldUpdateCarrierInvoiceNumber(json) {
  return {
    carrierInvoiceNumber: assertString(json.carrierInvoiceNumber)
  };
}
export function encodeFactorInvoiceFieldUpdateCarrierInvoiceNumber(value) {
  return {
    carrierInvoiceNumber: value.carrierInvoiceNumber
  };
}
export function decodeFactorInvoiceFieldUpdateDebtor(json) {
  return {
    factorBrokerId: assertNumber(json.factorBrokerId)
  };
}
export function encodeFactorInvoiceFieldUpdateDebtor(value) {
  return {
    factorBrokerId: value.factorBrokerId
  };
}
export function decodeFactorInvoiceFieldUpdateDebtorLoadIdAutofill(json) {
  return {
    enabled: assertBoolean(json.enabled)
  };
}
export function encodeFactorInvoiceFieldUpdateDebtorLoadIdAutofill(value) {
  return {
    enabled: value.enabled
  };
}
export function decodeFactorInvoiceFieldUpdateDocumentType(json) {
  return {
    documentId: assertNumber(json.documentId),
    documentType: decodeDocumentType(json.documentType)
  };
}
export function encodeFactorInvoiceFieldUpdateDocumentType(value) {
  return {
    documentId: value.documentId,
    documentType: encodeDocumentType(value.documentType)
  };
}
export function decodeFactorInvoiceFieldUpdateInvoiceDate(json) {
  return {
    invoiceDate: assertString(json.invoiceDate)
  };
}
export function encodeFactorInvoiceFieldUpdateInvoiceDate(value) {
  return {
    invoiceDate: value.invoiceDate
  };
}
export function decodeFactorInvoiceFieldUpdateInvoiceNumber(json) {
  return {
    invoiceNumber: assertString(json.invoiceNumber)
  };
}
export function encodeFactorInvoiceFieldUpdateInvoiceNumber(value) {
  return {
    invoiceNumber: value.invoiceNumber
  };
}
export function decodeFactorInvoiceFieldUpdateIsTonu(json) {
  return {
    isTonu: assertBoolean(json.isTonu)
  };
}
export function encodeFactorInvoiceFieldUpdateIsTonu(value) {
  return {
    isTonu: value.isTonu
  };
}
export function decodeFactorInvoiceFieldUpdateLoadId(json) {
  return {
    loadId: assertString(json.loadId)
  };
}
export function encodeFactorInvoiceFieldUpdateLoadId(value) {
  return {
    loadId: value.loadId
  };
}
export function decodeFactorInvoiceFieldUpdateMustVerify(json) {
  return {
    mustVerify: assertBoolean(json.mustVerify)
  };
}
export function encodeFactorInvoiceFieldUpdateMustVerify(value) {
  return {
    mustVerify: value.mustVerify
  };
}
export function decodeFactorInvoiceFieldUpdateNotes(json) {
  return {
    notes: assertString(json.notes)
  };
}
export function encodeFactorInvoiceFieldUpdateNotes(value) {
  return {
    notes: value.notes
  };
}
export function decodeFactorInvoicePossibleMatch(json) {
  return {
    id: assertNumber(json.id),
    invoice_number: optionalDecoder(assertString)(json.invoice_number),
    formatted_amount: optionalDecoder(assertString)(json.formatted_amount),
    load_id: optionalDecoder(assertString)(json.load_id),
    debtor_name: optionalDecoder(assertString)(json.debtor_name),
    email_subject: optionalDecoder(assertString)(json.email_subject),
    document_types: (json.document_types || []).map(
      decodeFactorInvoicePossibleMatchDocType
    ),
    origin_matches: assertBoolean(json.origin_matches),
    destination_matches: assertBoolean(json.destination_matches),
    matched_references: (json.matched_references || []).map(assertString),
    email_date: optionalDecoder(decodeTimestamp)(json.email_date),
    origin: optionalDecoder(assertString)(json.origin),
    destination: optionalDecoder(assertString)(json.destination)
  };
}
export function encodeFactorInvoicePossibleMatch(value) {
  return {
    id: value.id,
    invoice_number: value.invoice_number,
    formatted_amount: value.formatted_amount,
    load_id: value.load_id,
    debtor_name: value.debtor_name,
    email_subject: value.email_subject,
    document_types: value.document_types.map(
      encodeFactorInvoicePossibleMatchDocType
    ),
    origin_matches: value.origin_matches,
    destination_matches: value.destination_matches,
    matched_references: value.matched_references,
    email_date: optionalEncoder(encodeTimestamp)(value.email_date),
    origin: value.origin,
    destination: value.destination
  };
}
export function decodeFactorInvoicePossibleMatchDocType(json) {
  return {
    document_type: decodeUnresolvedDocumentType(json.document_type),
    count: assertNumber(json.count)
  };
}
export function encodeFactorInvoicePossibleMatchDocType(value) {
  return {
    document_type: encodeUnresolvedDocumentType(value.document_type),
    count: value.count
  };
}
export function decodeFactorInvoiceServiceAbandonRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId),
    reason: assertString(json.reason)
  };
}
export function encodeFactorInvoiceServiceAbandonRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId,
    reason: value.reason
  };
}
export function decodeFactorInvoiceServiceAbandonResponse(json) {
  return {
    outcome: decodeFactorInvoiceServiceAbandonResponse_Outcome(json)
  };
}
export function encodeFactorInvoiceServiceAbandonResponse(value) {
  return __spreadValues({}, encodeFactorInvoiceServiceAbandonResponse_Outcome(value.outcome));
}
export var FactorInvoiceServiceAbandonResponse_OutcomeType = /* @__PURE__ */ ((FactorInvoiceServiceAbandonResponse_OutcomeType2) => {
  FactorInvoiceServiceAbandonResponse_OutcomeType2["Success"] = "success";
  FactorInvoiceServiceAbandonResponse_OutcomeType2["Error"] = "error";
  return FactorInvoiceServiceAbandonResponse_OutcomeType2;
})(FactorInvoiceServiceAbandonResponse_OutcomeType || {});
export function decodeFactorInvoiceServiceAbandonResponse_Outcome(json) {
  if (json.success !== void 0) {
    return {
      type: "success" /* Success */,
      success: decodeFactorInvoiceServiceAbandonSuccess(json.success)
    };
  } else if (json.error !== void 0) {
    return {
      type: "error" /* Error */,
      error: decodeFactorInvoiceServiceUpdateError(json.error)
    };
  }
  throw new Error(
    "No field set in oneof FactorInvoiceServiceAbandonResponse_Outcome"
  );
}
export function encodeFactorInvoiceServiceAbandonResponse_Outcome(value) {
  switch (value.type) {
    case "success" /* Success */:
      return {
        success: encodeFactorInvoiceServiceAbandonSuccess(value.success)
      };
    case "error" /* Error */:
      return { error: encodeFactorInvoiceServiceUpdateError(value.error) };
    default:
      throw new Error(
        "No field set in oneof FactorInvoiceServiceAbandonResponse_Outcome"
      );
  }
}
export function decodeFactorInvoiceServiceAbandonSuccess(json) {
  return {};
}
export function encodeFactorInvoiceServiceAbandonSuccess(value) {
  return {};
}
export function decodeFactorInvoiceServiceCorrectDebtorRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId),
    factorBrokerId: assertNumber(json.factorBrokerId),
    notes: optionalDecoder(assertString)(json.notes)
  };
}
export function encodeFactorInvoiceServiceCorrectDebtorRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId,
    factorBrokerId: value.factorBrokerId,
    notes: value.notes
  };
}
export function decodeFactorInvoiceServiceCorrectDebtorResponse(json) {
  return {
    outcome: decodeFactorInvoiceServiceCorrectDebtorResponse_Outcome(json)
  };
}
export function encodeFactorInvoiceServiceCorrectDebtorResponse(value) {
  return __spreadValues({}, encodeFactorInvoiceServiceCorrectDebtorResponse_Outcome(value.outcome));
}
export var FactorInvoiceServiceCorrectDebtorResponse_OutcomeType = /* @__PURE__ */ ((FactorInvoiceServiceCorrectDebtorResponse_OutcomeType2) => {
  FactorInvoiceServiceCorrectDebtorResponse_OutcomeType2["Success"] = "success";
  FactorInvoiceServiceCorrectDebtorResponse_OutcomeType2["Error"] = "error";
  return FactorInvoiceServiceCorrectDebtorResponse_OutcomeType2;
})(FactorInvoiceServiceCorrectDebtorResponse_OutcomeType || {});
export function decodeFactorInvoiceServiceCorrectDebtorResponse_Outcome(json) {
  if (json.success !== void 0) {
    return {
      type: "success" /* Success */,
      success: decodeFactorInvoiceServiceCorrectDebtorSuccess(json.success)
    };
  } else if (json.error !== void 0) {
    return {
      type: "error" /* Error */,
      error: decodeFactorInvoiceServiceUpdateError(json.error)
    };
  }
  throw new Error(
    "No field set in oneof FactorInvoiceServiceCorrectDebtorResponse_Outcome"
  );
}
export function encodeFactorInvoiceServiceCorrectDebtorResponse_Outcome(value) {
  switch (value.type) {
    case "success" /* Success */:
      return {
        success: encodeFactorInvoiceServiceCorrectDebtorSuccess(value.success)
      };
    case "error" /* Error */:
      return { error: encodeFactorInvoiceServiceUpdateError(value.error) };
    default:
      throw new Error(
        "No field set in oneof FactorInvoiceServiceCorrectDebtorResponse_Outcome"
      );
  }
}
export function decodeFactorInvoiceServiceCorrectDebtorSuccess(json) {
  return {};
}
export function encodeFactorInvoiceServiceCorrectDebtorSuccess(value) {
  return {};
}
export function decodeFactorInvoiceServiceCreateExceptionRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId),
    message: assertString(json.message)
  };
}
export function encodeFactorInvoiceServiceCreateExceptionRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId,
    message: value.message
  };
}
export function decodeFactorInvoiceServiceCreateExceptionResponse(json) {
  return {
    outcome: decodeFactorInvoiceServiceCreateExceptionResponse_Outcome(json)
  };
}
export function encodeFactorInvoiceServiceCreateExceptionResponse(value) {
  return __spreadValues({}, encodeFactorInvoiceServiceCreateExceptionResponse_Outcome(value.outcome));
}
export var FactorInvoiceServiceCreateExceptionResponse_OutcomeType = /* @__PURE__ */ ((FactorInvoiceServiceCreateExceptionResponse_OutcomeType2) => {
  FactorInvoiceServiceCreateExceptionResponse_OutcomeType2["Success"] = "success";
  FactorInvoiceServiceCreateExceptionResponse_OutcomeType2["Error"] = "error";
  return FactorInvoiceServiceCreateExceptionResponse_OutcomeType2;
})(FactorInvoiceServiceCreateExceptionResponse_OutcomeType || {});
export function decodeFactorInvoiceServiceCreateExceptionResponse_Outcome(json) {
  if (json.success !== void 0) {
    return {
      type: "success" /* Success */,
      success: decodeFactorInvoiceServiceCreateExceptionSuccess(json.success)
    };
  } else if (json.error !== void 0) {
    return {
      type: "error" /* Error */,
      error: decodeFactorInvoiceServiceUpdateError(json.error)
    };
  }
  throw new Error(
    "No field set in oneof FactorInvoiceServiceCreateExceptionResponse_Outcome"
  );
}
export function encodeFactorInvoiceServiceCreateExceptionResponse_Outcome(value) {
  switch (value.type) {
    case "success" /* Success */:
      return {
        success: encodeFactorInvoiceServiceCreateExceptionSuccess(
          value.success
        )
      };
    case "error" /* Error */:
      return { error: encodeFactorInvoiceServiceUpdateError(value.error) };
    default:
      throw new Error(
        "No field set in oneof FactorInvoiceServiceCreateExceptionResponse_Outcome"
      );
  }
}
export function decodeFactorInvoiceServiceCreateExceptionSuccess(json) {
  return {};
}
export function encodeFactorInvoiceServiceCreateExceptionSuccess(value) {
  return {};
}
export var FactorInvoiceServiceManuallyAddressRuleFailureAction = /* @__PURE__ */ ((FactorInvoiceServiceManuallyAddressRuleFailureAction2) => {
  FactorInvoiceServiceManuallyAddressRuleFailureAction2["FactorInvoiceServiceManuallyAddressRuleFailureActionIgnore"] = "IGNORE";
  FactorInvoiceServiceManuallyAddressRuleFailureAction2["FactorInvoiceServiceManuallyAddressRuleFailureActionUnignore"] = "UNIGNORE";
  return FactorInvoiceServiceManuallyAddressRuleFailureAction2;
})(FactorInvoiceServiceManuallyAddressRuleFailureAction || {});
export const decodeFactorInvoiceServiceManuallyAddressRuleFailureAction = (value) => value;
export const encodeFactorInvoiceServiceManuallyAddressRuleFailureAction = (value) => value;
export function decodeFactorInvoiceServiceManuallyAddressRuleFailureRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId),
    ruleEvaluationId: assertNumber(json.ruleEvaluationId),
    action: decodeFactorInvoiceServiceManuallyAddressRuleFailureAction(
      json.action
    )
  };
}
export function encodeFactorInvoiceServiceManuallyAddressRuleFailureRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId,
    ruleEvaluationId: value.ruleEvaluationId,
    action: encodeFactorInvoiceServiceManuallyAddressRuleFailureAction(
      value.action
    )
  };
}
export function decodeFactorInvoiceServiceMarkAsProcessedRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId),
    debtorLoadIdAutofill: optionalDecoder(assertBoolean)(
      json.debtorLoadIdAutofill
    )
  };
}
export function encodeFactorInvoiceServiceMarkAsProcessedRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId,
    debtorLoadIdAutofill: value.debtorLoadIdAutofill
  };
}
export function decodeFactorInvoiceServiceMergeFromRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId),
    otherInvoiceId: assertNumber(json.otherInvoiceId)
  };
}
export function encodeFactorInvoiceServiceMergeFromRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId,
    otherInvoiceId: value.otherInvoiceId
  };
}
export function decodeFactorInvoiceServiceMergeFromResponse(json) {
  return {
    outcome: decodeFactorInvoiceServiceMergeFromResponse_Outcome(json)
  };
}
export function encodeFactorInvoiceServiceMergeFromResponse(value) {
  return __spreadValues({}, encodeFactorInvoiceServiceMergeFromResponse_Outcome(value.outcome));
}
export var FactorInvoiceServiceMergeFromResponse_OutcomeType = /* @__PURE__ */ ((FactorInvoiceServiceMergeFromResponse_OutcomeType2) => {
  FactorInvoiceServiceMergeFromResponse_OutcomeType2["Success"] = "success";
  FactorInvoiceServiceMergeFromResponse_OutcomeType2["Error"] = "error";
  return FactorInvoiceServiceMergeFromResponse_OutcomeType2;
})(FactorInvoiceServiceMergeFromResponse_OutcomeType || {});
export function decodeFactorInvoiceServiceMergeFromResponse_Outcome(json) {
  if (json.success !== void 0) {
    return {
      type: "success" /* Success */,
      success: decodeFactorInvoiceServiceMergeFromSuccess(json.success)
    };
  } else if (json.error !== void 0) {
    return {
      type: "error" /* Error */,
      error: decodeFactorInvoiceServiceUpdateError(json.error)
    };
  }
  throw new Error(
    "No field set in oneof FactorInvoiceServiceMergeFromResponse_Outcome"
  );
}
export function encodeFactorInvoiceServiceMergeFromResponse_Outcome(value) {
  switch (value.type) {
    case "success" /* Success */:
      return {
        success: encodeFactorInvoiceServiceMergeFromSuccess(value.success)
      };
    case "error" /* Error */:
      return { error: encodeFactorInvoiceServiceUpdateError(value.error) };
    default:
      throw new Error(
        "No field set in oneof FactorInvoiceServiceMergeFromResponse_Outcome"
      );
  }
}
export function decodeFactorInvoiceServiceMergeFromSuccess(json) {
  return {};
}
export function encodeFactorInvoiceServiceMergeFromSuccess(value) {
  return {};
}
export function decodeFactorInvoiceServicePageLoadedRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId)
  };
}
export function encodeFactorInvoiceServicePageLoadedRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId
  };
}
export function decodeFactorInvoiceServicePageLoadedResponse(json) {
  return {
    outcome: decodeFactorInvoiceServicePageLoadedResponse_Outcome(json)
  };
}
export function encodeFactorInvoiceServicePageLoadedResponse(value) {
  return __spreadValues({}, encodeFactorInvoiceServicePageLoadedResponse_Outcome(value.outcome));
}
export var FactorInvoiceServicePageLoadedResponse_OutcomeType = /* @__PURE__ */ ((FactorInvoiceServicePageLoadedResponse_OutcomeType2) => {
  FactorInvoiceServicePageLoadedResponse_OutcomeType2["Success"] = "success";
  FactorInvoiceServicePageLoadedResponse_OutcomeType2["Error"] = "error";
  return FactorInvoiceServicePageLoadedResponse_OutcomeType2;
})(FactorInvoiceServicePageLoadedResponse_OutcomeType || {});
export function decodeFactorInvoiceServicePageLoadedResponse_Outcome(json) {
  if (json.success !== void 0) {
    return {
      type: "success" /* Success */,
      success: decodeFactorInvoiceServicePageLoadedSuccess(json.success)
    };
  } else if (json.error !== void 0) {
    return {
      type: "error" /* Error */,
      error: decodeFactorInvoiceServiceUpdateError(json.error)
    };
  }
  throw new Error(
    "No field set in oneof FactorInvoiceServicePageLoadedResponse_Outcome"
  );
}
export function encodeFactorInvoiceServicePageLoadedResponse_Outcome(value) {
  switch (value.type) {
    case "success" /* Success */:
      return {
        success: encodeFactorInvoiceServicePageLoadedSuccess(value.success)
      };
    case "error" /* Error */:
      return { error: encodeFactorInvoiceServiceUpdateError(value.error) };
    default:
      throw new Error(
        "No field set in oneof FactorInvoiceServicePageLoadedResponse_Outcome"
      );
  }
}
export function decodeFactorInvoiceServicePageLoadedSuccess(json) {
  return {};
}
export function encodeFactorInvoiceServicePageLoadedSuccess(value) {
  return {};
}
export function decodeFactorInvoiceServiceRejectRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId)
  };
}
export function encodeFactorInvoiceServiceRejectRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId
  };
}
export function decodeFactorInvoiceServiceRejectResponse(json) {
  return {
    outcome: decodeFactorInvoiceServiceRejectResponse_Outcome(json)
  };
}
export function encodeFactorInvoiceServiceRejectResponse(value) {
  return __spreadValues({}, encodeFactorInvoiceServiceRejectResponse_Outcome(value.outcome));
}
export var FactorInvoiceServiceRejectResponse_OutcomeType = /* @__PURE__ */ ((FactorInvoiceServiceRejectResponse_OutcomeType2) => {
  FactorInvoiceServiceRejectResponse_OutcomeType2["Success"] = "success";
  FactorInvoiceServiceRejectResponse_OutcomeType2["Error"] = "error";
  return FactorInvoiceServiceRejectResponse_OutcomeType2;
})(FactorInvoiceServiceRejectResponse_OutcomeType || {});
export function decodeFactorInvoiceServiceRejectResponse_Outcome(json) {
  if (json.success !== void 0) {
    return {
      type: "success" /* Success */,
      success: decodeFactorInvoiceServiceRejectSuccess(json.success)
    };
  } else if (json.error !== void 0) {
    return {
      type: "error" /* Error */,
      error: decodeFactorInvoiceServiceUpdateError(json.error)
    };
  }
  throw new Error(
    "No field set in oneof FactorInvoiceServiceRejectResponse_Outcome"
  );
}
export function encodeFactorInvoiceServiceRejectResponse_Outcome(value) {
  switch (value.type) {
    case "success" /* Success */:
      return {
        success: encodeFactorInvoiceServiceRejectSuccess(value.success)
      };
    case "error" /* Error */:
      return { error: encodeFactorInvoiceServiceUpdateError(value.error) };
    default:
      throw new Error(
        "No field set in oneof FactorInvoiceServiceRejectResponse_Outcome"
      );
  }
}
export function decodeFactorInvoiceServiceRejectSuccess(json) {
  return {};
}
export function encodeFactorInvoiceServiceRejectSuccess(value) {
  return {};
}
export function decodeFactorInvoiceServiceResolveExceptionEventRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId),
    exceptionEventId: assertNumber(json.exceptionEventId),
    resolutionMessage: assertString(json.resolutionMessage)
  };
}
export function encodeFactorInvoiceServiceResolveExceptionEventRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId,
    exceptionEventId: value.exceptionEventId,
    resolutionMessage: value.resolutionMessage
  };
}
export function decodeFactorInvoiceServiceReviveRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId)
  };
}
export function encodeFactorInvoiceServiceReviveRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId
  };
}
export function decodeFactorInvoiceServiceReviveResponse(json) {
  return {
    outcome: decodeFactorInvoiceServiceReviveResponse_Outcome(json)
  };
}
export function encodeFactorInvoiceServiceReviveResponse(value) {
  return __spreadValues({}, encodeFactorInvoiceServiceReviveResponse_Outcome(value.outcome));
}
export var FactorInvoiceServiceReviveResponse_OutcomeType = /* @__PURE__ */ ((FactorInvoiceServiceReviveResponse_OutcomeType2) => {
  FactorInvoiceServiceReviveResponse_OutcomeType2["Success"] = "success";
  FactorInvoiceServiceReviveResponse_OutcomeType2["Error"] = "error";
  return FactorInvoiceServiceReviveResponse_OutcomeType2;
})(FactorInvoiceServiceReviveResponse_OutcomeType || {});
export function decodeFactorInvoiceServiceReviveResponse_Outcome(json) {
  if (json.success !== void 0) {
    return {
      type: "success" /* Success */,
      success: decodeFactorInvoiceServiceReviveSuccess(json.success)
    };
  } else if (json.error !== void 0) {
    return {
      type: "error" /* Error */,
      error: decodeFactorInvoiceServiceUpdateError(json.error)
    };
  }
  throw new Error(
    "No field set in oneof FactorInvoiceServiceReviveResponse_Outcome"
  );
}
export function encodeFactorInvoiceServiceReviveResponse_Outcome(value) {
  switch (value.type) {
    case "success" /* Success */:
      return {
        success: encodeFactorInvoiceServiceReviveSuccess(value.success)
      };
    case "error" /* Error */:
      return { error: encodeFactorInvoiceServiceUpdateError(value.error) };
    default:
      throw new Error(
        "No field set in oneof FactorInvoiceServiceReviveResponse_Outcome"
      );
  }
}
export function decodeFactorInvoiceServiceReviveSuccess(json) {
  return {};
}
export function encodeFactorInvoiceServiceReviveSuccess(value) {
  return {};
}
export function decodeFactorInvoiceServiceSaveAndApproveRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId),
    documentBatchId: optionalDecoder(assertNumber)(json.documentBatchId),
    debtorLoadIdAutofill: optionalDecoder(assertBoolean)(
      json.debtorLoadIdAutofill
    )
  };
}
export function encodeFactorInvoiceServiceSaveAndApproveRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId,
    documentBatchId: value.documentBatchId,
    debtorLoadIdAutofill: value.debtorLoadIdAutofill
  };
}
export function decodeFactorInvoiceServiceSaveWithErrorsRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId),
    emailId: optionalDecoder(assertNumber)(json.emailId),
    pendingCheckIds: (json.pendingCheckIds || []).map(assertString)
  };
}
export function encodeFactorInvoiceServiceSaveWithErrorsRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId,
    emailId: value.emailId,
    pendingCheckIds: value.pendingCheckIds
  };
}
export function decodeFactorInvoiceServiceSaveWithErrorsResponse(json) {
  return {
    outcome: decodeFactorInvoiceServiceSaveWithErrorsResponse_Outcome(json)
  };
}
export function encodeFactorInvoiceServiceSaveWithErrorsResponse(value) {
  return __spreadValues({}, encodeFactorInvoiceServiceSaveWithErrorsResponse_Outcome(value.outcome));
}
export var FactorInvoiceServiceSaveWithErrorsResponse_OutcomeType = /* @__PURE__ */ ((FactorInvoiceServiceSaveWithErrorsResponse_OutcomeType2) => {
  FactorInvoiceServiceSaveWithErrorsResponse_OutcomeType2["Success"] = "success";
  FactorInvoiceServiceSaveWithErrorsResponse_OutcomeType2["Error"] = "error";
  return FactorInvoiceServiceSaveWithErrorsResponse_OutcomeType2;
})(FactorInvoiceServiceSaveWithErrorsResponse_OutcomeType || {});
export function decodeFactorInvoiceServiceSaveWithErrorsResponse_Outcome(json) {
  if (json.success !== void 0) {
    return {
      type: "success" /* Success */,
      success: decodeFactorInvoiceServiceUpdateSuccess(json.success)
    };
  } else if (json.error !== void 0) {
    return {
      type: "error" /* Error */,
      error: decodeFactorInvoiceServiceUpdateError(json.error)
    };
  }
  throw new Error(
    "No field set in oneof FactorInvoiceServiceSaveWithErrorsResponse_Outcome"
  );
}
export function encodeFactorInvoiceServiceSaveWithErrorsResponse_Outcome(value) {
  switch (value.type) {
    case "success" /* Success */:
      return {
        success: encodeFactorInvoiceServiceUpdateSuccess(value.success)
      };
    case "error" /* Error */:
      return { error: encodeFactorInvoiceServiceUpdateError(value.error) };
    default:
      throw new Error(
        "No field set in oneof FactorInvoiceServiceSaveWithErrorsResponse_Outcome"
      );
  }
}
export function decodeFactorInvoiceServiceUnapproveRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId)
  };
}
export function encodeFactorInvoiceServiceUnapproveRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId
  };
}
export function decodeFactorInvoiceServiceUnapproveResponse(json) {
  return {
    outcome: decodeFactorInvoiceServiceUnapproveResponse_Outcome(json)
  };
}
export function encodeFactorInvoiceServiceUnapproveResponse(value) {
  return __spreadValues({}, encodeFactorInvoiceServiceUnapproveResponse_Outcome(value.outcome));
}
export var FactorInvoiceServiceUnapproveResponse_OutcomeType = /* @__PURE__ */ ((FactorInvoiceServiceUnapproveResponse_OutcomeType2) => {
  FactorInvoiceServiceUnapproveResponse_OutcomeType2["Success"] = "success";
  FactorInvoiceServiceUnapproveResponse_OutcomeType2["Error"] = "error";
  return FactorInvoiceServiceUnapproveResponse_OutcomeType2;
})(FactorInvoiceServiceUnapproveResponse_OutcomeType || {});
export function decodeFactorInvoiceServiceUnapproveResponse_Outcome(json) {
  if (json.success !== void 0) {
    return {
      type: "success" /* Success */,
      success: decodeFactorInvoiceServiceUnapproveSuccess(json.success)
    };
  } else if (json.error !== void 0) {
    return {
      type: "error" /* Error */,
      error: decodeFactorInvoiceServiceUpdateError(json.error)
    };
  }
  throw new Error(
    "No field set in oneof FactorInvoiceServiceUnapproveResponse_Outcome"
  );
}
export function encodeFactorInvoiceServiceUnapproveResponse_Outcome(value) {
  switch (value.type) {
    case "success" /* Success */:
      return {
        success: encodeFactorInvoiceServiceUnapproveSuccess(value.success)
      };
    case "error" /* Error */:
      return { error: encodeFactorInvoiceServiceUpdateError(value.error) };
    default:
      throw new Error(
        "No field set in oneof FactorInvoiceServiceUnapproveResponse_Outcome"
      );
  }
}
export function decodeFactorInvoiceServiceUnapproveSuccess(json) {
  return {};
}
export function encodeFactorInvoiceServiceUnapproveSuccess(value) {
  return {};
}
export function decodeFactorInvoiceServiceUpdateError(json) {
  return {
    message: assertString(json.message)
  };
}
export function encodeFactorInvoiceServiceUpdateError(value) {
  return {
    message: value.message
  };
}
export function decodeFactorInvoiceServiceUpdateRequest(json) {
  return {
    factorInvoiceId: assertNumber(json.factorInvoiceId),
    factorInvoiceUpdate: decodeFactorInvoiceUpdate(json.factorInvoiceUpdate)
  };
}
export function encodeFactorInvoiceServiceUpdateRequest(value) {
  return {
    factorInvoiceId: value.factorInvoiceId,
    factorInvoiceUpdate: encodeFactorInvoiceUpdate(value.factorInvoiceUpdate)
  };
}
export function decodeFactorInvoiceServiceUpdateResponse(json) {
  return {
    outcome: decodeFactorInvoiceServiceUpdateResponse_Outcome(json)
  };
}
export function encodeFactorInvoiceServiceUpdateResponse(value) {
  return __spreadValues({}, encodeFactorInvoiceServiceUpdateResponse_Outcome(value.outcome));
}
export var FactorInvoiceServiceUpdateResponse_OutcomeType = /* @__PURE__ */ ((FactorInvoiceServiceUpdateResponse_OutcomeType2) => {
  FactorInvoiceServiceUpdateResponse_OutcomeType2["Success"] = "success";
  FactorInvoiceServiceUpdateResponse_OutcomeType2["Error"] = "error";
  return FactorInvoiceServiceUpdateResponse_OutcomeType2;
})(FactorInvoiceServiceUpdateResponse_OutcomeType || {});
export function decodeFactorInvoiceServiceUpdateResponse_Outcome(json) {
  if (json.success !== void 0) {
    return {
      type: "success" /* Success */,
      success: decodeFactorInvoiceServiceUpdateSuccess(json.success)
    };
  } else if (json.error !== void 0) {
    return {
      type: "error" /* Error */,
      error: decodeFactorInvoiceServiceUpdateError(json.error)
    };
  }
  throw new Error(
    "No field set in oneof FactorInvoiceServiceUpdateResponse_Outcome"
  );
}
export function encodeFactorInvoiceServiceUpdateResponse_Outcome(value) {
  switch (value.type) {
    case "success" /* Success */:
      return {
        success: encodeFactorInvoiceServiceUpdateSuccess(value.success)
      };
    case "error" /* Error */:
      return { error: encodeFactorInvoiceServiceUpdateError(value.error) };
    default:
      throw new Error(
        "No field set in oneof FactorInvoiceServiceUpdateResponse_Outcome"
      );
  }
}
export var FactorInvoiceServiceUpdateResult = /* @__PURE__ */ ((FactorInvoiceServiceUpdateResult2) => {
  FactorInvoiceServiceUpdateResult2["FactorInvoiceServiceUpdateResultException"] = "EXCEPTION";
  FactorInvoiceServiceUpdateResult2["FactorInvoiceServiceUpdateResultProcessed"] = "PROCESSED";
  return FactorInvoiceServiceUpdateResult2;
})(FactorInvoiceServiceUpdateResult || {});
export const decodeFactorInvoiceServiceUpdateResult = (value) => value;
export const encodeFactorInvoiceServiceUpdateResult = (value) => value;
export function decodeFactorInvoiceServiceUpdateSuccess(json) {
  return {
    result: decodeFactorInvoiceServiceUpdateResult(json.result)
  };
}
export function encodeFactorInvoiceServiceUpdateSuccess(value) {
  return {
    result: encodeFactorInvoiceServiceUpdateResult(value.result)
  };
}
export function decodeFactorInvoiceSubmission(json) {
  return {
    id: assertNumber(json.id),
    tms_carrier_id: assertNumber(json.tms_carrier_id),
    factor_broker_id: optionalDecoder(assertNumber)(json.factor_broker_id),
    load_id: assertString(json.load_id),
    invoice_number: assertString(json.invoice_number),
    amount: assertString(json.amount),
    formatted_amount: assertString(json.formatted_amount),
    invoice_date: optionalDecoder(decodeTimestamp)(json.invoice_date),
    manual_debtor_name: assertString(json.manual_debtor_name),
    audit_client_invoice_document: assertBoolean(
      json.audit_client_invoice_document
    ),
    factor_invoice_id: assertNumber(json.factor_invoice_id)
  };
}
export function encodeFactorInvoiceSubmission(value) {
  return {
    id: value.id,
    tms_carrier_id: value.tms_carrier_id,
    factor_broker_id: value.factor_broker_id,
    load_id: value.load_id,
    invoice_number: value.invoice_number,
    amount: value.amount,
    formatted_amount: value.formatted_amount,
    invoice_date: optionalEncoder(encodeTimestamp)(value.invoice_date),
    manual_debtor_name: value.manual_debtor_name,
    audit_client_invoice_document: value.audit_client_invoice_document,
    factor_invoice_id: value.factor_invoice_id
  };
}
export function decodeFactorInvoiceUpdate(json) {
  return {
    updates: (json.updates || []).map(decodeFactorInvoiceFieldUpdate),
    ignoreRuleEvaluationId: optionalDecoder(assertNumber)(
      json.ignoreRuleEvaluationId
    )
  };
}
export function encodeFactorInvoiceUpdate(value) {
  return {
    updates: value.updates.map(encodeFactorInvoiceFieldUpdate),
    ignoreRuleEvaluationId: value.ignoreRuleEvaluationId
  };
}
export function decodeFactorLaneRateCheck(json) {
  return {};
}
export function encodeFactorLaneRateCheck(value) {
  return {};
}
export function decodeFactorSoftAPIBatchExportCompleteInput(json) {
  return {
    transmission_batch_id: assertNumber(json.transmission_batch_id),
    factor_invoice_ids: (json.factor_invoice_ids || []).map(assertNumber),
    batch_guid: assertString(json.batch_guid),
    trans_key: assertNumber(json.trans_key),
    invoice_keys: (json.invoice_keys || []).map(
      decodeFactorSoftAPIBatchExportInvoiceKey
    ),
    response_error: assertString(json.response_error)
  };
}
export function encodeFactorSoftAPIBatchExportCompleteInput(value) {
  return {
    transmission_batch_id: value.transmission_batch_id,
    factor_invoice_ids: value.factor_invoice_ids,
    batch_guid: value.batch_guid,
    trans_key: value.trans_key,
    invoice_keys: value.invoice_keys.map(
      encodeFactorSoftAPIBatchExportInvoiceKey
    ),
    response_error: value.response_error
  };
}
export function decodeFactorSoftAPIBatchExportCreateInput(json) {
  return {
    transmission_batch_id: assertNumber(json.transmission_batch_id),
    factor_invoice_ids: (json.factor_invoice_ids || []).map(assertNumber),
    batch_guid: assertString(json.batch_guid),
    images: (json.images || []).map(decodeFactorSoftAPIBatchExportImage)
  };
}
export function encodeFactorSoftAPIBatchExportCreateInput(value) {
  return {
    transmission_batch_id: value.transmission_batch_id,
    factor_invoice_ids: value.factor_invoice_ids,
    batch_guid: value.batch_guid,
    images: value.images.map(encodeFactorSoftAPIBatchExportImage)
  };
}
export function decodeFactorSoftAPIBatchExportCreateOutput(json) {
  return {
    trans_key: assertNumber(json.trans_key),
    invoice_keys: (json.invoice_keys || []).map(
      decodeFactorSoftAPIBatchExportInvoiceKey
    ),
    response_error: assertString(json.response_error)
  };
}
export function encodeFactorSoftAPIBatchExportCreateOutput(value) {
  return {
    trans_key: value.trans_key,
    invoice_keys: value.invoice_keys.map(
      encodeFactorSoftAPIBatchExportInvoiceKey
    ),
    response_error: value.response_error
  };
}
export function decodeFactorSoftAPIBatchExportImage(json) {
  return {
    invoice_key: assertString(json.invoice_key),
    guid: assertString(json.guid)
  };
}
export function encodeFactorSoftAPIBatchExportImage(value) {
  return {
    invoice_key: value.invoice_key,
    guid: value.guid
  };
}
export function decodeFactorSoftAPIBatchExportInvoiceKey(json) {
  return {
    factor_invoice_id: assertNumber(json.factor_invoice_id),
    invoice_key: assertString(json.invoice_key)
  };
}
export function encodeFactorSoftAPIBatchExportInvoiceKey(value) {
  return {
    factor_invoice_id: value.factor_invoice_id,
    invoice_key: value.invoice_key
  };
}
export function decodeFactorSoftAPIBatchExportUploadInput(json) {
  return {
    transmission_batch_id: assertNumber(json.transmission_batch_id),
    factor_invoice_ids: (json.factor_invoice_ids || []).map(assertNumber),
    batch_guid: assertString(json.batch_guid)
  };
}
export function encodeFactorSoftAPIBatchExportUploadInput(value) {
  return {
    transmission_batch_id: value.transmission_batch_id,
    factor_invoice_ids: value.factor_invoice_ids,
    batch_guid: value.batch_guid
  };
}
export function decodeFactorSoftAPIBatchExportUploadOutput(json) {
  return {
    images: (json.images || []).map(decodeFactorSoftAPIBatchExportImage)
  };
}
export function encodeFactorSoftAPIBatchExportUploadOutput(value) {
  return {
    images: value.images.map(encodeFactorSoftAPIBatchExportImage)
  };
}
export function decodeFactorSoftAPIBatchExportWorkflowInput(json) {
  return {
    transmission_batch_id: assertNumber(json.transmission_batch_id),
    factor_invoice_ids: (json.factor_invoice_ids || []).map(assertNumber),
    batch_guid: assertString(json.batch_guid)
  };
}
export function encodeFactorSoftAPIBatchExportWorkflowInput(value) {
  return {
    transmission_batch_id: value.transmission_batch_id,
    factor_invoice_ids: value.factor_invoice_ids,
    batch_guid: value.batch_guid
  };
}
export function decodeFactorSoftAPISyncAfterSyncInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    table: assertString(json.table),
    complete_pull: assertBoolean(json.complete_pull)
  };
}
export function encodeFactorSoftAPISyncAfterSyncInput(value) {
  return {
    account_id: value.account_id,
    table: value.table,
    complete_pull: value.complete_pull
  };
}
export function decodeFactorSoftAPISyncTableEdge(json) {
  return {
    table: assertString(json.table),
    dependents: (json.dependents || []).map(assertString)
  };
}
export function encodeFactorSoftAPISyncTableEdge(value) {
  return {
    table: value.table,
    dependents: value.dependents
  };
}
export function decodeFactorSoftAPISyncTableInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    table: assertString(json.table),
    force: assertBoolean(json.force)
  };
}
export function encodeFactorSoftAPISyncTableInput(value) {
  return {
    account_id: value.account_id,
    table: value.table,
    force: value.force
  };
}
export function decodeFactorSoftAPISyncTableOutput(json) {
  return {
    table: assertString(json.table),
    api_row_count: assertNumber(json.api_row_count),
    created_count: assertNumber(json.created_count),
    updated_count: assertNumber(json.updated_count),
    unchanged_count: assertNumber(json.unchanged_count),
    not_written_reason: optionalDecoder(assertString)(json.not_written_reason),
    deleted_count: assertNumber(json.deleted_count),
    not_built_count: assertNumber(json.not_built_count),
    sync_mode: optionalDecoder(assertString)(json.sync_mode),
    tombstone_count: assertNumber(json.tombstone_count),
    complete_pull: assertBoolean(json.complete_pull),
    after_sync: assertBoolean(json.after_sync)
  };
}
export function encodeFactorSoftAPISyncTableOutput(value) {
  return {
    table: value.table,
    api_row_count: value.api_row_count,
    created_count: value.created_count,
    updated_count: value.updated_count,
    unchanged_count: value.unchanged_count,
    not_written_reason: value.not_written_reason,
    deleted_count: value.deleted_count,
    not_built_count: value.not_built_count,
    sync_mode: value.sync_mode,
    tombstone_count: value.tombstone_count,
    complete_pull: value.complete_pull,
    after_sync: value.after_sync
  };
}
export function decodeFactorSoftAPISyncTableTreeInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    table: assertString(json.table),
    graph: (json.graph || []).map(decodeFactorSoftAPISyncTableEdge),
    force: assertBoolean(json.force)
  };
}
export function encodeFactorSoftAPISyncTableTreeInput(value) {
  return {
    account_id: value.account_id,
    table: value.table,
    graph: value.graph.map(encodeFactorSoftAPISyncTableEdge),
    force: value.force
  };
}
export function decodeFactorSoftAPISyncWorkflowInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    graph: (json.graph || []).map(decodeFactorSoftAPISyncTableEdge),
    force: assertBoolean(json.force)
  };
}
export function encodeFactorSoftAPISyncWorkflowInput(value) {
  return {
    account_id: value.account_id,
    graph: value.graph.map(encodeFactorSoftAPISyncTableEdge),
    force: value.force
  };
}
export function decodeFactorSoftAPISyncWorkflowOutput(json) {
  return {
    results: (json.results || []).map(decodeFactorSoftAPISyncTableOutput)
  };
}
export function encodeFactorSoftAPISyncWorkflowOutput(value) {
  return {
    results: value.results.map(encodeFactorSoftAPISyncTableOutput)
  };
}
export function decodeFactorSubmissionBatch(json) {
  return {
    id: assertNumber(json.id),
    external_id: optionalDecoder(assertString)(json.external_id),
    created_at: decodeTimestamp(json.created_at),
    updated_at: decodeTimestamp(json.updated_at)
  };
}
export function encodeFactorSubmissionBatch(value) {
  return {
    id: value.id,
    external_id: value.external_id,
    created_at: encodeTimestamp(value.created_at),
    updated_at: encodeTimestamp(value.updated_at)
  };
}
export function decodeFeatureFlags(json) {
  return {
    allow_hiding_items_in_queues_on_presence: assertBoolean(
      json.allow_hiding_items_in_queues_on_presence
    )
  };
}
export function encodeFeatureFlags(value) {
  return {
    allow_hiding_items_in_queues_on_presence: value.allow_hiding_items_in_queues_on_presence
  };
}
export var FileStatus = /* @__PURE__ */ ((FileStatus2) => {
  FileStatus2["FileStatusImporting"] = "importing";
  FileStatus2["FileStatusImported"] = "imported";
  FileStatus2["FileStatusImportFailed"] = "import_failed";
  FileStatus2["FileStatusSkipped"] = "skipped";
  return FileStatus2;
})(FileStatus || {});
export const decodeFileStatus = (value) => value;
export const encodeFileStatus = (value) => value;
export var FlaggedForManualReviewReason = /* @__PURE__ */ ((FlaggedForManualReviewReason2) => {
  FlaggedForManualReviewReason2["FlaggedForManualReviewReasonHitClientThreshold"] = "hit_client_threshold";
  FlaggedForManualReviewReason2["FlaggedForManualReviewReasonHitAccountThreshold"] = "hit_account_threshold";
  FlaggedForManualReviewReason2["FlaggedForManualReviewReasonFailedAutoVerification"] = "failed_auto_verification";
  FlaggedForManualReviewReason2["FlaggedForManualReviewReasonMarkedAsFuelAdvance"] = "marked_as_fuel_advance";
  FlaggedForManualReviewReason2["FlaggedForManualReviewReasonBrokerDomainMismatch"] = "broker_domain_mismatch";
  FlaggedForManualReviewReason2["FlaggedForManualReviewReasonHitClientCreditRatingThreshold"] = "hit_client_credit_rating_threshold";
  FlaggedForManualReviewReason2["FlaggedForManualReviewReasonAutoprocessingDisabledForDebtor"] = "autoprocessing_disabled_for_debtor";
  return FlaggedForManualReviewReason2;
})(FlaggedForManualReviewReason || {});
export const decodeFlaggedForManualReviewReason = (value) => value;
export const encodeFlaggedForManualReviewReason = (value) => value;
export function decodeForwardAPIPaginatedResult(json) {
  return {
    entries: (json.entries || []).map(assertNumber),
    pageNumber: assertNumber(json.pageNumber),
    entriesPerPage: assertNumber(json.entriesPerPage),
    totalEntries: assertNumber(json.totalEntries)
  };
}
export function encodeForwardAPIPaginatedResult(value) {
  return {
    entries: value.entries,
    pageNumber: value.pageNumber,
    entriesPerPage: value.entriesPerPage,
    totalEntries: value.totalEntries
  };
}
export var FtpProtocolType = /* @__PURE__ */ ((FtpProtocolType2) => {
  FtpProtocolType2["FtpProtocolTypeFtp"] = "ftp";
  FtpProtocolType2["FtpProtocolTypeSftp"] = "sftp";
  return FtpProtocolType2;
})(FtpProtocolType || {});
export const decodeFtpProtocolType = (value) => value;
export const encodeFtpProtocolType = (value) => value;
export function decodeFundingInstructionsDetectedCheck(json) {
  return {
    text: assertString(json.text)
  };
}
export function encodeFundingInstructionsDetectedCheck(value) {
  return {
    text: value.text
  };
}
export function decodeHoneybadgerReport(json) {
  return {
    name: assertString(json.name),
    fingerprint: optionalDecoder(assertString)(json.fingerprint),
    json: assertString(json.json),
    message: assertString(json.message)
  };
}
export function encodeHoneybadgerReport(value) {
  return {
    name: value.name,
    fingerprint: value.fingerprint,
    json: value.json,
    message: value.message
  };
}
export var IgnoreMode = /* @__PURE__ */ ((IgnoreMode2) => {
  IgnoreMode2["IgnoreModeBulkIgnore"] = "BulkIgnore";
  IgnoreMode2["IgnoreModeIgnore"] = "Ignore";
  IgnoreMode2["IgnoreModeSubmissionsWorkflowNoIgnore"] = "SubmissionsWorkflowNoIgnore";
  return IgnoreMode2;
})(IgnoreMode || {});
export const decodeIgnoreMode = (value) => value;
export const encodeIgnoreMode = (value) => value;
export function decodeImplicitInvoice(json) {
  return {
    override_enabled: assertBoolean(json.override_enabled),
    allow_implicit_invoice: assertBoolean(json.allow_implicit_invoice)
  };
}
export function encodeImplicitInvoice(value) {
  return {
    override_enabled: value.override_enabled,
    allow_implicit_invoice: value.allow_implicit_invoice
  };
}
export function decodeImportFileAttachmentsActivityInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id)
  };
}
export function encodeImportFileAttachmentsActivityInput(value) {
  return {
    import_process_id: value.import_process_id
  };
}
export function decodeImportFileAttachmentsActivityOutput(json) {
  return {
    batchResult: decodeImportFileAttachmentsActivityOutput_BatchResult(json)
  };
}
export function encodeImportFileAttachmentsActivityOutput(value) {
  return __spreadValues({}, encodeImportFileAttachmentsActivityOutput_BatchResult(value.batchResult));
}
export var ImportFileAttachmentsActivityOutput_BatchResultType = /* @__PURE__ */ ((ImportFileAttachmentsActivityOutput_BatchResultType2) => {
  ImportFileAttachmentsActivityOutput_BatchResultType2["ResultBatch"] = "result_batch";
  ImportFileAttachmentsActivityOutput_BatchResultType2["ResultNoBatch"] = "result_no_batch";
  ImportFileAttachmentsActivityOutput_BatchResultType2["ResultDidNotRun"] = "result_did_not_run";
  return ImportFileAttachmentsActivityOutput_BatchResultType2;
})(ImportFileAttachmentsActivityOutput_BatchResultType || {});
export function decodeImportFileAttachmentsActivityOutput_BatchResult(json) {
  if (json.result_batch !== void 0) {
    return {
      type: "result_batch" /* ResultBatch */,
      result_batch: decodeImportFileAttachmentsOutputBatchCreated(
        json.result_batch
      )
    };
  } else if (json.result_no_batch !== void 0) {
    return {
      type: "result_no_batch" /* ResultNoBatch */,
      result_no_batch: decodeImportFileAttachmentsActivityOutputNoImportableFiles(
        json.result_no_batch
      )
    };
  } else if (json.result_did_not_run !== void 0) {
    return {
      type: "result_did_not_run" /* ResultDidNotRun */,
      result_did_not_run: decodeImportStageDidNotRun(json.result_did_not_run)
    };
  }
  throw new Error(
    "No field set in oneof ImportFileAttachmentsActivityOutput_BatchResult"
  );
}
export function encodeImportFileAttachmentsActivityOutput_BatchResult(value) {
  switch (value.type) {
    case "result_batch" /* ResultBatch */:
      return {
        result_batch: encodeImportFileAttachmentsOutputBatchCreated(
          value.result_batch
        )
      };
    case "result_no_batch" /* ResultNoBatch */:
      return {
        result_no_batch: encodeImportFileAttachmentsActivityOutputNoImportableFiles(
          value.result_no_batch
        )
      };
    case "result_did_not_run" /* ResultDidNotRun */:
      return {
        result_did_not_run: encodeImportStageDidNotRun(
          value.result_did_not_run
        )
      };
    default:
      throw new Error(
        "No field set in oneof ImportFileAttachmentsActivityOutput_BatchResult"
      );
  }
}
export function decodeImportFileAttachmentsActivityOutputNoImportableFiles(json) {
  return {};
}
export function encodeImportFileAttachmentsActivityOutputNoImportableFiles(value) {
  return {};
}
export function decodeImportFileAttachmentsOutputBatchCreated(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeImportFileAttachmentsOutputBatchCreated(value) {
  return {
    document_batch_id: value.document_batch_id
  };
}
export function decodeImportStageDidNotRun(json) {
  return {};
}
export function encodeImportStageDidNotRun(value) {
  return {};
}
export function decodeImportStageEmailIgnored(json) {
  return {};
}
export function encodeImportStageEmailIgnored(value) {
  return {};
}
export function decodeImportStageOutcome(json) {
  return {
    outcome: decodeImportStageOutcome_Outcome(json)
  };
}
export function encodeImportStageOutcome(value) {
  return __spreadValues({}, encodeImportStageOutcome_Outcome(value.outcome));
}
export var ImportStageOutcome_OutcomeType = /* @__PURE__ */ ((ImportStageOutcome_OutcomeType2) => {
  ImportStageOutcome_OutcomeType2["Ran"] = "ran";
  ImportStageOutcome_OutcomeType2["DidNotRun"] = "did_not_run";
  ImportStageOutcome_OutcomeType2["EmailIgnored"] = "email_ignored";
  return ImportStageOutcome_OutcomeType2;
})(ImportStageOutcome_OutcomeType || {});
export function decodeImportStageOutcome_Outcome(json) {
  if (json.ran !== void 0) {
    return {
      type: "ran" /* Ran */,
      ran: decodeImportStageRan(json.ran)
    };
  } else if (json.did_not_run !== void 0) {
    return {
      type: "did_not_run" /* DidNotRun */,
      did_not_run: decodeImportStageDidNotRun(json.did_not_run)
    };
  } else if (json.email_ignored !== void 0) {
    return {
      type: "email_ignored" /* EmailIgnored */,
      email_ignored: decodeImportStageEmailIgnored(json.email_ignored)
    };
  }
  throw new Error("No field set in oneof ImportStageOutcome_Outcome");
}
export function encodeImportStageOutcome_Outcome(value) {
  switch (value.type) {
    case "ran" /* Ran */:
      return { ran: encodeImportStageRan(value.ran) };
    case "did_not_run" /* DidNotRun */:
      return { did_not_run: encodeImportStageDidNotRun(value.did_not_run) };
    case "email_ignored" /* EmailIgnored */:
      return {
        email_ignored: encodeImportStageEmailIgnored(value.email_ignored)
      };
    default:
      throw new Error("No field set in oneof ImportStageOutcome_Outcome");
  }
}
export function decodeImportStageRan(json) {
  return {};
}
export function encodeImportStageRan(value) {
  return {};
}
export function decodeImportStatus(json) {
  return {
    id: assertNumber(json.id),
    pages: assertNumber(json.pages),
    step: decodeImportStep(json.step),
    page_in_step: assertNumber(json.page_in_step)
  };
}
export function encodeImportStatus(value) {
  return {
    id: value.id,
    pages: value.pages,
    step: encodeImportStep(value.step),
    page_in_step: value.page_in_step
  };
}
export var ImportStep = /* @__PURE__ */ ((ImportStep2) => {
  ImportStep2["ImportStepUnknown"] = "unknown";
  ImportStep2["ImportStepSplitPages"] = "split_pages";
  ImportStep2["ImportStepOcr"] = "ocr";
  ImportStep2["ImportStepExtractData"] = "extract_data";
  return ImportStep2;
})(ImportStep || {});
export const decodeImportStep = (value) => value;
export const encodeImportStep = (value) => value;
export var InsightsBrokerStatLineItemType = /* @__PURE__ */ ((InsightsBrokerStatLineItemType2) => {
  InsightsBrokerStatLineItemType2["InsightsBrokerStatLineItemTypeCommodity"] = "commodity";
  InsightsBrokerStatLineItemType2["InsightsBrokerStatLineItemTypeAccessorial"] = "accessorial";
  return InsightsBrokerStatLineItemType2;
})(InsightsBrokerStatLineItemType || {});
export const decodeInsightsBrokerStatLineItemType = (value) => value;
export const encodeInsightsBrokerStatLineItemType = (value) => value;
export var InsightsBrokerStatLoadType = /* @__PURE__ */ ((InsightsBrokerStatLoadType2) => {
  InsightsBrokerStatLoadType2["InsightsBrokerStatLoadTypeAll"] = "all";
  InsightsBrokerStatLoadType2["InsightsBrokerStatLoadTypeLessThanTruckload"] = "ltl";
  InsightsBrokerStatLoadType2["InsightsBrokerStatLoadTypeTruckload"] = "tl";
  return InsightsBrokerStatLoadType2;
})(InsightsBrokerStatLoadType || {});
export const decodeInsightsBrokerStatLoadType = (value) => value;
export const encodeInsightsBrokerStatLoadType = (value) => value;
export var InsightsBrokerStatSubset = /* @__PURE__ */ ((InsightsBrokerStatSubset2) => {
  InsightsBrokerStatSubset2["InsightsBrokerStatSubsetLoadDeliveryToInvoiceApproved"] = "load_delivery_to_approved";
  InsightsBrokerStatSubset2["InsightsBrokerStatSubsetLoadDeliveredToFirstBolReceived"] = "load_delivered_to_first_bol_received";
  InsightsBrokerStatSubset2["InsightsBrokerStatSubsetLoadDeliveredToFirstInvoiceReceived"] = "load_delivered_to_first_invoice_received";
  InsightsBrokerStatSubset2["InsightsBrokerStatSubsetPaperworkReceivedToIndexed"] = "paperwork_received_to_indexed";
  InsightsBrokerStatSubset2["InsightsBrokerStatSubsetIndexedToApproved"] = "indexed_to_approved";
  return InsightsBrokerStatSubset2;
})(InsightsBrokerStatSubset || {});
export const decodeInsightsBrokerStatSubset = (value) => value;
export const encodeInsightsBrokerStatSubset = (value) => value;
export function decodeInsightsDashboardApplyToleranceRecommendationRequest(json) {
  return {
    overThreshold: optionalDecoder(assertString)(json.overThreshold),
    underThreshold: optionalDecoder(assertString)(json.underThreshold),
    percentile: assertString(json.percentile)
  };
}
export function encodeInsightsDashboardApplyToleranceRecommendationRequest(value) {
  return {
    overThreshold: value.overThreshold,
    underThreshold: value.underThreshold,
    percentile: value.percentile
  };
}
export var InsightsDashboardCustomExceptionMessageRowType = /* @__PURE__ */ ((InsightsDashboardCustomExceptionMessageRowType2) => {
  InsightsDashboardCustomExceptionMessageRowType2["InsightsDashboardCustomExceptionMessageRowTypeCustomExceptionMessageRowTypeAccount"] = "account";
  InsightsDashboardCustomExceptionMessageRowType2["InsightsDashboardCustomExceptionMessageRowTypeCustomExceptionMessageRowTypeCustomer"] = "customer";
  return InsightsDashboardCustomExceptionMessageRowType2;
})(InsightsDashboardCustomExceptionMessageRowType || {});
export const decodeInsightsDashboardCustomExceptionMessageRowType = (value) => value;
export const encodeInsightsDashboardCustomExceptionMessageRowType = (value) => value;
export function decodeInsightsDashboardCustomExceptionMessageStat(json) {
  return {
    rowType: decodeInsightsDashboardCustomExceptionMessageRowType(json.rowType),
    tmsCustomerId: optionalDecoder(assertNumber)(json.tmsCustomerId),
    customerName: optionalDecoder(assertString)(json.customerName),
    ruleMessages: (json.ruleMessages || []).map(assertString),
    count: assertNumber(json.count),
    averageHoursToResolve: assertNumber(json.averageHoursToResolve),
    isDeleted: assertBoolean(json.isDeleted)
  };
}
export function encodeInsightsDashboardCustomExceptionMessageStat(value) {
  return {
    rowType: encodeInsightsDashboardCustomExceptionMessageRowType(
      value.rowType
    ),
    tmsCustomerId: value.tmsCustomerId,
    customerName: value.customerName,
    ruleMessages: value.ruleMessages,
    count: value.count,
    averageHoursToResolve: value.averageHoursToResolve,
    isDeleted: value.isDeleted
  };
}
export function decodeInsightsDashboardCustomExceptionMessageStats(json) {
  return {
    accountRow: optionalDecoder(
      decodeInsightsDashboardCustomExceptionMessageStat
    )(json.accountRow),
    customerRows: (json.customerRows || []).map(
      decodeInsightsDashboardCustomExceptionMessageStat
    )
  };
}
export function encodeInsightsDashboardCustomExceptionMessageStats(value) {
  return {
    accountRow: optionalEncoder(
      encodeInsightsDashboardCustomExceptionMessageStat
    )(value.accountRow),
    customerRows: value.customerRows.map(
      encodeInsightsDashboardCustomExceptionMessageStat
    )
  };
}
export function decodeInsightsDashboardCustomExceptionRuleTarget(json) {
  return {
    rowType: decodeInsightsDashboardCustomExceptionMessageRowType(json.rowType),
    tmsCustomerId: optionalDecoder(assertNumber)(json.tmsCustomerId)
  };
}
export function encodeInsightsDashboardCustomExceptionRuleTarget(value) {
  return {
    rowType: encodeInsightsDashboardCustomExceptionMessageRowType(
      value.rowType
    ),
    tmsCustomerId: value.tmsCustomerId
  };
}
export function decodeInsightsDashboardDetailedInvoices(json) {
  return {
    pageNumber: assertNumber(json.pageNumber),
    entriesPerPage: assertNumber(json.entriesPerPage),
    totalEntries: assertNumber(json.totalEntries),
    entries: (json.entries || []).map(
      decodeInsightsDashboardDetailedInvoices_Entry
    )
  };
}
export function encodeInsightsDashboardDetailedInvoices(value) {
  return {
    pageNumber: value.pageNumber,
    entriesPerPage: value.entriesPerPage,
    totalEntries: value.totalEntries,
    entries: value.entries.map(encodeInsightsDashboardDetailedInvoices_Entry)
  };
}
export function decodeInsightsDashboardDetailedInvoices_Entry(json) {
  return {
    loadId: assertString(json.loadId),
    varianceAmount: assertNumber(json.varianceAmount),
    isWin: assertBoolean(json.isWin),
    timeToResolveDays: assertNumber(json.timeToResolveDays),
    carrierInvoiceId: assertNumber(json.carrierInvoiceId),
    loadStateId: assertNumber(json.loadStateId)
  };
}
export function encodeInsightsDashboardDetailedInvoices_Entry(value) {
  return {
    loadId: value.loadId,
    varianceAmount: value.varianceAmount,
    isWin: value.isWin,
    timeToResolveDays: value.timeToResolveDays,
    carrierInvoiceId: value.carrierInvoiceId,
    loadStateId: value.loadStateId
  };
}
export function decodeInsightsDashboardDetailedInvoicesQuery(json) {
  return {
    startDate: assertString(json.startDate),
    endDate: assertString(json.endDate),
    page: assertNumber(json.page),
    perPage: assertNumber(json.perPage),
    sortBy: decodeInsightsDashboardVarianceInvoiceSortField(json.sortBy),
    sortDirection: decodeSortOrder(json.sortDirection),
    entityType: decodeInsightsDashboardVarianceEntityType(json.entityType),
    entityId: assertNumber(json.entityId)
  };
}
export function encodeInsightsDashboardDetailedInvoicesQuery(value) {
  return {
    startDate: value.startDate,
    endDate: value.endDate,
    page: value.page,
    perPage: value.perPage,
    sortBy: encodeInsightsDashboardVarianceInvoiceSortField(value.sortBy),
    sortDirection: encodeSortOrder(value.sortDirection),
    entityType: encodeInsightsDashboardVarianceEntityType(value.entityType),
    entityId: value.entityId
  };
}
export function decodeInsightsDashboardDetailedTopEntities(json) {
  return {
    pageNumber: assertNumber(json.pageNumber),
    entriesPerPage: assertNumber(json.entriesPerPage),
    totalEntries: assertNumber(json.totalEntries),
    entries: (json.entries || []).map(
      decodeInsightsDashboardDetailedTopEntities_Entry
    )
  };
}
export function encodeInsightsDashboardDetailedTopEntities(value) {
  return {
    pageNumber: value.pageNumber,
    entriesPerPage: value.entriesPerPage,
    totalEntries: value.totalEntries,
    entries: value.entries.map(
      encodeInsightsDashboardDetailedTopEntities_Entry
    )
  };
}
export function decodeInsightsDashboardDetailedTopEntities_Entry(json) {
  return {
    name: assertString(json.name),
    varianceTotalAmount: assertNumber(json.varianceTotalAmount),
    invoiceCount: assertNumber(json.invoiceCount),
    avgTimeToResolveDays: assertNumber(json.avgTimeToResolveDays),
    entityType: decodeInsightsDashboardVarianceEntityType(json.entityType),
    winRate: assertNumber(json.winRate),
    entityId: assertNumber(json.entityId)
  };
}
export function encodeInsightsDashboardDetailedTopEntities_Entry(value) {
  return {
    name: value.name,
    varianceTotalAmount: value.varianceTotalAmount,
    invoiceCount: value.invoiceCount,
    avgTimeToResolveDays: value.avgTimeToResolveDays,
    entityType: encodeInsightsDashboardVarianceEntityType(value.entityType),
    winRate: value.winRate,
    entityId: value.entityId
  };
}
export function decodeInsightsDashboardDetailedTopEntitiesQuery(json) {
  return {
    startDate: assertString(json.startDate),
    endDate: assertString(json.endDate),
    page: assertNumber(json.page),
    perPage: assertNumber(json.perPage),
    sortBy: decodeInsightsDashboardVarianceSortField(json.sortBy),
    sortDirection: decodeSortOrder(json.sortDirection)
  };
}
export function encodeInsightsDashboardDetailedTopEntitiesQuery(value) {
  return {
    startDate: value.startDate,
    endDate: value.endDate,
    page: value.page,
    perPage: value.perPage,
    sortBy: encodeInsightsDashboardVarianceSortField(value.sortBy),
    sortDirection: encodeSortOrder(value.sortDirection)
  };
}
export function decodeInsightsDashboardExceptionStat(json) {
  return {
    count: assertNumber(json.count),
    exceptionName: assertString(json.exceptionName),
    averageHoursToResolve: assertNumber(json.averageHoursToResolve),
    exceptionKey: assertString(json.exceptionKey),
    priorCount: assertNumber(json.priorCount)
  };
}
export function encodeInsightsDashboardExceptionStat(value) {
  return {
    count: value.count,
    exceptionName: value.exceptionName,
    averageHoursToResolve: value.averageHoursToResolve,
    exceptionKey: value.exceptionKey,
    priorCount: value.priorCount
  };
}
export function decodeInsightsDashboardExceptionTrend(json) {
  return {
    exceptionCounts: (json.exceptionCounts || []).map(
      decodeInsightsDashboardExceptionTrend_Day
    ),
    approvedInvoiceCounts: (json.approvedInvoiceCounts || []).map(
      decodeInsightsDashboardExceptionTrend_Day
    )
  };
}
export function encodeInsightsDashboardExceptionTrend(value) {
  return {
    exceptionCounts: value.exceptionCounts.map(
      encodeInsightsDashboardExceptionTrend_Day
    ),
    approvedInvoiceCounts: value.approvedInvoiceCounts.map(
      encodeInsightsDashboardExceptionTrend_Day
    )
  };
}
export function decodeInsightsDashboardExceptionTrend_Day(json) {
  return {
    date: assertString(json.date),
    count: assertNumber(json.count)
  };
}
export function encodeInsightsDashboardExceptionTrend_Day(value) {
  return {
    date: value.date,
    count: value.count
  };
}
export function decodeInsightsDashboardExceptionTrendQuery(json) {
  return {
    startDate: assertString(json.startDate),
    endDate: assertString(json.endDate),
    loadType: decodeInsightsBrokerStatLoadType(json.loadType),
    exceptionKey: assertString(json.exceptionKey)
  };
}
export function encodeInsightsDashboardExceptionTrendQuery(value) {
  return {
    startDate: value.startDate,
    endDate: value.endDate,
    loadType: encodeInsightsBrokerStatLoadType(value.loadType),
    exceptionKey: value.exceptionKey
  };
}
export var InsightsDashboardExportData = /* @__PURE__ */ ((InsightsDashboardExportData2) => {
  InsightsDashboardExportData2["InsightsDashboardExportDataExceptions"] = "exceptions";
  InsightsDashboardExportData2["InsightsDashboardExportDataEmails"] = "emails";
  InsightsDashboardExportData2["InsightsDashboardExportDataLoads"] = "loads";
  InsightsDashboardExportData2["InsightsDashboardExportDataInvoices"] = "invoices";
  InsightsDashboardExportData2["InsightsDashboardExportDataLtlItemDiscrepancies"] = "ltl_item_discrepancies";
  InsightsDashboardExportData2["InsightsDashboardExportDataLtlCarrierLoadMatches"] = "ltl_carrier_load_matches";
  InsightsDashboardExportData2["InsightsDashboardExportDataVarianceInvoices"] = "variance_invoices";
  InsightsDashboardExportData2["InsightsDashboardExportDataVarianceEntities"] = "variance_entities";
  return InsightsDashboardExportData2;
})(InsightsDashboardExportData || {});
export const decodeInsightsDashboardExportData = (value) => value;
export const encodeInsightsDashboardExportData = (value) => value;
export function decodeInsightsDashboardExportDataRequest(json) {
  return {
    data: decodeInsightsDashboardExportData(json.data),
    startDate: assertString(json.startDate),
    endDate: assertString(json.endDate),
    varianceInvoiceData: optionalDecoder(
      decodeInsightsDashboardExportDataRequest_VarianceInvoiceData
    )(json.varianceInvoiceData)
  };
}
export function encodeInsightsDashboardExportDataRequest(value) {
  return {
    data: encodeInsightsDashboardExportData(value.data),
    startDate: value.startDate,
    endDate: value.endDate,
    varianceInvoiceData: optionalEncoder(
      encodeInsightsDashboardExportDataRequest_VarianceInvoiceData
    )(value.varianceInvoiceData)
  };
}
export function decodeInsightsDashboardExportDataRequest_VarianceInvoiceData(json) {
  return {
    entityType: decodeInsightsDashboardVarianceEntityType(json.entityType),
    entityId: assertNumber(json.entityId)
  };
}
export function encodeInsightsDashboardExportDataRequest_VarianceInvoiceData(value) {
  return {
    entityType: encodeInsightsDashboardVarianceEntityType(value.entityType),
    entityId: value.entityId
  };
}
export function decodeInsightsDashboardRemoveAllCustomExceptionRulesRequest(json) {
  return {
    target: decodeInsightsDashboardCustomExceptionRuleTarget(json.target)
  };
}
export function encodeInsightsDashboardRemoveAllCustomExceptionRulesRequest(value) {
  return {
    target: encodeInsightsDashboardCustomExceptionRuleTarget(value.target)
  };
}
export function decodeInsightsDashboardRemoveCustomExceptionRuleRequest(json) {
  return {
    target: decodeInsightsDashboardCustomExceptionRuleTarget(json.target),
    message: assertString(json.message)
  };
}
export function encodeInsightsDashboardRemoveCustomExceptionRuleRequest(value) {
  return {
    target: encodeInsightsDashboardCustomExceptionRuleTarget(value.target),
    message: value.message
  };
}
export function decodeInsightsDashboardStatQuery(json) {
  return {
    startDate: assertString(json.startDate),
    endDate: assertString(json.endDate),
    measurementType: optionalDecoder(decodeInsightsFactorMeasurementType)(
      json.measurementType
    ),
    loadType: decodeInsightsBrokerStatLoadType(json.loadType),
    statSubset: optionalDecoder(decodeInsightsBrokerStatSubset)(
      json.statSubset
    ),
    limit: optionalDecoder(assertNumber)(json.limit)
  };
}
export function encodeInsightsDashboardStatQuery(value) {
  return {
    startDate: value.startDate,
    endDate: value.endDate,
    measurementType: optionalEncoder(encodeInsightsFactorMeasurementType)(
      value.measurementType
    ),
    loadType: encodeInsightsBrokerStatLoadType(value.loadType),
    statSubset: optionalEncoder(encodeInsightsBrokerStatSubset)(
      value.statSubset
    ),
    limit: value.limit
  };
}
export var InsightsDashboardToleranceDeclinedReason = /* @__PURE__ */ ((InsightsDashboardToleranceDeclinedReason2) => {
  InsightsDashboardToleranceDeclinedReason2["InsightsDashboardToleranceDeclinedReasonSmallSample"] = "small_sample";
  InsightsDashboardToleranceDeclinedReason2["InsightsDashboardToleranceDeclinedReasonNoWiderThreshold"] = "no_wider_threshold";
  InsightsDashboardToleranceDeclinedReason2["InsightsDashboardToleranceDeclinedReasonAlreadyCovered"] = "already_covered";
  return InsightsDashboardToleranceDeclinedReason2;
})(InsightsDashboardToleranceDeclinedReason || {});
export const decodeInsightsDashboardToleranceDeclinedReason = (value) => value;
export const encodeInsightsDashboardToleranceDeclinedReason = (value) => value;
export function decodeInsightsDashboardToleranceRecommendation(json) {
  return {
    currentToleranceOver: assertString(json.currentToleranceOver),
    currentToleranceUnder: assertString(json.currentToleranceUnder),
    run: optionalDecoder(decodeInsightsDashboardToleranceRecommendation_Run)(
      json.run
    ),
    isLineItemTypeMissing: assertBoolean(json.isLineItemTypeMissing)
  };
}
export function encodeInsightsDashboardToleranceRecommendation(value) {
  return {
    currentToleranceOver: value.currentToleranceOver,
    currentToleranceUnder: value.currentToleranceUnder,
    run: optionalEncoder(encodeInsightsDashboardToleranceRecommendation_Run)(
      value.run
    ),
    isLineItemTypeMissing: value.isLineItemTypeMissing
  };
}
export function decodeInsightsDashboardToleranceRecommendation_Run(json) {
  return {
    windowStartedOn: assertString(json.windowStartedOn),
    windowEndedOn: assertString(json.windowEndedOn),
    mismatchExceptionCount: assertNumber(json.mismatchExceptionCount),
    percentOfAllExceptions: assertNumber(json.percentOfAllExceptions),
    tiers: (json.tiers || []).map(
      decodeInsightsDashboardToleranceRecommendation_Tier
    )
  };
}
export function encodeInsightsDashboardToleranceRecommendation_Run(value) {
  return {
    windowStartedOn: value.windowStartedOn,
    windowEndedOn: value.windowEndedOn,
    mismatchExceptionCount: value.mismatchExceptionCount,
    percentOfAllExceptions: value.percentOfAllExceptions,
    tiers: value.tiers.map(encodeInsightsDashboardToleranceRecommendation_Tier)
  };
}
export function decodeInsightsDashboardToleranceRecommendation_Tier(json) {
  return {
    percentile: assertString(json.percentile),
    overThreshold: optionalDecoder(assertString)(json.overThreshold),
    underThreshold: optionalDecoder(assertString)(json.underThreshold),
    exceptionsAvoided: optionalDecoder(assertNumber)(json.exceptionsAvoided),
    declinedReason: optionalDecoder(
      decodeInsightsDashboardToleranceDeclinedReason
    )(json.declinedReason)
  };
}
export function encodeInsightsDashboardToleranceRecommendation_Tier(value) {
  return {
    percentile: value.percentile,
    overThreshold: value.overThreshold,
    underThreshold: value.underThreshold,
    exceptionsAvoided: value.exceptionsAvoided,
    declinedReason: optionalEncoder(
      encodeInsightsDashboardToleranceDeclinedReason
    )(value.declinedReason)
  };
}
export function decodeInsightsDashboardTopExceptionStats(json) {
  return {
    byExceptionCount: (json.byExceptionCount || []).map(
      decodeInsightsDashboardExceptionStat
    ),
    byAverageHoursToResolve: (json.byAverageHoursToResolve || []).map(
      decodeInsightsDashboardExceptionStat
    )
  };
}
export function encodeInsightsDashboardTopExceptionStats(value) {
  return {
    byExceptionCount: value.byExceptionCount.map(
      encodeInsightsDashboardExceptionStat
    ),
    byAverageHoursToResolve: value.byAverageHoursToResolve.map(
      encodeInsightsDashboardExceptionStat
    )
  };
}
export function decodeInsightsDashboardUnmatchedEdiForCarrierStat(json) {
  return {
    totalEdisCount: assertNumber(json.totalEdisCount),
    matchedEdisCount: assertNumber(json.matchedEdisCount),
    mismatchedEdisCount: assertNumber(json.mismatchedEdisCount),
    unmatchedEdisCount: assertNumber(json.unmatchedEdisCount),
    weightedConfidenceScore: assertNumber(json.weightedConfidenceScore),
    name: assertString(json.name),
    dotNumber: optionalDecoder(assertString)(json.dotNumber)
  };
}
export function encodeInsightsDashboardUnmatchedEdiForCarrierStat(value) {
  return {
    totalEdisCount: value.totalEdisCount,
    matchedEdisCount: value.matchedEdisCount,
    mismatchedEdisCount: value.mismatchedEdisCount,
    unmatchedEdisCount: value.unmatchedEdisCount,
    weightedConfidenceScore: value.weightedConfidenceScore,
    name: value.name,
    dotNumber: value.dotNumber
  };
}
export function decodeInsightsDashboardUnmatchedEdiForCustomerStat(json) {
  return {
    totalEdisCount: assertNumber(json.totalEdisCount),
    matchedEdisCount: assertNumber(json.matchedEdisCount),
    mismatchedEdisCount: assertNumber(json.mismatchedEdisCount),
    unmatchedEdisCount: assertNumber(json.unmatchedEdisCount),
    weightedConfidenceScore: assertNumber(json.weightedConfidenceScore),
    name: optionalDecoder(assertString)(json.name),
    accountNumber: optionalDecoder(assertString)(json.accountNumber)
  };
}
export function encodeInsightsDashboardUnmatchedEdiForCustomerStat(value) {
  return {
    totalEdisCount: value.totalEdisCount,
    matchedEdisCount: value.matchedEdisCount,
    mismatchedEdisCount: value.mismatchedEdisCount,
    unmatchedEdisCount: value.unmatchedEdisCount,
    weightedConfidenceScore: value.weightedConfidenceScore,
    name: value.name,
    accountNumber: value.accountNumber
  };
}
export function decodeInsightsDashboardUnmatchedEdiStat(json) {
  return {
    id: assertNumber(json.id),
    date: assertString(json.date),
    totalEdisCount: assertNumber(json.totalEdisCount),
    matchedEdisCount: assertNumber(json.matchedEdisCount),
    mismatchedEdisCount: assertNumber(json.mismatchedEdisCount),
    unmatchedEdisCount: assertNumber(json.unmatchedEdisCount)
  };
}
export function encodeInsightsDashboardUnmatchedEdiStat(value) {
  return {
    id: value.id,
    date: value.date,
    totalEdisCount: value.totalEdisCount,
    matchedEdisCount: value.matchedEdisCount,
    mismatchedEdisCount: value.mismatchedEdisCount,
    unmatchedEdisCount: value.unmatchedEdisCount
  };
}
export var InsightsDashboardVarianceEntityType = /* @__PURE__ */ ((InsightsDashboardVarianceEntityType2) => {
  InsightsDashboardVarianceEntityType2["InsightsDashboardVarianceEntityTypeCarrier"] = "carrier";
  InsightsDashboardVarianceEntityType2["InsightsDashboardVarianceEntityTypeCustomer"] = "customer";
  return InsightsDashboardVarianceEntityType2;
})(InsightsDashboardVarianceEntityType || {});
export const decodeInsightsDashboardVarianceEntityType = (value) => value;
export const encodeInsightsDashboardVarianceEntityType = (value) => value;
export function decodeInsightsDashboardVarianceForCarrierStat(json) {
  return {
    name: assertString(json.name),
    varianceTotalAmount: assertNumber(json.varianceTotalAmount),
    invoiceCount: assertNumber(json.invoiceCount),
    winCount: assertNumber(json.winCount),
    timeToResolveTotalDays: assertNumber(json.timeToResolveTotalDays),
    wonDollarAmount: assertNumber(json.wonDollarAmount)
  };
}
export function encodeInsightsDashboardVarianceForCarrierStat(value) {
  return {
    name: value.name,
    varianceTotalAmount: value.varianceTotalAmount,
    invoiceCount: value.invoiceCount,
    winCount: value.winCount,
    timeToResolveTotalDays: value.timeToResolveTotalDays,
    wonDollarAmount: value.wonDollarAmount
  };
}
export function decodeInsightsDashboardVarianceForCustomerStat(json) {
  return {
    name: assertString(json.name),
    varianceTotalAmount: assertNumber(json.varianceTotalAmount),
    invoiceCount: assertNumber(json.invoiceCount),
    winCount: assertNumber(json.winCount),
    timeToResolveTotalDays: assertNumber(json.timeToResolveTotalDays),
    wonDollarAmount: assertNumber(json.wonDollarAmount)
  };
}
export function encodeInsightsDashboardVarianceForCustomerStat(value) {
  return {
    name: value.name,
    varianceTotalAmount: value.varianceTotalAmount,
    invoiceCount: value.invoiceCount,
    winCount: value.winCount,
    timeToResolveTotalDays: value.timeToResolveTotalDays,
    wonDollarAmount: value.wonDollarAmount
  };
}
export var InsightsDashboardVarianceInvoiceSortField = /* @__PURE__ */ ((InsightsDashboardVarianceInvoiceSortField2) => {
  InsightsDashboardVarianceInvoiceSortField2["InsightsDashboardVarianceInvoiceSortFieldVarianceAmount"] = "variance_amount";
  InsightsDashboardVarianceInvoiceSortField2["InsightsDashboardVarianceInvoiceSortFieldIsWin"] = "is_win";
  InsightsDashboardVarianceInvoiceSortField2["InsightsDashboardVarianceInvoiceSortFieldTimeToResolveDays"] = "time_to_resolve_days";
  return InsightsDashboardVarianceInvoiceSortField2;
})(InsightsDashboardVarianceInvoiceSortField || {});
export const decodeInsightsDashboardVarianceInvoiceSortField = (value) => value;
export const encodeInsightsDashboardVarianceInvoiceSortField = (value) => value;
export var InsightsDashboardVarianceSortField = /* @__PURE__ */ ((InsightsDashboardVarianceSortField2) => {
  InsightsDashboardVarianceSortField2["InsightsDashboardVarianceSortFieldVarianceTotalAmount"] = "variance_total_amount";
  InsightsDashboardVarianceSortField2["InsightsDashboardVarianceSortFieldInvoiceCount"] = "invoice_count";
  InsightsDashboardVarianceSortField2["InsightsDashboardVarianceSortFieldWinRate"] = "win_rate";
  InsightsDashboardVarianceSortField2["InsightsDashboardVarianceSortFieldAvgTimeToResolveDays"] = "avg_time_to_resolve_days";
  InsightsDashboardVarianceSortField2["InsightsDashboardVarianceSortFieldEntityType"] = "entity_type";
  InsightsDashboardVarianceSortField2["InsightsDashboardVarianceSortFieldName"] = "name";
  return InsightsDashboardVarianceSortField2;
})(InsightsDashboardVarianceSortField || {});
export const decodeInsightsDashboardVarianceSortField = (value) => value;
export const encodeInsightsDashboardVarianceSortField = (value) => value;
export function decodeInsightsDashboardVarianceStat(json) {
  return {
    id: assertNumber(json.id),
    date: assertString(json.date),
    invoiceCount: assertNumber(json.invoiceCount),
    varianceTotalAmount: assertNumber(json.varianceTotalAmount),
    winCount: assertNumber(json.winCount),
    timeToResolveTotalDays: assertNumber(json.timeToResolveTotalDays),
    wonDollarAmount: assertNumber(json.wonDollarAmount)
  };
}
export function encodeInsightsDashboardVarianceStat(value) {
  return {
    id: value.id,
    date: value.date,
    invoiceCount: value.invoiceCount,
    varianceTotalAmount: value.varianceTotalAmount,
    winCount: value.winCount,
    timeToResolveTotalDays: value.timeToResolveTotalDays,
    wonDollarAmount: value.wonDollarAmount
  };
}
export var InsightsFactorMeasurementType = /* @__PURE__ */ ((InsightsFactorMeasurementType2) => {
  InsightsFactorMeasurementType2["InsightsFactorMeasurementTypeAverage"] = "average";
  InsightsFactorMeasurementType2["InsightsFactorMeasurementTypeMedian"] = "median";
  return InsightsFactorMeasurementType2;
})(InsightsFactorMeasurementType || {});
export const decodeInsightsFactorMeasurementType = (value) => value;
export const encodeInsightsFactorMeasurementType = (value) => value;
export function decodeIntegration(json) {
  return {
    id: assertNumber(json.id),
    integrationDetails: decodeIntegration_IntegrationDetails(json)
  };
}
export function encodeIntegration(value) {
  return __spreadValues({
    id: value.id
  }, encodeIntegration_IntegrationDetails(value.integrationDetails));
}
export var Integration_IntegrationDetailsType = /* @__PURE__ */ ((Integration_IntegrationDetailsType2) => {
  Integration_IntegrationDetailsType2["MercuryGateDetails"] = "mercuryGateDetails";
  Integration_IntegrationDetailsType2["CadenceDetails"] = "cadenceDetails";
  Integration_IntegrationDetailsType2["FactorSqlDetails"] = "factorSqlDetails";
  Integration_IntegrationDetailsType2["UnknownDetails"] = "unknownDetails";
  Integration_IntegrationDetailsType2["FactorSoftAPIDetails"] = "factorSoftAPIDetails";
  return Integration_IntegrationDetailsType2;
})(Integration_IntegrationDetailsType || {});
export function decodeIntegration_IntegrationDetails(json) {
  if (json.mercuryGateDetails !== void 0) {
    return {
      type: "mercuryGateDetails" /* MercuryGateDetails */,
      mercuryGateDetails: decodeIntegration_MercuryGateDetails(
        json.mercuryGateDetails
      )
    };
  } else if (json.cadenceDetails !== void 0) {
    return {
      type: "cadenceDetails" /* CadenceDetails */,
      cadenceDetails: decodeIntegration_CadenceDetails(json.cadenceDetails)
    };
  } else if (json.factorSqlDetails !== void 0) {
    return {
      type: "factorSqlDetails" /* FactorSqlDetails */,
      factorSqlDetails: decodeIntegration_FactorSqlDetails(
        json.factorSqlDetails
      )
    };
  } else if (json.unknownDetails !== void 0) {
    return {
      type: "unknownDetails" /* UnknownDetails */,
      unknownDetails: decodeIntegration_UnknownIntegrationDetails(
        json.unknownDetails
      )
    };
  } else if (json.factorSoftAPIDetails !== void 0) {
    return {
      type: "factorSoftAPIDetails" /* FactorSoftAPIDetails */,
      factorSoftAPIDetails: decodeIntegration_FactorSoftAPIDetails(
        json.factorSoftAPIDetails
      )
    };
  }
  throw new Error("No field set in oneof Integration_IntegrationDetails");
}
export function encodeIntegration_IntegrationDetails(value) {
  switch (value.type) {
    case "mercuryGateDetails" /* MercuryGateDetails */:
      return {
        mercuryGateDetails: encodeIntegration_MercuryGateDetails(
          value.mercuryGateDetails
        )
      };
    case "cadenceDetails" /* CadenceDetails */:
      return {
        cadenceDetails: encodeIntegration_CadenceDetails(value.cadenceDetails)
      };
    case "factorSqlDetails" /* FactorSqlDetails */:
      return {
        factorSqlDetails: encodeIntegration_FactorSqlDetails(
          value.factorSqlDetails
        )
      };
    case "unknownDetails" /* UnknownDetails */:
      return {
        unknownDetails: encodeIntegration_UnknownIntegrationDetails(
          value.unknownDetails
        )
      };
    case "factorSoftAPIDetails" /* FactorSoftAPIDetails */:
      return {
        factorSoftAPIDetails: encodeIntegration_FactorSoftAPIDetails(
          value.factorSoftAPIDetails
        )
      };
    default:
      throw new Error("No field set in oneof Integration_IntegrationDetails");
  }
}
export function decodeIntegration_CadenceDetails(json) {
  return {
    fileStatusData: decodeIntegration_FileStatusData(json.fileStatusData),
    transmissionData: (json.transmissionData || []).map(
      decodeIntegration_TransmissionStatusData
    )
  };
}
export function encodeIntegration_CadenceDetails(value) {
  return {
    fileStatusData: encodeIntegration_FileStatusData(value.fileStatusData),
    transmissionData: value.transmissionData.map(
      encodeIntegration_TransmissionStatusData
    )
  };
}
export function decodeIntegration_FactorSoftAPIDetails(json) {
  return {
    tables: (json.tables || []).map(decodeIntegration_TableSyncStatus)
  };
}
export function encodeIntegration_FactorSoftAPIDetails(value) {
  return {
    tables: value.tables.map(encodeIntegration_TableSyncStatus)
  };
}
export function decodeIntegration_FactorSqlDetails(json) {
  return {};
}
export function encodeIntegration_FactorSqlDetails(value) {
  return {};
}
export function decodeIntegration_FileDetails(json) {
  return {
    status: optionalDecoder(decodeFileStatus)(json.status),
    time: optionalDecoder(decodeTimestamp)(json.time),
    replacedByAPI: assertBoolean(json.replacedByAPI)
  };
}
export function encodeIntegration_FileDetails(value) {
  return {
    status: optionalEncoder(encodeFileStatus)(value.status),
    time: optionalEncoder(encodeTimestamp)(value.time),
    replacedByAPI: value.replacedByAPI
  };
}
export function decodeIntegration_FileStatusData(json) {
  return {
    invoices: decodeIntegration_FileDetails(json.invoices),
    debtors: decodeIntegration_FileDetails(json.debtors),
    carriers: decodeIntegration_FileDetails(json.carriers),
    agings: decodeIntegration_FileDetails(json.agings)
  };
}
export function encodeIntegration_FileStatusData(value) {
  return {
    invoices: encodeIntegration_FileDetails(value.invoices),
    debtors: encodeIntegration_FileDetails(value.debtors),
    carriers: encodeIntegration_FileDetails(value.carriers),
    agings: encodeIntegration_FileDetails(value.agings)
  };
}
export function decodeIntegration_MercuryGateDetails(json) {
  return {
    remitToAddressTypes: (json.remitToAddressTypes || []).map(assertString),
    importEnterpriseBillTo: assertBoolean(json.importEnterpriseBillTo),
    billToCustomerBillingAdapter: assertString(
      json.billToCustomerBillingAdapter
    )
  };
}
export function encodeIntegration_MercuryGateDetails(value) {
  return {
    remitToAddressTypes: value.remitToAddressTypes,
    importEnterpriseBillTo: value.importEnterpriseBillTo,
    billToCustomerBillingAdapter: value.billToCustomerBillingAdapter
  };
}
export function decodeIntegration_TableSyncStatus(json) {
  return {
    label: assertString(json.label),
    lastSyncedAt: optionalDecoder(decodeTimestamp)(json.lastSyncedAt),
    lastFailedAt: optionalDecoder(decodeTimestamp)(json.lastFailedAt)
  };
}
export function encodeIntegration_TableSyncStatus(value) {
  return {
    label: value.label,
    lastSyncedAt: optionalEncoder(encodeTimestamp)(value.lastSyncedAt),
    lastFailedAt: optionalEncoder(encodeTimestamp)(value.lastFailedAt)
  };
}
export function decodeIntegration_TransmissionStatusData(json) {
  return {
    transmissionDetails: assertString(json.transmissionDetails),
    time: decodeTimestamp(json.time)
  };
}
export function encodeIntegration_TransmissionStatusData(value) {
  return {
    transmissionDetails: value.transmissionDetails,
    time: encodeTimestamp(value.time)
  };
}
export function decodeIntegration_UnknownIntegrationDetails(json) {
  return {};
}
export function encodeIntegration_UnknownIntegrationDetails(value) {
  return {};
}
export function decodeInvoiceAmountCheck(json) {
  return {
    suggestions: (json.suggestions || []).map(
      decodeFactorInvoiceAuditCheckSuggestion
    )
  };
}
export function encodeInvoiceAmountCheck(value) {
  return {
    suggestions: value.suggestions.map(encodeFactorInvoiceAuditCheckSuggestion)
  };
}
export function decodeInvoiceAmountReview(json) {
  return {
    id: assertNumber(json.id),
    document_id: assertNumber(json.document_id)
  };
}
export function encodeInvoiceAmountReview(value) {
  return {
    id: value.id,
    document_id: value.document_id
  };
}
export function decodeInvoiceAmountReviewCompletionRequest(json) {
  return {
    invoiceAmountReviewId: assertNumber(json.invoiceAmountReviewId),
    result: decodeInvoiceAmountReviewCompletionRequest_Result(json)
  };
}
export function encodeInvoiceAmountReviewCompletionRequest(value) {
  return __spreadValues({
    invoiceAmountReviewId: value.invoiceAmountReviewId
  }, encodeInvoiceAmountReviewCompletionRequest_Result(value.result));
}
export var InvoiceAmountReviewCompletionRequest_ResultType = /* @__PURE__ */ ((InvoiceAmountReviewCompletionRequest_ResultType2) => {
  InvoiceAmountReviewCompletionRequest_ResultType2["ResultFound"] = "resultFound";
  InvoiceAmountReviewCompletionRequest_ResultType2["NoResultFound"] = "noResultFound";
  return InvoiceAmountReviewCompletionRequest_ResultType2;
})(InvoiceAmountReviewCompletionRequest_ResultType || {});
export function decodeInvoiceAmountReviewCompletionRequest_Result(json) {
  if (json.resultFound !== void 0) {
    return {
      type: "resultFound" /* ResultFound */,
      resultFound: decodeInvoiceAmountReviewResultFound(json.resultFound)
    };
  } else if (json.noResultFound !== void 0) {
    return {
      type: "noResultFound" /* NoResultFound */,
      noResultFound: decodeInvoiceAmountReviewNoResultFound(json.noResultFound)
    };
  }
  throw new Error(
    "No field set in oneof InvoiceAmountReviewCompletionRequest_Result"
  );
}
export function encodeInvoiceAmountReviewCompletionRequest_Result(value) {
  switch (value.type) {
    case "resultFound" /* ResultFound */:
      return {
        resultFound: encodeInvoiceAmountReviewResultFound(value.resultFound)
      };
    case "noResultFound" /* NoResultFound */:
      return {
        noResultFound: encodeInvoiceAmountReviewNoResultFound(
          value.noResultFound
        )
      };
    default:
      throw new Error(
        "No field set in oneof InvoiceAmountReviewCompletionRequest_Result"
      );
  }
}
export function decodeInvoiceAmountReviewId(json) {
  return {
    invoiceAmountReviewId: optionalDecoder(assertNumber)(
      json.invoiceAmountReviewId
    )
  };
}
export function encodeInvoiceAmountReviewId(value) {
  return {
    invoiceAmountReviewId: value.invoiceAmountReviewId
  };
}
export var InvoiceAmountReviewNoResultFound = /* @__PURE__ */ ((InvoiceAmountReviewNoResultFound2) => {
  InvoiceAmountReviewNoResultFound2["InvoiceAmountReviewNoResultFoundNoInvoiceAmount"] = "NO_INVOICE_AMOUNT";
  return InvoiceAmountReviewNoResultFound2;
})(InvoiceAmountReviewNoResultFound || {});
export const decodeInvoiceAmountReviewNoResultFound = (value) => value;
export const encodeInvoiceAmountReviewNoResultFound = (value) => value;
export function decodeInvoiceAmountReviewResultFound(json) {
  return {
    assigned_amount: assertString(json.assigned_amount)
  };
}
export function encodeInvoiceAmountReviewResultFound(value) {
  return {
    assigned_amount: value.assigned_amount
  };
}
export function decodeInvoiceAutoProcessingData(json) {
  return {
    invoiceId: assertNumber(json.invoiceId),
    invoiceType: decodeInvoiceAutoProcessingInvoiceType(json.invoiceType),
    wasAutoProcessed: assertBoolean(json.wasAutoProcessed)
  };
}
export function encodeInvoiceAutoProcessingData(value) {
  return {
    invoiceId: value.invoiceId,
    invoiceType: encodeInvoiceAutoProcessingInvoiceType(value.invoiceType),
    wasAutoProcessed: value.wasAutoProcessed
  };
}
export var InvoiceAutoProcessingInvoiceType = /* @__PURE__ */ ((InvoiceAutoProcessingInvoiceType2) => {
  InvoiceAutoProcessingInvoiceType2["InvoiceAutoProcessingInvoiceTypeFactorInvoice"] = "FactorInvoice";
  InvoiceAutoProcessingInvoiceType2["InvoiceAutoProcessingInvoiceTypeCarrierInvoice"] = "CarrierInvoice";
  return InvoiceAutoProcessingInvoiceType2;
})(InvoiceAutoProcessingInvoiceType || {});
export const decodeInvoiceAutoProcessingInvoiceType = (value) => value;
export const encodeInvoiceAutoProcessingInvoiceType = (value) => value;
export function decodeInvoiceDateCheck(json) {
  return {};
}
export function encodeInvoiceDateCheck(value) {
  return {};
}
export function decodeInvoiceDeliveryDateCheck(json) {
  return {
    deliveryDate: optionalDecoder(assertString)(json.deliveryDate),
    checkDays: optionalDecoder(assertNumber)(json.checkDays),
    invoiceDate: optionalDecoder(assertString)(json.invoiceDate),
    daysSince: optionalDecoder(assertNumber)(json.daysSince),
    deliveryDateSource: optionalDecoder(assertString)(json.deliveryDateSource)
  };
}
export function encodeInvoiceDeliveryDateCheck(value) {
  return {
    deliveryDate: value.deliveryDate,
    checkDays: value.checkDays,
    invoiceDate: value.invoiceDate,
    daysSince: value.daysSince,
    deliveryDateSource: value.deliveryDateSource
  };
}
export function decodeInvoiceNumberCheck(json) {
  return {
    suggestions: (json.suggestions || []).map(
      decodeFactorInvoiceAuditCheckSuggestion
    )
  };
}
export function encodeInvoiceNumberCheck(value) {
  return {
    suggestions: value.suggestions.map(encodeFactorInvoiceAuditCheckSuggestion)
  };
}
export function decodeInvoiceNumberReview(json) {
  return {
    id: assertNumber(json.id),
    document_id: assertNumber(json.document_id)
  };
}
export function encodeInvoiceNumberReview(value) {
  return {
    id: value.id,
    document_id: value.document_id
  };
}
export function decodeInvoiceNumberReviewCompletionRequest(json) {
  return {
    invoiceNumberReviewId: assertNumber(json.invoiceNumberReviewId),
    assignedInvoiceNumber: assertString(json.assignedInvoiceNumber)
  };
}
export function encodeInvoiceNumberReviewCompletionRequest(value) {
  return {
    invoiceNumberReviewId: value.invoiceNumberReviewId,
    assignedInvoiceNumber: value.assignedInvoiceNumber
  };
}
export function decodeInvoiceNumberReviewId(json) {
  return {
    invoiceNumberReviewId: optionalDecoder(assertNumber)(
      json.invoiceNumberReviewId
    )
  };
}
export function encodeInvoiceNumberReviewId(value) {
  return {
    invoiceNumberReviewId: value.invoiceNumberReviewId
  };
}
export function decodeLabel(json) {
  return {
    name: assertString(json.name),
    source: assertString(json.source)
  };
}
export function encodeLabel(value) {
  return {
    name: value.name,
    source: value.source
  };
}
export var LabelFilterState = /* @__PURE__ */ ((LabelFilterState2) => {
  LabelFilterState2["LabelFilterStateUnselected"] = "";
  LabelFilterState2["LabelFilterStateSelected"] = "+";
  LabelFilterState2["LabelFilterStateNegated"] = "-";
  return LabelFilterState2;
})(LabelFilterState || {});
export const decodeLabelFilterState = (value) => value;
export const encodeLabelFilterState = (value) => value;
export function decodeLabelRuleFailureCheck(json) {
  return {
    title: assertString(json.title),
    labelingRules: (json.labelingRules || []).map(decodeLabelingRule)
  };
}
export function encodeLabelRuleFailureCheck(value) {
  return {
    title: value.title,
    labelingRules: value.labelingRules.map(encodeLabelingRule)
  };
}
export function decodeLabelingCarrier(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name)
  };
}
export function encodeLabelingCarrier(value) {
  return {
    id: value.id,
    name: value.name
  };
}
export function decodeLabelingCustomers(json) {
  return {
    names: (json.names || []).map(assertString)
  };
}
export function encodeLabelingCustomers(value) {
  return {
    names: value.names
  };
}
export function decodeLabelingLoadIdPrefixes(json) {
  return {
    prefixes: (json.prefixes || []).map(assertString),
    loadId: assertString(json.loadId)
  };
}
export function encodeLabelingLoadIdPrefixes(value) {
  return {
    prefixes: value.prefixes,
    loadId: value.loadId
  };
}
export function decodeLabelingNoRecord(json) {
  return {};
}
export function encodeLabelingNoRecord(value) {
  return {};
}
export function decodeLabelingPeople(json) {
  return {
    people: (json.people || []).map(decodeLabelingPerson)
  };
}
export function encodeLabelingPeople(value) {
  return {
    people: value.people.map(encodeLabelingPerson)
  };
}
export function decodeLabelingPerson(json) {
  return {
    field: decodeLabelingPersonField(json.field),
    value: assertString(json.value)
  };
}
export function encodeLabelingPerson(value) {
  return {
    field: encodeLabelingPersonField(value.field),
    value: value.value
  };
}
export var LabelingPersonField = /* @__PURE__ */ ((LabelingPersonField2) => {
  LabelingPersonField2["LabelingPersonFieldOffice"] = "office";
  LabelingPersonField2["LabelingPersonFieldSalesRep"] = "sales_rep";
  LabelingPersonField2["LabelingPersonFieldDispatcher"] = "dispatcher";
  return LabelingPersonField2;
})(LabelingPersonField || {});
export const decodeLabelingPersonField = (value) => value;
export const encodeLabelingPersonField = (value) => value;
export function decodeLabelingRecord(json) {
  return {
    record: decodeLabelingRecord_Record(json)
  };
}
export function encodeLabelingRecord(value) {
  return __spreadValues({}, encodeLabelingRecord_Record(value.record));
}
export var LabelingRecord_RecordType = /* @__PURE__ */ ((LabelingRecord_RecordType2) => {
  LabelingRecord_RecordType2["None"] = "none";
  LabelingRecord_RecordType2["References"] = "references";
  LabelingRecord_RecordType2["Carrier"] = "carrier";
  LabelingRecord_RecordType2["People"] = "people";
  LabelingRecord_RecordType2["Customers"] = "customers";
  LabelingRecord_RecordType2["LoadIdPrefixes"] = "loadIdPrefixes";
  return LabelingRecord_RecordType2;
})(LabelingRecord_RecordType || {});
export function decodeLabelingRecord_Record(json) {
  if (json.none !== void 0) {
    return {
      type: "none" /* None */,
      none: decodeLabelingNoRecord(json.none)
    };
  } else if (json.references !== void 0) {
    return {
      type: "references" /* References */,
      references: decodeLabelingReferences(json.references)
    };
  } else if (json.carrier !== void 0) {
    return {
      type: "carrier" /* Carrier */,
      carrier: decodeLabelingCarrier(json.carrier)
    };
  } else if (json.people !== void 0) {
    return {
      type: "people" /* People */,
      people: decodeLabelingPeople(json.people)
    };
  } else if (json.customers !== void 0) {
    return {
      type: "customers" /* Customers */,
      customers: decodeLabelingCustomers(json.customers)
    };
  } else if (json.loadIdPrefixes !== void 0) {
    return {
      type: "loadIdPrefixes" /* LoadIdPrefixes */,
      loadIdPrefixes: decodeLabelingLoadIdPrefixes(json.loadIdPrefixes)
    };
  }
  throw new Error("No field set in oneof LabelingRecord_Record");
}
export function encodeLabelingRecord_Record(value) {
  switch (value.type) {
    case "none" /* None */:
      return { none: encodeLabelingNoRecord(value.none) };
    case "references" /* References */:
      return { references: encodeLabelingReferences(value.references) };
    case "carrier" /* Carrier */:
      return { carrier: encodeLabelingCarrier(value.carrier) };
    case "people" /* People */:
      return { people: encodeLabelingPeople(value.people) };
    case "customers" /* Customers */:
      return { customers: encodeLabelingCustomers(value.customers) };
    case "loadIdPrefixes" /* LoadIdPrefixes */:
      return {
        loadIdPrefixes: encodeLabelingLoadIdPrefixes(value.loadIdPrefixes)
      };
    default:
      throw new Error("No field set in oneof LabelingRecord_Record");
  }
}
export function decodeLabelingReference(json) {
  return {
    key: assertString(json.key),
    value: assertString(json.value),
    owner: decodeLabelingReferenceOwner(json.owner)
  };
}
export function encodeLabelingReference(value) {
  return {
    key: value.key,
    value: value.value,
    owner: encodeLabelingReferenceOwner(value.owner)
  };
}
export var LabelingReferenceOwner = /* @__PURE__ */ ((LabelingReferenceOwner2) => {
  LabelingReferenceOwner2["LabelingReferenceOwnerLoad"] = "load";
  LabelingReferenceOwner2["LabelingReferenceOwnerShipment"] = "shipment";
  LabelingReferenceOwner2["LabelingReferenceOwnerCustomer"] = "customer";
  return LabelingReferenceOwner2;
})(LabelingReferenceOwner || {});
export const decodeLabelingReferenceOwner = (value) => value;
export const encodeLabelingReferenceOwner = (value) => value;
export function decodeLabelingReferences(json) {
  return {
    references: (json.references || []).map(decodeLabelingReference)
  };
}
export function encodeLabelingReferences(value) {
  return {
    references: value.references.map(encodeLabelingReference)
  };
}
export function decodeLabelingRule(json) {
  return {
    label: assertString(json.label),
    kind: decodeLabelingRuleKind(json.kind),
    category: assertString(json.category),
    record: decodeLabelingRecord(json.record)
  };
}
export function encodeLabelingRule(value) {
  return {
    label: value.label,
    kind: encodeLabelingRuleKind(value.kind),
    category: value.category,
    record: encodeLabelingRecord(value.record)
  };
}
export var LabelingRuleKind = /* @__PURE__ */ ((LabelingRuleKind2) => {
  LabelingRuleKind2["LabelingRuleKindUnrecorded"] = "unrecorded";
  LabelingRuleKind2["LabelingRuleKindLoadReference"] = "load_reference";
  LabelingRuleKind2["LabelingRuleKindShipmentReference"] = "shipment_reference";
  LabelingRuleKind2["LabelingRuleKindCustomerReference"] = "customer_reference";
  LabelingRuleKind2["LabelingRuleKindReferenceCategory"] = "reference_category";
  LabelingRuleKind2["LabelingRuleKindLoadCustomerList"] = "load_customer_list";
  LabelingRuleKind2["LabelingRuleKindLoadPeople"] = "load_people";
  LabelingRuleKind2["LabelingRuleKindLoadOffice"] = "load_office";
  LabelingRuleKind2["LabelingRuleKindLoadBusinessUnit"] = "load_business_unit";
  LabelingRuleKind2["LabelingRuleKindLoadPriority"] = "load_priority";
  LabelingRuleKind2["LabelingRuleKindLoadMode"] = "load_mode";
  LabelingRuleKind2["LabelingRuleKindLoadDaysToPay"] = "load_days_to_pay";
  LabelingRuleKind2["LabelingRuleKindLoadQuickpay"] = "load_quickpay";
  LabelingRuleKind2["LabelingRuleKindLoadMultiDrop"] = "load_multi_drop";
  LabelingRuleKind2["LabelingRuleKindLoadMultiLeg"] = "load_multi_leg";
  LabelingRuleKind2["LabelingRuleKindLoadIdPrefix"] = "load_id_prefix";
  LabelingRuleKind2["LabelingRuleKindAuditBilling"] = "audit_billing";
  LabelingRuleKind2["LabelingRuleKindTonu"] = "tonu";
  LabelingRuleKind2["LabelingRuleKindLtlPayables"] = "ltl_payables";
  LabelingRuleKind2["LabelingRuleKindInvoiceException"] = "invoice_exception";
  LabelingRuleKind2["LabelingRuleKindEmail"] = "email";
  LabelingRuleKind2["LabelingRuleKindReplyParent"] = "reply_parent";
  LabelingRuleKind2["LabelingRuleKindDocumentTemplate"] = "document_template";
  LabelingRuleKind2["LabelingRuleKindLargeBatch"] = "large_batch";
  LabelingRuleKind2["LabelingRuleKindNoDocuments"] = "no_documents";
  LabelingRuleKind2["LabelingRuleKindSubmission"] = "submission";
  LabelingRuleKind2["LabelingRuleKindManual"] = "manual";
  LabelingRuleKind2["LabelingRuleKindCarrier"] = "carrier";
  LabelingRuleKind2["LabelingRuleKindAdHoc"] = "ad_hoc";
  return LabelingRuleKind2;
})(LabelingRuleKind || {});
export const decodeLabelingRuleKind = (value) => value;
export const encodeLabelingRuleKind = (value) => value;
export function decodeLlmPODDetectionFinalizeInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    account_id: assertNumber(json.account_id),
    document_ids: (json.document_ids || []).map(assertNumber)
  };
}
export function encodeLlmPODDetectionFinalizeInput(value) {
  return {
    import_process_id: value.import_process_id,
    account_id: value.account_id,
    document_ids: value.document_ids
  };
}
export function decodeLlmPODDetectionRunInput(json) {
  return {
    document_id: assertNumber(json.document_id)
  };
}
export function encodeLlmPODDetectionRunInput(value) {
  return {
    document_id: value.document_id
  };
}
export function decodeLlmPODDetectionRunOutput(json) {
  return {
    document_id: assertNumber(json.document_id)
  };
}
export function encodeLlmPODDetectionRunOutput(value) {
  return {
    document_id: value.document_id
  };
}
export function decodeLlmPODDetectionWorkflowInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    account_id: assertNumber(json.account_id),
    document_ids: (json.document_ids || []).map(assertNumber)
  };
}
export function encodeLlmPODDetectionWorkflowInput(value) {
  return {
    import_process_id: value.import_process_id,
    account_id: value.account_id,
    document_ids: value.document_ids
  };
}
export function decodeLoadIdExtractionAndConfirmationCheck(json) {
  return {
    suggestions: (json.suggestions || []).map(
      decodeFactorInvoiceAuditCheckSuggestion
    ),
    debtorAutofillOffer: optionalDecoder(
      decodeLoadIdExtractionAndConfirmationCheck_DebtorAutofillOffer
    )(json.debtorAutofillOffer)
  };
}
export function encodeLoadIdExtractionAndConfirmationCheck(value) {
  return {
    suggestions: value.suggestions.map(encodeFactorInvoiceAuditCheckSuggestion),
    debtorAutofillOffer: optionalEncoder(
      encodeLoadIdExtractionAndConfirmationCheck_DebtorAutofillOffer
    )(value.debtorAutofillOffer)
  };
}
export function decodeLoadIdExtractionAndConfirmationCheck_DebtorAutofillOffer(json) {
  return {
    debtorName: assertString(json.debtorName),
    keptPercentage: assertNumber(json.keptPercentage),
    priorCount: assertNumber(json.priorCount),
    enabled: assertBoolean(json.enabled)
  };
}
export function encodeLoadIdExtractionAndConfirmationCheck_DebtorAutofillOffer(value) {
  return {
    debtorName: value.debtorName,
    keptPercentage: value.keptPercentage,
    priorCount: value.priorCount,
    enabled: value.enabled
  };
}
export var LoadIdStrategy = /* @__PURE__ */ ((LoadIdStrategy2) => {
  LoadIdStrategy2["LoadIdStrategyStripDashedSuffix"] = "strip_dashed_suffix";
  LoadIdStrategy2["LoadIdStrategyStripLeadingZeros"] = "strip_leading_zeros";
  LoadIdStrategy2["LoadIdStrategyStripWhitespace"] = "strip_whitespace";
  LoadIdStrategy2["LoadIdStrategyStripColons"] = "strip_colons";
  LoadIdStrategy2["LoadIdStrategyStripPipe"] = "strip_pipe";
  LoadIdStrategy2["LoadIdStrategyUpcase"] = "upcase";
  LoadIdStrategy2["LoadIdStrategyNumbersOnly"] = "numbers_only";
  LoadIdStrategy2["LoadIdStrategyAlphanumericOnly"] = "alphanumeric_only";
  return LoadIdStrategy2;
})(LoadIdStrategy || {});
export const decodeLoadIdStrategy = (value) => value;
export const encodeLoadIdStrategy = (value) => value;
export function decodeLoadMatchCheck(json) {
  return {};
}
export function encodeLoadMatchCheck(value) {
  return {};
}
export function decodeLoadNote(json) {
  return {
    kind: decodeLoadNoteKind(json.kind),
    text: assertString(json.text)
  };
}
export function encodeLoadNote(value) {
  return {
    kind: encodeLoadNoteKind(value.kind),
    text: value.text
  };
}
export var LoadNoteKind = /* @__PURE__ */ ((LoadNoteKind2) => {
  LoadNoteKind2["LoadNoteKindLoad"] = "load_note";
  LoadNoteKind2["LoadNoteKindPayables"] = "payables_note";
  return LoadNoteKind2;
})(LoadNoteKind || {});
export const decodeLoadNoteKind = (value) => value;
export const encodeLoadNoteKind = (value) => value;
export function decodeLoadState(json) {
  return {
    id: assertNumber(json.id),
    load_id: assertNumber(json.load_id),
    created_at: decodeTimestamp(json.created_at),
    updated_at: decodeTimestamp(json.updated_at)
  };
}
export function encodeLoadState(value) {
  return {
    id: value.id,
    load_id: value.load_id,
    created_at: encodeTimestamp(value.created_at),
    updated_at: encodeTimestamp(value.updated_at)
  };
}
export function decodeLtlCarrierInvoiceMoveToTMSQueueRequest(json) {
  return {
    carrierInvoiceId: assertNumber(json.carrierInvoiceId),
    data: decodeLtlInvoiceMovedToTMSQueueData(json.data)
  };
}
export function encodeLtlCarrierInvoiceMoveToTMSQueueRequest(value) {
  return {
    carrierInvoiceId: value.carrierInvoiceId,
    data: encodeLtlInvoiceMovedToTMSQueueData(value.data)
  };
}
export function decodeLtlCarrierInvoiceReconcileRequest(json) {
  return {
    carrierInvoiceId: assertNumber(json.carrierInvoiceId),
    shippedItemReconcileItems: (json.shippedItemReconcileItems || []).map(
      decodeLtlCarrierInvoiceReconcileRequest_ShippedItemReconcileItem
    ),
    lineItemReconcileItems: (json.lineItemReconcileItems || []).map(
      decodeLtlCarrierInvoiceReconcileRequest_LineItemReconcileItem
    )
  };
}
export function encodeLtlCarrierInvoiceReconcileRequest(value) {
  return {
    carrierInvoiceId: value.carrierInvoiceId,
    shippedItemReconcileItems: value.shippedItemReconcileItems.map(
      encodeLtlCarrierInvoiceReconcileRequest_ShippedItemReconcileItem
    ),
    lineItemReconcileItems: value.lineItemReconcileItems.map(
      encodeLtlCarrierInvoiceReconcileRequest_LineItemReconcileItem
    )
  };
}
export function decodeLtlCarrierInvoiceReconcileRequest_LineItemReconcileItem(json) {
  return {
    loadItemId: assertNumber(json.loadItemId),
    carrierItemId: optionalDecoder(assertNumber)(json.carrierItemId),
    description: assertString(json.description),
    oldAmount: assertString(json.oldAmount),
    newAmount: assertString(json.newAmount)
  };
}
export function encodeLtlCarrierInvoiceReconcileRequest_LineItemReconcileItem(value) {
  return {
    loadItemId: value.loadItemId,
    carrierItemId: value.carrierItemId,
    description: value.description,
    oldAmount: value.oldAmount,
    newAmount: value.newAmount
  };
}
export function decodeLtlCarrierInvoiceReconcileRequest_ShippedItemReconcileItem(json) {
  return {
    loadItemId: assertNumber(json.loadItemId),
    carrierItemId: optionalDecoder(assertNumber)(json.carrierItemId),
    description: assertString(json.description),
    oldWeight: assertString(json.oldWeight),
    newWeight: assertString(json.newWeight),
    oldClass: assertString(json.oldClass),
    newClass: assertString(json.newClass)
  };
}
export function encodeLtlCarrierInvoiceReconcileRequest_ShippedItemReconcileItem(value) {
  return {
    loadItemId: value.loadItemId,
    carrierItemId: value.carrierItemId,
    description: value.description,
    oldWeight: value.oldWeight,
    newWeight: value.newWeight,
    oldClass: value.oldClass,
    newClass: value.newClass
  };
}
export var LtlCarrierInvoiceReconcileType = /* @__PURE__ */ ((LtlCarrierInvoiceReconcileType2) => {
  LtlCarrierInvoiceReconcileType2["LtlCarrierInvoiceReconcileTypeClass"] = "class";
  LtlCarrierInvoiceReconcileType2["LtlCarrierInvoiceReconcileTypeWeight"] = "weight";
  LtlCarrierInvoiceReconcileType2["LtlCarrierInvoiceReconcileTypeAmount"] = "amount";
  return LtlCarrierInvoiceReconcileType2;
})(LtlCarrierInvoiceReconcileType || {});
export const decodeLtlCarrierInvoiceReconcileType = (value) => value;
export const encodeLtlCarrierInvoiceReconcileType = (value) => value;
export function decodeLtlInvoiceMovedToTMSQueueData(json) {
  return {
    reason: assertString(json.reason),
    queueName: assertString(json.queueName)
  };
}
export function encodeLtlInvoiceMovedToTMSQueueData(value) {
  return {
    reason: value.reason,
    queueName: value.queueName
  };
}
export function decodeLtlItemPairMetadata(json) {
  return {
    amountMismatch: assertBoolean(json.amountMismatch),
    weightMismatch: assertBoolean(json.weightMismatch),
    classMismatch: assertBoolean(json.classMismatch),
    displayMismatches: assertBoolean(json.displayMismatches),
    itemType: decodeLtlItemType(json.itemType),
    description: assertString(json.description),
    sortScore: assertNumber(json.sortScore)
  };
}
export function encodeLtlItemPairMetadata(value) {
  return {
    amountMismatch: value.amountMismatch,
    weightMismatch: value.weightMismatch,
    classMismatch: value.classMismatch,
    displayMismatches: value.displayMismatches,
    itemType: encodeLtlItemType(value.itemType),
    description: value.description,
    sortScore: value.sortScore
  };
}
export var LtlItemType = /* @__PURE__ */ ((LtlItemType2) => {
  LtlItemType2["LtlItemTypeLineHaul"] = "line_haul";
  LtlItemType2["LtlItemTypeMinimum"] = "minimum";
  LtlItemType2["LtlItemTypeDiscount"] = "discount";
  LtlItemType2["LtlItemTypeFuelSurcharge"] = "fuel_surcharge";
  LtlItemType2["LtlItemTypeAdditionalAccessorial"] = "additional_accessorial";
  return LtlItemType2;
})(LtlItemType || {});
export const decodeLtlItemType = (value) => value;
export const encodeLtlItemType = (value) => value;
export function decodeLtlPayablesCarrierInvoiceSubmission(json) {
  return {
    id: assertNumber(json.id),
    receivedDate: decodeTimestamp(json.receivedDate),
    importState: decodeLtlPayablesCarrierInvoiceSubmissionImportState(
      json.importState
    ),
    loadStateId: optionalDecoder(assertNumber)(json.loadStateId),
    proNumber: optionalDecoder(assertString)(json.proNumber),
    scac: optionalDecoder(assertString)(json.scac),
    tmsCarrierId: optionalDecoder(assertNumber)(json.tmsCarrierId),
    documents: (json.documents || []).map(
      decodeLtlPayablesCarrierInvoiceSubmission_Document
    )
  };
}
export function encodeLtlPayablesCarrierInvoiceSubmission(value) {
  return {
    id: value.id,
    receivedDate: encodeTimestamp(value.receivedDate),
    importState: encodeLtlPayablesCarrierInvoiceSubmissionImportState(
      value.importState
    ),
    loadStateId: value.loadStateId,
    proNumber: value.proNumber,
    scac: value.scac,
    tmsCarrierId: value.tmsCarrierId,
    documents: value.documents.map(
      encodeLtlPayablesCarrierInvoiceSubmission_Document
    )
  };
}
export function decodeLtlPayablesCarrierInvoiceSubmission_Document(json) {
  return {
    documentType: decodeDocumentType(json.documentType)
  };
}
export function encodeLtlPayablesCarrierInvoiceSubmission_Document(value) {
  return {
    documentType: encodeDocumentType(value.documentType)
  };
}
export function decodeLtlPayablesCarrierInvoiceSubmissionIdsQuery(json) {
  return {
    importState: decodeLtlPayablesCarrierInvoiceSubmissionImportState(
      json.importState
    ),
    pageNumber: assertNumber(json.pageNumber),
    entriesPerPage: assertNumber(json.entriesPerPage),
    sortOrder: decodeSortOrder(json.sortOrder),
    searchTerm: assertString(json.searchTerm)
  };
}
export function encodeLtlPayablesCarrierInvoiceSubmissionIdsQuery(value) {
  return {
    importState: encodeLtlPayablesCarrierInvoiceSubmissionImportState(
      value.importState
    ),
    pageNumber: value.pageNumber,
    entriesPerPage: value.entriesPerPage,
    sortOrder: encodeSortOrder(value.sortOrder),
    searchTerm: value.searchTerm
  };
}
export var LtlPayablesCarrierInvoiceSubmissionImportState = /* @__PURE__ */ ((LtlPayablesCarrierInvoiceSubmissionImportState2) => {
  LtlPayablesCarrierInvoiceSubmissionImportState2["LtlPayablesCarrierInvoiceSubmissionImportStatePendingImport"] = "pending_import";
  LtlPayablesCarrierInvoiceSubmissionImportState2["LtlPayablesCarrierInvoiceSubmissionImportStateSentToImport"] = "sent_to_import";
  LtlPayablesCarrierInvoiceSubmissionImportState2["LtlPayablesCarrierInvoiceSubmissionImportStateNotSentToImport"] = "not_sent_to_import";
  return LtlPayablesCarrierInvoiceSubmissionImportState2;
})(LtlPayablesCarrierInvoiceSubmissionImportState || {});
export const decodeLtlPayablesCarrierInvoiceSubmissionImportState = (value) => value;
export const encodeLtlPayablesCarrierInvoiceSubmissionImportState = (value) => value;
export function decodeManualPODMarkReason(json) {
  return {
    code: decodeManualPODMarkReasonCode(json.code),
    other_text: optionalDecoder(assertString)(json.other_text)
  };
}
export function encodeManualPODMarkReason(value) {
  return {
    code: encodeManualPODMarkReasonCode(value.code),
    other_text: value.other_text
  };
}
export var ManualPODMarkReasonCode = /* @__PURE__ */ ((ManualPODMarkReasonCode2) => {
  ManualPODMarkReasonCode2["ManualPODMarkReasonCodeSignature"] = "signature";
  ManualPODMarkReasonCode2["ManualPODMarkReasonCodeNoDamages"] = "no_damages";
  ManualPODMarkReasonCode2["ManualPODMarkReasonCodeOneReferenceMatch"] = "one_reference_match";
  ManualPODMarkReasonCode2["ManualPODMarkReasonCodeAllPagesPresent"] = "all_pages_present";
  ManualPODMarkReasonCode2["ManualPODMarkReasonCodeOther"] = "other";
  ManualPODMarkReasonCode2["ManualPODMarkReasonCodeNoCarrierRate"] = "no_carrier_rate";
  ManualPODMarkReasonCode2["ManualPODMarkReasonCodeIllegible"] = "illegible";
  return ManualPODMarkReasonCode2;
})(ManualPODMarkReasonCode || {});
export const decodeManualPODMarkReasonCode = (value) => value;
export const encodeManualPODMarkReasonCode = (value) => value;
export function decodeMaxAmountReviewCheck(json) {
  return {
    amount: optionalDecoder(assertString)(json.amount),
    maxAmount: optionalDecoder(assertString)(json.maxAmount)
  };
}
export function encodeMaxAmountReviewCheck(value) {
  return {
    amount: value.amount,
    maxAmount: value.maxAmount
  };
}
export function decodeMultiPageBolReview(json) {
  return {
    id: assertNumber(json.id),
    document_id: assertNumber(json.document_id),
    all_pages_are_same_bol: optionalDecoder(decodeMultiPageBolReviewAnswer)(
      json.all_pages_are_same_bol
    ),
    all_pages_of_bol_present: optionalDecoder(decodeMultiPageBolReviewAnswer)(
      json.all_pages_of_bol_present
    )
  };
}
export function encodeMultiPageBolReview(value) {
  return {
    id: value.id,
    document_id: value.document_id,
    all_pages_are_same_bol: optionalEncoder(encodeMultiPageBolReviewAnswer)(
      value.all_pages_are_same_bol
    ),
    all_pages_of_bol_present: optionalEncoder(encodeMultiPageBolReviewAnswer)(
      value.all_pages_of_bol_present
    )
  };
}
export var MultiPageBolReviewAnswer = /* @__PURE__ */ ((MultiPageBolReviewAnswer2) => {
  MultiPageBolReviewAnswer2["MultiPageBolReviewAnswerNo"] = "no";
  MultiPageBolReviewAnswer2["MultiPageBolReviewAnswerUnsure"] = "unsure";
  MultiPageBolReviewAnswer2["MultiPageBolReviewAnswerYes"] = "yes";
  return MultiPageBolReviewAnswer2;
})(MultiPageBolReviewAnswer || {});
export const decodeMultiPageBolReviewAnswer = (value) => value;
export const encodeMultiPageBolReviewAnswer = (value) => value;
export function decodeMultiPageBolReviewCompletionRequest(json) {
  return {
    multiPageBolReviewId: assertNumber(json.multiPageBolReviewId),
    allPagesAreSameBol: assertString(json.allPagesAreSameBol),
    allPagesOfBolPresent: assertString(json.allPagesOfBolPresent)
  };
}
export function encodeMultiPageBolReviewCompletionRequest(value) {
  return {
    multiPageBolReviewId: value.multiPageBolReviewId,
    allPagesAreSameBol: value.allPagesAreSameBol,
    allPagesOfBolPresent: value.allPagesOfBolPresent
  };
}
export function decodeMultiPageBolReviewId(json) {
  return {
    multiPageBolReviewId: optionalDecoder(assertNumber)(
      json.multiPageBolReviewId
    )
  };
}
export function encodeMultiPageBolReviewId(value) {
  return {
    multiPageBolReviewId: value.multiPageBolReviewId
  };
}
export function decodeMyClientsAccountExecutive(json) {
  return {
    name: assertString(json.name)
  };
}
export function encodeMyClientsAccountExecutive(value) {
  return {
    name: value.name
  };
}
export var MyClientsBucket = /* @__PURE__ */ ((MyClientsBucket2) => {
  MyClientsBucket2["MyClientsBucketNew"] = "new";
  MyClientsBucket2["MyClientsBucketIncomplete"] = "incomplete";
  MyClientsBucket2["MyClientsBucketWaiting"] = "waiting";
  MyClientsBucket2["MyClientsBucketReplies"] = "replies";
  MyClientsBucket2["MyClientsBucketVerify"] = "verify";
  MyClientsBucket2["MyClientsBucketProcessed"] = "processed";
  MyClientsBucket2["MyClientsBucketSentToFMS"] = "sent_to_fms";
  MyClientsBucket2["MyClientsBucketPurchased"] = "purchased";
  return MyClientsBucket2;
})(MyClientsBucket || {});
export const decodeMyClientsBucket = (value) => value;
export const encodeMyClientsBucket = (value) => value;
export function decodeMyClientsBucketCount(json) {
  return {
    bucket: decodeMyClientsBucket(json.bucket),
    count: assertNumber(json.count),
    heat: decodeMyClientsHeat(json.heat)
  };
}
export function encodeMyClientsBucketCount(value) {
  return {
    bucket: encodeMyClientsBucket(value.bucket),
    count: value.count,
    heat: encodeMyClientsHeat(value.heat)
  };
}
export var MyClientsGroup = /* @__PURE__ */ ((MyClientsGroup2) => {
  MyClientsGroup2["MyClientsGroupNeedsAttention"] = "needs_attention";
  MyClientsGroup2["MyClientsGroupInFlight"] = "in_flight";
  MyClientsGroup2["MyClientsGroupFullyFunded"] = "fully_funded";
  return MyClientsGroup2;
})(MyClientsGroup || {});
export const decodeMyClientsGroup = (value) => value;
export const encodeMyClientsGroup = (value) => value;
export var MyClientsHeat = /* @__PURE__ */ ((MyClientsHeat2) => {
  MyClientsHeat2["MyClientsHeatStopped"] = "stopped";
  MyClientsHeat2["MyClientsHeatWaiting"] = "waiting";
  MyClientsHeat2["MyClientsHeatForYou"] = "for_you";
  MyClientsHeat2["MyClientsHeatAuto"] = "auto";
  MyClientsHeat2["MyClientsHeatDone"] = "done";
  return MyClientsHeat2;
})(MyClientsHeat || {});
export const decodeMyClientsHeat = (value) => value;
export const encodeMyClientsHeat = (value) => value;
export function decodeMyClientsInvoice(json) {
  return {
    factor_invoice_id: optionalDecoder(assertNumber)(json.factor_invoice_id),
    email_message_id: optionalDecoder(assertNumber)(json.email_message_id),
    invoice_number: optionalDecoder(assertString)(json.invoice_number),
    load_id: optionalDecoder(assertString)(json.load_id),
    debtor: optionalDecoder(assertString)(json.debtor),
    bucket: decodeMyClientsBucket(json.bucket),
    heat: decodeMyClientsHeat(json.heat),
    reason: optionalDecoder(assertString)(json.reason),
    amount: optionalDecoder(assertString)(json.amount),
    age: assertString(json.age),
    amount_value: optionalDecoder(assertNumber)(json.amount_value),
    age_at: decodeTimestamp(json.age_at),
    verification_batch_id: optionalDecoder(assertNumber)(
      json.verification_batch_id
    )
  };
}
export function encodeMyClientsInvoice(value) {
  return {
    factor_invoice_id: value.factor_invoice_id,
    email_message_id: value.email_message_id,
    invoice_number: value.invoice_number,
    load_id: value.load_id,
    debtor: value.debtor,
    bucket: encodeMyClientsBucket(value.bucket),
    heat: encodeMyClientsHeat(value.heat),
    reason: value.reason,
    amount: value.amount,
    age: value.age,
    amount_value: value.amount_value,
    age_at: encodeTimestamp(value.age_at),
    verification_batch_id: value.verification_batch_id
  };
}
export function decodeMyClientsOverviewRow(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    mc_number: optionalDecoder(assertString)(json.mc_number),
    account_executive: optionalDecoder(assertString)(json.account_executive),
    counts: (json.counts || []).map(decodeMyClientsBucketCount),
    total: optionalDecoder(assertString)(json.total),
    oldest: optionalDecoder(assertString)(json.oldest),
    group: decodeMyClientsGroup(json.group),
    lines: (json.lines || []).map(decodeMyClientsInvoice),
    total_amount: optionalDecoder(assertNumber)(json.total_amount),
    oldest_at: optionalDecoder(decodeTimestamp)(json.oldest_at)
  };
}
export function encodeMyClientsOverviewRow(value) {
  return {
    id: value.id,
    name: value.name,
    mc_number: value.mc_number,
    account_executive: value.account_executive,
    counts: value.counts.map(encodeMyClientsBucketCount),
    total: value.total,
    oldest: value.oldest,
    group: encodeMyClientsGroup(value.group),
    lines: value.lines.map(encodeMyClientsInvoice),
    total_amount: value.total_amount,
    oldest_at: optionalEncoder(encodeTimestamp)(value.oldest_at)
  };
}
export function decodeNetworkBroker(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    mc_number: optionalDecoder(assertString)(json.mc_number),
    verifications_mode: decodeVerificationsMode(json.verifications_mode),
    is_matched: assertBoolean(json.is_matched)
  };
}
export function encodeNetworkBroker(value) {
  return {
    id: value.id,
    name: value.name,
    mc_number: value.mc_number,
    verifications_mode: encodeVerificationsMode(value.verifications_mode),
    is_matched: value.is_matched
  };
}
export function decodeNgaIgnoreForwardRulesAccountInput(json) {
  return {
    account_id: assertNumber(json.account_id)
  };
}
export function encodeNgaIgnoreForwardRulesAccountInput(value) {
  return {
    account_id: value.account_id
  };
}
export function decodeNgaIgnoreForwardRulesBackfillInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    as_of_date: assertString(json.as_of_date)
  };
}
export function encodeNgaIgnoreForwardRulesBackfillInput(value) {
  return {
    account_id: value.account_id,
    as_of_date: value.as_of_date
  };
}
export function decodeNgaIgnoreForwardRulesWeekInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    week_start: assertString(json.week_start)
  };
}
export function encodeNgaIgnoreForwardRulesWeekInput(value) {
  return {
    account_id: value.account_id,
    week_start: value.week_start
  };
}
export function decodeNgaIndexingSlaAccountInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    report_id: assertNumber(json.report_id)
  };
}
export function encodeNgaIndexingSlaAccountInput(value) {
  return {
    account_id: value.account_id,
    report_id: value.report_id
  };
}
export function decodeNgaIndexingSlaAccountOutput(json) {
  return {};
}
export function encodeNgaIndexingSlaAccountOutput(value) {
  return {};
}
export function decodeNgaIndexingSlaAccountStatsOutput(json) {
  return {
    account_id: assertNumber(json.account_id),
    sla_breached: assertBoolean(json.sla_breached),
    queue_median: assertNumber(json.queue_median),
    queue_p90: assertNumber(json.queue_p90),
    at_risk_peak: assertNumber(json.at_risk_peak),
    missed_sla_peak: assertNumber(json.missed_sla_peak),
    queue_peak: assertNumber(json.queue_peak),
    manual_review_queue_document_type_peak: assertNumber(
      json.manual_review_queue_document_type_peak
    ),
    manual_review_queue_invoice_number_peak: assertNumber(
      json.manual_review_queue_invoice_number_peak
    ),
    manual_review_queue_invoice_amount_peak: assertNumber(
      json.manual_review_queue_invoice_amount_peak
    ),
    manual_review_queue_multipage_bol_peak: assertNumber(
      json.manual_review_queue_multipage_bol_peak
    ),
    manual_review_queue_batch_grouping_peak: assertNumber(
      json.manual_review_queue_batch_grouping_peak
    ),
    manual_review_queue_group_load_peak: assertNumber(
      json.manual_review_queue_group_load_peak
    ),
    manual_review_queue_load_reference_match_peak: assertNumber(
      json.manual_review_queue_load_reference_match_peak
    ),
    snapshot_count: assertNumber(json.snapshot_count)
  };
}
export function encodeNgaIndexingSlaAccountStatsOutput(value) {
  return {
    account_id: value.account_id,
    sla_breached: value.sla_breached,
    queue_median: value.queue_median,
    queue_p90: value.queue_p90,
    at_risk_peak: value.at_risk_peak,
    missed_sla_peak: value.missed_sla_peak,
    queue_peak: value.queue_peak,
    manual_review_queue_document_type_peak: value.manual_review_queue_document_type_peak,
    manual_review_queue_invoice_number_peak: value.manual_review_queue_invoice_number_peak,
    manual_review_queue_invoice_amount_peak: value.manual_review_queue_invoice_amount_peak,
    manual_review_queue_multipage_bol_peak: value.manual_review_queue_multipage_bol_peak,
    manual_review_queue_batch_grouping_peak: value.manual_review_queue_batch_grouping_peak,
    manual_review_queue_group_load_peak: value.manual_review_queue_group_load_peak,
    manual_review_queue_load_reference_match_peak: value.manual_review_queue_load_reference_match_peak,
    snapshot_count: value.snapshot_count
  };
}
export function decodeNgaIndexingSlaComputeStatsInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    date: assertString(json.date)
  };
}
export function encodeNgaIndexingSlaComputeStatsInput(value) {
  return {
    account_id: value.account_id,
    date: value.date
  };
}
export function decodeNgaIndexingSlaReportOutput(json) {
  return {
    report_id: assertNumber(json.report_id)
  };
}
export function encodeNgaIndexingSlaReportOutput(value) {
  return {
    report_id: value.report_id
  };
}
export function decodeNgaIndexingSlaRollupInput(json) {
  return {
    account_ids: (json.account_ids || []).map(assertNumber),
    date: assertString(json.date)
  };
}
export function encodeNgaIndexingSlaRollupInput(value) {
  return {
    account_ids: value.account_ids,
    date: value.date
  };
}
export function decodeNgaIndexingSlaSnapshotInput(json) {
  return {
    account_ids: (json.account_ids || []).map(assertNumber),
    slot: assertString(json.slot)
  };
}
export function encodeNgaIndexingSlaSnapshotInput(value) {
  return {
    account_ids: value.account_ids,
    slot: value.slot
  };
}
export function decodeNgaIndexingSlaWriteRollupInput(json) {
  return {
    stats: (json.stats || []).map(decodeNgaIndexingSlaAccountStatsOutput),
    date: assertString(json.date)
  };
}
export function encodeNgaIndexingSlaWriteRollupInput(value) {
  return {
    stats: value.stats.map(encodeNgaIndexingSlaAccountStatsOutput),
    date: value.date
  };
}
export function decodeNgaRulesResponse(json) {
  return {
    reviewPercentageRules: (json.reviewPercentageRules || []).map(
      decodeNgaRulesResponse_ReviewPercentageRule
    ),
    availableCreditRatings: (json.availableCreditRatings || []).map(
      assertString
    )
  };
}
export function encodeNgaRulesResponse(value) {
  return {
    reviewPercentageRules: value.reviewPercentageRules.map(
      encodeNgaRulesResponse_ReviewPercentageRule
    ),
    availableCreditRatings: value.availableCreditRatings
  };
}
export function decodeNgaRulesResponse_ReviewPercentageRule(json) {
  return {
    creditRatings: (json.creditRatings || []).map(assertString),
    reviewPercentage: assertNumber(json.reviewPercentage),
    ngaRuleId: optionalDecoder(assertNumber)(json.ngaRuleId)
  };
}
export function encodeNgaRulesResponse_ReviewPercentageRule(value) {
  return {
    creditRatings: value.creditRatings,
    reviewPercentage: value.reviewPercentage,
    ngaRuleId: value.ngaRuleId
  };
}
export function decodeNgaRulesUpdateRequest(json) {
  return {
    reviewPercentageRules: (json.reviewPercentageRules || []).map(
      decodeNgaRulesUpdateRequest_ReviewPercentageRule
    ),
    accountDefaultReviewPercentage: assertNumber(
      json.accountDefaultReviewPercentage
    )
  };
}
export function encodeNgaRulesUpdateRequest(value) {
  return {
    reviewPercentageRules: value.reviewPercentageRules.map(
      encodeNgaRulesUpdateRequest_ReviewPercentageRule
    ),
    accountDefaultReviewPercentage: value.accountDefaultReviewPercentage
  };
}
export function decodeNgaRulesUpdateRequest_ReviewPercentageRule(json) {
  return {
    creditRatings: (json.creditRatings || []).map(assertString),
    reviewPercentage: assertNumber(json.reviewPercentage),
    ngaRuleId: optionalDecoder(assertNumber)(json.ngaRuleId)
  };
}
export function encodeNgaRulesUpdateRequest_ReviewPercentageRule(value) {
  return {
    creditRatings: value.creditRatings,
    reviewPercentage: value.reviewPercentage,
    ngaRuleId: value.ngaRuleId
  };
}
export function decodeNoaEvent(json) {
  return {
    created_at: decodeTimestamp(json.created_at),
    factor_broker_id: assertNumber(json.factor_broker_id),
    factor_broker_name: assertString(json.factor_broker_name),
    tms_carrier_id: assertNumber(json.tms_carrier_id),
    carrier_name: assertString(json.carrier_name),
    factor_invoice_id: assertNumber(json.factor_invoice_id),
    load_id: optionalDecoder(assertString)(json.load_id)
  };
}
export function encodeNoaEvent(value) {
  return {
    created_at: encodeTimestamp(value.created_at),
    factor_broker_id: value.factor_broker_id,
    factor_broker_name: value.factor_broker_name,
    tms_carrier_id: value.tms_carrier_id,
    carrier_name: value.carrier_name,
    factor_invoice_id: value.factor_invoice_id,
    load_id: value.load_id
  };
}
export function decodeNoaSandboxRecipients(json) {
  return {
    recipients: optionalDecoder(assertString)(json.recipients)
  };
}
export function encodeNoaSandboxRecipients(value) {
  return {
    recipients: value.recipients
  };
}
export function decodeNoaSendQueueItem(json) {
  return {
    id: assertNumber(json.id),
    debtor_name: assertString(json.debtor_name),
    client_name: assertString(json.client_name),
    recipients: (json.recipients || []).map(decodeNoaSendQueueItem_Recipient),
    document_display: assertString(json.document_display),
    document_uploaded_at: optionalDecoder(decodeTimestamp)(
      json.document_uploaded_at
    ),
    warnings: (json.warnings || []).map(decodeNoaSendQueueItem_Warning),
    created_at: decodeTimestamp(json.created_at),
    document_id: optionalDecoder(assertNumber)(json.document_id),
    document_preview_url: assertString(json.document_preview_url),
    reopened_from_email: optionalDecoder(assertString)(
      json.reopened_from_email
    ),
    tms_carrier_id: assertNumber(json.tms_carrier_id),
    reopened_by_name: optionalDecoder(assertString)(json.reopened_by_name),
    suppressed_recipients: (json.suppressed_recipients || []).map(
      decodeNoaSendQueueItem_SuppressedRecipient
    )
  };
}
export function encodeNoaSendQueueItem(value) {
  return {
    id: value.id,
    debtor_name: value.debtor_name,
    client_name: value.client_name,
    recipients: value.recipients.map(encodeNoaSendQueueItem_Recipient),
    document_display: value.document_display,
    document_uploaded_at: optionalEncoder(encodeTimestamp)(
      value.document_uploaded_at
    ),
    warnings: value.warnings.map(encodeNoaSendQueueItem_Warning),
    created_at: encodeTimestamp(value.created_at),
    document_id: value.document_id,
    document_preview_url: value.document_preview_url,
    reopened_from_email: value.reopened_from_email,
    tms_carrier_id: value.tms_carrier_id,
    reopened_by_name: value.reopened_by_name,
    suppressed_recipients: value.suppressed_recipients.map(
      encodeNoaSendQueueItem_SuppressedRecipient
    )
  };
}
export function decodeNoaSendQueueItem_Recipient(json) {
  return {
    email: assertString(json.email),
    source_label: assertString(json.source_label)
  };
}
export function encodeNoaSendQueueItem_Recipient(value) {
  return {
    email: value.email,
    source_label: value.source_label
  };
}
export function decodeNoaSendQueueItem_SuppressedRecipient(json) {
  return {
    email: assertString(json.email),
    suppressed_at: optionalDecoder(decodeTimestamp)(json.suppressed_at)
  };
}
export function encodeNoaSendQueueItem_SuppressedRecipient(value) {
  return {
    email: value.email,
    suppressed_at: optionalEncoder(encodeTimestamp)(value.suppressed_at)
  };
}
export var NoaSendQueueItem_Warning = /* @__PURE__ */ ((NoaSendQueueItem_Warning2) => {
  NoaSendQueueItem_Warning2["NoaSendQueueItem_WarningUnknownWarning"] = "UNKNOWN_WARNING";
  NoaSendQueueItem_Warning2["NoaSendQueueItem_WarningNoAddress"] = "NO_ADDRESS";
  NoaSendQueueItem_Warning2["NoaSendQueueItem_WarningNoDocument"] = "NO_DOCUMENT";
  NoaSendQueueItem_Warning2["NoaSendQueueItem_WarningAddressBounced"] = "ADDRESS_BOUNCED";
  return NoaSendQueueItem_Warning2;
})(NoaSendQueueItem_Warning || {});
export const decodeNoaSendQueueItem_Warning = (value) => value;
export const encodeNoaSendQueueItem_Warning = (value) => value;
export function decodeNoaSendQueueItemsQuery(json) {
  return {
    page: assertNumber(json.page)
  };
}
export function encodeNoaSendQueueItemsQuery(value) {
  return {
    page: value.page
  };
}
export function decodeNoteOnLoadCheck(json) {
  return {
    note: optionalDecoder(decodeLoadNote)(json.note)
  };
}
export function encodeNoteOnLoadCheck(value) {
  return {
    note: optionalEncoder(encodeLoadNote)(value.note)
  };
}
export function decodeOfficePeopleMapping(json) {
  return {
    id: assertNumber(json.id),
    office: assertString(json.office),
    person: assertString(json.person)
  };
}
export function encodeOfficePeopleMapping(value) {
  return {
    id: value.id,
    office: value.office,
    person: value.person
  };
}
export function decodeOsdCheck(json) {
  return {};
}
export function encodeOsdCheck(value) {
  return {};
}
export function decodeOtherCheck(json) {
  return {};
}
export function encodeOtherCheck(value) {
  return {};
}
export function decodePageAnnotation(json) {
  return {
    kind: decodePageAnnotationKind(json.kind),
    top: assertNumber(json.top),
    left: assertNumber(json.left),
    width: assertNumber(json.width),
    height: assertNumber(json.height),
    text: assertString(json.text)
  };
}
export function encodePageAnnotation(value) {
  return {
    kind: encodePageAnnotationKind(value.kind),
    top: value.top,
    left: value.left,
    width: value.width,
    height: value.height,
    text: value.text
  };
}
export var PageAnnotationKind = /* @__PURE__ */ ((PageAnnotationKind2) => {
  PageAnnotationKind2["PageAnnotationKindUnknown"] = "unknown";
  PageAnnotationKind2["PageAnnotationKindStamp"] = "stamp";
  PageAnnotationKind2["PageAnnotationKindRedaction"] = "redaction";
  PageAnnotationKind2["PageAnnotationKindText"] = "text";
  PageAnnotationKind2["PageAnnotationKindCrop"] = "crop";
  return PageAnnotationKind2;
})(PageAnnotationKind || {});
export const decodePageAnnotationKind = (value) => value;
export const encodePageAnnotationKind = (value) => value;
export function decodePageAnnotationServiceReplaceRequest(json) {
  return {
    document_page_id: assertNumber(json.document_page_id),
    kind: decodePageAnnotationKind(json.kind),
    annotations: (json.annotations || []).map(decodePageAnnotation)
  };
}
export function encodePageAnnotationServiceReplaceRequest(value) {
  return {
    document_page_id: value.document_page_id,
    kind: encodePageAnnotationKind(value.kind),
    annotations: value.annotations.map(encodePageAnnotation)
  };
}
export function decodePageAnnotationServiceShowRequest(json) {
  return {
    document_page_id: assertNumber(json.document_page_id)
  };
}
export function encodePageAnnotationServiceShowRequest(value) {
  return {
    document_page_id: value.document_page_id
  };
}
export function decodePageAnnotationServiceShowResponse(json) {
  return {
    stamp_url: assertString(json.stamp_url),
    annotations: (json.annotations || []).map(decodePageAnnotation)
  };
}
export function encodePageAnnotationServiceShowResponse(value) {
  return {
    stamp_url: value.stamp_url,
    annotations: value.annotations.map(encodePageAnnotation)
  };
}
export function decodePlanLlmPODDetectionOutputSkip(json) {
  return {};
}
export function encodePlanLlmPODDetectionOutputSkip(value) {
  return {};
}
export function decodePlanSplitOutputSkip(json) {
  return {};
}
export function encodePlanSplitOutputSkip(value) {
  return {};
}
export function decodePODRequiredCheck(json) {
  return {};
}
export function encodePODRequiredCheck(value) {
  return {};
}
export var PODRequirementPolicy = /* @__PURE__ */ ((PODRequirementPolicy2) => {
  PODRequirementPolicy2["PODRequirementPolicyUseAccountSetting"] = "use_account_setting";
  PODRequirementPolicy2["PODRequirementPolicyAlways"] = "always";
  PODRequirementPolicy2["PODRequirementPolicyDetermineFromLoad"] = "determine from load";
  PODRequirementPolicy2["PODRequirementPolicyNever"] = "never";
  PODRequirementPolicy2["PODRequirementPolicyFlagAllBolsForMercuryGate"] = "flag_all_bols_for_mercury_gate";
  return PODRequirementPolicy2;
})(PODRequirementPolicy || {});
export const decodePODRequirementPolicy = (value) => value;
export const encodePODRequirementPolicy = (value) => value;
export function decodePortalDisplaySettings(json) {
  return {
    invoice_list_columns: (json.invoice_list_columns || []).map(
      decodePortalDisplaySettingsColumn
    ),
    show_invoice_number: assertBoolean(json.show_invoice_number),
    show_rush_fee: assertBoolean(json.show_rush_fee),
    rush_fee_label_override: optionalDecoder(assertString)(
      json.rush_fee_label_override
    ),
    factor_client_portal_enabled: assertBoolean(
      json.factor_client_portal_enabled
    ),
    required_fields: (json.required_fields || []).map(
      decodePortalDisplaySettingsInputField
    ),
    funding_methods: (json.funding_methods || []).map(
      decodePortalDisplaySettings_FundingMethod
    ),
    features: (json.features || []).map(decodePortalDisplaySettings_Feature),
    public_debtor_credit_check_path_segment: optionalDecoder(assertString)(
      json.public_debtor_credit_check_path_segment
    ),
    custom_message: optionalDecoder(assertString)(json.custom_message),
    funding_disclosure: optionalDecoder(assertString)(json.funding_disclosure)
  };
}
export function encodePortalDisplaySettings(value) {
  return {
    invoice_list_columns: value.invoice_list_columns.map(
      encodePortalDisplaySettingsColumn
    ),
    show_invoice_number: value.show_invoice_number,
    show_rush_fee: value.show_rush_fee,
    rush_fee_label_override: value.rush_fee_label_override,
    factor_client_portal_enabled: value.factor_client_portal_enabled,
    required_fields: value.required_fields.map(
      encodePortalDisplaySettingsInputField
    ),
    funding_methods: value.funding_methods.map(
      encodePortalDisplaySettings_FundingMethod
    ),
    features: value.features.map(encodePortalDisplaySettings_Feature),
    public_debtor_credit_check_path_segment: value.public_debtor_credit_check_path_segment,
    custom_message: value.custom_message,
    funding_disclosure: value.funding_disclosure
  };
}
export function decodePortalDisplaySettings_Feature(json) {
  return {
    id: assertString(json.id),
    label: assertString(json.label),
    enabled: assertBoolean(json.enabled),
    can_override_for_client: assertBoolean(json.can_override_for_client)
  };
}
export function encodePortalDisplaySettings_Feature(value) {
  return {
    id: value.id,
    label: value.label,
    enabled: value.enabled,
    can_override_for_client: value.can_override_for_client
  };
}
export function decodePortalDisplaySettings_FundingMethod(json) {
  return {
    portal_field_name: assertString(json.portal_field_name),
    spanish_translation: optionalDecoder(assertString)(
      json.spanish_translation
    ),
    payables_label: optionalDecoder(assertString)(json.payables_label)
  };
}
export function encodePortalDisplaySettings_FundingMethod(value) {
  return {
    portal_field_name: value.portal_field_name,
    spanish_translation: value.spanish_translation,
    payables_label: value.payables_label
  };
}
export var PortalDisplaySettingsColumn = /* @__PURE__ */ ((PortalDisplaySettingsColumn2) => {
  PortalDisplaySettingsColumn2["PortalDisplaySettingsColumnReceivedAt"] = "received_at";
  PortalDisplaySettingsColumn2["PortalDisplaySettingsColumnInvoiceNumber"] = "invoice_number";
  PortalDisplaySettingsColumn2["PortalDisplaySettingsColumnIsFuelAdvance"] = "is_fuel_advance";
  PortalDisplaySettingsColumn2["PortalDisplaySettingsColumnAmount"] = "amount";
  PortalDisplaySettingsColumn2["PortalDisplaySettingsColumnStatus"] = "status";
  PortalDisplaySettingsColumn2["PortalDisplaySettingsColumnLoadNumber"] = "load_number";
  return PortalDisplaySettingsColumn2;
})(PortalDisplaySettingsColumn || {});
export const decodePortalDisplaySettingsColumn = (value) => value;
export const encodePortalDisplaySettingsColumn = (value) => value;
export var PortalDisplaySettingsInputField = /* @__PURE__ */ ((PortalDisplaySettingsInputField2) => {
  PortalDisplaySettingsInputField2["PortalDisplaySettingsInputFieldDebtor"] = "debtor";
  PortalDisplaySettingsInputField2["PortalDisplaySettingsInputFieldInvoiceNumberField"] = "invoice_number";
  PortalDisplaySettingsInputField2["PortalDisplaySettingsInputFieldLoadId"] = "load_id";
  PortalDisplaySettingsInputField2["PortalDisplaySettingsInputFieldAmountField"] = "amount";
  PortalDisplaySettingsInputField2["PortalDisplaySettingsInputFieldComments"] = "comments";
  return PortalDisplaySettingsInputField2;
})(PortalDisplaySettingsInputField || {});
export const decodePortalDisplaySettingsInputField = (value) => value;
export const encodePortalDisplaySettingsInputField = (value) => value;
export var PortalSubmissionStrategy = /* @__PURE__ */ ((PortalSubmissionStrategy2) => {
  PortalSubmissionStrategy2["PortalSubmissionStrategyPortalOnly"] = "portal_only";
  PortalSubmissionStrategy2["PortalSubmissionStrategyFactorOnly"] = "factor_only";
  return PortalSubmissionStrategy2;
})(PortalSubmissionStrategy || {});
export const decodePortalSubmissionStrategy = (value) => value;
export const encodePortalSubmissionStrategy = (value) => value;
export function decodePostProcessActivityOutput(json) {
  return {
    batchResult: decodePostProcessActivityOutput_BatchResult(json)
  };
}
export function encodePostProcessActivityOutput(value) {
  return __spreadValues({}, encodePostProcessActivityOutput_BatchResult(value.batchResult));
}
export var PostProcessActivityOutput_BatchResultType = /* @__PURE__ */ ((PostProcessActivityOutput_BatchResultType2) => {
  PostProcessActivityOutput_BatchResultType2["ResultBatch"] = "result_batch";
  PostProcessActivityOutput_BatchResultType2["ResultNoBatch"] = "result_no_batch";
  PostProcessActivityOutput_BatchResultType2["ResultDidNotRun"] = "result_did_not_run";
  return PostProcessActivityOutput_BatchResultType2;
})(PostProcessActivityOutput_BatchResultType || {});
export function decodePostProcessActivityOutput_BatchResult(json) {
  if (json.result_batch !== void 0) {
    return {
      type: "result_batch" /* ResultBatch */,
      result_batch: decodePostProcessOutputBatch(json.result_batch)
    };
  } else if (json.result_no_batch !== void 0) {
    return {
      type: "result_no_batch" /* ResultNoBatch */,
      result_no_batch: decodePostProcessOutputNoBatch(json.result_no_batch)
    };
  } else if (json.result_did_not_run !== void 0) {
    return {
      type: "result_did_not_run" /* ResultDidNotRun */,
      result_did_not_run: decodeImportStageDidNotRun(json.result_did_not_run)
    };
  }
  throw new Error(
    "No field set in oneof PostProcessActivityOutput_BatchResult"
  );
}
export function encodePostProcessActivityOutput_BatchResult(value) {
  switch (value.type) {
    case "result_batch" /* ResultBatch */:
      return { result_batch: encodePostProcessOutputBatch(value.result_batch) };
    case "result_no_batch" /* ResultNoBatch */:
      return {
        result_no_batch: encodePostProcessOutputNoBatch(value.result_no_batch)
      };
    case "result_did_not_run" /* ResultDidNotRun */:
      return {
        result_did_not_run: encodeImportStageDidNotRun(
          value.result_did_not_run
        )
      };
    default:
      throw new Error(
        "No field set in oneof PostProcessActivityOutput_BatchResult"
      );
  }
}
export function decodePostProcessOutputBatch(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodePostProcessOutputBatch(value) {
  return {
    document_batch_id: value.document_batch_id
  };
}
export function decodePostProcessOutputNoBatch(json) {
  return {};
}
export function encodePostProcessOutputNoBatch(value) {
  return {};
}
export function decodeProcessInferenceResultsOutputBatch(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeProcessInferenceResultsOutputBatch(value) {
  return {
    document_batch_id: value.document_batch_id
  };
}
export function decodeProcessInferenceResultsOutputNoBatch(json) {
  return {};
}
export function encodeProcessInferenceResultsOutputNoBatch(value) {
  return {};
}
export function decodeProcessedCarrierInvoiceRowData(json) {
  return {
    invoice_number: optionalDecoder(assertString)(json.invoice_number),
    amount_to_pay: optionalDecoder(assertString)(json.amount_to_pay),
    date_to_pay: optionalDecoder(assertString)(json.date_to_pay),
    processed_at: optionalDecoder(decodeTimestamp)(json.processed_at),
    payment_address: optionalDecoder(decodeAddress)(json.payment_address),
    pay_via_triumph_pay: assertBoolean(json.pay_via_triumph_pay)
  };
}
export function encodeProcessedCarrierInvoiceRowData(value) {
  return {
    invoice_number: value.invoice_number,
    amount_to_pay: value.amount_to_pay,
    date_to_pay: value.date_to_pay,
    processed_at: optionalEncoder(encodeTimestamp)(value.processed_at),
    payment_address: optionalEncoder(encodeAddress)(value.payment_address),
    pay_via_triumph_pay: value.pay_via_triumph_pay
  };
}
export function decodeProcessedFactorInvoiceIdGroup(json) {
  return {
    carrier_id: optionalDecoder(assertNumber)(json.carrier_id),
    factor_invoice_ids: (json.factor_invoice_ids || []).map(assertNumber)
  };
}
export function encodeProcessedFactorInvoiceIdGroup(value) {
  return {
    carrier_id: value.carrier_id,
    factor_invoice_ids: value.factor_invoice_ids
  };
}
export function decodeProcessedFactorInvoiceIdGroups(json) {
  return {
    groups: (json.groups || []).map(decodeProcessedFactorInvoiceIdGroup)
  };
}
export function encodeProcessedFactorInvoiceIdGroups(value) {
  return {
    groups: value.groups.map(encodeProcessedFactorInvoiceIdGroup)
  };
}
export var QueueDate = /* @__PURE__ */ ((QueueDate2) => {
  QueueDate2["QueueDateUnsorted"] = "unsorted";
  QueueDate2["QueueDateDelivered"] = "delivered";
  QueueDate2["QueueDateReceived"] = "received";
  QueueDate2["QueueDateLastContact"] = "last_contact";
  QueueDate2["QueueDateEstimatedDue"] = "estimated_due";
  QueueDate2["QueueDateReviewOn"] = "review_on";
  QueueDate2["QueueDateProcessed"] = "processed";
  QueueDate2["QueueDatePayment"] = "payment";
  return QueueDate2;
})(QueueDate || {});
export const decodeQueueDate = (value) => value;
export const encodeQueueDate = (value) => value;
export function decodeRateConBolConsistencyCheck(json) {
  return {};
}
export function encodeRateConBolConsistencyCheck(value) {
  return {};
}
export var ReRateStrategy = /* @__PURE__ */ ((ReRateStrategy2) => {
  ReRateStrategy2["ReRateStrategyAcceptAll"] = "accept_all";
  ReRateStrategy2["ReRateStrategyEditableLines"] = "editable_lines";
  return ReRateStrategy2;
})(ReRateStrategy || {});
export const decodeReRateStrategy = (value) => value;
export const encodeReRateStrategy = (value) => value;
export function decodeReferenceRequirementsCheck(json) {
  return {
    message: assertString(json.message),
    reference: assertString(json.reference),
    loadValue: assertString(json.loadValue),
    requirements: (json.requirements || []).map(decodeReferenceRule),
    source: decodeReferenceRequirementsCheck_Source(json)
  };
}
export function encodeReferenceRequirementsCheck(value) {
  return __spreadValues({
    message: value.message,
    reference: value.reference,
    loadValue: value.loadValue,
    requirements: value.requirements.map(encodeReferenceRule)
  }, encodeReferenceRequirementsCheck_Source(value.source));
}
export var ReferenceRequirementsCheck_SourceType = /* @__PURE__ */ ((ReferenceRequirementsCheck_SourceType2) => {
  ReferenceRequirementsCheck_SourceType2["AccountSource"] = "accountSource";
  ReferenceRequirementsCheck_SourceType2["CustomerSource"] = "customerSource";
  return ReferenceRequirementsCheck_SourceType2;
})(ReferenceRequirementsCheck_SourceType || {});
export function decodeReferenceRequirementsCheck_Source(json) {
  if (json.accountSource !== void 0) {
    return {
      type: "accountSource" /* AccountSource */,
      accountSource: decodeReferenceRequirementsCheck_AccountSource(
        json.accountSource
      )
    };
  } else if (json.customerSource !== void 0) {
    return {
      type: "customerSource" /* CustomerSource */,
      customerSource: decodeReferenceRequirementsCheck_CustomerSource(
        json.customerSource
      )
    };
  }
  throw new Error("No field set in oneof ReferenceRequirementsCheck_Source");
}
export function encodeReferenceRequirementsCheck_Source(value) {
  switch (value.type) {
    case "accountSource" /* AccountSource */:
      return {
        accountSource: encodeReferenceRequirementsCheck_AccountSource(
          value.accountSource
        )
      };
    case "customerSource" /* CustomerSource */:
      return {
        customerSource: encodeReferenceRequirementsCheck_CustomerSource(
          value.customerSource
        )
      };
    default:
      throw new Error(
        "No field set in oneof ReferenceRequirementsCheck_Source"
      );
  }
}
export function decodeReferenceRequirementsCheck_AccountSource(json) {
  return {};
}
export function encodeReferenceRequirementsCheck_AccountSource(value) {
  return {};
}
export function decodeReferenceRequirementsCheck_CustomerSource(json) {
  return {
    name: assertString(json.name)
  };
}
export function encodeReferenceRequirementsCheck_CustomerSource(value) {
  return {
    name: value.name
  };
}
export function decodeReferenceRule(json) {
  return {
    reference: assertString(json.reference),
    rule: decodeReferenceRule_Rule(json)
  };
}
export function encodeReferenceRule(value) {
  return __spreadValues({
    reference: value.reference
  }, encodeReferenceRule_Rule(value.rule));
}
export var ReferenceRule_RuleType = /* @__PURE__ */ ((ReferenceRule_RuleType2) => {
  ReferenceRule_RuleType2["IsPresent"] = "is_present";
  ReferenceRule_RuleType2["IsEmpty"] = "is_empty";
  ReferenceRule_RuleType2["HasValue"] = "has_value";
  ReferenceRule_RuleType2["BeginsWith"] = "begins_with";
  ReferenceRule_RuleType2["NotValue"] = "not_value";
  return ReferenceRule_RuleType2;
})(ReferenceRule_RuleType || {});
export function decodeReferenceRule_Rule(json) {
  if (json.is_present !== void 0) {
    return {
      type: "is_present" /* IsPresent */,
      is_present: decodeReferenceRule_IsPresent(json.is_present)
    };
  } else if (json.is_empty !== void 0) {
    return {
      type: "is_empty" /* IsEmpty */,
      is_empty: decodeReferenceRule_IsEmpty(json.is_empty)
    };
  } else if (json.has_value !== void 0) {
    return {
      type: "has_value" /* HasValue */,
      has_value: decodeReferenceRule_HasValue(json.has_value)
    };
  } else if (json.begins_with !== void 0) {
    return {
      type: "begins_with" /* BeginsWith */,
      begins_with: decodeReferenceRule_BeginsWith(json.begins_with)
    };
  } else if (json.not_value !== void 0) {
    return {
      type: "not_value" /* NotValue */,
      not_value: decodeReferenceRule_NotValue(json.not_value)
    };
  }
  throw new Error("No field set in oneof ReferenceRule_Rule");
}
export function encodeReferenceRule_Rule(value) {
  switch (value.type) {
    case "is_present" /* IsPresent */:
      return { is_present: encodeReferenceRule_IsPresent(value.is_present) };
    case "is_empty" /* IsEmpty */:
      return { is_empty: encodeReferenceRule_IsEmpty(value.is_empty) };
    case "has_value" /* HasValue */:
      return { has_value: encodeReferenceRule_HasValue(value.has_value) };
    case "begins_with" /* BeginsWith */:
      return { begins_with: encodeReferenceRule_BeginsWith(value.begins_with) };
    case "not_value" /* NotValue */:
      return { not_value: encodeReferenceRule_NotValue(value.not_value) };
    default:
      throw new Error("No field set in oneof ReferenceRule_Rule");
  }
}
export function decodeReferenceRule_BeginsWith(json) {
  return {
    value: assertString(json.value)
  };
}
export function encodeReferenceRule_BeginsWith(value) {
  return {
    value: value.value
  };
}
export function decodeReferenceRule_HasValue(json) {
  return {
    value: assertString(json.value)
  };
}
export function encodeReferenceRule_HasValue(value) {
  return {
    value: value.value
  };
}
export function decodeReferenceRule_IsEmpty(json) {
  return {};
}
export function encodeReferenceRule_IsEmpty(value) {
  return {};
}
export function decodeReferenceRule_IsPresent(json) {
  return {};
}
export function encodeReferenceRule_IsPresent(value) {
  return {};
}
export function decodeReferenceRule_NotValue(json) {
  return {
    value: assertString(json.value)
  };
}
export function encodeReferenceRule_NotValue(value) {
  return {
    value: value.value
  };
}
export function decodeReviewIdGroup(json) {
  return {
    carrier_id: optionalDecoder(assertNumber)(json.carrier_id),
    approvable_factor_invoices: (json.approvable_factor_invoices || []).map(
      decodeReviewIdGroupInvoice
    ),
    draft_factor_invoices: (json.draft_factor_invoices || []).map(
      decodeReviewIdGroupInvoice
    ),
    reply_factor_invoices: (json.reply_factor_invoices || []).map(
      decodeReviewIdGroupInvoice
    ),
    verifying_factor_invoices: (json.verifying_factor_invoices || []).map(
      decodeReviewIdGroupInvoice
    ),
    unsent_exception_factor_invoices: (json.unsent_exception_factor_invoices || []).map(decodeReviewIdGroupInvoice),
    waiting_exception_factor_invoices: (json.waiting_exception_factor_invoices || []).map(decodeReviewIdGroupInvoice),
    new_email_message_ids: (json.new_email_message_ids || []).map(assertNumber),
    reply_email_message_ids: (json.reply_email_message_ids || []).map(
      assertNumber
    ),
    exception_invoices_created_at: (json.exception_invoices_created_at || []).map(decodeTimestamp),
    labels: (json.labels || []).map(assertString)
  };
}
export function encodeReviewIdGroup(value) {
  return {
    carrier_id: value.carrier_id,
    approvable_factor_invoices: value.approvable_factor_invoices.map(
      encodeReviewIdGroupInvoice
    ),
    draft_factor_invoices: value.draft_factor_invoices.map(
      encodeReviewIdGroupInvoice
    ),
    reply_factor_invoices: value.reply_factor_invoices.map(
      encodeReviewIdGroupInvoice
    ),
    verifying_factor_invoices: value.verifying_factor_invoices.map(
      encodeReviewIdGroupInvoice
    ),
    unsent_exception_factor_invoices: value.unsent_exception_factor_invoices.map(encodeReviewIdGroupInvoice),
    waiting_exception_factor_invoices: value.waiting_exception_factor_invoices.map(encodeReviewIdGroupInvoice),
    new_email_message_ids: value.new_email_message_ids,
    reply_email_message_ids: value.reply_email_message_ids,
    exception_invoices_created_at: value.exception_invoices_created_at.map(encodeTimestamp),
    labels: value.labels
  };
}
export function decodeReviewIdGroupInvoice(json) {
  return {
    id: assertNumber(json.id),
    updated_at: decodeTimestamp(json.updated_at)
  };
}
export function encodeReviewIdGroupInvoice(value) {
  return {
    id: value.id,
    updated_at: encodeTimestamp(value.updated_at)
  };
}
export function decodeReviewIdGroups(json) {
  return {
    groups: (json.groups || []).map(decodeReviewIdGroup)
  };
}
export function encodeReviewIdGroups(value) {
  return {
    groups: value.groups.map(encodeReviewIdGroup)
  };
}
export var RuleEvaluationStatus = /* @__PURE__ */ ((RuleEvaluationStatus2) => {
  RuleEvaluationStatus2["RuleEvaluationStatusSkipped"] = "skipped";
  RuleEvaluationStatus2["RuleEvaluationStatusPassed"] = "passed";
  RuleEvaluationStatus2["RuleEvaluationStatusFailed"] = "failed";
  RuleEvaluationStatus2["RuleEvaluationStatusIgnored"] = "ignored";
  RuleEvaluationStatus2["RuleEvaluationStatusResolved"] = "resolved";
  return RuleEvaluationStatus2;
})(RuleEvaluationStatus || {});
export const decodeRuleEvaluationStatus = (value) => value;
export const encodeRuleEvaluationStatus = (value) => value;
export var RulesMode = /* @__PURE__ */ ((RulesMode2) => {
  RulesMode2["RulesModeUnset"] = "unset";
  RulesMode2["RulesModeSecret"] = "secret";
  RulesMode2["RulesModeOn"] = "on";
  RulesMode2["RulesModeOff"] = "off";
  return RulesMode2;
})(RulesMode || {});
export const decodeRulesMode = (value) => value;
export const encodeRulesMode = (value) => value;
export function decodeSameAttachmentInvoicesAccountInput(json) {
  return {
    account_id: assertNumber(json.account_id)
  };
}
export function encodeSameAttachmentInvoicesAccountInput(value) {
  return {
    account_id: value.account_id
  };
}
export function decodeSameAttachmentInvoicesInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    as_of_date: assertString(json.as_of_date)
  };
}
export function encodeSameAttachmentInvoicesInput(value) {
  return {
    account_id: value.account_id,
    as_of_date: value.as_of_date
  };
}
export function decodeSameAttachmentInvoicesRangeInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    starting_from: assertString(json.starting_from),
    ending_on: assertString(json.ending_on)
  };
}
export function encodeSameAttachmentInvoicesRangeInput(value) {
  return {
    account_id: value.account_id,
    starting_from: value.starting_from,
    ending_on: value.ending_on
  };
}
export var ScraperAuthRequirement = /* @__PURE__ */ ((ScraperAuthRequirement2) => {
  ScraperAuthRequirement2["ScraperAuthRequirementNone"] = "none";
  ScraperAuthRequirement2["ScraperAuthRequirementToken"] = "auth_requirement_token";
  ScraperAuthRequirement2["ScraperAuthRequirementUsernamePassword"] = "auth_requirement_username_password";
  ScraperAuthRequirement2["ScraperAuthRequirementUsernamePasswordAndToken"] = "auth_requirement_username_password_and_token";
  return ScraperAuthRequirement2;
})(ScraperAuthRequirement || {});
export const decodeScraperAuthRequirement = (value) => value;
export const encodeScraperAuthRequirement = (value) => value;
export var ScrapingService = /* @__PURE__ */ ((ScrapingService2) => {
  ScrapingService2["ScrapingServiceScrAPINgServiceDocs"] = "service_scraping_docs";
  ScrapingService2["ScrapingServiceScrAPINgServiceInvoices"] = "service_scraping_invoice";
  return ScrapingService2;
})(ScrapingService || {});
export const decodeScrapingService = (value) => value;
export const encodeScrapingService = (value) => value;
export function decodeSentMessage(json) {
  return {
    id: assertNumber(json.id),
    message_type: optionalDecoder(decodeEmailMessageType)(json.message_type),
    subject: assertString(json.subject),
    text_body: assertString(json.text_body),
    created_at: decodeTimestamp(json.created_at),
    updated_at: decodeTimestamp(json.updated_at),
    sender_address: assertString(json.sender_address),
    recipient_address: optionalDecoder(assertString)(json.recipient_address),
    raw_to: (json.raw_to || []).map(decodeEmailAddress),
    raw_cc: (json.raw_cc || []).map(decodeEmailAddress)
  };
}
export function encodeSentMessage(value) {
  return {
    id: value.id,
    message_type: optionalEncoder(encodeEmailMessageType)(value.message_type),
    subject: value.subject,
    text_body: value.text_body,
    created_at: encodeTimestamp(value.created_at),
    updated_at: encodeTimestamp(value.updated_at),
    sender_address: value.sender_address,
    recipient_address: value.recipient_address,
    raw_to: value.raw_to.map(encodeEmailAddress),
    raw_cc: value.raw_cc.map(encodeEmailAddress)
  };
}
export function decodeSentNoa(json) {
  return {
    id: assertNumber(json.id),
    sent_at: decodeTimestamp(json.sent_at),
    factor_broker_id: assertNumber(json.factor_broker_id),
    debtor_name: assertString(json.debtor_name),
    tms_carrier_id: assertNumber(json.tms_carrier_id),
    client_name: assertString(json.client_name),
    sent_to_email_address: assertString(json.sent_to_email_address),
    address_source: decodeSentNoa_AddressSource(json.address_source),
    document_display: assertString(json.document_display),
    sent_by_name: assertString(json.sent_by_name),
    factoring_system_status: decodeSentNoa_FactoringSystemStatus(
      json.factoring_system_status
    )
  };
}
export function encodeSentNoa(value) {
  return {
    id: value.id,
    sent_at: encodeTimestamp(value.sent_at),
    factor_broker_id: value.factor_broker_id,
    debtor_name: value.debtor_name,
    tms_carrier_id: value.tms_carrier_id,
    client_name: value.client_name,
    sent_to_email_address: value.sent_to_email_address,
    address_source: encodeSentNoa_AddressSource(value.address_source),
    document_display: value.document_display,
    sent_by_name: value.sent_by_name,
    factoring_system_status: encodeSentNoa_FactoringSystemStatus(
      value.factoring_system_status
    )
  };
}
export var SentNoa_AddressSource = /* @__PURE__ */ ((SentNoa_AddressSource2) => {
  SentNoa_AddressSource2["SentNoa_AddressSourceUnknownAddressSource"] = "UNKNOWN_ADDRESS_SOURCE";
  SentNoa_AddressSource2["SentNoa_AddressSourceProposed"] = "PROPOSED";
  SentNoa_AddressSource2["SentNoa_AddressSourceTyped"] = "TYPED";
  return SentNoa_AddressSource2;
})(SentNoa_AddressSource || {});
export const decodeSentNoa_AddressSource = (value) => value;
export const encodeSentNoa_AddressSource = (value) => value;
export var SentNoa_FactoringSystemStatus = /* @__PURE__ */ ((SentNoa_FactoringSystemStatus2) => {
  SentNoa_FactoringSystemStatus2["SentNoa_FactoringSystemStatusUnknownFactoringSystemStatus"] = "UNKNOWN_FACTORING_SYSTEM_STATUS";
  SentNoa_FactoringSystemStatus2["SentNoa_FactoringSystemStatusPending"] = "PENDING";
  SentNoa_FactoringSystemStatus2["SentNoa_FactoringSystemStatusSucceeded"] = "SUCCEEDED";
  SentNoa_FactoringSystemStatus2["SentNoa_FactoringSystemStatusFailed"] = "FAILED";
  SentNoa_FactoringSystemStatus2["SentNoa_FactoringSystemStatusNotApplicable"] = "NOT_APPLICABLE";
  SentNoa_FactoringSystemStatus2["SentNoa_FactoringSystemStatusAwaitingRelationship"] = "AWAITING_RELATIONSHIP";
  return SentNoa_FactoringSystemStatus2;
})(SentNoa_FactoringSystemStatus || {});
export const decodeSentNoa_FactoringSystemStatus = (value) => value;
export const encodeSentNoa_FactoringSystemStatus = (value) => value;
export function decodeSentNoasQuery(json) {
  return {
    page: assertNumber(json.page),
    factor_broker_id: optionalDecoder(assertNumber)(json.factor_broker_id),
    tms_carrier_id: optionalDecoder(assertNumber)(json.tms_carrier_id)
  };
}
export function encodeSentNoasQuery(value) {
  return {
    page: value.page,
    factor_broker_id: value.factor_broker_id,
    tms_carrier_id: value.tms_carrier_id
  };
}
export function decodeSingleGroupSendersAccountInput(json) {
  return {
    account_id: assertNumber(json.account_id)
  };
}
export function encodeSingleGroupSendersAccountInput(value) {
  return {
    account_id: value.account_id
  };
}
export function decodeSingleGroupSendersInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    as_of_date: assertString(json.as_of_date)
  };
}
export function encodeSingleGroupSendersInput(value) {
  return {
    account_id: value.account_id,
    as_of_date: value.as_of_date
  };
}
export function decodeSingleGroupSendersRangeInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    starting_from: assertString(json.starting_from),
    ending_on: assertString(json.ending_on)
  };
}
export function encodeSingleGroupSendersRangeInput(value) {
  return {
    account_id: value.account_id,
    starting_from: value.starting_from,
    ending_on: value.ending_on
  };
}
export var SkippedManualReviewReason = /* @__PURE__ */ ((SkippedManualReviewReason2) => {
  SkippedManualReviewReason2["SkippedManualReviewReasonPassedAutoVerification"] = "passed_auto_verification";
  return SkippedManualReviewReason2;
})(SkippedManualReviewReason || {});
export const decodeSkippedManualReviewReason = (value) => value;
export const encodeSkippedManualReviewReason = (value) => value;
export function decodeSkippedNoa(json) {
  return {
    id: assertNumber(json.id),
    skipped_at: decodeTimestamp(json.skipped_at),
    factor_broker_id: assertNumber(json.factor_broker_id),
    debtor_name: assertString(json.debtor_name),
    tms_carrier_id: assertNumber(json.tms_carrier_id),
    client_name: assertString(json.client_name),
    reason: decodeSkippedNoa_Reason(json.reason),
    note: assertString(json.note),
    skipped_by_name: assertString(json.skipped_by_name)
  };
}
export function encodeSkippedNoa(value) {
  return {
    id: value.id,
    skipped_at: encodeTimestamp(value.skipped_at),
    factor_broker_id: value.factor_broker_id,
    debtor_name: value.debtor_name,
    tms_carrier_id: value.tms_carrier_id,
    client_name: value.client_name,
    reason: encodeSkippedNoa_Reason(value.reason),
    note: value.note,
    skipped_by_name: value.skipped_by_name
  };
}
export var SkippedNoa_Reason = /* @__PURE__ */ ((SkippedNoa_Reason2) => {
  SkippedNoa_Reason2["SkippedNoa_ReasonUnknownReason"] = "UNKNOWN_REASON";
  SkippedNoa_Reason2["SkippedNoa_ReasonNoAddressYet"] = "NO_ADDRESS_YET";
  SkippedNoa_Reason2["SkippedNoa_ReasonNotRequired"] = "NOT_REQUIRED";
  SkippedNoa_Reason2["SkippedNoa_ReasonOther"] = "OTHER";
  return SkippedNoa_Reason2;
})(SkippedNoa_Reason || {});
export const decodeSkippedNoa_Reason = (value) => value;
export const encodeSkippedNoa_Reason = (value) => value;
export function decodeSkippedNoasQuery(json) {
  return {
    page: assertNumber(json.page)
  };
}
export function encodeSkippedNoasQuery(value) {
  return {
    page: value.page
  };
}
export var SortOrder = /* @__PURE__ */ ((SortOrder2) => {
  SortOrder2["SortOrderAsc"] = "asc";
  SortOrder2["SortOrderDesc"] = "desc";
  return SortOrder2;
})(SortOrder || {});
export const decodeSortOrder = (value) => value;
export const encodeSortOrder = (value) => value;
export function decodeSplitAttachmentsChunk(json) {
  return {
    chunk: decodeSplitAttachmentsChunk_Chunk(json)
  };
}
export function encodeSplitAttachmentsChunk(value) {
  return __spreadValues({}, encodeSplitAttachmentsChunk_Chunk(value.chunk));
}
export var SplitAttachmentsChunk_ChunkType = /* @__PURE__ */ ((SplitAttachmentsChunk_ChunkType2) => {
  SplitAttachmentsChunk_ChunkType2["EntireAttachment"] = "entire_attachment";
  SplitAttachmentsChunk_ChunkType2["PageRange"] = "page_range";
  return SplitAttachmentsChunk_ChunkType2;
})(SplitAttachmentsChunk_ChunkType || {});
export function decodeSplitAttachmentsChunk_Chunk(json) {
  if (json.entire_attachment !== void 0) {
    return {
      type: "entire_attachment" /* EntireAttachment */,
      entire_attachment: decodeSplitAttachmentsChunk_EntireAttachment(
        json.entire_attachment
      )
    };
  } else if (json.page_range !== void 0) {
    return {
      type: "page_range" /* PageRange */,
      page_range: decodeSplitAttachmentsChunk_PageRange(json.page_range)
    };
  }
  throw new Error("No field set in oneof SplitAttachmentsChunk_Chunk");
}
export function encodeSplitAttachmentsChunk_Chunk(value) {
  switch (value.type) {
    case "entire_attachment" /* EntireAttachment */:
      return {
        entire_attachment: encodeSplitAttachmentsChunk_EntireAttachment(
          value.entire_attachment
        )
      };
    case "page_range" /* PageRange */:
      return {
        page_range: encodeSplitAttachmentsChunk_PageRange(value.page_range)
      };
    default:
      throw new Error("No field set in oneof SplitAttachmentsChunk_Chunk");
  }
}
export function decodeSplitAttachmentsChunk_EntireAttachment(json) {
  return {
    attachment_id: assertNumber(json.attachment_id)
  };
}
export function encodeSplitAttachmentsChunk_EntireAttachment(value) {
  return {
    attachment_id: value.attachment_id
  };
}
export function decodeSplitAttachmentsChunk_PageRange(json) {
  return {
    attachment_id: assertNumber(json.attachment_id),
    page_start: assertNumber(json.page_start),
    page_end: assertNumber(json.page_end)
  };
}
export function encodeSplitAttachmentsChunk_PageRange(value) {
  return {
    attachment_id: value.attachment_id,
    page_start: value.page_start,
    page_end: value.page_end
  };
}
export function decodeSplitAttachmentsEnqueueThumbnailsInput(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeSplitAttachmentsEnqueueThumbnailsInput(value) {
  return {
    document_batch_id: value.document_batch_id
  };
}
export function decodeSplitAttachmentsFinalizeInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeSplitAttachmentsFinalizeInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id
  };
}
export function decodeSplitAttachmentsGroup(json) {
  return {
    starting_document_index: assertNumber(json.starting_document_index),
    chunks: (json.chunks || []).map(decodeSplitAttachmentsChunk)
  };
}
export function encodeSplitAttachmentsGroup(value) {
  return {
    starting_document_index: value.starting_document_index,
    chunks: value.chunks.map(encodeSplitAttachmentsChunk)
  };
}
export function decodeSplitAttachmentsGroupInput(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id),
    starting_document_index: assertNumber(json.starting_document_index),
    skip_thumbnail: assertBoolean(json.skip_thumbnail),
    chunks: (json.chunks || []).map(decodeSplitAttachmentsChunk)
  };
}
export function encodeSplitAttachmentsGroupInput(value) {
  return {
    document_batch_id: value.document_batch_id,
    starting_document_index: value.starting_document_index,
    skip_thumbnail: value.skip_thumbnail,
    chunks: value.chunks.map(encodeSplitAttachmentsChunk)
  };
}
export function decodeSplitAttachmentsOcrCompleteSignal(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeSplitAttachmentsOcrCompleteSignal(value) {
  return {
    document_batch_id: value.document_batch_id
  };
}
export function decodeSplitAttachmentsOcrGroup(json) {
  return {
    attachment_ids: (json.attachment_ids || []).map(assertNumber)
  };
}
export function encodeSplitAttachmentsOcrGroup(value) {
  return {
    attachment_ids: value.attachment_ids
  };
}
export function decodeSplitAttachmentsOcrGroupAndFinalizeInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id),
    attachment_ids: (json.attachment_ids || []).map(assertNumber)
  };
}
export function encodeSplitAttachmentsOcrGroupAndFinalizeInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id,
    attachment_ids: value.attachment_ids
  };
}
export function decodeSplitAttachmentsOcrGroupInput(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id),
    attachment_ids: (json.attachment_ids || []).map(assertNumber)
  };
}
export function encodeSplitAttachmentsOcrGroupInput(value) {
  return {
    document_batch_id: value.document_batch_id,
    attachment_ids: value.attachment_ids
  };
}
export function decodeSplitAttachmentsPlanOcrGroupsInput(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id),
    import_process_id: assertNumber(json.import_process_id)
  };
}
export function encodeSplitAttachmentsPlanOcrGroupsInput(value) {
  return {
    document_batch_id: value.document_batch_id,
    import_process_id: value.import_process_id
  };
}
export function decodeSplitAttachmentsPlanOcrGroupsOutput(json) {
  return {
    groups: (json.groups || []).map(decodeSplitAttachmentsOcrGroup),
    finalized: assertBoolean(json.finalized)
  };
}
export function encodeSplitAttachmentsPlanOcrGroupsOutput(value) {
  return {
    groups: value.groups.map(encodeSplitAttachmentsOcrGroup),
    finalized: value.finalized
  };
}
export function decodeSplitAttachmentsSetupAndSplitInput(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id),
    skip_thumbnail: assertBoolean(json.skip_thumbnail),
    groups: (json.groups || []).map(decodeSplitAttachmentsGroup)
  };
}
export function encodeSplitAttachmentsSetupAndSplitInput(value) {
  return {
    document_batch_id: value.document_batch_id,
    skip_thumbnail: value.skip_thumbnail,
    groups: value.groups.map(encodeSplitAttachmentsGroup)
  };
}
export function decodeSplitAttachmentsSetupInput(json) {
  return {
    document_batch_id: assertNumber(json.document_batch_id)
  };
}
export function encodeSplitAttachmentsSetupInput(value) {
  return {
    document_batch_id: value.document_batch_id
  };
}
export function decodeSplitAttachmentsSetupOutput(json) {
  return {
    submitted_entire_document_ocr: assertBoolean(
      json.submitted_entire_document_ocr
    )
  };
}
export function encodeSplitAttachmentsSetupOutput(value) {
  return {
    submitted_entire_document_ocr: value.submitted_entire_document_ocr
  };
}
export function decodeSplitAttachmentsWorkflowInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id),
    document_batch_id: assertNumber(json.document_batch_id),
    account_id: assertNumber(json.account_id),
    skip_thumbnail: assertBoolean(json.skip_thumbnail),
    groups: (json.groups || []).map(decodeSplitAttachmentsGroup)
  };
}
export function encodeSplitAttachmentsWorkflowInput(value) {
  return {
    import_process_id: value.import_process_id,
    document_batch_id: value.document_batch_id,
    account_id: value.account_id,
    skip_thumbnail: value.skip_thumbnail,
    groups: value.groups.map(encodeSplitAttachmentsGroup)
  };
}
export function decodeStartAutoProcessingWorkflowInput(json) {
  return {
    import_process_id: assertNumber(json.import_process_id)
  };
}
export function encodeStartAutoProcessingWorkflowInput(value) {
  return {
    import_process_id: value.import_process_id
  };
}
export function decodeStream(json) {
  return {
    ids: (json.ids || []).map(assertNumber),
    has_more: assertBoolean(json.has_more)
  };
}
export function encodeStream(value) {
  return {
    ids: value.ids,
    has_more: value.has_more
  };
}
export function decodeSubmissionDropdownData(json) {
  return {
    submission_id: assertNumber(json.submission_id),
    debtor_name: optionalDecoder(assertString)(json.debtor_name),
    load_id: assertString(json.load_id)
  };
}
export function encodeSubmissionDropdownData(value) {
  return {
    submission_id: value.submission_id,
    debtor_name: value.debtor_name,
    load_id: value.load_id
  };
}
export var SuggestionSource = /* @__PURE__ */ ((SuggestionSource2) => {
  SuggestionSource2["SuggestionSourceClientSubmission"] = "clientSubmission";
  SuggestionSource2["SuggestionSourceClientInvoice"] = "clientInvoice";
  SuggestionSource2["SuggestionSourceRateConfirmation"] = "rateConfirmation";
  SuggestionSource2["SuggestionSourceBillOfLading"] = "billOfLading";
  SuggestionSource2["SuggestionSourceAiRecommendation"] = "aiRecommendation";
  return SuggestionSource2;
})(SuggestionSource || {});
export const decodeSuggestionSource = (value) => value;
export const encodeSuggestionSource = (value) => value;
export function decodeTest(json) {
  return {
    signal: decodeTest_Signal(json)
  };
}
export function encodeTest(value) {
  return __spreadValues({}, encodeTest_Signal(value.signal));
}
export var Test_SignalType = /* @__PURE__ */ ((Test_SignalType2) => {
  Test_SignalType2["Approve"] = "approve";
  Test_SignalType2["Reject"] = "reject";
  return Test_SignalType2;
})(Test_SignalType || {});
export function decodeTest_Signal(json) {
  if (json.approve !== void 0) {
    return {
      type: "approve" /* Approve */,
      approve: decodeTest_ApproveSignal(json.approve)
    };
  } else if (json.reject !== void 0) {
    return {
      type: "reject" /* Reject */,
      reject: decodeTest_RejectSignal(json.reject)
    };
  }
  throw new Error("No field set in oneof Test_Signal");
}
export function encodeTest_Signal(value) {
  switch (value.type) {
    case "approve" /* Approve */:
      return { approve: encodeTest_ApproveSignal(value.approve) };
    case "reject" /* Reject */:
      return { reject: encodeTest_RejectSignal(value.reject) };
    default:
      throw new Error("No field set in oneof Test_Signal");
  }
}
export function decodeTest_ApproveSignal(json) {
  return {};
}
export function encodeTest_ApproveSignal(value) {
  return {};
}
export function decodeTest_JustInt32(json) {
  return {
    value: assertNumber(json.value)
  };
}
export function encodeTest_JustInt32(value) {
  return {
    value: value.value
  };
}
export function decodeTest_RejectSignal(json) {
  return {};
}
export function encodeTest_RejectSignal(value) {
  return {};
}
export function decodeTMSCarrier(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    account_exec: optionalDecoder(assertString)(json.account_exec),
    mc_number: optionalDecoder(assertString)(json.mc_number),
    dot_number: optionalDecoder(assertString)(json.dot_number),
    scac: optionalDecoder(assertString)(json.scac),
    ein: optionalDecoder(assertString)(json.ein),
    external_vendor_id: optionalDecoder(assertString)(json.external_vendor_id),
    address: decodeAddress(json.address),
    payment_address: optionalDecoder(decodeAddress)(json.payment_address),
    effective_days_to_pay: assertNumber(json.effective_days_to_pay),
    effective_quickpay_discount: optionalDecoder(assertNumber)(
      json.effective_quickpay_discount
    ),
    payment_strategy: assertString(json.payment_strategy),
    attempt_to_auto_process_document_batches: assertBoolean(
      json.attempt_to_auto_process_document_batches
    ),
    rate_confirmation_required: assertBoolean(json.rate_confirmation_required),
    pod_required: assertBoolean(json.pod_required),
    receivables_invoice_customer: assertBoolean(
      json.receivables_invoice_customer
    ),
    skip_auto_stamp: assertBoolean(json.skip_auto_stamp),
    factor_send_customer_invoice_to_cadence: optionalDecoder(assertBoolean)(
      json.factor_send_customer_invoice_to_cadence
    ),
    transportation_client: assertBoolean(json.transportation_client),
    scraper_available: assertBoolean(json.scraper_available),
    scraping_enabled: assertBoolean(json.scraping_enabled),
    supports_scraping_invoice: assertBoolean(json.supports_scraping_invoice),
    supports_credential_testing: assertBoolean(
      json.supports_credential_testing
    ),
    scraping_documents_auth_requirement: decodeScraperAuthRequirement(
      json.scraping_documents_auth_requirement
    ),
    scraping_invoice_auth_requirement: decodeScraperAuthRequirement(
      json.scraping_invoice_auth_requirement
    ),
    document_types_retrieved: (json.document_types_retrieved || []).map(
      decodeUnresolvedDocumentType
    ),
    factor_client_portal_enabled: assertBoolean(
      json.factor_client_portal_enabled
    ),
    portal_submission_strategy: decodePortalSubmissionStrategy(
      json.portal_submission_strategy
    ),
    labels: (json.labels || []).map(assertString),
    external_labels: (json.external_labels || []).map(assertString),
    contacts: (json.contacts || []).map(decodeTMSCarrier_Contact),
    external_logins: (json.external_logins || []).map(
      decodeTMSCarrierExternalLogin
    ),
    setup_help_text: optionalDecoder(assertString)(json.setup_help_text),
    funding_notes: optionalDecoder(assertString)(json.funding_notes),
    notes: optionalDecoder(assertString)(json.notes),
    warning: optionalDecoder(assertString)(json.warning),
    exclude_from_verifications_workflow: assertBoolean(
      json.exclude_from_verifications_workflow
    ),
    verify_percentile: optionalDecoder(assertNumber)(json.verify_percentile),
    cadence_verification_instructions: optionalDecoder(assertString)(
      json.cadence_verification_instructions
    ),
    credit_rating: optionalDecoder(assertString)(json.credit_rating),
    verification_percentage: assertNumber(json.verification_percentage),
    inactive_at: optionalDecoder(decodeTimestamp)(json.inactive_at),
    features: (json.features || []).map(decodeTMSCarrier_Feature),
    warn_if_amount_over: optionalDecoder(assertNumber)(
      json.warn_if_amount_over
    ),
    manual_review_percentage: optionalDecoder(assertNumber)(
      json.manual_review_percentage
    ),
    factor_invoice_number_strategy: optionalDecoder(assertString)(
      json.factor_invoice_number_strategy
    ),
    auto_generate_factor_invoice_numbers: optionalDecoder(assertString)(
      json.auto_generate_factor_invoice_numbers
    ),
    invoice_tolerance: decodeTolerance(json.invoice_tolerance),
    program_external_id: optionalDecoder(assertString)(
      json.program_external_id
    ),
    implicit_invoice: decodeImplicitInvoice(json.implicit_invoice)
  };
}
export function encodeTMSCarrier(value) {
  return {
    id: value.id,
    name: value.name,
    account_exec: value.account_exec,
    mc_number: value.mc_number,
    dot_number: value.dot_number,
    scac: value.scac,
    ein: value.ein,
    external_vendor_id: value.external_vendor_id,
    address: encodeAddress(value.address),
    payment_address: optionalEncoder(encodeAddress)(value.payment_address),
    effective_days_to_pay: value.effective_days_to_pay,
    effective_quickpay_discount: value.effective_quickpay_discount,
    payment_strategy: value.payment_strategy,
    attempt_to_auto_process_document_batches: value.attempt_to_auto_process_document_batches,
    rate_confirmation_required: value.rate_confirmation_required,
    pod_required: value.pod_required,
    receivables_invoice_customer: value.receivables_invoice_customer,
    skip_auto_stamp: value.skip_auto_stamp,
    factor_send_customer_invoice_to_cadence: value.factor_send_customer_invoice_to_cadence,
    transportation_client: value.transportation_client,
    scraper_available: value.scraper_available,
    scraping_enabled: value.scraping_enabled,
    supports_scraping_invoice: value.supports_scraping_invoice,
    supports_credential_testing: value.supports_credential_testing,
    scraping_documents_auth_requirement: encodeScraperAuthRequirement(
      value.scraping_documents_auth_requirement
    ),
    scraping_invoice_auth_requirement: encodeScraperAuthRequirement(
      value.scraping_invoice_auth_requirement
    ),
    document_types_retrieved: value.document_types_retrieved.map(
      encodeUnresolvedDocumentType
    ),
    factor_client_portal_enabled: value.factor_client_portal_enabled,
    portal_submission_strategy: encodePortalSubmissionStrategy(
      value.portal_submission_strategy
    ),
    labels: value.labels,
    external_labels: value.external_labels,
    contacts: value.contacts.map(encodeTMSCarrier_Contact),
    external_logins: value.external_logins.map(encodeTMSCarrierExternalLogin),
    setup_help_text: value.setup_help_text,
    funding_notes: value.funding_notes,
    notes: value.notes,
    warning: value.warning,
    exclude_from_verifications_workflow: value.exclude_from_verifications_workflow,
    verify_percentile: value.verify_percentile,
    cadence_verification_instructions: value.cadence_verification_instructions,
    credit_rating: value.credit_rating,
    verification_percentage: value.verification_percentage,
    inactive_at: optionalEncoder(encodeTimestamp)(value.inactive_at),
    features: value.features.map(encodeTMSCarrier_Feature),
    warn_if_amount_over: value.warn_if_amount_over,
    manual_review_percentage: value.manual_review_percentage,
    factor_invoice_number_strategy: value.factor_invoice_number_strategy,
    auto_generate_factor_invoice_numbers: value.auto_generate_factor_invoice_numbers,
    invoice_tolerance: encodeTolerance(value.invoice_tolerance),
    program_external_id: value.program_external_id,
    implicit_invoice: encodeImplicitInvoice(value.implicit_invoice)
  };
}
export function decodeTMSCarrier_Contact(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    email: assertString(json.email),
    admin: assertBoolean(json.admin),
    factor_client_portal_enabled: assertBoolean(
      json.factor_client_portal_enabled
    ),
    portal_submission_strategy: decodePortalSubmissionStrategy(
      json.portal_submission_strategy
    ),
    url_hash: assertString(json.url_hash),
    preferred_contact: assertBoolean(json.preferred_contact)
  };
}
export function encodeTMSCarrier_Contact(value) {
  return {
    id: value.id,
    name: value.name,
    email: value.email,
    admin: value.admin,
    factor_client_portal_enabled: value.factor_client_portal_enabled,
    portal_submission_strategy: encodePortalSubmissionStrategy(
      value.portal_submission_strategy
    ),
    url_hash: value.url_hash,
    preferred_contact: value.preferred_contact
  };
}
export function decodeTMSCarrier_Feature(json) {
  return {
    id: assertString(json.id),
    label: assertString(json.label),
    enabled: assertBoolean(json.enabled),
    can_override_for_client: assertBoolean(json.can_override_for_client),
    client_override: optionalDecoder(assertBoolean)(json.client_override)
  };
}
export function encodeTMSCarrier_Feature(value) {
  return {
    id: value.id,
    label: value.label,
    enabled: value.enabled,
    can_override_for_client: value.can_override_for_client,
    client_override: value.client_override
  };
}
export function decodeTMSCarrierExternalLogin(json) {
  return {
    id: assertNumber(json.id),
    tms_customer_id: optionalDecoder(assertNumber)(json.tms_customer_id),
    scraping_services: (json.scraping_services || []).map(
      decodeScrapingService
    )
  };
}
export function encodeTMSCarrierExternalLogin(value) {
  return {
    id: value.id,
    tms_customer_id: value.tms_customer_id,
    scraping_services: value.scraping_services.map(encodeScrapingService)
  };
}
export function decodeTMSCarrierIdsSettingsQuery(json) {
  return {
    page: assertNumber(json.page),
    term: assertString(json.term),
    labels: (json.labels || []).map(assertString),
    scraperOnly: assertBoolean(json.scraperOnly),
    withLoginFailures: assertBoolean(json.withLoginFailures),
    eligibleForAutoprocessing: assertBoolean(json.eligibleForAutoprocessing)
  };
}
export function encodeTMSCarrierIdsSettingsQuery(value) {
  return {
    page: value.page,
    term: value.term,
    labels: value.labels,
    scraperOnly: value.scraperOnly,
    withLoginFailures: value.withLoginFailures,
    eligibleForAutoprocessing: value.eligibleForAutoprocessing
  };
}
export function decodeTMSCustomer(json) {
  return {
    id: assertNumber(json.id),
    account_number: optionalDecoder(assertString)(json.account_number),
    name: assertString(json.name),
    instructions_list: (json.instructions_list || []).map(assertString),
    external_id: assertString(json.external_id)
  };
}
export function encodeTMSCustomer(value) {
  return {
    id: value.id,
    account_number: value.account_number,
    name: value.name,
    instructions_list: value.instructions_list,
    external_id: value.external_id
  };
}
export function decodeTMSCustomerIdsQuery(json) {
  return {
    query: assertString(json.query)
  };
}
export function encodeTMSCustomerIdsQuery(value) {
  return {
    query: value.query
  };
}
export function decodeTMSCustomerInfoQuery(json) {
  return {
    page: assertNumber(json.page),
    term: assertString(json.term)
  };
}
export function encodeTMSCustomerInfoQuery(value) {
  return {
    page: value.page,
    term: value.term
  };
}
export function decodeTMSCustomerSettings(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    account_number: optionalDecoder(assertString)(json.account_number),
    effective_days_to_pay: assertNumber(json.effective_days_to_pay),
    customer_invoice_template_id: optionalDecoder(assertNumber)(
      json.customer_invoice_template_id
    ),
    statement_customer_invoice_template_id: optionalDecoder(assertNumber)(
      json.statement_customer_invoice_template_id
    ),
    master_customer_invoice_template_id: optionalDecoder(assertNumber)(
      json.master_customer_invoice_template_id
    ),
    instructions_for_write: assertString(json.instructions_for_write),
    instructions_list: (json.instructions_list || []).map(assertString),
    billing_interval: decodeBillingInterval(json.billing_interval),
    pod_requirement_policy: decodePODRequirementPolicy(
      json.pod_requirement_policy
    ),
    custom_subject_template: assertString(json.custom_subject_template),
    billing_email: assertString(json.billing_email),
    template_variables: (json.template_variables || []).map(assertString),
    ignore_customer_invoice_document: assertBoolean(
      json.ignore_customer_invoice_document
    ),
    account_remit_to_address_id: optionalDecoder(assertNumber)(
      json.account_remit_to_address_id
    ),
    override_default_ltl_tolerance: assertBoolean(
      json.override_default_ltl_tolerance
    ),
    ltl_carrier_invoice_amount_tolerance_unit: decodeToleranceUnit(
      json.ltl_carrier_invoice_amount_tolerance_unit
    ),
    ltl_carrier_invoice_amount_tolerance_over: assertString(
      json.ltl_carrier_invoice_amount_tolerance_over
    ),
    ltl_carrier_invoice_amount_tolerance_under: assertString(
      json.ltl_carrier_invoice_amount_tolerance_under
    ),
    override_default_ltl_customer_margin_tolerance: assertBoolean(
      json.override_default_ltl_customer_margin_tolerance
    ),
    ltl_customer_margin_tolerance_unit: decodeToleranceUnit(
      json.ltl_customer_margin_tolerance_unit
    ),
    ltl_customer_margin_tolerance_minimum: assertString(
      json.ltl_customer_margin_tolerance_minimum
    ),
    ltl_customer_margin_tolerance_maximum: assertString(
      json.ltl_customer_margin_tolerance_maximum
    ),
    ltl_auto_rerate_enabled: assertBoolean(json.ltl_auto_rerate_enabled),
    ltl_reweigh_charge_exceptions_enabled: assertBoolean(
      json.ltl_reweigh_charge_exceptions_enabled
    ),
    ltl_require_manual_reweigh_review: assertBoolean(
      json.ltl_require_manual_reweigh_review
    ),
    bill_to_address: optionalDecoder(decodeAddress)(json.bill_to_address),
    customer_visible_document_types: (json.customer_visible_document_types || []).map(decodeUnresolvedDocumentType),
    required_receivables_types: (json.required_receivables_types || []).map(
      decodeUnresolvedDocumentType
    ),
    required_payables_types: (json.required_payables_types || []).map(
      decodeUnresolvedDocumentType
    ),
    contacts: (json.contacts || []).map(decodeTMSCustomerSettings_Contact),
    references: (json.references || []).map(
      decodeTMSCustomerSettings_Reference
    ),
    rules_broker_required_references: (json.rules_broker_required_references || []).map(decodeReferenceRule),
    rules_broker_custom_exception_messages: (json.rules_broker_custom_exception_messages || []).map(decodeCustomExceptionMessage),
    delivery_method: decodeDeliveryMethod(json.delivery_method)
  };
}
export function encodeTMSCustomerSettings(value) {
  return {
    id: value.id,
    name: value.name,
    account_number: value.account_number,
    effective_days_to_pay: value.effective_days_to_pay,
    customer_invoice_template_id: value.customer_invoice_template_id,
    statement_customer_invoice_template_id: value.statement_customer_invoice_template_id,
    master_customer_invoice_template_id: value.master_customer_invoice_template_id,
    instructions_for_write: value.instructions_for_write,
    instructions_list: value.instructions_list,
    billing_interval: encodeBillingInterval(value.billing_interval),
    pod_requirement_policy: encodePODRequirementPolicy(
      value.pod_requirement_policy
    ),
    custom_subject_template: value.custom_subject_template,
    billing_email: value.billing_email,
    template_variables: value.template_variables,
    ignore_customer_invoice_document: value.ignore_customer_invoice_document,
    account_remit_to_address_id: value.account_remit_to_address_id,
    override_default_ltl_tolerance: value.override_default_ltl_tolerance,
    ltl_carrier_invoice_amount_tolerance_unit: encodeToleranceUnit(
      value.ltl_carrier_invoice_amount_tolerance_unit
    ),
    ltl_carrier_invoice_amount_tolerance_over: value.ltl_carrier_invoice_amount_tolerance_over,
    ltl_carrier_invoice_amount_tolerance_under: value.ltl_carrier_invoice_amount_tolerance_under,
    override_default_ltl_customer_margin_tolerance: value.override_default_ltl_customer_margin_tolerance,
    ltl_customer_margin_tolerance_unit: encodeToleranceUnit(
      value.ltl_customer_margin_tolerance_unit
    ),
    ltl_customer_margin_tolerance_minimum: value.ltl_customer_margin_tolerance_minimum,
    ltl_customer_margin_tolerance_maximum: value.ltl_customer_margin_tolerance_maximum,
    ltl_auto_rerate_enabled: value.ltl_auto_rerate_enabled,
    ltl_reweigh_charge_exceptions_enabled: value.ltl_reweigh_charge_exceptions_enabled,
    ltl_require_manual_reweigh_review: value.ltl_require_manual_reweigh_review,
    bill_to_address: optionalEncoder(encodeAddress)(value.bill_to_address),
    customer_visible_document_types: value.customer_visible_document_types.map(
      encodeUnresolvedDocumentType
    ),
    required_receivables_types: value.required_receivables_types.map(
      encodeUnresolvedDocumentType
    ),
    required_payables_types: value.required_payables_types.map(
      encodeUnresolvedDocumentType
    ),
    contacts: value.contacts.map(encodeTMSCustomerSettings_Contact),
    references: value.references.map(encodeTMSCustomerSettings_Reference),
    rules_broker_required_references: value.rules_broker_required_references.map(encodeReferenceRule),
    rules_broker_custom_exception_messages: value.rules_broker_custom_exception_messages.map(
      encodeCustomExceptionMessage
    ),
    delivery_method: encodeDeliveryMethod(value.delivery_method)
  };
}
export function decodeTMSCustomerSettings_Contact(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    email: assertString(json.email),
    url_hash: assertString(json.url_hash),
    deleted: assertBoolean(json.deleted)
  };
}
export function encodeTMSCustomerSettings_Contact(value) {
  return {
    id: value.id,
    name: value.name,
    email: value.email,
    url_hash: value.url_hash,
    deleted: value.deleted
  };
}
export function decodeTMSCustomerSettings_Reference(json) {
  return {
    name: assertString(json.name),
    values: (json.values || []).map(assertString)
  };
}
export function encodeTMSCustomerSettings_Reference(value) {
  return {
    name: value.name,
    values: value.values
  };
}
export function decodeTMSCustomerSettingsIdsQuery(json) {
  return {
    page: assertNumber(json.page),
    term: assertString(json.term)
  };
}
export function encodeTMSCustomerSettingsIdsQuery(value) {
  return {
    page: value.page,
    term: value.term
  };
}
export function decodeTMSLoad(json) {
  return {
    id: assertNumber(json.id),
    load_id: assertString(json.load_id),
    display_identifier: optionalDecoder(decodeTMSLoad_DisplayIdentifier)(
      json.display_identifier
    ),
    tms_customer_id: optionalDecoder(assertNumber)(json.tms_customer_id),
    created_at: decodeTimestamp(json.created_at),
    updated_at: decodeTimestamp(json.updated_at),
    delivered_at: optionalDecoder(decodeTimestamp)(json.delivered_at)
  };
}
export function encodeTMSLoad(value) {
  return {
    id: value.id,
    load_id: value.load_id,
    display_identifier: optionalEncoder(encodeTMSLoad_DisplayIdentifier)(
      value.display_identifier
    ),
    tms_customer_id: value.tms_customer_id,
    created_at: encodeTimestamp(value.created_at),
    updated_at: encodeTimestamp(value.updated_at),
    delivered_at: optionalEncoder(encodeTimestamp)(value.delivered_at)
  };
}
export function decodeTMSLoad_DisplayIdentifier(json) {
  return {
    label: assertString(json.label),
    identifier: assertString(json.identifier)
  };
}
export function encodeTMSLoad_DisplayIdentifier(value) {
  return {
    label: value.label,
    identifier: value.identifier
  };
}
export function decodeTMSLoadDetails(json) {
  return {
    load: decodeTMSLoadDetails_Load(json.load),
    document_ids: (json.document_ids || []).map(assertNumber),
    line_items: (json.line_items || []).map(decodeTMSLoadDetails_LineItem),
    reference_fields: (json.reference_fields || []).map(
      decodeTMSLoadDetails_Reference
    ),
    shipment_ids: (json.shipment_ids || []).map(assertNumber),
    customer_visible_document_types: (json.customer_visible_document_types || []).map(decodeUnresolvedDocumentType),
    carrier_payables: (json.carrier_payables || []).map(
      decodeTMSLoadDetails_CarrierPayable
    )
  };
}
export function encodeTMSLoadDetails(value) {
  return {
    load: encodeTMSLoadDetails_Load(value.load),
    document_ids: value.document_ids,
    line_items: value.line_items.map(encodeTMSLoadDetails_LineItem),
    reference_fields: value.reference_fields.map(
      encodeTMSLoadDetails_Reference
    ),
    shipment_ids: value.shipment_ids,
    customer_visible_document_types: value.customer_visible_document_types.map(
      encodeUnresolvedDocumentType
    ),
    carrier_payables: value.carrier_payables.map(
      encodeTMSLoadDetails_CarrierPayable
    )
  };
}
export function decodeTMSLoadDetails_CarrierPayable(json) {
  return {
    id: assertNumber(json.id),
    external_id: assertString(json.external_id),
    currency: optionalDecoder(assertString)(json.currency)
  };
}
export function encodeTMSLoadDetails_CarrierPayable(value) {
  return {
    id: value.id,
    external_id: value.external_id,
    currency: value.currency
  };
}
export function decodeTMSLoadDetails_LineItem(json) {
  return {
    id: assertNumber(json.id),
    description: assertString(json.description),
    rate_qualifier: assertString(json.rate_qualifier),
    rate: assertString(json.rate),
    quantity: optionalDecoder(assertString)(json.quantity),
    total: assertString(json.total),
    external_id: assertString(json.external_id),
    carrier_id: optionalDecoder(assertNumber)(json.carrier_id),
    carrier_payable_id: optionalDecoder(assertNumber)(json.carrier_payable_id),
    customer_total: optionalDecoder(assertString)(json.customer_total),
    from_adjustment_price_sheet: assertBoolean(
      json.from_adjustment_price_sheet
    )
  };
}
export function encodeTMSLoadDetails_LineItem(value) {
  return {
    id: value.id,
    description: value.description,
    rate_qualifier: value.rate_qualifier,
    rate: value.rate,
    quantity: value.quantity,
    total: value.total,
    external_id: value.external_id,
    carrier_id: value.carrier_id,
    carrier_payable_id: value.carrier_payable_id,
    customer_total: value.customer_total,
    from_adjustment_price_sheet: value.from_adjustment_price_sheet
  };
}
export function decodeTMSLoadDetails_Load(json) {
  return {
    id: assertNumber(json.id),
    external_id: assertString(json.external_id),
    load_id: assertString(json.load_id),
    origin: optionalDecoder(decodeAddress)(json.origin),
    destination: optionalDecoder(decodeAddress)(json.destination),
    actual_shipped_at: optionalDecoder(decodeTimestamp)(json.actual_shipped_at),
    actual_delivered_at: optionalDecoder(decodeTimestamp)(
      json.actual_delivered_at
    ),
    customer_charge: optionalDecoder(assertString)(json.customer_charge),
    pro_number: optionalDecoder(assertString)(json.pro_number),
    distance: optionalDecoder(assertString)(json.distance),
    status: optionalDecoder(assertString)(json.status),
    carrier_id: optionalDecoder(assertNumber)(json.carrier_id),
    customer_id: optionalDecoder(assertNumber)(json.customer_id),
    currency: optionalDecoder(assertString)(json.currency),
    carrier_currency: optionalDecoder(assertString)(json.carrier_currency)
  };
}
export function encodeTMSLoadDetails_Load(value) {
  return {
    id: value.id,
    external_id: value.external_id,
    load_id: value.load_id,
    origin: optionalEncoder(encodeAddress)(value.origin),
    destination: optionalEncoder(encodeAddress)(value.destination),
    actual_shipped_at: optionalEncoder(encodeTimestamp)(
      value.actual_shipped_at
    ),
    actual_delivered_at: optionalEncoder(encodeTimestamp)(
      value.actual_delivered_at
    ),
    customer_charge: value.customer_charge,
    pro_number: value.pro_number,
    distance: value.distance,
    status: value.status,
    carrier_id: value.carrier_id,
    customer_id: value.customer_id,
    currency: value.currency,
    carrier_currency: value.carrier_currency
  };
}
export function decodeTMSLoadDetails_Reference(json) {
  return {
    key: assertString(json.key),
    value: assertString(json.value)
  };
}
export function encodeTMSLoadDetails_Reference(value) {
  return {
    key: value.key,
    value: value.value
  };
}
export function decodeTMSLoadInformation(json) {
  return {
    shipments: (json.shipments || []).map(decodeTMSLoadInformation_Shipment),
    unshipped_stops: (json.unshipped_stops || []).map(
      decodeTMSLoadInformation_Stop
    ),
    references: (json.references || []).map(decodeTMSLoadInformation_Reference),
    customer_instructions: (json.customer_instructions || []).map(
      decodeCustomerInstructions
    )
  };
}
export function encodeTMSLoadInformation(value) {
  return {
    shipments: value.shipments.map(encodeTMSLoadInformation_Shipment),
    unshipped_stops: value.unshipped_stops.map(encodeTMSLoadInformation_Stop),
    references: value.references.map(encodeTMSLoadInformation_Reference),
    customer_instructions: value.customer_instructions.map(
      encodeCustomerInstructions
    )
  };
}
export var TMSLoadInformation_Kind = /* @__PURE__ */ ((TMSLoadInformation_Kind2) => {
  TMSLoadInformation_Kind2["TMSLoadInformation_KindPickup"] = "PICKUP";
  TMSLoadInformation_Kind2["TMSLoadInformation_KindDrop"] = "DROP";
  return TMSLoadInformation_Kind2;
})(TMSLoadInformation_Kind || {});
export const decodeTMSLoadInformation_Kind = (value) => value;
export const encodeTMSLoadInformation_Kind = (value) => value;
export function decodeTMSLoadInformation_Reference(json) {
  return {
    key: assertString(json.key),
    value: assertString(json.value)
  };
}
export function encodeTMSLoadInformation_Reference(value) {
  return {
    key: value.key,
    value: value.value
  };
}
export function decodeTMSLoadInformation_Shipment(json) {
  return {
    id: assertNumber(json.id),
    shipment_id: assertString(json.shipment_id),
    actual_shipped_at: optionalDecoder(decodeTimestamp)(json.actual_shipped_at),
    actual_delivered_at: optionalDecoder(decodeTimestamp)(
      json.actual_delivered_at
    ),
    weight: optionalDecoder(assertNumber)(json.weight),
    po: optionalDecoder(assertString)(json.po),
    pickup_reference: optionalDecoder(assertString)(json.pickup_reference),
    ship_ref: optionalDecoder(assertString)(json.ship_ref),
    shipper_name: optionalDecoder(assertString)(json.shipper_name),
    customer_name: optionalDecoder(assertString)(json.customer_name),
    references: (json.references || []).map(decodeTMSLoadInformation_Reference),
    notes: (json.notes || []).map(assertString),
    stops: (json.stops || []).map(decodeTMSLoadInformation_Stop)
  };
}
export function encodeTMSLoadInformation_Shipment(value) {
  return {
    id: value.id,
    shipment_id: value.shipment_id,
    actual_shipped_at: optionalEncoder(encodeTimestamp)(
      value.actual_shipped_at
    ),
    actual_delivered_at: optionalEncoder(encodeTimestamp)(
      value.actual_delivered_at
    ),
    weight: value.weight,
    po: value.po,
    pickup_reference: value.pickup_reference,
    ship_ref: value.ship_ref,
    shipper_name: value.shipper_name,
    customer_name: value.customer_name,
    references: value.references.map(encodeTMSLoadInformation_Reference),
    notes: value.notes,
    stops: value.stops.map(encodeTMSLoadInformation_Stop)
  };
}
export function decodeTMSLoadInformation_Stop(json) {
  return {
    id: assertNumber(json.id),
    kind: decodeTMSLoadInformation_Kind(json.kind),
    address: decodeAddress(json.address),
    actual_arrived_at: optionalDecoder(decodeTimestamp)(json.actual_arrived_at),
    actual_departed_at: optionalDecoder(decodeTimestamp)(
      json.actual_departed_at
    ),
    weight: optionalDecoder(assertNumber)(json.weight),
    references: (json.references || []).map(decodeTMSLoadInformation_Reference)
  };
}
export function encodeTMSLoadInformation_Stop(value) {
  return {
    id: value.id,
    kind: encodeTMSLoadInformation_Kind(value.kind),
    address: encodeAddress(value.address),
    actual_arrived_at: optionalEncoder(encodeTimestamp)(
      value.actual_arrived_at
    ),
    actual_departed_at: optionalEncoder(encodeTimestamp)(
      value.actual_departed_at
    ),
    weight: value.weight,
    references: value.references.map(encodeTMSLoadInformation_Reference)
  };
}
export function decodeTMSLoadShipment(json) {
  return {
    id: assertNumber(json.id),
    tms_load_id: assertNumber(json.tms_load_id),
    customer_name: optionalDecoder(assertString)(json.customer_name)
  };
}
export function encodeTMSLoadShipment(value) {
  return {
    id: value.id,
    tms_load_id: value.tms_load_id,
    customer_name: value.customer_name
  };
}
export var TMSOption = /* @__PURE__ */ ((TMSOption2) => {
  TMSOption2["TMSOptionCustom"] = "Custom";
  TMSOption2["TMSOptionThreegTMS"] = "3Gtms";
  TMSOption2["TMSOptionThreeplSystems"] = "3PLSystems";
  TMSOption2["TMSOptionAljex"] = "aljex";
  TMSOption2["TMSOptionAmous"] = "Amous";
  TMSOption2["TMSOptionAscendTMS"] = "AscendTMS";
  TMSOption2["TMSOptionBrokerPro"] = "BrokerPro";
  TMSOption2["TMSOptionCargoTel"] = "CargoTel";
  TMSOption2["TMSOptionDrDispatch"] = "DrDispatch";
  TMSOption2["TMSOptionEdgeTMS"] = "EdgeTMS";
  TMSOption2["TMSOptionEzLoader"] = "EZLoader";
  TMSOption2["TMSOptionFMS"] = "FMS";
  TMSOption2["TMSOptionFreightHauler"] = "FreightHauler";
  TMSOption2["TMSOptionHaulFox"] = "HaulFox";
  TMSOption2["TMSOptionItsDispatch"] = "ITSDispatch";
  TMSOption2["TMSOptionKeypoint"] = "Keypoint";
  TMSOption2["TMSOptionKeypointAPI"] = "KeypointApi";
  TMSOption2["TMSOptionLogistically"] = "Logistically";
  TMSOption2["TMSOptionMasterMind"] = "MasterMind";
  TMSOption2["TMSOptionMCLeod"] = "McLeod";
  TMSOption2["TMSOptionMercuryGate"] = "MercuryGate";
  TMSOption2["TMSOptionOmnis"] = "Omnis";
  TMSOption2["TMSOptionPortTMS"] = "PortTMS";
  TMSOption2["TMSOptionPrimeTMS"] = "PrimeTMS";
  TMSOption2["TMSOptionRevenova"] = "Revenova";
  TMSOption2["TMSOptionRoseRocket"] = "RoseRocket";
  TMSOption2["TMSOptionTai"] = "Tai";
  TMSOption2["TMSOptionTmw"] = "TMW";
  TMSOption2["TMSOptionTransportPro"] = "TransportPro";
  TMSOption2["TMSOptionTruckmate"] = "Truckmate";
  TMSOption2["TMSOptionTurvo"] = "Turvo";
  TMSOption2["TMSOptionViaTMS"] = "ViaTMS";
  TMSOption2["TMSOptionWave"] = "Wave";
  TMSOption2["TMSOptionWinFactor"] = "WinFactor";
  TMSOption2["TMSOptionBusinessManager"] = "BusinessManager";
  TMSOption2["TMSOptionCadence"] = "Cadence";
  TMSOption2["TMSOptionFactorFox"] = "FactorFox";
  TMSOption2["TMSOptionFactorSql"] = "FactorSQL";
  TMSOption2["TMSOptionFactorView"] = "FactorView";
  return TMSOption2;
})(TMSOption || {});
export const decodeTMSOption = (value) => value;
export const encodeTMSOption = (value) => value;
export function decodeTMSShipment(json) {
  return {
    id: assertNumber(json.id),
    shipment_id: assertString(json.shipment_id),
    weight: optionalDecoder(assertNumber)(json.weight),
    shipper_name: optionalDecoder(assertString)(json.shipper_name),
    references: assertRecord(assertString)(json.references),
    document_ids: (json.document_ids || []).map(assertNumber),
    customer_id: optionalDecoder(assertNumber)(json.customer_id),
    shipment_notes: (json.shipment_notes || []).map(assertString),
    stops: (json.stops || []).map(decodeTMSShipment_TMSStopWrapper)
  };
}
export function encodeTMSShipment(value) {
  return {
    id: value.id,
    shipment_id: value.shipment_id,
    weight: value.weight,
    shipper_name: value.shipper_name,
    references: value.references,
    document_ids: value.document_ids,
    customer_id: value.customer_id,
    shipment_notes: value.shipment_notes,
    stops: value.stops.map(encodeTMSShipment_TMSStopWrapper)
  };
}
export function decodeTMSShipment_Drop(json) {
  return {
    id: assertNumber(json.id),
    address: decodeAddress(json.address)
  };
}
export function encodeTMSShipment_Drop(value) {
  return {
    id: value.id,
    address: encodeAddress(value.address)
  };
}
export function decodeTMSShipment_Pickup(json) {
  return {
    id: assertNumber(json.id),
    address: decodeAddress(json.address)
  };
}
export function encodeTMSShipment_Pickup(value) {
  return {
    id: value.id,
    address: encodeAddress(value.address)
  };
}
export function decodeTMSShipment_TMSStopWrapper(json) {
  return {
    tmsStop: decodeTMSShipment_TMSStopWrapper_TMSStop(json)
  };
}
export function encodeTMSShipment_TMSStopWrapper(value) {
  return __spreadValues({}, encodeTMSShipment_TMSStopWrapper_TMSStop(value.tmsStop));
}
export var TMSShipment_TMSStopWrapper_TMSStopType = /* @__PURE__ */ ((TMSShipment_TMSStopWrapper_TMSStopType2) => {
  TMSShipment_TMSStopWrapper_TMSStopType2["Pickup"] = "pickup";
  TMSShipment_TMSStopWrapper_TMSStopType2["Drop"] = "drop";
  return TMSShipment_TMSStopWrapper_TMSStopType2;
})(TMSShipment_TMSStopWrapper_TMSStopType || {});
export function decodeTMSShipment_TMSStopWrapper_TMSStop(json) {
  if (json.pickup !== void 0) {
    return {
      type: "pickup" /* Pickup */,
      pickup: decodeTMSShipment_Pickup(json.pickup)
    };
  } else if (json.drop !== void 0) {
    return {
      type: "drop" /* Drop */,
      drop: decodeTMSShipment_Drop(json.drop)
    };
  }
  throw new Error("No field set in oneof TMSShipment_TMSStopWrapper_TMSStop");
}
export function encodeTMSShipment_TMSStopWrapper_TMSStop(value) {
  switch (value.type) {
    case "pickup" /* Pickup */:
      return { pickup: encodeTMSShipment_Pickup(value.pickup) };
    case "drop" /* Drop */:
      return { drop: encodeTMSShipment_Drop(value.drop) };
    default:
      throw new Error(
        "No field set in oneof TMSShipment_TMSStopWrapper_TMSStop"
      );
  }
}
export function decodeTMSShipmentLineItems(json) {
  return {
    id: assertNumber(json.id),
    external_id: assertString(json.external_id),
    customer_id: optionalDecoder(assertNumber)(json.customer_id),
    customer_currency: optionalDecoder(assertString)(json.customer_currency),
    line_items: (json.line_items || []).map(decodeTMSLoadDetails_LineItem)
  };
}
export function encodeTMSShipmentLineItems(value) {
  return {
    id: value.id,
    external_id: value.external_id,
    customer_id: value.customer_id,
    customer_currency: value.customer_currency,
    line_items: value.line_items.map(encodeTMSLoadDetails_LineItem)
  };
}
export function decodeTolerance(json) {
  return {
    override_enabled: assertBoolean(json.override_enabled),
    amount_above: assertString(json.amount_above),
    amount_below: assertString(json.amount_below),
    unit: decodeToleranceUnit(json.unit)
  };
}
export function encodeTolerance(value) {
  return {
    override_enabled: value.override_enabled,
    amount_above: value.amount_above,
    amount_below: value.amount_below,
    unit: encodeToleranceUnit(value.unit)
  };
}
export var ToleranceUnit = /* @__PURE__ */ ((ToleranceUnit2) => {
  ToleranceUnit2["ToleranceUnitDollars"] = "absolute";
  ToleranceUnit2["ToleranceUnitPercentage"] = "percent";
  return ToleranceUnit2;
})(ToleranceUnit || {});
export const decodeToleranceUnit = (value) => value;
export const encodeToleranceUnit = (value) => value;
export function decodeUnresolvedDocumentType(json) {
  return {
    value: decodeUnresolvedDocumentType_Value(json)
  };
}
export function encodeUnresolvedDocumentType(value) {
  return __spreadValues({}, encodeUnresolvedDocumentType_Value(value.value));
}
export var UnresolvedDocumentType_ValueType = /* @__PURE__ */ ((UnresolvedDocumentType_ValueType2) => {
  UnresolvedDocumentType_ValueType2["DocumentType"] = "document_type";
  UnresolvedDocumentType_ValueType2["DocumentTypePartial"] = "document_type_partial";
  UnresolvedDocumentType_ValueType2["Unknown"] = "unknown";
  return UnresolvedDocumentType_ValueType2;
})(UnresolvedDocumentType_ValueType || {});
export function decodeUnresolvedDocumentType_Value(json) {
  if (json.document_type !== void 0) {
    return {
      type: "document_type" /* DocumentType */,
      document_type: decodeDocumentType(json.document_type)
    };
  } else if (json.document_type_partial !== void 0) {
    return {
      type: "document_type_partial" /* DocumentTypePartial */,
      document_type_partial: decodeDocumentTypePartial(
        json.document_type_partial
      )
    };
  } else if (json.unknown !== void 0) {
    return {
      type: "unknown" /* Unknown */,
      unknown: decodeUnresolvedDocumentType_Unknown(json.unknown)
    };
  }
  throw new Error("No field set in oneof UnresolvedDocumentType_Value");
}
export function encodeUnresolvedDocumentType_Value(value) {
  switch (value.type) {
    case "document_type" /* DocumentType */:
      return { document_type: encodeDocumentType(value.document_type) };
    case "document_type_partial" /* DocumentTypePartial */:
      return {
        document_type_partial: encodeDocumentTypePartial(
          value.document_type_partial
        )
      };
    case "unknown" /* Unknown */:
      return { unknown: encodeUnresolvedDocumentType_Unknown(value.unknown) };
    default:
      throw new Error("No field set in oneof UnresolvedDocumentType_Value");
  }
}
export function decodeUnresolvedDocumentType_Unknown(json) {
  return {};
}
export function encodeUnresolvedDocumentType_Unknown(value) {
  return {};
}
export function decodeUser(json) {
  return {
    id: assertNumber(json.id),
    name: assertString(json.name),
    email_address: assertString(json.email_address),
    is_admin: assertBoolean(json.is_admin),
    is_disabled: assertBoolean(json.is_disabled),
    is_approver: assertBoolean(json.is_approver),
    is_indexer: assertBoolean(json.is_indexer),
    is_auditor: assertBoolean(json.is_auditor),
    is_managed_indexer: assertBoolean(json.is_managed_indexer),
    is_customer_representative: assertBoolean(json.is_customer_representative),
    receivables: assertBoolean(json.receivables),
    has_settings_role: assertBoolean(json.has_settings_role),
    preferred_filters: (json.preferred_filters || []).map(
      decodeDashboardLabelFilter
    ),
    preferred_categories: (json.preferred_categories || []).map(
      decodeDashboardCategoryFilter
    ),
    email_signature: assertString(json.email_signature),
    hide_items_in_queues_on_presence: assertBoolean(
      json.hide_items_in_queues_on_presence
    ),
    created_at: decodeTimestamp(json.created_at),
    updated_at: decodeTimestamp(json.updated_at),
    receives_emails_at: assertString(json.receives_emails_at),
    group_user_id: optionalDecoder(assertNumber)(json.group_user_id),
    has_insights_dashboard_access: assertBoolean(
      json.has_insights_dashboard_access
    ),
    terms_agreed_to_at: optionalDecoder(decodeTimestamp)(
      json.terms_agreed_to_at
    ),
    labels_collapsed: assertBoolean(json.labels_collapsed),
    read_only_factor_invoices: assertBoolean(json.read_only_factor_invoices),
    can_unapprove_invoices: assertBoolean(json.can_unapprove_invoices),
    use_factor_invoice_workflow_page: assertBoolean(
      json.use_factor_invoice_workflow_page
    ),
    audit_documents_column_width: optionalDecoder(assertNumber)(
      json.audit_documents_column_width
    ),
    use_carrier_invoice_workflow_page: assertBoolean(
      json.use_carrier_invoice_workflow_page
    )
  };
}
export function encodeUser(value) {
  return {
    id: value.id,
    name: value.name,
    email_address: value.email_address,
    is_admin: value.is_admin,
    is_disabled: value.is_disabled,
    is_approver: value.is_approver,
    is_indexer: value.is_indexer,
    is_auditor: value.is_auditor,
    is_managed_indexer: value.is_managed_indexer,
    is_customer_representative: value.is_customer_representative,
    receivables: value.receivables,
    has_settings_role: value.has_settings_role,
    preferred_filters: value.preferred_filters.map(encodeDashboardLabelFilter),
    preferred_categories: value.preferred_categories.map(
      encodeDashboardCategoryFilter
    ),
    email_signature: value.email_signature,
    hide_items_in_queues_on_presence: value.hide_items_in_queues_on_presence,
    created_at: encodeTimestamp(value.created_at),
    updated_at: encodeTimestamp(value.updated_at),
    receives_emails_at: value.receives_emails_at,
    group_user_id: value.group_user_id,
    has_insights_dashboard_access: value.has_insights_dashboard_access,
    terms_agreed_to_at: optionalEncoder(encodeTimestamp)(
      value.terms_agreed_to_at
    ),
    labels_collapsed: value.labels_collapsed,
    read_only_factor_invoices: value.read_only_factor_invoices,
    can_unapprove_invoices: value.can_unapprove_invoices,
    use_factor_invoice_workflow_page: value.use_factor_invoice_workflow_page,
    audit_documents_column_width: value.audit_documents_column_width,
    use_carrier_invoice_workflow_page: value.use_carrier_invoice_workflow_page
  };
}
export function decodeVerification(json) {
  return {
    state: decodeVerification_State(json)
  };
}
export function encodeVerification(value) {
  return __spreadValues({}, encodeVerification_State(value.state));
}
export var Verification_StateType = /* @__PURE__ */ ((Verification_StateType2) => {
  Verification_StateType2["Success"] = "success";
  Verification_StateType2["Pending"] = "pending";
  Verification_StateType2["NonParticipatingCarrier"] = "non_participating_carrier";
  Verification_StateType2["UnverifiableLoad"] = "unverifiable_load";
  Verification_StateType2["UnconfirmedDelivery"] = "unconfirmed_delivery";
  Verification_StateType2["Fail"] = "fail";
  return Verification_StateType2;
})(Verification_StateType || {});
export function decodeVerification_State(json) {
  if (json.success !== void 0) {
    return {
      type: "success" /* Success */,
      success: decodeVerification_Success(json.success)
    };
  } else if (json.pending !== void 0) {
    return {
      type: "pending" /* Pending */,
      pending: decodeVerification_Pending(json.pending)
    };
  } else if (json.non_participating_carrier !== void 0) {
    return {
      type: "non_participating_carrier" /* NonParticipatingCarrier */,
      non_participating_carrier: decodeVerification_NonParticipatingCarrier(
        json.non_participating_carrier
      )
    };
  } else if (json.unverifiable_load !== void 0) {
    return {
      type: "unverifiable_load" /* UnverifiableLoad */,
      unverifiable_load: decodeVerification_UnverifiableLoad(
        json.unverifiable_load
      )
    };
  } else if (json.unconfirmed_delivery !== void 0) {
    return {
      type: "unconfirmed_delivery" /* UnconfirmedDelivery */,
      unconfirmed_delivery: decodeVerification_UnconfirmedDelivery(
        json.unconfirmed_delivery
      )
    };
  } else if (json.fail !== void 0) {
    return {
      type: "fail" /* Fail */,
      fail: decodeVerification_Fail(json.fail)
    };
  }
  throw new Error("No field set in oneof Verification_State");
}
export function encodeVerification_State(value) {
  switch (value.type) {
    case "success" /* Success */:
      return { success: encodeVerification_Success(value.success) };
    case "pending" /* Pending */:
      return { pending: encodeVerification_Pending(value.pending) };
    case "non_participating_carrier" /* NonParticipatingCarrier */:
      return {
        non_participating_carrier: encodeVerification_NonParticipatingCarrier(
          value.non_participating_carrier
        )
      };
    case "unverifiable_load" /* UnverifiableLoad */:
      return {
        unverifiable_load: encodeVerification_UnverifiableLoad(
          value.unverifiable_load
        )
      };
    case "unconfirmed_delivery" /* UnconfirmedDelivery */:
      return {
        unconfirmed_delivery: encodeVerification_UnconfirmedDelivery(
          value.unconfirmed_delivery
        )
      };
    case "fail" /* Fail */:
      return { fail: encodeVerification_Fail(value.fail) };
    default:
      throw new Error("No field set in oneof Verification_State");
  }
}
export function decodeVerification_Fail(json) {
  return {
    mode: decodeVerificationsMode(json.mode),
    failures: (json.failures || []).map(decodeVerificationFailure)
  };
}
export function encodeVerification_Fail(value) {
  return {
    mode: encodeVerificationsMode(value.mode),
    failures: value.failures.map(encodeVerificationFailure)
  };
}
export function decodeVerification_NonParticipatingCarrier(json) {
  return {};
}
export function encodeVerification_NonParticipatingCarrier(value) {
  return {};
}
export function decodeVerification_Pending(json) {
  return {};
}
export function encodeVerification_Pending(value) {
  return {};
}
export function decodeVerification_Success(json) {
  return {};
}
export function encodeVerification_Success(value) {
  return {};
}
export function decodeVerification_UnconfirmedDelivery(json) {
  return {};
}
export function encodeVerification_UnconfirmedDelivery(value) {
  return {};
}
export function decodeVerification_UnverifiableLoad(json) {
  return {};
}
export function encodeVerification_UnverifiableLoad(value) {
  return {};
}
export function decodeVerificationFailure(json) {
  return {
    message: decodeVerificationFailureMessage(json.message),
    broker_amount: optionalDecoder(assertString)(json.broker_amount)
  };
}
export function encodeVerificationFailure(value) {
  return {
    message: encodeVerificationFailureMessage(value.message),
    broker_amount: value.broker_amount
  };
}
export var VerificationFailureMessage = /* @__PURE__ */ ((VerificationFailureMessage2) => {
  VerificationFailureMessage2["VerificationFailureMessageLoadIssueFailure"] = "load_issue_failure";
  VerificationFailureMessage2["VerificationFailureMessageAmountMismatchFailure"] = "amount_mismatch_failure";
  VerificationFailureMessage2["VerificationFailureMessageUndeliveredFailure"] = "undelivered_failue";
  VerificationFailureMessage2["VerificationFailureMessageLoadNotFoundFailure"] = "load_not_found_failure";
  VerificationFailureMessage2["VerificationFailureMessageCarrierMismatchFailure"] = "carrier_mismatch_failure";
  VerificationFailureMessage2["VerificationFailureMessagePossibleFeesFailure"] = "possible_fees_failure";
  VerificationFailureMessage2["VerificationFailureMessageRemitToFailure"] = "remit_to_failure";
  return VerificationFailureMessage2;
})(VerificationFailureMessage || {});
export const decodeVerificationFailureMessage = (value) => value;
export const encodeVerificationFailureMessage = (value) => value;
export function decodeVerificationsDebtorBatch(json) {
  return {
    id: assertNumber(json.id),
    factor_broker_id: assertNumber(json.factor_broker_id),
    state: decodeVerificationsDebtorBatchState(json.state),
    sort_score: assertNumber(json.sort_score),
    labels: (json.labels || []).map(assertString),
    opt_outs: (json.opt_outs || []).map(
      decodeVerificationsDebtorConfirmationOptOut
    ),
    bouncing_addresses: (json.bouncing_addresses || []).map(assertString),
    recipient_edits: (json.recipient_edits || []).map(
      decodeVerificationsDebtorConfirmationRecipientEdit
    ),
    confirmation_reset_available: assertBoolean(
      json.confirmation_reset_available
    ),
    confirmation_email_message_ids: (json.confirmation_email_message_ids || []).map(assertNumber)
  };
}
export function encodeVerificationsDebtorBatch(value) {
  return {
    id: value.id,
    factor_broker_id: value.factor_broker_id,
    state: encodeVerificationsDebtorBatchState(value.state),
    sort_score: value.sort_score,
    labels: value.labels,
    opt_outs: value.opt_outs.map(encodeVerificationsDebtorConfirmationOptOut),
    bouncing_addresses: value.bouncing_addresses,
    recipient_edits: value.recipient_edits.map(
      encodeVerificationsDebtorConfirmationRecipientEdit
    ),
    confirmation_reset_available: value.confirmation_reset_available,
    confirmation_email_message_ids: value.confirmation_email_message_ids
  };
}
export function decodeVerificationsDebtorBatchItem(json) {
  return {
    id: assertNumber(json.id),
    verifications_debtor_batch_id: assertNumber(
      json.verifications_debtor_batch_id
    ),
    previous_verifications_debtor_batch_id: optionalDecoder(assertNumber)(
      json.previous_verifications_debtor_batch_id
    ),
    factor_invoice_id: assertNumber(json.factor_invoice_id),
    state: decodeVerificationsDebtorBatchItemState(json.state),
    priority: assertNumber(json.priority),
    note: assertString(json.note),
    cadence_debtor_contact_id: optionalDecoder(assertNumber)(
      json.cadence_debtor_contact_id
    ),
    cadence_verification_method_id: optionalDecoder(assertNumber)(
      json.cadence_verification_method_id
    ),
    verify_level: decodeVerifyLevel(json.verify_level),
    verify_level_reason: decodeVerifyLevelReason(json.verify_level_reason),
    failed_verify_rules: (json.failed_verify_rules || []).map(
      decodeVerificationsDebtorBatchItemFailedVerifyRule
    ),
    effective_custom_state: decodeVerificationsDebtorBatchItemCustomState(
      json.effective_custom_state
    ),
    debtor_confirmation: optionalDecoder(decodeVerificationsDebtorConfirmation)(
      json.debtor_confirmation
    )
  };
}
export function encodeVerificationsDebtorBatchItem(value) {
  return {
    id: value.id,
    verifications_debtor_batch_id: value.verifications_debtor_batch_id,
    previous_verifications_debtor_batch_id: value.previous_verifications_debtor_batch_id,
    factor_invoice_id: value.factor_invoice_id,
    state: encodeVerificationsDebtorBatchItemState(value.state),
    priority: value.priority,
    note: value.note,
    cadence_debtor_contact_id: value.cadence_debtor_contact_id,
    cadence_verification_method_id: value.cadence_verification_method_id,
    verify_level: encodeVerifyLevel(value.verify_level),
    verify_level_reason: encodeVerifyLevelReason(value.verify_level_reason),
    failed_verify_rules: value.failed_verify_rules.map(
      encodeVerificationsDebtorBatchItemFailedVerifyRule
    ),
    effective_custom_state: encodeVerificationsDebtorBatchItemCustomState(
      value.effective_custom_state
    ),
    debtor_confirmation: optionalEncoder(encodeVerificationsDebtorConfirmation)(
      value.debtor_confirmation
    )
  };
}
export function decodeVerificationsDebtorBatchItemCustomState(json) {
  return {
    id: assertString(json.id),
    name: assertString(json.name),
    state: decodeVerificationsDebtorBatchItemState(json.state),
    selectable: assertBoolean(json.selectable)
  };
}
export function encodeVerificationsDebtorBatchItemCustomState(value) {
  return {
    id: value.id,
    name: value.name,
    state: encodeVerificationsDebtorBatchItemState(value.state),
    selectable: value.selectable
  };
}
export function decodeVerificationsDebtorBatchItemFailedVerifyRule(json) {
  return {
    level: decodeVerifyLevel(json.level),
    message: assertString(json.message),
    reason: decodeVerifyLevelReason(json.reason)
  };
}
export function encodeVerificationsDebtorBatchItemFailedVerifyRule(value) {
  return {
    level: encodeVerifyLevel(value.level),
    message: value.message,
    reason: encodeVerifyLevelReason(value.reason)
  };
}
export var VerificationsDebtorBatchItemState = /* @__PURE__ */ ((VerificationsDebtorBatchItemState2) => {
  VerificationsDebtorBatchItemState2["VerificationsDebtorBatchItemStateUnverified"] = "unverified";
  VerificationsDebtorBatchItemState2["VerificationsDebtorBatchItemStateVerified"] = "verified";
  VerificationsDebtorBatchItemState2["VerificationsDebtorBatchItemStateSkipped"] = "skipped";
  VerificationsDebtorBatchItemState2["VerificationsDebtorBatchItemStateRejected"] = "rejected";
  VerificationsDebtorBatchItemState2["VerificationsDebtorBatchItemStateHeld"] = "held";
  VerificationsDebtorBatchItemState2["VerificationsDebtorBatchItemStateAutoSkipped"] = "auto_skipped";
  VerificationsDebtorBatchItemState2["VerificationsDebtorBatchItemStateNotReady"] = "not_ready";
  VerificationsDebtorBatchItemState2["VerificationsDebtorBatchItemStateWaiting"] = "waiting";
  return VerificationsDebtorBatchItemState2;
})(VerificationsDebtorBatchItemState || {});
export const decodeVerificationsDebtorBatchItemState = (value) => value;
export const encodeVerificationsDebtorBatchItemState = (value) => value;
export var VerificationsDebtorBatchState = /* @__PURE__ */ ((VerificationsDebtorBatchState2) => {
  VerificationsDebtorBatchState2["VerificationsDebtorBatchStateAvailable"] = "available";
  VerificationsDebtorBatchState2["VerificationsDebtorBatchStateStarted"] = "started";
  VerificationsDebtorBatchState2["VerificationsDebtorBatchStateWaiting"] = "waiting";
  VerificationsDebtorBatchState2["VerificationsDebtorBatchStateFinished"] = "finished";
  VerificationsDebtorBatchState2["VerificationsDebtorBatchStateReply"] = "reply";
  return VerificationsDebtorBatchState2;
})(VerificationsDebtorBatchState || {});
export const decodeVerificationsDebtorBatchState = (value) => value;
export const encodeVerificationsDebtorBatchState = (value) => value;
export function decodeVerificationsDebtorConfirmation(json) {
  return {
    state: decodeVerificationsDebtorConfirmationState(json.state),
    asked_at: optionalDecoder(decodeTimestamp)(json.asked_at),
    responded_at: optionalDecoder(decodeTimestamp)(json.responded_at),
    verdict: optionalDecoder(decodeVerificationsDebtorConfirmationVerdict)(
      json.verdict
    ),
    drifted_fields: (json.drifted_fields || []).map(assertString),
    asked_amount: optionalDecoder(assertString)(json.asked_amount),
    free_text: assertString(json.free_text),
    answers: optionalDecoder(decodeVerificationsDebtorConfirmationAnswers)(
      json.answers
    ),
    request_reply: optionalDecoder(
      decodeVerificationsDebtorConfirmationRequestReply
    )(json.request_reply),
    asked_load_id: optionalDecoder(assertString)(json.asked_load_id),
    asked_invoice_date: optionalDecoder(assertString)(json.asked_invoice_date),
    asked_carrier_name: optionalDecoder(assertString)(json.asked_carrier_name),
    channel: optionalDecoder(decodeVerificationsDebtorConfirmationChannel)(
      json.channel
    ),
    answered_questions: (json.answered_questions || []).map(
      decodeVerificationsDebtorConfirmationAnswer
    ),
    recipients: (json.recipients || []).map(
      decodeVerificationsDebtorConfirmationRecipient
    ),
    answered_by: optionalDecoder(
      decodeVerificationsDebtorConfirmationRecipient
    )(json.answered_by),
    answered_at: optionalDecoder(decodeTimestamp)(json.answered_at)
  };
}
export function encodeVerificationsDebtorConfirmation(value) {
  return {
    state: encodeVerificationsDebtorConfirmationState(value.state),
    asked_at: optionalEncoder(encodeTimestamp)(value.asked_at),
    responded_at: optionalEncoder(encodeTimestamp)(value.responded_at),
    verdict: optionalEncoder(encodeVerificationsDebtorConfirmationVerdict)(
      value.verdict
    ),
    drifted_fields: value.drifted_fields,
    asked_amount: value.asked_amount,
    free_text: value.free_text,
    answers: optionalEncoder(encodeVerificationsDebtorConfirmationAnswers)(
      value.answers
    ),
    request_reply: optionalEncoder(
      encodeVerificationsDebtorConfirmationRequestReply
    )(value.request_reply),
    asked_load_id: value.asked_load_id,
    asked_invoice_date: value.asked_invoice_date,
    asked_carrier_name: value.asked_carrier_name,
    channel: optionalEncoder(encodeVerificationsDebtorConfirmationChannel)(
      value.channel
    ),
    answered_questions: value.answered_questions.map(
      encodeVerificationsDebtorConfirmationAnswer
    ),
    recipients: value.recipients.map(
      encodeVerificationsDebtorConfirmationRecipient
    ),
    answered_by: optionalEncoder(
      encodeVerificationsDebtorConfirmationRecipient
    )(value.answered_by),
    answered_at: optionalEncoder(encodeTimestamp)(value.answered_at)
  };
}
export function decodeVerificationsDebtorConfirmationAnswer(json) {
  return {
    question: assertString(json.question),
    answer: optionalDecoder(assertString)(json.answer)
  };
}
export function encodeVerificationsDebtorConfirmationAnswer(value) {
  return {
    question: value.question,
    answer: value.answer
  };
}
export function decodeVerificationsDebtorConfirmationAnswers(json) {
  return {
    amount_to_pay: optionalDecoder(assertString)(json.amount_to_pay),
    delivery: optionalDecoder(decodeVerificationsDebtorConfirmationDelivery)(
      json.delivery
    ),
    carrier_disputed: optionalDecoder(assertBoolean)(json.carrier_disputed),
    actual_carrier: optionalDecoder(assertString)(json.actual_carrier),
    advances: optionalDecoder(assertString)(json.advances),
    deductions: optionalDecoder(assertString)(json.deductions),
    accessorials: optionalDecoder(assertString)(json.accessorials)
  };
}
export function encodeVerificationsDebtorConfirmationAnswers(value) {
  return {
    amount_to_pay: value.amount_to_pay,
    delivery: optionalEncoder(encodeVerificationsDebtorConfirmationDelivery)(
      value.delivery
    ),
    carrier_disputed: value.carrier_disputed,
    actual_carrier: value.actual_carrier,
    advances: value.advances,
    deductions: value.deductions,
    accessorials: value.accessorials
  };
}
export var VerificationsDebtorConfirmationChannel = /* @__PURE__ */ ((VerificationsDebtorConfirmationChannel2) => {
  VerificationsDebtorConfirmationChannel2["VerificationsDebtorConfirmationChannelOneClick"] = "one_click";
  VerificationsDebtorConfirmationChannel2["VerificationsDebtorConfirmationChannelWebPage"] = "web_page";
  VerificationsDebtorConfirmationChannel2["VerificationsDebtorConfirmationChannelCorrectionForm"] = "correction_form";
  VerificationsDebtorConfirmationChannel2["VerificationsDebtorConfirmationChannelEmailReply"] = "email_reply";
  return VerificationsDebtorConfirmationChannel2;
})(VerificationsDebtorConfirmationChannel || {});
export const decodeVerificationsDebtorConfirmationChannel = (value) => value;
export const encodeVerificationsDebtorConfirmationChannel = (value) => value;
export var VerificationsDebtorConfirmationDelivery = /* @__PURE__ */ ((VerificationsDebtorConfirmationDelivery2) => {
  VerificationsDebtorConfirmationDelivery2["VerificationsDebtorConfirmationDeliveryUnknown"] = "unknown";
  VerificationsDebtorConfirmationDelivery2["VerificationsDebtorConfirmationDeliveryDelivered"] = "delivered";
  VerificationsDebtorConfirmationDelivery2["VerificationsDebtorConfirmationDeliveryNotYet"] = "not_yet";
  VerificationsDebtorConfirmationDelivery2["VerificationsDebtorConfirmationDeliveryNever"] = "never";
  return VerificationsDebtorConfirmationDelivery2;
})(VerificationsDebtorConfirmationDelivery || {});
export const decodeVerificationsDebtorConfirmationDelivery = (value) => value;
export const encodeVerificationsDebtorConfirmationDelivery = (value) => value;
export function decodeVerificationsDebtorConfirmationOptOut(json) {
  return {
    email_address: assertString(json.email_address),
    opted_out_at: decodeTimestamp(json.opted_out_at),
    free_text: assertString(json.free_text)
  };
}
export function encodeVerificationsDebtorConfirmationOptOut(value) {
  return {
    email_address: value.email_address,
    opted_out_at: encodeTimestamp(value.opted_out_at),
    free_text: value.free_text
  };
}
export function decodeVerificationsDebtorConfirmationRecipient(json) {
  return {
    email_address: assertString(json.email_address),
    name: assertString(json.name),
    accepted_at: optionalDecoder(decodeTimestamp)(json.accepted_at),
    delivered_at: optionalDecoder(decodeTimestamp)(json.delivered_at),
    opened_at: optionalDecoder(decodeTimestamp)(json.opened_at),
    bounced_at: optionalDecoder(decodeTimestamp)(json.bounced_at),
    bounce_reason: optionalDecoder(assertString)(json.bounce_reason),
    delivery_error: optionalDecoder(assertString)(json.delivery_error)
  };
}
export function encodeVerificationsDebtorConfirmationRecipient(value) {
  return {
    email_address: value.email_address,
    name: value.name,
    accepted_at: optionalEncoder(encodeTimestamp)(value.accepted_at),
    delivered_at: optionalEncoder(encodeTimestamp)(value.delivered_at),
    opened_at: optionalEncoder(encodeTimestamp)(value.opened_at),
    bounced_at: optionalEncoder(encodeTimestamp)(value.bounced_at),
    bounce_reason: value.bounce_reason,
    delivery_error: value.delivery_error
  };
}
export function decodeVerificationsDebtorConfirmationRecipientEdit(json) {
  return {
    email_address: assertString(json.email_address),
    name: assertString(json.name),
    kind: decodeVerificationsDebtorConfirmationRecipientEditKind(json.kind)
  };
}
export function encodeVerificationsDebtorConfirmationRecipientEdit(value) {
  return {
    email_address: value.email_address,
    name: value.name,
    kind: encodeVerificationsDebtorConfirmationRecipientEditKind(value.kind)
  };
}
export var VerificationsDebtorConfirmationRecipientEditKind = /* @__PURE__ */ ((VerificationsDebtorConfirmationRecipientEditKind2) => {
  VerificationsDebtorConfirmationRecipientEditKind2["VerificationsDebtorConfirmationRecipientEditKindAdded"] = "added";
  VerificationsDebtorConfirmationRecipientEditKind2["VerificationsDebtorConfirmationRecipientEditKindExcluded"] = "excluded";
  return VerificationsDebtorConfirmationRecipientEditKind2;
})(VerificationsDebtorConfirmationRecipientEditKind || {});
export const decodeVerificationsDebtorConfirmationRecipientEditKind = (value) => value;
export const encodeVerificationsDebtorConfirmationRecipientEditKind = (value) => value;
export function decodeVerificationsDebtorConfirmationRequestReply(json) {
  return {
    verdict: optionalDecoder(decodeVerificationsDebtorConfirmationVerdict)(
      json.verdict
    ),
    responded_at: decodeTimestamp(json.responded_at),
    free_text: assertString(json.free_text)
  };
}
export function encodeVerificationsDebtorConfirmationRequestReply(value) {
  return {
    verdict: optionalEncoder(encodeVerificationsDebtorConfirmationVerdict)(
      value.verdict
    ),
    responded_at: encodeTimestamp(value.responded_at),
    free_text: value.free_text
  };
}
export var VerificationsDebtorConfirmationState = /* @__PURE__ */ ((VerificationsDebtorConfirmationState2) => {
  VerificationsDebtorConfirmationState2["VerificationsDebtorConfirmationStateNotAsked"] = "not_asked";
  VerificationsDebtorConfirmationState2["VerificationsDebtorConfirmationStateAsked"] = "asked";
  VerificationsDebtorConfirmationState2["VerificationsDebtorConfirmationStateAnswered"] = "answered";
  VerificationsDebtorConfirmationState2["VerificationsDebtorConfirmationStateSendFailed"] = "send_failed";
  return VerificationsDebtorConfirmationState2;
})(VerificationsDebtorConfirmationState || {});
export const decodeVerificationsDebtorConfirmationState = (value) => value;
export const encodeVerificationsDebtorConfirmationState = (value) => value;
export var VerificationsDebtorConfirmationVerdict = /* @__PURE__ */ ((VerificationsDebtorConfirmationVerdict2) => {
  VerificationsDebtorConfirmationVerdict2["VerificationsDebtorConfirmationVerdictConfirmed"] = "confirmed";
  VerificationsDebtorConfirmationVerdict2["VerificationsDebtorConfirmationVerdictCorrection"] = "correction";
  VerificationsDebtorConfirmationVerdict2["VerificationsDebtorConfirmationVerdictUnparsed"] = "unparsed";
  return VerificationsDebtorConfirmationVerdict2;
})(VerificationsDebtorConfirmationVerdict || {});
export const decodeVerificationsDebtorConfirmationVerdict = (value) => value;
export const encodeVerificationsDebtorConfirmationVerdict = (value) => value;
export function decodeVerificationsLogin(json) {
  return {
    login_id: optionalDecoder(assertNumber)(json.login_id),
    login_user: optionalDecoder(assertString)(json.login_user),
    debtor_id: assertNumber(json.debtor_id),
    debtor_name: assertString(json.debtor_name)
  };
}
export function encodeVerificationsLogin(value) {
  return {
    login_id: value.login_id,
    login_user: value.login_user,
    debtor_id: value.debtor_id,
    debtor_name: value.debtor_name
  };
}
export var VerificationsMode = /* @__PURE__ */ ((VerificationsMode2) => {
  VerificationsMode2["VerificationsModeFull"] = "full";
  VerificationsMode2["VerificationsModeTest"] = "test";
  VerificationsMode2["VerificationsModeRestricted"] = "restricted";
  return VerificationsMode2;
})(VerificationsMode || {});
export const decodeVerificationsMode = (value) => value;
export const encodeVerificationsMode = (value) => value;
export function decodeVerifyDebtorBatchIdGroup(json) {
  return {
    debtor_batch_id: assertNumber(json.debtor_batch_id),
    item_ids: (json.item_ids || []).map(assertNumber)
  };
}
export function encodeVerifyDebtorBatchIdGroup(value) {
  return {
    debtor_batch_id: value.debtor_batch_id,
    item_ids: value.item_ids
  };
}
export function decodeVerifyDebtorBatchIdGroups(json) {
  return {
    groups: (json.groups || []).map(decodeVerifyDebtorBatchIdGroup)
  };
}
export function encodeVerifyDebtorBatchIdGroups(value) {
  return {
    groups: value.groups.map(encodeVerifyDebtorBatchIdGroup)
  };
}
export var VerifyLevel = /* @__PURE__ */ ((VerifyLevel2) => {
  VerifyLevel2["VerifyLevelUndefined"] = "undefined";
  VerifyLevel2["VerifyLevelLow"] = "low";
  VerifyLevel2["VerifyLevelMedium"] = "medium";
  VerifyLevel2["VerifyLevelHigh"] = "high";
  return VerifyLevel2;
})(VerifyLevel || {});
export const decodeVerifyLevel = (value) => value;
export const encodeVerifyLevel = (value) => value;
export var VerifyLevelReason = /* @__PURE__ */ ((VerifyLevelReason2) => {
  VerifyLevelReason2["VerifyLevelReasonUnknown"] = "unknown";
  VerifyLevelReason2["VerifyLevelReasonDefault"] = "default";
  VerifyLevelReason2["VerifyLevelReasonCarrierVerificationRequired"] = "carrier_verification_required";
  VerifyLevelReason2["VerifyLevelReasonClientThresholdExceeded"] = "client_threshold_exceeded";
  VerifyLevelReason2["VerifyLevelReasonFailedVerification"] = "failed_verification";
  VerifyLevelReason2["VerifyLevelReasonNewCarrier"] = "new_carrier";
  VerifyLevelReason2["VerifyLevelReasonNoticeOfAssignment"] = "notice_of_assignment";
  VerifyLevelReason2["VerifyLevelReasonTopInvoiceAmountPercentile"] = "top_invoice_amount_percentile";
  VerifyLevelReason2["VerifyLevelReasonWarnAmountExceeded"] = "warn_amount_exceeded";
  VerifyLevelReason2["VerifyLevelReasonAbnormalLaneRate"] = "abnormal_lane_rate";
  VerifyLevelReason2["VerifyLevelReasonIgnoredNoClientDebtorOnBol"] = "ignored_no_client_debtor_on_bol";
  VerifyLevelReason2["VerifyLevelReasonStaleDeliveryDate"] = "stale_delivery_date";
  return VerifyLevelReason2;
})(VerifyLevelReason || {});
export const decodeVerifyLevelReason = (value) => value;
export const encodeVerifyLevelReason = (value) => value;
export function decodeVisionOneGroupAccountInput(json) {
  return {
    account_id: assertNumber(json.account_id)
  };
}
export function encodeVisionOneGroupAccountInput(value) {
  return {
    account_id: value.account_id
  };
}
export function decodeVisionOneGroupBackfillInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    as_of_date: assertString(json.as_of_date)
  };
}
export function encodeVisionOneGroupBackfillInput(value) {
  return {
    account_id: value.account_id,
    as_of_date: value.as_of_date
  };
}
export function decodeVisionOneGroupReplayInput(json) {
  return {
    account_id: assertNumber(json.account_id),
    starting_from: assertString(json.starting_from),
    ending_on: assertString(json.ending_on),
    after_review_id: assertInt64(json.after_review_id)
  };
}
export function encodeVisionOneGroupReplayInput(value) {
  return {
    account_id: value.account_id,
    starting_from: value.starting_from,
    ending_on: value.ending_on,
    after_review_id: value.after_review_id
  };
}
export function decodeVisionOneGroupReplayOutput(json) {
  return {
    last_review_id: assertInt64(json.last_review_id),
    done: assertBoolean(json.done)
  };
}
export function encodeVisionOneGroupReplayOutput(value) {
  return {
    last_review_id: value.last_review_id,
    done: value.done
  };
}
export var WorkflowEventInitiatorType = /* @__PURE__ */ ((WorkflowEventInitiatorType2) => {
  WorkflowEventInitiatorType2["WorkflowEventInitiatorTypeRoot"] = "root";
  WorkflowEventInitiatorType2["WorkflowEventInitiatorTypeWorkflow"] = "workflow";
  WorkflowEventInitiatorType2["WorkflowEventInitiatorTypeActivity"] = "activity";
  WorkflowEventInitiatorType2["WorkflowEventInitiatorTypeSignal"] = "signal";
  WorkflowEventInitiatorType2["WorkflowEventInitiatorTypeSweeper"] = "sweeper";
  WorkflowEventInitiatorType2["WorkflowEventInitiatorTypeAdminUser"] = "admin_user";
  WorkflowEventInitiatorType2["WorkflowEventInitiatorTypeTimer"] = "timer";
  return WorkflowEventInitiatorType2;
})(WorkflowEventInitiatorType || {});
export const decodeWorkflowEventInitiatorType = (value) => value;
export const encodeWorkflowEventInitiatorType = (value) => value;
export function decodeWorkflowJobArgs(json) {
  return {
    workflow_id: assertInt64(json.workflow_id),
    retry_until: optionalDecoder(assertInt64)(json.retry_until),
    trigger: decodeWorkflowJobArgs_Trigger(json)
  };
}
export function encodeWorkflowJobArgs(value) {
  return __spreadValues({
    workflow_id: value.workflow_id,
    retry_until: value.retry_until
  }, encodeWorkflowJobArgs_Trigger(value.trigger));
}
export var WorkflowJobArgs_TriggerType = /* @__PURE__ */ ((WorkflowJobArgs_TriggerType2) => {
  WorkflowJobArgs_TriggerType2["FromRoot"] = "from_root";
  WorkflowJobArgs_TriggerType2["FromParent"] = "from_parent";
  WorkflowJobArgs_TriggerType2["FromSignal"] = "from_signal";
  WorkflowJobArgs_TriggerType2["FromFinalChild"] = "from_final_child";
  WorkflowJobArgs_TriggerType2["FromSweep"] = "from_sweep";
  WorkflowJobArgs_TriggerType2["FromAdmin"] = "from_admin";
  WorkflowJobArgs_TriggerType2["FromTimer"] = "from_timer";
  return WorkflowJobArgs_TriggerType2;
})(WorkflowJobArgs_TriggerType || {});
export function decodeWorkflowJobArgs_Trigger(json) {
  if (json.from_root !== void 0) {
    return {
      type: "from_root" /* FromRoot */,
      from_root: decodeWorkflowJobArgs_FromRoot(json.from_root)
    };
  } else if (json.from_parent !== void 0) {
    return {
      type: "from_parent" /* FromParent */,
      from_parent: decodeWorkflowJobArgs_FromParent(json.from_parent)
    };
  } else if (json.from_signal !== void 0) {
    return {
      type: "from_signal" /* FromSignal */,
      from_signal: decodeWorkflowJobArgs_FromSignal(json.from_signal)
    };
  } else if (json.from_final_child !== void 0) {
    return {
      type: "from_final_child" /* FromFinalChild */,
      from_final_child: decodeWorkflowJobArgs_FromFinalChild(
        json.from_final_child
      )
    };
  } else if (json.from_sweep !== void 0) {
    return {
      type: "from_sweep" /* FromSweep */,
      from_sweep: decodeWorkflowJobArgs_FromSweep(json.from_sweep)
    };
  } else if (json.from_admin !== void 0) {
    return {
      type: "from_admin" /* FromAdmin */,
      from_admin: decodeWorkflowJobArgs_FromAdmin(json.from_admin)
    };
  } else if (json.from_timer !== void 0) {
    return {
      type: "from_timer" /* FromTimer */,
      from_timer: decodeWorkflowJobArgs_FromTimer(json.from_timer)
    };
  }
  throw new Error("No field set in oneof WorkflowJobArgs_Trigger");
}
export function encodeWorkflowJobArgs_Trigger(value) {
  switch (value.type) {
    case "from_root" /* FromRoot */:
      return { from_root: encodeWorkflowJobArgs_FromRoot(value.from_root) };
    case "from_parent" /* FromParent */:
      return {
        from_parent: encodeWorkflowJobArgs_FromParent(value.from_parent)
      };
    case "from_signal" /* FromSignal */:
      return {
        from_signal: encodeWorkflowJobArgs_FromSignal(value.from_signal)
      };
    case "from_final_child" /* FromFinalChild */:
      return {
        from_final_child: encodeWorkflowJobArgs_FromFinalChild(
          value.from_final_child
        )
      };
    case "from_sweep" /* FromSweep */:
      return { from_sweep: encodeWorkflowJobArgs_FromSweep(value.from_sweep) };
    case "from_admin" /* FromAdmin */:
      return { from_admin: encodeWorkflowJobArgs_FromAdmin(value.from_admin) };
    case "from_timer" /* FromTimer */:
      return { from_timer: encodeWorkflowJobArgs_FromTimer(value.from_timer) };
    default:
      throw new Error("No field set in oneof WorkflowJobArgs_Trigger");
  }
}
export function decodeWorkflowJobArgs_FromAdmin(json) {
  return {
    admin_user_id: assertNumber(json.admin_user_id)
  };
}
export function encodeWorkflowJobArgs_FromAdmin(value) {
  return {
    admin_user_id: value.admin_user_id
  };
}
export function decodeWorkflowJobArgs_FromFinalChild(json) {
  return {
    retry_count: assertNumber(json.retry_count),
    child: decodeWorkflowJobArgs_FromFinalChild_Child(json)
  };
}
export function encodeWorkflowJobArgs_FromFinalChild(value) {
  return __spreadValues({
    retry_count: value.retry_count
  }, encodeWorkflowJobArgs_FromFinalChild_Child(value.child));
}
export var WorkflowJobArgs_FromFinalChild_ChildType = /* @__PURE__ */ ((WorkflowJobArgs_FromFinalChild_ChildType2) => {
  WorkflowJobArgs_FromFinalChild_ChildType2["ChildWorkflow"] = "child_workflow";
  WorkflowJobArgs_FromFinalChild_ChildType2["ChildActivity"] = "child_activity";
  return WorkflowJobArgs_FromFinalChild_ChildType2;
})(WorkflowJobArgs_FromFinalChild_ChildType || {});
export function decodeWorkflowJobArgs_FromFinalChild_Child(json) {
  if (json.child_workflow !== void 0) {
    return {
      type: "child_workflow" /* ChildWorkflow */,
      child_workflow: decodeWorkflowJobArgs_FromFinalChild_ChildWorkflow(
        json.child_workflow
      )
    };
  } else if (json.child_activity !== void 0) {
    return {
      type: "child_activity" /* ChildActivity */,
      child_activity: decodeWorkflowJobArgs_FromFinalChild_ChildActivity(
        json.child_activity
      )
    };
  }
  throw new Error("No field set in oneof WorkflowJobArgs_FromFinalChild_Child");
}
export function encodeWorkflowJobArgs_FromFinalChild_Child(value) {
  switch (value.type) {
    case "child_workflow" /* ChildWorkflow */:
      return {
        child_workflow: encodeWorkflowJobArgs_FromFinalChild_ChildWorkflow(
          value.child_workflow
        )
      };
    case "child_activity" /* ChildActivity */:
      return {
        child_activity: encodeWorkflowJobArgs_FromFinalChild_ChildActivity(
          value.child_activity
        )
      };
    default:
      throw new Error(
        "No field set in oneof WorkflowJobArgs_FromFinalChild_Child"
      );
  }
}
export function decodeWorkflowJobArgs_FromFinalChild_ChildActivity(json) {
  return {
    sequence: assertNumber(json.sequence)
  };
}
export function encodeWorkflowJobArgs_FromFinalChild_ChildActivity(value) {
  return {
    sequence: value.sequence
  };
}
export function decodeWorkflowJobArgs_FromFinalChild_ChildWorkflow(json) {
  return {
    workflow_id: assertInt64(json.workflow_id)
  };
}
export function encodeWorkflowJobArgs_FromFinalChild_ChildWorkflow(value) {
  return {
    workflow_id: value.workflow_id
  };
}
export function decodeWorkflowJobArgs_FromParent(json) {
  return {
    initiated_by_workflow_id: assertInt64(json.initiated_by_workflow_id)
  };
}
export function encodeWorkflowJobArgs_FromParent(value) {
  return {
    initiated_by_workflow_id: value.initiated_by_workflow_id
  };
}
export function decodeWorkflowJobArgs_FromRoot(json) {
  return {};
}
export function encodeWorkflowJobArgs_FromRoot(value) {
  return {};
}
export function decodeWorkflowJobArgs_FromSignal(json) {
  return {
    signal_number: assertNumber(json.signal_number)
  };
}
export function encodeWorkflowJobArgs_FromSignal(value) {
  return {
    signal_number: value.signal_number
  };
}
export function decodeWorkflowJobArgs_FromSweep(json) {
  return {};
}
export function encodeWorkflowJobArgs_FromSweep(value) {
  return {};
}
export function decodeWorkflowJobArgs_FromTimer(json) {
  return {
    sequence: assertNumber(json.sequence)
  };
}
export function encodeWorkflowJobArgs_FromTimer(value) {
  return {
    sequence: value.sequence
  };
}
export function decodeWorkflowShowActivity(json) {
  return {
    sequence: assertNumber(json.sequence),
    activity_class_name: assertString(json.activity_class_name),
    status: assertString(json.status),
    queue: assertString(json.queue),
    attempts: assertNumber(json.attempts),
    max_attempts: assertNumber(json.max_attempts),
    created_at: assertString(json.created_at),
    url: assertString(json.url),
    started_at: optionalDecoder(assertString)(json.started_at),
    completed_at: optionalDecoder(assertString)(json.completed_at),
    elapsed: optionalDecoder(assertString)(json.elapsed)
  };
}
export function encodeWorkflowShowActivity(value) {
  return {
    sequence: value.sequence,
    activity_class_name: value.activity_class_name,
    status: value.status,
    queue: value.queue,
    attempts: value.attempts,
    max_attempts: value.max_attempts,
    created_at: value.created_at,
    url: value.url,
    started_at: value.started_at,
    completed_at: value.completed_at,
    elapsed: value.elapsed
  };
}
export function decodeWorkflowShowEvent(json) {
  return {
    number: assertNumber(json.number),
    event_type: assertString(json.event_type),
    initiated_by_workflow_id: optionalDecoder(assertNumber)(
      json.initiated_by_workflow_id
    ),
    initiated_by_activity_sequence: optionalDecoder(assertNumber)(
      json.initiated_by_activity_sequence
    ),
    message: optionalDecoder(assertString)(json.message),
    error: optionalDecoder(assertString)(json.error),
    created_at: assertString(json.created_at),
    duration: optionalDecoder(assertString)(json.duration),
    initiator_type: optionalDecoder(decodeWorkflowEventInitiatorType)(
      json.initiator_type
    ),
    admin_user_id: optionalDecoder(assertNumber)(json.admin_user_id)
  };
}
export function encodeWorkflowShowEvent(value) {
  return {
    number: value.number,
    event_type: value.event_type,
    initiated_by_workflow_id: value.initiated_by_workflow_id,
    initiated_by_activity_sequence: value.initiated_by_activity_sequence,
    message: value.message,
    error: value.error,
    created_at: value.created_at,
    duration: value.duration,
    initiator_type: optionalEncoder(encodeWorkflowEventInitiatorType)(
      value.initiator_type
    ),
    admin_user_id: value.admin_user_id
  };
}
export function decodeWorkflowShowRecord(json) {
  return {
    id: assertNumber(json.id),
    handle: optionalDecoder(assertString)(json.handle),
    workflow_class_name: assertString(json.workflow_class_name),
    status: assertString(json.status),
    queue: assertString(json.queue),
    parent_id: optionalDecoder(assertNumber)(json.parent_id),
    sequence: optionalDecoder(assertNumber)(json.sequence),
    wakeups: assertNumber(json.wakeups),
    locked_until: optionalDecoder(assertString)(json.locked_until),
    completed_at: optionalDecoder(assertString)(json.completed_at),
    created_at: assertString(json.created_at),
    updated_at: assertString(json.updated_at),
    input_type: optionalDecoder(assertString)(json.input_type),
    input_json: optionalDecoder(assertString)(json.input_json),
    output_type: optionalDecoder(assertString)(json.output_type),
    output_json: optionalDecoder(assertString)(json.output_json),
    url: assertString(json.url)
  };
}
export function encodeWorkflowShowRecord(value) {
  return {
    id: value.id,
    handle: value.handle,
    workflow_class_name: value.workflow_class_name,
    status: value.status,
    queue: value.queue,
    parent_id: value.parent_id,
    sequence: value.sequence,
    wakeups: value.wakeups,
    locked_until: value.locked_until,
    completed_at: value.completed_at,
    created_at: value.created_at,
    updated_at: value.updated_at,
    input_type: value.input_type,
    input_json: value.input_json,
    output_type: value.output_type,
    output_json: value.output_json,
    url: value.url
  };
}
export function decodeWorkflowShowRequest(json) {
  return {
    workflow_id: assertNumber(json.workflow_id)
  };
}
export function encodeWorkflowShowRequest(value) {
  return {
    workflow_id: value.workflow_id
  };
}
export function decodeWorkflowShowResponse(json) {
  return {
    workflow: decodeWorkflowShowRecord(json.workflow),
    children: (json.children || []).map(decodeWorkflowShowRecord),
    activities: (json.activities || []).map(decodeWorkflowShowActivity),
    events: (json.events || []).map(decodeWorkflowShowEvent),
    signals: (json.signals || []).map(decodeWorkflowShowSignal),
    timeline_items: (json.timeline_items || []).map(decodeWorkflowTimelineItem),
    timeline_groups: (json.timeline_groups || []).map(
      decodeWorkflowTimelineGroup
    ),
    terminal: assertBoolean(json.terminal)
  };
}
export function encodeWorkflowShowResponse(value) {
  return {
    workflow: encodeWorkflowShowRecord(value.workflow),
    children: value.children.map(encodeWorkflowShowRecord),
    activities: value.activities.map(encodeWorkflowShowActivity),
    events: value.events.map(encodeWorkflowShowEvent),
    signals: value.signals.map(encodeWorkflowShowSignal),
    timeline_items: value.timeline_items.map(encodeWorkflowTimelineItem),
    timeline_groups: value.timeline_groups.map(encodeWorkflowTimelineGroup),
    terminal: value.terminal
  };
}
export function decodeWorkflowShowSignal(json) {
  return {
    id: assertNumber(json.id),
    signal_type: assertString(json.signal_type),
    data_json: assertString(json.data_json),
    created_at: assertString(json.created_at)
  };
}
export function encodeWorkflowShowSignal(value) {
  return {
    id: value.id,
    signal_type: value.signal_type,
    data_json: value.data_json,
    created_at: value.created_at
  };
}
export var WorkflowTimelineClass = /* @__PURE__ */ ((WorkflowTimelineClass2) => {
  WorkflowTimelineClass2["WorkflowTimelineClassWorkflowRunning"] = "workflow-running";
  WorkflowTimelineClass2["WorkflowTimelineClassWorkflowCompleted"] = "workflow-completed";
  WorkflowTimelineClass2["WorkflowTimelineClassWorkflowFailed"] = "workflow-failed";
  WorkflowTimelineClass2["WorkflowTimelineClassWorkflowPending"] = "workflow-pending";
  WorkflowTimelineClass2["WorkflowTimelineClassWorkflowSuspended"] = "workflow-suspended";
  WorkflowTimelineClass2["WorkflowTimelineClassWorkflowWaitingSignal"] = "workflow-waiting-signal";
  WorkflowTimelineClass2["WorkflowTimelineClassWorkflowWaitingChildren"] = "workflow-waiting-children";
  WorkflowTimelineClass2["WorkflowTimelineClassActivityRunning"] = "activity-running";
  WorkflowTimelineClass2["WorkflowTimelineClassActivityCompleted"] = "activity-completed";
  WorkflowTimelineClass2["WorkflowTimelineClassActivityFailed"] = "activity-failed";
  WorkflowTimelineClass2["WorkflowTimelineClassActivityAbandoned"] = "activity-abandoned";
  return WorkflowTimelineClass2;
})(WorkflowTimelineClass || {});
export const decodeWorkflowTimelineClass = (value) => value;
export const encodeWorkflowTimelineClass = (value) => value;
export function decodeWorkflowTimelineGroup(json) {
  return {
    id: assertString(json.id),
    content: assertString(json.content),
    order: assertNumber(json.order)
  };
}
export function encodeWorkflowTimelineGroup(value) {
  return {
    id: value.id,
    content: value.content,
    order: value.order
  };
}
export function decodeWorkflowTimelineItem(json) {
  return {
    id: assertNumber(json.id),
    content: assertString(json.content),
    title: assertString(json.title),
    start_ms: assertInt64(json.start_ms),
    end_ms: assertInt64(json.end_ms),
    group: assertString(json.group),
    class_enum: decodeWorkflowTimelineClass(json.class_enum),
    url: assertString(json.url)
  };
}
export function encodeWorkflowTimelineItem(value) {
  return {
    id: value.id,
    content: value.content,
    title: value.title,
    start_ms: value.start_ms,
    end_ms: value.end_ms,
    group: value.group,
    class_enum: encodeWorkflowTimelineClass(value.class_enum),
    url: value.url
  };
}
export function decodeWorkflowTimelineSegment(json) {
  return {
    start_ms: assertInt64(json.start_ms),
    end_ms: assertInt64(json.end_ms),
    class_enum: decodeWorkflowTimelineClass(json.class_enum)
  };
}
export function encodeWorkflowTimelineSegment(value) {
  return {
    start_ms: value.start_ms,
    end_ms: value.end_ms,
    class_enum: encodeWorkflowTimelineClass(value.class_enum)
  };
}
